import ErrorBoundary from './components/ErrorBoundary';
import { fetchStartupRead, getStartupReadGeneration, invalidateStartupReads, setStartupReadScope } from './api/startupRequests';
import DcmaCrrWorkflow from './components/DcmaCrrWorkflow';
import { fetchWithChatFeedback } from './api/chatFeedbackRequired';
import { Dialog } from './components/FarChat/ChatFeedbackOverlays';
import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo, lazy, Suspense } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { createAppTheme } from './theme';
import { ColorModeProvider, useColorMode } from './theme/ColorModeContext';
import { LazyMotion, MotionConfig } from 'motion/react';
import { RESPECT_REDUCED_MOTION } from './theme/motion';
import CssBaseline from '@mui/material/CssBaseline';
import {
  Alert,
  Snackbar,
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Link,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Tooltip,
  Badge,
  CircularProgress,
  Stack,
  FormControlLabel,
  Radio,
  RadioGroup,
  type PaperProps,
} from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import StageWrapper from './components/StageWrapper';
import { fetchStageRatings, WORKFLOW_STAGE_BASE } from './api/stageRatingFunctions';
import type { RatingValue } from './api/stageRatingFunctions';
import { Transition } from './components/motion';
import { ToastProvider } from './components/Toast/ToastProvider';
import EmailGate from './components/EmailGate';
import { clearProjectTrackerProtectedState } from './api/projectTrackerFunctions';
import WorkflowTypeSelectorPage, { WorkflowTypeSelectorPageHandle } from './pages/WorkflowTypeSelectorPage';
import type { TrainingFeedbackContext } from './training/feedbackContext';
import { TrainingEntryTracker, type TrainingEntrySource } from './training/trainingEntry';
import { recordTrainingEntry } from './api/trainingEntryFunctions';
import type { WorkflowType } from './components/WorkflowTypeGrid';
import { clearDcmaPbpBrowserDrafts } from './utils/dcmaPbpBrowserDrafts';
import { clearUsageDashboardProtectedSessionCaches } from './utils/usageDashboardProtectedSessionCaches';
import { isWorkflowReadOnly } from './utils/workflowAccess';
import OtherTransactionsWorkflow, { type OtherTransactionsDisplayStage } from './components/OtherTransactionsWorkflow';
import { OTHER_TRANSACTIONS_STAGES } from './constants/otherTransactionsStages';
import {
  checkAdminStatus,
  clearUsageDashboardProtectedClientState,
  fetchPendingRequests,
  fetchUnreadFeedbackCount,
} from './api/adminFunctions';
import { verifySessionOrShowGate } from './api/apiClient';
import { fetchSupportIssueUnreadCount } from './api/supportIssues';
import Footer from './components/Footer';
import FeedbackAttachmentPicker from './components/FeedbackAttachmentPicker';
import GlobalNavbar from './components/GlobalNavbar';
import { GLOBAL_NAVBAR_HEIGHT } from './components/globalNavbarLayout';
import { useContactChatInbox } from './contact-chat/useContactChatInbox';
import { usePeopleChatNavigation } from './contact-chat/usePeopleChatNavigation';
import WorkflowChatCompanion from './components/HomeChat/WorkflowChatCompanion';
import { DocumentAssistantProvider, useDocumentAssistant, useDocumentNavigationState } from './components/HomeChat/DocumentAssistantContext';
import MandatoryChatFeedbackGate from './components/FarChat/MandatoryChatFeedbackGate';
import { getPostAwardWorkflowChatPageContext, getMarketResearchWorkflowChatPageContext } from './components/HomeChat/workflowChatPageContext';
import {
  getWorkflowCompanionSession,
  setWorkflowCompanionSession,
} from './components/HomeChat/api';
import {
  attributedHomeChatSessionId,
  createSerializedWorkflowCompanionWriter,
  resolveWorkflowChatSessionId,
} from './components/HomeChat/workflowChatSession';
import type {
  HomeChatHandoffContext,
  HomeChatPageContext,
  WorkflowChatSessionChangeOptions,
  WorkflowNavigationCheckpoint,
  WorkflowRecommendationType,
  WorkflowRecommendationEntryMode,
} from './components/HomeChat/types';
import { navigateFromHomeChat } from './api/homeChatNavigation';
import { fetchGpcCompletion, resolveGpcCompletionMetadata } from './api/gpcCompletion';
import { restoreGpcWorkflow, resolveGpcStageRequirement } from './utils/gpcWorkflowRestore';
import type { GpcRequiredFieldsValidation } from './components/GpcRequiredFields';
import ChangelogDialog from './components/ChangelogDialog';
import WorkflowAccessDialog, { WorkflowAccessState } from './components/WorkflowAccessDialog';
import AccountChangelogMenuItem from './components/AccountChangelogMenuItem';
import {
  canShowRolePreviewControl,
  getPresentationCapabilities,
  ROLE_PREVIEW_OPTIONS,
  type RolePreview,
} from './roles/rolePreview';
import { getServerRolePreview, setServerRolePreview } from './api/rolePreview';
import {
  type ActiveFlowRouteMetadata,
  activeFlowRouteSignature, constructionHasStage, constructionPathKeys,
  resumeOneTimeConstructionProjectedStage,
} from './utils/constructionRouteProjection';
import type { RequirementDocumentMetadata } from './api/stageFunctions';
import { shouldArmFirstProductDiscoveryEntry } from './utils/productDiscoveryEntry';
import { getMarketResearchBrandNameJa } from './api/marketResearchBrandNameJaFunctions';
import type { CodeMethod } from './utils/inferCodeMethod';
import type { MarketResearchCoordinationProduct } from './stages/MarketResearchDd2579';
import type { MappedClause } from './stages/CLSResults';
import {
  getSOWDocuments,
  getSOWVehicleContext,
} from './api/sowVerificationFunctions';
import type { GPCSalientCharacteristicsHandle } from './stages/GPCSalientCharacteristics';
// GPC per-form stages (S3b–S11). Mandatory Source (S3b, 803, Path B only) is the
// dedicated first post-Product-Discovery stage; eCARS (S8) reuses GPCApprovalRouting.
import { reconcileSeededQuotes } from './utils/gpcSeedQuotes';
import type {
  GpcBuyParams,
  GpcPriceQuote,
  GpcThresholdResult,
  GpcSourceDecision,
  GpcSourceStatus,
  GpcCarpetIiCheck,
  GpcNaicsPsc,
  GpcVendor,
  GpcApproval,
  Gpc889Representation as Gpc889RepresentationData,
  GpcAbstract as GpcAbstractData,
  GpcDd2579 as GpcDd2579Data,
  GpcAfForm9 as GpcAfForm9Data,
  GpcTaxExempt as GpcTaxExemptData,
  GpcReceipt as GpcReceiptData,
  GpcIuid as GpcIuidData,
  GpcFormStageKey,
} from './api/gpcFunctions';
import {
  applicableGpcFormStages,
  gpcDistinctSourceCount,
  allGpcFormStages,
  gpcDisabledStageIndices,
  gpcDisabledStageReasonMap,
  isGpcStageEnabled,
  nextEnabledGpcStage,
  prevEnabledGpcStage,
  GPC_FORM_STAGE_BASE_OFFSET,
} from './api/gpcFunctions';
import { getRawStageData } from './utils/cacheUtils';
import {
  getMarketResearchDd2579Coordination,
  type MarketResearchDd2579Coordination,
} from './api/marketResearchDd2579Functions';
import {
  restoreMarketResearchDd2579StateFromStageOutput,
  type MarketResearchDd2579Form,
  type RestoredMarketResearchDd2579State,
} from './api/marketResearchDd2579FormFunctions';
import type { FOPRData } from './api/foprFunctions';
import { getFOPRData } from './api/foprFunctions';
import type { ServicesComboData } from './api/servicesComboFunctions';
import { getServicesComboData } from './api/servicesComboFunctions';
import { servicesStages, complexServicesStages, postAwardStages } from './constants/workflowStages';
import {
  oneTimeConstructionStages,
  oneTimeConstructionPriceEstimatorStages,
  generationConstructionStages,
  oneTimeConstructionGenerationStages,
  oneTimeConstructionGenerationPriceEstimatorStages,
  standaloneOneTimeConstructionPriceEstimatorStages,
  resolveOneTimeConstructionEditReturnStage,
} from './constants/oneTimeConstructionStages';
import { servicesPwsGenerationStages } from './constants/servicesPwsGenerationStages';
import {
  COMPLEX_SERVICES_ARTIFACT_STAGE_COMPONENTS,
} from './constants/complexServicesArtifactStages';
import {
  applicableComplexStages,
  complexDisabledStageIndices,
  complexDisabledStageReasonMap,
  isComplexStageEnabled,
  nextEnabledComplexStage,
  prevEnabledComplexStage,
  lastEnabledComplexStageAtOrBelow,
  COMPLEX_SERVICES_STAGE_ORDER,
  COMPLEX_SERVICES_STAGE_CODE,
  isComplexServicesFlowComplete,
  getComplexServicesIge,
  getComplexServicesIgd,
  getComplexServicesMrr,
  getComplexServicesForm9,
  getComplexServicesPwsDiff,
  getComplexServicesOptions,
  getComplexServicesSolicitation,
  getComplexServicesClearance,
  getComplexServicesRecord,
  type AcquisitionMode,
  type SatThreshold,
  type PopStructure,
  type ComplexStageKey,
} from './api/complexServicesFunctions';
import { completeComplexServicesRecord } from './utils/complexServicesCompletion';
import { 
  StageConfig,
  MFR,
  Product,
  ProductWithPSC,
  ProductWithCodes,
  ProductWithVendors,
  PipelineSession,
  Question,
} from './types/pipeline';
import { CLSCorrectionResult, CLSContractParams } from './types/cls';
import { saveAnswers as saveStageAnswers, updateCurrentStage, getStageStatus, submitFeedback } from './api/stageFunctions';
import {
  FeedbackSourceTracker,
  trainingFeedbackSource,
  type FeedbackSourceConfidence,
  type FeedbackSourceTuple,
  type HomepageSourceArea,
  type UserFeedbackSourceArea,
} from './feedback/feedbackSourceContext';
import type { ProductSelectionBrandNameRecord, ProductSelectionResponse } from './api/stageFunctions';
import type {
  BrandNameJaCompletion,
  BrandNameJaProjection,
} from './types/marketResearchBrandNameJa';
import {
  getPreviousApplicableMarketResearchStage,
  getCustomerMarketResearchStages,
  MARKET_RESEARCH_STAGE_DESCRIPTORS,
  type MarketResearchStageApplicability,
  type MarketResearchStageCode,
} from './constants/marketResearchStages';
import { isMarketResearchBrandNameJaEnabled } from './features/marketResearchBrandNameJa';
import MarketResearchNotifyControls from './components/MarketResearchNotifyControls';
import MarketResearchPostHandoffProgress from './components/MarketResearchPostHandoffProgress';
import { MarketResearchHandoffConversation } from './components/MarketResearchHandoffConversation';
import { MarketResearchEditActivity } from './components/MarketResearchEditActivity';
import {
  getMarketResearchHandoffAvailability,
  isMarketResearchHandoffAvailable,
  type MarketResearchHandoffState,
} from './api/marketResearchHandoffConversation';
import {
  acknowledgeMarketResearchStageEdit,
  getMarketResearchEditAwarenessSummary,
  type MarketResearchInlineEdit,
  type MarketResearchEditAwarenessSummary,
} from './api/marketResearchEditAwareness';
import {
  shouldAcknowledgeMarketResearchStageOnExit,
  type LoadedMarketResearchStageEdit,
  type MarketResearchStageRoute,
} from './utils/marketResearchEditAwarenessLifecycle';
import WorkflowNotificationsPanel from './components/WorkflowNotificationsPanel';
import { parseHomeChatMode, type HomeChatMode } from './components/HomeChat/homeChatMode';
import MarketResearchNotifyShareDialog from './components/MarketResearchNotifyShareDialog';
import type {
  MarketResearchNotificationContext,
  WorkflowNotificationKind,
} from './api/workflowNotifications';
import { fetchWorkflowNotificationUnreadCount } from './api/workflowNotifications';
import { tryFireBusinessImpact } from './utils/businessImpactTrigger';
import { hasNonEmptyBIAnswer } from './utils/businessImpact';
import { getCLSStageData } from './api/clsFunctions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import { normalizeProductsWithVendors } from './utils/vendorUtils';
import { normalizeStage0Signature, normalizeStage1Signature, normalizeStage2Signature, pscCodesToStrings, normalizeStage4Signature, normalizeStage5Signature, normalizeStage6Signature } from './utils/stageSignatures';
import { toUserFacingError, USER_FACING_ERROR_MESSAGES } from './utils/userFacingError';
import { mapMfrStageOutput } from './utils/mfrProjection';
import { loadRestorableStageData, restoredAnswersForStage } from './utils/stageRestore';

// Secondary views load only when their authorized branch is rendered.
const FlowSelector = lazy(() => import('./pages/FlowSelector'));
const TrainingApp = lazy(() => import('./training/TrainingApp'));
const ResourceAdvisorTraining = lazy(() => import('./ra-training/ResourceAdvisorTraining'));
const PostAwardActionWorkspace = lazy(() => import('./components/PostAwardActionWorkspace'));
const SupportIssuesPage = lazy(() => import('./pages/SupportIssuesPage'));
const ManageUsers = lazy(() => import('./pages/ManageUsers'));
const PeopleChats = lazy(() => import('./contact-chat/PeopleChats'));
const GenerateQuestions = lazy(() => import('./stages/GenerateQuestions'));
const GenerateMFR = lazy(() => import('./stages/GenerateMFR'));
const EnhancedProductDiscovery = lazy(() => import('./stages/EnhancedProductDiscovery'));
const MarketResearchBrandNameJa = lazy(() => import('./stages/MarketResearchBrandNameJa'));
const PSCCodeAssignment = lazy(() => import('./stages/PSCCodeAssignment'));
const NAICSCodeMapping = lazy(() => import('./stages/NAICSCodeMapping'));
const VendorDiscovery = lazy(() => import('./stages/VendorDiscovery'));
const SectionBGeneration = lazy(() => import('./stages/SectionBGeneration'));
const SectionCGeneration = lazy(() => import('./stages/SectionCGeneration'));
const SectionDGeneration = lazy(() => import('./stages/SectionDGeneration'));
const SectionEGeneration = lazy(() => import('./stages/SectionEGeneration'));
const SynopsisGeneration = lazy(() => import('./stages/SynopsisGeneration'));
const CLSDocumentUpload = lazy(() => import('./stages/CLSDocumentUpload'));
const CLSFinalStep = lazy(() => import('./stages/CLSFinalStep'));
const VendorBidsStage = lazy(() => import('./stages/VendorBidsStage'));
const SOWVerificationUpload = lazy(() => import('./stages/SOWVerificationUpload'));
const SOWVerificationResults = lazy(() => import('./stages/SOWVerificationResults'));
const SOWVerificationEdit = lazy(() => import('./stages/SOWVerificationEdit'));
const ConstructionRequirementIntake = lazy(() => import('./stages/ConstructionRequirementIntake'));
const ConstructionContextQuestions = lazy(() => import('./stages/ConstructionContextQuestions'));
const ServicesPwsRequirementIntake = lazy(() => import('./stages/ServicesPwsRequirementIntake'));
const ServicesPwsContextQuestions = lazy(() => import('./stages/ServicesPwsContextQuestions'));
const ServicesPwsGenerationEdit = lazy(() => import('./stages/ServicesPwsGenerationEdit'));
const WageDeterminationStage = lazy(() => import('./stages/WageDeterminationStage'));
const PNMStage = lazy(() => import('./stages/PNMStage'));
const FOPRUpload = lazy(() => import('./stages/FOPRUpload'));
const FOPRForm = lazy(() => import('./stages/FOPRForm'));
const ConstructionAbstractStage = lazy(() => import('./stages/ConstructionAbstractStage'));
const ServicesSOWUpload = lazy(() => import('./stages/ServicesSOWUpload'));
const ServicesContextQuestions = lazy(() => import('./stages/ServicesContextQuestions'));
const ServicesSuggestions = lazy(() => import('./stages/ServicesSuggestions'));
const ServicesSalientCharacteristics = lazy(() => import('./stages/ServicesSalientCharacteristics'));
const ServicesManualEdit = lazy(() => import('./stages/ServicesManualEdit'));
const ServicesCodeSelection = lazy(() => import('./stages/ServicesCodeSelection'));
const ServicesVendorDiscovery = lazy(() => import('./stages/ServicesVendorDiscovery'));
const ConstructionPriceEstimator = lazy(() => import('./stages/ConstructionPriceEstimator'));
const ServicesComboUpload = lazy(() => import('./stages/ServicesComboUpload'));
const ServicesComboForm = lazy(() => import('./stages/ServicesComboForm'));
const ComplexServicesCompliance = lazy(() => import('./stages/ComplexServicesCompliance'));
const ComplexServicesDd2579 = lazy(() => import('./stages/ComplexServicesDd2579'));
const ComplexServicesRecord = lazy(() => import('./stages/ComplexServicesRecord'));
const ComplexServicesPwsDiff = lazy(() => import('./stages/ComplexServicesPwsDiff'));
const ComplexServicesOptions = lazy(() => import('./stages/ComplexServicesOptions'));
const ComplexServicesReqval = lazy(() => import('./stages/ComplexServicesReqval'));
const ComplexServicesQasp = lazy(() => import('./stages/ComplexServicesQasp'));
const ComplexServicesSolicitation = lazy(() => import('./stages/ComplexServicesSolicitation'));
const ComplexServicesClearance = lazy(() => import('./stages/ComplexServicesClearance'));
const ComplexServicesIge = lazy(() => import('./stages/ComplexServicesIge'));
const GPCGenerateQuestions = lazy(() => import('./stages/GPCGenerateQuestions'));
const GPCProductDiscovery = lazy(() => import('./stages/GPCProductDiscovery'));
const GPCNaicsPscSelection = lazy(() => import('./stages/GPCNaicsPscSelection'));
const GPCQuoteCapture = lazy(() => import('./stages/GPCQuoteCapture'));
const GPCMandatorySource = lazy(() => import('./stages/GPCMandatorySource'));
const GPCApprovalRouting = lazy(() => import('./stages/GPCApprovalRouting'));
const GPC889Representation = lazy(() => import('./stages/GPC889Representation'));
const GPCAbstract = lazy(() => import('./stages/GPCAbstract'));
const GPCDd2579 = lazy(() => import('./stages/GPCDd2579'));
const GPCAfForm9 = lazy(() => import('./stages/GPCAfForm9'));
const GPCTaxExempt = lazy(() => import('./stages/GPCTaxExempt'));
const GPCReceipt = lazy(() => import('./stages/GPCReceipt'));
const GPCIuid = lazy(() => import('./stages/GPCIuid'));
const UsageDashboard = lazy(() => import('./pages/UsageDashboard'));
const DcmaPbpWorkflow = lazy(() => import('./components/DcmaPbpWorkflow'));
const CLSResults = lazy(() => import('./stages/CLSResults'));
const GPCSalientCharacteristics = lazy(() => import('./stages/GPCSalientCharacteristics'));
const MarketResearchDd2579 = lazy(() => import('./stages/MarketResearchDd2579'));

const loadMotionFeatures = () => import('motion/react').then(m => m.domAnimation);

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/** Drop every protected Dashboard cache before an authentication boundary. */
function clearUsageDashboardAuthenticationState(): void {
  clearProjectTrackerProtectedState();
  clearUsageDashboardProtectedClientState();
  clearUsageDashboardProtectedSessionCaches();
}

export const getWorkflowDisplayLabel = (workflowType: string): string => {
  const workflowLabels: Record<string, string> = {
    market_research: 'Market Research',
    clause_logic: 'Clause Logic',
    sow_verification: 'SOW Verification',
    construction_sow_generation: 'Construction SOW Generation',
    services_pws_generation: 'Services PWS Generation',
    wage_determination: 'Wage Determination',
    pnm: 'IGE vs Vendor',
    fopr: 'FOPR',
    services: 'One-time Services',
    one_time_construction: 'One-time Construction',
    services_combo: 'Services',
    gpc: 'GPC',
    complex_services: 'Complex Services',
    other_transactions: 'Other Transactions',
    post_award_actions: 'Post-award Actions',
    dcma_pbp: 'DCMA PBP Review',
    dcma_crr: 'Contract Receipt and Review',
  };
  return workflowLabels[workflowType] || workflowType;
};

/** Resume only a canonical OT stage code from server metadata. */
export const getOtherTransactionsResumeStage = (metadata: { currentStage?: number }): number => {
  const index = OTHER_TRANSACTIONS_STAGES.findIndex(stage => stage.code === metadata.currentStage);
  return index >= 0 ? index : 0;
};

/** Maps canonical post-award persistence markers to the three UI pages. */
export const getPostAwardResumeState = (metadata: {
  post_award_workspace_state?: string;
  post_award_active_stage?: number;
  completedStages?: number[];
  completed_stages?: number[];
}): { canHydrate: boolean; maxAvailableStage: number } => {
  const completedStages = metadata.completedStages || metadata.completed_stages || [];
  return {
    // A selector-created flow is intentionally empty until the first intake
    // submission. Do not ask the restore endpoint to hydrate that state.
    canHydrate: metadata.post_award_workspace_state === 'initialized',
    // Stage 1002 can contain an editable, saved draft before its separate
    // review acknowledgement completes the workflow. Resume that genuine
    // saved page without treating it as terminal completion.
    maxAvailableStage: metadata.post_award_active_stage === 1002 || completedStages.includes(1002)
      ? 2
      : completedStages.includes(1001) ? 1 : 0,
  };
};

/** A DCMA-only list is an exclusive server authorization, not a card filter. */
export const isDcmaOnlyWorkflowAccess = (
  allowedWorkflowTypes: string[] | null,
): boolean => Boolean(allowedWorkflowTypes?.length) && allowedWorkflowTypes!.every(type => type === 'dcma_pbp' || type === 'dcma_crr');

/**
 * Notification actions must reflect the presentation role while an admin is
 * previewing another role. The authenticated permission list remains only for
 * server authorization, never for deciding which actions the preview exposes.
 */
export const canAccessMarketResearchNotifications = (
  workflowPermissionsLoaded: boolean,
  presentationAllowedWorkflowTypes: string[] | null,
): boolean => workflowPermissionsLoaded && (
  presentationAllowedWorkflowTypes === null
  || presentationAllowedWorkflowTypes.includes('market_research')
);

/** The server decides whether the optional final Market Research artifact applies. */
export const isMarketResearchDd2579StageEnabled = (
  coordination: Pick<MarketResearchDd2579Coordination, 'requires_coordination' | 'dd2579_eligible' | 'applicability'> | null | undefined,
): boolean => coordination?.applicability
  ? coordination.applicability.status !== 'exempt'
  : Boolean(coordination?.requires_coordination && coordination.dd2579_eligible);

/**
 * Keep the completed DD2579 workflow mounted so its final PDF action remains
 * available and its stepper can still render any earlier stage the user opens.
 * The ordinary no-DD branch retains its existing completion screen.
 */
export const shouldRenderMarketResearchStage = (
  isComplete: boolean,
  _currentStage: number,
  dd2579Enabled: boolean,
): boolean => !isComplete || dd2579Enabled;

export type MarketResearchVendorBidsAction =
  | 'loading'
  | 'blocked'
  | 'complete-workflow'
  | 'continue-dd2579';

/** Keep the Stage 12 terminal action aligned with server-owned coordination state. */
export const getMarketResearchVendorBidsAction = (
  coordination: Pick<
    MarketResearchDd2579Coordination,
    'requires_coordination' | 'dd2579_eligible' | 'unknown_product_ids' | 'applicability'
  > | null | undefined,
): MarketResearchVendorBidsAction => {
  if (!coordination) return 'loading';
  if (coordination.applicability) {
    return coordination.applicability.status === 'exempt' || coordination.dd2579_eligible
      ? 'complete-workflow' : 'blocked';
  }
  if (coordination.requires_coordination && coordination.dd2579_eligible) {
    return 'continue-dd2579';
  }
  if (coordination.requires_coordination || (coordination.unknown_product_ids?.length ?? 0) > 0) {
    return 'blocked';
  }
  return 'complete-workflow';
};

type MarketResearchNavigationStage = StageConfig & { backendCode: MarketResearchStageCode };

export function buildMarketResearchNavigation(
  applicability: MarketResearchStageApplicability,
  brandNameJaFeatureEnabled = true,
): {
  stages: MarketResearchNavigationStage[];
  disabledStageIndices: number[];
  disabledStageReasons: Record<number, string>;
} {
  const descriptors = MARKET_RESEARCH_STAGE_DESCRIPTORS.filter(
    stage => (stage.backendCode !== 14 || brandNameJaFeatureEnabled)
      && (stage.backendCode !== 13 || applicability.dd2579Required),
  );
  const stages = descriptors.map((stage, index) => ({
    id: stage.id,
    name: stage.name,
    description: stage.description,
    backendCode: stage.backendCode,
    prevStage: descriptors[index - 1]?.id,
    nextStage: descriptors[index + 1]?.id,
  }));
  const brandNameIndex = descriptors.findIndex(stage => stage.backendCode === 14);
  const disabledStageIndices = !applicability.brandNameJaRequired && brandNameIndex >= 0
    ? [brandNameIndex]
    : [];
  return {
    stages,
    disabledStageIndices,
    disabledStageReasons: disabledStageIndices.length
      ? { [brandNameIndex]: 'Not required' }
      : {},
  };
}

export function marketResearchBackendCodeToUiIndex(
  backendCode: number,
  dd2579Required: boolean,
  brandNameJaFeatureEnabled = true,
): number {
  const navigation = buildMarketResearchNavigation({
    brandNameJaRequired: true,
    dd2579Required,
  }, brandNameJaFeatureEnabled);
  const index = navigation.stages.findIndex(stage => stage.backendCode === backendCode);
  return index >= 0 ? index : 0;
}

export function marketResearchUiIndexToBackendCode(
  uiIndex: number,
  dd2579Required: boolean,
  brandNameJaFeatureEnabled = true,
): MarketResearchStageCode | undefined {
  return buildMarketResearchNavigation({
    brandNameJaRequired: true,
    dd2579Required,
  }, brandNameJaFeatureEnabled).stages[uiIndex]?.backendCode;
}

/**
 * Restore the data the reopened page needs before older, unrelated caches.
 *
 * Stage cache requests are issued together, but applying them strictly in
 * numeric order made a slow early-stage response hold a later saved page
 * hostage.  Keep each visible page's real data dependency ordered (for
 * example, Product Discovery before PSC), then let historical hydration
 * continue without delaying the restored page.  Processing status is still
 * checked before *any* cached data is applied.
 */
export function prioritizeMarketResearchRestoreStages(
  completedStageCodes: readonly number[],
  currentBackendStage: number,
): number[] {
  const requiredForVisibleStage: Record<number, readonly number[]> = {
    1: [1],
    2: [1],
    3: [3],
    4: [3, 4],
    5: [3, 4, 5],
    // Stage 5 owns the code mappings passed to Vendor Discovery; Stage 6
    // restores its saved vendor choices. Earlier Question/MFR caches are not
    // a render dependency for this page.
    6: [5, 6],
    // Section B through Section E need the saved vendor result, not every
    // historical cache that preceded it.
    7: [6],
    8: [6],
    9: [6],
    10: [6],
  };
  const available = new Set(completedStageCodes);
  const priority = (requiredForVisibleStage[currentBackendStage] ?? [])
    .filter((stageCode) => available.has(stageCode));
  const prioritySet = new Set(priority);
  return [
    ...priority,
    ...completedStageCodes.filter((stageCode) => !prioritySet.has(stageCode)),
  ];
}

export function resolveMarketResearchProductDestination(
  acknowledgement: Pick<ProductSelectionResponse, 'next_stage'> | { success: false; next_stage?: number } | undefined,
): ProductSelectionResponse['next_stage'] | null {
  if (!acknowledgement || 'success' in acknowledgement) return null;
  return [4, 5, 6, 14].includes(acknowledgement.next_stage)
    ? acknowledgement.next_stage
    : null;
}

export interface MarketResearchRefreshRequirement {
  requiredStage: 14;
  requestedStage: number;
}

const isRuntimeRecord = (value: unknown): value is Record<string, unknown> => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
);

const isRuntimeString = (value: unknown): value is string => typeof value === 'string';
const isRuntimeNonemptyString = (value: unknown): value is string => (
  isRuntimeString(value) && value.trim().length > 0
);
const isRuntimeNullableString = (value: unknown): value is string | null => (
  value === null || isRuntimeString(value)
);
const isRuntimeNonnegativeInteger = (value: unknown): value is number => (
  typeof value === 'number' && Number.isInteger(value) && value >= 0
);
const isRuntimeNullableNonnegativeInteger = (value: unknown): boolean => (
  value === null || isRuntimeNonnegativeInteger(value)
);
const isRuntimeRecordArray = (value: unknown): value is Record<string, unknown>[] => (
  Array.isArray(value) && value.every(isRuntimeRecord)
);
const isRuntimeStringArray = (value: unknown): value is string[] => (
  Array.isArray(value) && value.every(isRuntimeString)
);

function isUsableQuestion(value: unknown): boolean {
  if (!isRuntimeRecord(value)) return false;
  const validPriority = ['High', 'Medium', 'Low'].includes(value.priority as string)
    || (isRuntimeNonnegativeInteger(value.priority) && value.priority >= 1 && value.priority <= 3);
  return isRuntimeNonemptyString(value.id)
    && isRuntimeNonemptyString(value.question)
    && isRuntimeNonemptyString(value.category)
    && validPriority
    && (value.required === undefined || typeof value.required === 'boolean')
    && (value.isRequired === undefined || typeof value.isRequired === 'boolean')
    && (value.helpText === undefined || isRuntimeString(value.helpText))
    && (value.rationale === undefined || isRuntimeString(value.rationale))
    && (
      value.buyParamKey === undefined
      || ['dollar_amount', 'quantity', 'buy_type'].includes(value.buyParamKey as string)
    );
}

function isUsableQuestionsStageData(value: unknown): boolean {
  if (!isRuntimeRecord(value) || !isRuntimeRecord(value.output)) return false;
  return Array.isArray(value.output.questions)
    && value.output.questions.every(isUsableQuestion);
}

const signatureStates = ['empty', 'current', 'invalidated', 'removed', 'copied'];
const brandNameJaStates = ['draft', 'ready', 'needs_review', 'inactive'];
const approvalBands = [
  'at_or_below_10m',
  'over_10m_to_100m',
  'over_100m_to_500m',
  'over_500m',
  'agency_local_policy',
];
const constraintCategories = [
  'performance', 'interoperability', 'compatibility', 'form_fit_function',
  'safety', 'cybersecurity', 'warranty', 'training', 'tooling',
  'certification', 'data_rights', 'support', 'other',
];

function hasUsableSignatureCore(value: Record<string, unknown>): boolean {
  return isRuntimeString(value.block_id)
    && isRuntimeString(value.role)
    && isRuntimeString(value.signer_name)
    && isRuntimeString(value.signer_title)
    && isRuntimeNullableString(value.signed_date)
    && isRuntimeString(value.signature_text)
    && isRuntimeNullableString(value.action_at)
    && isRuntimeNullableString(value.acting_user_id)
    && isRuntimeNullableString(value.acting_user_email)
    && isRuntimeString(value.attestation_version)
    && isRuntimeNullableString(value.content_hash);
}

function isUsableSignatureBlock(value: unknown): boolean {
  return isRuntimeRecord(value)
    && hasUsableSignatureCore(value)
    && signatureStates.includes(value.state as string)
    && isRuntimeNullableString(value.invalidation_reason);
}

function isUsableApprovalSignature(value: unknown): boolean {
  return isRuntimeRecord(value)
    && isUsableSignatureBlock(value)
    && typeof value.delegated === 'boolean'
    && typeof value.delegable === 'boolean'
    && isRuntimeString(value.policy_version);
}

function isUsableApprovalPolicy(value: unknown): boolean {
  return isRuntimeRecord(value)
    && isRuntimeString(value.policy_version)
    && isRuntimeString(value.citation)
    && approvalBands.includes(value.band as string)
    && isRuntimeString(value.role)
    && typeof value.delegable === 'boolean'
    && typeof value.separate_signature_required === 'boolean';
}

function isUsableAcquisitionValue(value: unknown): boolean {
  return isRuntimeRecord(value)
    && isRuntimeNullableString(value.estimated_acquisition_value)
    && isRuntimeString(value.value_basis)
    && typeof value.includes_all_options_confirmed === 'boolean'
    && (value.scope === null || value.scope === 'entire' || value.scope === 'portion')
    && isRuntimeNullableString(value.whole_acquisition_value)
    && isRuntimeNullableString(value.source_snapshot_hash)
    && ['user_confirmed', 'product_discovery_suggestion'].includes(value.provenance as string);
}

function isUsableBrandNameFact(value: Record<string, unknown>): boolean {
  return isRuntimeString(value.product_key)
    && isRuntimeString(value.brand_or_manufacturer)
    && isRuntimeString(value.product_or_model)
    && isRuntimeString(value.peculiar_feature)
    && isRuntimeString(value.covered_line_items)
    && isRuntimeNullableString(value.quantity)
    && isRuntimeNullableString(value.source)
    && isRuntimeNullableString(value.retrieved_at);
}

function isUsableStructuredEvidence(value: unknown): boolean {
  if (!isRuntimeRecord(value)
    || !isRuntimeRecordArray(value.constraints)
    || !isRuntimeRecordArray(value.alternatives_considered)
    || !isRuntimeRecordArray(value.market_sources)
    || !isRuntimeRecord(value.switching_or_duplicate_impact)
    || !isRuntimeRecordArray(value.future_barrier_actions)) return false;
  return value.constraints.every((item) => (
    constraintCategories.includes(item.category as string)
    && isRuntimeString(item.description)
  ))
    && value.alternatives_considered.every((item) => (
      isRuntimeString(item.alternative)
      && isRuntimeString(item.evaluated_by)
      && isRuntimeNullableString(item.evaluation_date)
      && isRuntimeString(item.reason_cannot_meet_need)
    ))
    && value.market_sources.every((item) => (
      isRuntimeString(item.source_name)
      && ['manufacturer', 'authorized_reseller', 'other'].includes(item.source_type as string)
      && typeof item.contacted === 'boolean'
      && isRuntimeNullableString(item.response)
      && isRuntimeNullableString(item.notice_or_rfi_status)
      && isRuntimeNullableString(item.evidence_reference)
    ))
    && typeof value.switching_or_duplicate_impact.claimed === 'boolean'
    && isRuntimeString(value.switching_or_duplicate_impact.calculation_support)
    && value.future_barrier_actions.every((item) => (
      isRuntimeString(item.action)
      && isRuntimeString(item.owner)
      && isRuntimeNullableString(item.target_date)
    ));
}

function isUsableJustificationRoute(value: unknown): value is Record<string, unknown> {
  if (!isRuntimeRecord(value)) return false;
  return value.schema_version === 1
    && ['part6', 'simplified', 'schedule_limited_sources', 'fair_opportunity_exception', 'no_justification', 'unresolved'].includes(value.code as string)
    && ['supported', 'not_required', 'unresolved'].includes(value.status as string)
    && ['document_type', 'authority_code', 'authority_text', 'policy_version', 'notice', 'context_digest'].every(key => isRuntimeString(value[key]))
    && typeof value.requires_document === 'boolean'
    && typeof value.approval_supported === 'boolean'
    && (value.approval_required === undefined || typeof value.approval_required === 'boolean')
    && isRuntimeStringArray(value.missing_facts)
    && isRuntimeStringArray(value.required_fields);
}

function isUsableNarrativeSections(value: unknown, route?: unknown): boolean {
  if (!isRuntimeRecord(value)) return false;
  return [
    'section_i', 'section_ii', 'section_iii', 'section_iv', 'section_v',
    'section_vi', 'section_vii', 'section_viii', 'section_ix',
    'section_ix_na_explanation', 'section_ix_guidance',
  ].every((field) => isRuntimeString(value[field]))
    && value.section_iv_authority_code === (isUsableJustificationRoute(route) ? route.authority_code : '10_USC_3204_A_1')
    && typeof value.section_viii_na === 'boolean'
    && typeof value.section_ix_na === 'boolean';
}

function isUsableBrandNameJaArtifact(value: unknown, expectedFlowId: string): boolean {
  if (!isRuntimeRecord(value)
    || !isRuntimeRecordArray(value.covered_facts)
    || !isRuntimeRecordArray(value.certifications)
    || !isRuntimeRecordArray(value.coordination)
    || !isRuntimeRecordArray(value.audit_events)
    || !isRuntimeRecordArray(value.attestation_history)) return false;
  const validDestination = value.post_ja_destination_stage === null
    || value.post_ja_destination_stage === 4
    || value.post_ja_destination_stage === 5
    || value.post_ja_destination_stage === 6;
  return value.schema_version === 1
    && isRuntimeString(value.approval_policy_version)
    && value.flow_id === expectedFlowId
    && isRuntimeNonnegativeInteger(value.revision)
    && brandNameJaStates.includes(value.state as string)
    && typeof value.active === 'boolean'
    && isRuntimeString(value.source_stage3_hash)
    && isRuntimeNonnegativeInteger(value.source_route_revision)
    && validDestination
    && isRuntimeString(value.security_marking)
    && isUsableAcquisitionValue(value.value)
    && (value.approval_policy === null || isUsableApprovalPolicy(value.approval_policy))
    && value.covered_facts.every(isUsableBrandNameFact)
    && isUsableStructuredEvidence(value.structured_evidence)
    && value.certifications.every((item) => (
      isUsableSignatureBlock(item) && isRuntimeString(item.certification_text)
    ))
    && value.coordination.every((item) => (
      isUsableSignatureBlock(item)
      && isRuntimeString(item.row_id)
      && isRuntimeString(item.title)
      && typeof item.active === 'boolean'
      && typeof item.required === 'boolean'
      && isRuntimeNonnegativeInteger(item.order)
    ))
    && (
      value.approval === null
      || isUsableApprovalSignature(value.approval)
    )
    && (value.justification_route == null || isUsableJustificationRoute(value.justification_route))
    && (value.document_body === undefined || isRuntimeString(value.document_body))
    && (value.publication_notice === undefined || isRuntimeString(value.publication_notice))
    && (value.draft_answers === undefined || (isRuntimeRecord(value.draft_answers) && Object.values(value.draft_answers).every(isRuntimeString)))
    && isUsableNarrativeSections(value.sections, value.justification_route)
    && value.audit_events.every((item) => (
      isRuntimeString(item.event_type)
      && isRuntimeString(item.at)
      && isRuntimeNullableString(item.actor_id)
      && isRuntimeNullableString(item.actor_email)
      && isRuntimeStringArray(item.field_keys)
      && isRuntimeNullableNonnegativeInteger(item.from_revision)
      && isRuntimeNullableNonnegativeInteger(item.to_revision)
    ))
    && value.attestation_history.every((item) => (
      hasUsableSignatureCore(item)
      && signatureStates.includes(item.former_state as string)
      && isRuntimeString(item.invalidation_reason)
      && isRuntimeString(item.recorded_at)
    ))
    && isRuntimeNullableString(value.created_at)
    && isRuntimeNullableString(value.updated_at)
    && isRuntimeNullableString(value.completed_at);
}

export function isUsableBrandNameJaProjection(
  value: unknown,
  expectedFlowId: string,
): value is BrandNameJaProjection {
  if (!isRuntimeRecord(value)) return false;
  const errors = value.errors;
  const policy = value.policy;
  const recovery = value.recovery;
  return (value.justification_route == null || isUsableJustificationRoute(value.justification_route))
    && (value.context_notice === undefined || isRuntimeString(value.context_notice))
    && (value.artifact === null || isUsableBrandNameJaArtifact(value.artifact, expectedFlowId))
    && typeof value.revision === 'number'
    && Number.isInteger(value.revision)
    && value.revision >= 0
    && typeof value.state === 'string'
    && ['draft', 'ready', 'needs_review', 'inactive', 'missing', 'stale']
      .includes(value.state)
    && typeof value.ready === 'boolean'
    && isRuntimeRecord(errors)
    && Object.values(errors).every((message) => typeof message === 'string')
    && typeof value.source_hash === 'string'
    && typeof value.source_status === 'string'
    && (
      value.post_ja_destination_stage === null
      || value.post_ja_destination_stage === 4
      || value.post_ja_destination_stage === 5
      || value.post_ja_destination_stage === 6
    )
    && isRuntimeRecord(policy)
    && typeof policy.version === 'string'
    && typeof policy.citation === 'string'
    && isRuntimeRecord(recovery)
    && typeof recovery.required === 'boolean'
    && (recovery.action === null || recovery.action === 'rebase_source');
}

/**
 * Recognize the mixed-version Stage-14 guard by stable machine code only.
 * Server-provided message text is deliberately ignored and never rendered.
 */
export function getMarketResearchRefreshRequirement(
  response: unknown,
): MarketResearchRefreshRequirement | null {
  if (!response || typeof response !== 'object') return null;
  const record = response as Record<string, unknown>;
  const code = record.error_code ?? record.code;
  if (code !== 'market_research_brand_name_ja_refresh_required') return null;
  if (record.required_stage !== 14
    || typeof record.requested_stage !== 'number'
    || record.requested_stage < 4
    || record.requested_stage > 13) return null;
  return { requiredStage: 14, requestedStage: record.requested_stage };
}

export function resolveMarketResearchBackDestination(
  currentBackendCode: MarketResearchStageCode,
  decision: Pick<ProductSelectionBrandNameRecord, 'requested' | 'post_ja_destination_stage'> | null,
  applicability: MarketResearchStageApplicability,
): MarketResearchStageCode | undefined {
  if (decision?.post_ja_destination_stage === currentBackendCode) {
    return decision.requested ? 14 : 3;
  }
  return getPreviousApplicableMarketResearchStage(
    currentBackendCode,
    applicability,
  )?.backendCode;
}

/**
 * One-time Construction has two persisted entry paths.  Always derive the
 * active UI branch from the server-owned metadata, including immediately after
 * creating a flow, so a previous flow's local state cannot choose its route.
 */
export const resolveOneTimeConstructionOrigin = (
  metadata: { sow_origin?: unknown } | null | undefined,
): 'existing_sow' | 'requirements_generation' => (
  metadata?.sow_origin === 'requirements_generation'
    ? 'requirements_generation'
    : 'existing_sow'
);

/** Services and Complex Services keep their workflow type; this immutable
 * server-owned origin selects whether the PWS is uploaded or generated. */
export const resolveServicesPwsOrigin = (
  metadata: { pws_origin?: unknown } | null | undefined,
): 'existing_pws' | 'requirements_generation' => (
  metadata?.pws_origin === 'requirements_generation'
    ? 'requirements_generation'
    : 'existing_pws'
);

/** Select the visible construction path from the persisted descriptor policy.
 * `includesPriceEstimator` is derived server-side; it is never inferred from
 * current stage or browser storage. */
export const oneTimeConstructionStageList = (
  origin: 'existing_sow' | 'requirements_generation',
  includesPriceEstimator: boolean,
) => origin === 'requirements_generation'
  ? (includesPriceEstimator ? oneTimeConstructionGenerationPriceEstimatorStages : oneTimeConstructionGenerationStages)
  : (includesPriceEstimator ? oneTimeConstructionPriceEstimatorStages : oneTimeConstructionStages);


export { standaloneOneTimeConstructionPriceEstimatorStages };

/**
 * Resume the generated Services PWS route from the server's logical-path
 * projection only. Stage codes 620–622 are opaque storage identifiers, and a
 * local browser position must never override the flow's authoritative path.
 * A missing/malformed projection deliberately reopens the safe intake stage.
 */
export const resumeServicesPwsGenerationStage = (
  metadata: { workflow_progress?: { path_id?: unknown; total_count?: unknown; completed_count?: unknown } } | null | undefined,
): number => {
  const progress = metadata?.workflow_progress;
  if (progress?.path_id !== 'services_pws_generation/standalone' || progress.total_count !== 3
    || typeof progress.completed_count !== 'number' || !Number.isFinite(progress.completed_count)) return 0;
  return Math.min(2, Math.max(0, Math.floor(progress.completed_count)));
};

/**
 * Services generation now lives under the existing `services` type.  The
 * progress projection remains authoritative; the fallback only supports a
 * rolling restart while the new projection is being persisted.
 */
export const resumeGeneratedServicesStage = (
  metadata: {
    workflow_progress?: { path_id?: unknown; total_count?: unknown; completed_count?: unknown };
    completedStages?: unknown;
    completed_stages?: unknown;
  } | null | undefined,
): number => {
  const progress = metadata?.workflow_progress;
  if (progress?.path_id === 'services/requirements_generation'
    && progress.total_count === 6
    && typeof progress.completed_count === 'number'
    && Number.isFinite(progress.completed_count)) {
    return Math.min(5, Math.max(0, Math.floor(progress.completed_count)));
  }
  const completedStages = metadata?.completedStages;
  const completedStagesSnake = metadata?.completed_stages;
  const completed = Array.isArray(completedStages)
    ? completedStages
    : Array.isArray(completedStagesSnake) ? completedStagesSnake : [];
  if (completed.includes(606) || completed.includes(605)) return 5;
  if (completed.includes(604)) return 4;
  if (completed.includes(622)) return 3;
  if (completed.includes(621)) return 2;
  if (completed.includes(620)) return 1;
  return 0;
};

/**
 * A generated-PWS Home Chat receipt is an explicit request to reopen the
 * editor at its named stage (622), rather than to continue the normal
 * post-generation Services path.  Keep this decision independent of a
 * browser-local resume position and reusable by both Services paths.
 */
export const resumeGeneratedServicesChatHandoffStage = (
  redeemedGeneratedPws: boolean,
  completedStages: readonly unknown[],
): number | null => (
  redeemedGeneratedPws && completedStages.includes(622) ? 2 : null
);

/** `null` means the generated Complex Services prefix is complete and App may
 * render the server-selected Complex Services suffix. */
export const resumeGeneratedComplexServicesPrefixStage = (
  metadata: { workflow_progress?: { path_id?: unknown; completed_count?: unknown; total_count?: unknown } } | null | undefined,
): number | null => {
  const progress = metadata?.workflow_progress;
  if (progress?.path_id !== 'complex_services/requirements_generation'
    || typeof progress.completed_count !== 'number'
    || !Number.isFinite(progress.completed_count)
    || typeof progress.total_count !== 'number'
    || progress.total_count < 3) return 0;
  const completed = Math.max(0, Math.floor(progress.completed_count));
  return completed >= 3 ? null : Math.min(2, completed);
};

const generatedServicesStages = [
  ...servicesPwsGenerationStages,
  ...servicesStages.slice(4),
];

/**
 * Existing-SOW verification has a Suggestions stage whose manual edits can be
 * invalidated by navigating back. Generated-SOW Manual Edit instead returns
 * directly to Context Questions, so it must not reuse that legacy warning.
 */
export const shouldConfirmOneTimeConstructionStepperBack = (
  origin: 'existing_sow' | 'requirements_generation',
  currentStage: number,
  targetStage: number,
): boolean => (
  origin === 'existing_sow' && currentStage === 2 && targetStage < currentStage
);

type UpdateNAICSCurrentStage = (
  flowId: string,
  stageNum: number,
) => Promise<{
  success: boolean;
  error?: string;
  error_code?: string;
  technical_detail?: string;
  code?: string;
  required_stage?: number;
  requested_stage?: number;
  feature?: string;
}>;

/** The API helper normalizes the legacy 200 message before returning here. */
export function isCurrentStageUpdateAcknowledged(
  result: Awaited<ReturnType<UpdateNAICSCurrentStage>>,
): boolean {
  return result.success === true;
}

export class MarketResearchRefreshRequiredError extends Error {
  readonly requirement: MarketResearchRefreshRequirement;

  constructor(requirement: MarketResearchRefreshRequirement) {
    super(USER_FACING_ERROR_MESSAGES.unrecognized);
    this.name = 'MarketResearchRefreshRequiredError';
    this.requirement = requirement;
  }
}

async function persistMarketResearchCurrentStage(
  flowId: string | null | undefined,
  stageNum: number,
  updateStage: UpdateNAICSCurrentStage = updateCurrentStage,
): Promise<void> {
  if (!flowId) return;
  const result = await updateStage(flowId, stageNum);
  if (!isCurrentStageUpdateAcknowledged(result)) {
    const refreshRequirement = getMarketResearchRefreshRequirement(result);
    if (refreshRequirement) {
      throw new MarketResearchRefreshRequiredError(refreshRequirement);
    }
    throw new Error(USER_FACING_ERROR_MESSAGES.unrecognized);
  }
}

/**
 * NAICS has already saved its selection when it calls this app-shell guard.
 * Keep the durable stage marker authoritative so a failed update leaves the
 * stage component mounted with its existing retry affordance.
 */
export async function persistNAICSCurrentStage(
  flowId: string | null | undefined,
  updateStage: UpdateNAICSCurrentStage = updateCurrentStage,
): Promise<void> {
  await persistMarketResearchCurrentStage(flowId, 6, updateStage);
}
export const DcmaPbpWorkflowGate: React.FC<{
  allow: boolean; flowId: string | null; workflowName?: string;
  currentUserEmail?: string; readOnly?: boolean; readOnlyReason?: string | null;
}> = ({ allow, flowId, workflowName, currentUserEmail, readOnly, readOnlyReason }) =>
  allow && flowId
    ? <DcmaPbpWorkflow flowId={flowId} workflowName={workflowName}
        currentUserEmail={currentUserEmail} readOnly={readOnly} readOnlyReason={readOnlyReason} />
    : null;
const stages: MarketResearchNavigationStage[] = buildMarketResearchNavigation({
  brandNameJaRequired: true,
  dd2579Required: true,
}).stages;

// CLS workflow stages (3 stages)
const clsStages = [
  { id: 'cls-upload', name: 'Document Upload', description: 'Upload draft XML and source documents' },
  { id: 'cls-results', name: 'Identify Clauses', description: 'Review and adjust clause corrections' },
  { id: 'cls-final', name: 'Final Review', description: 'Review final clause list and download files' },
];

// SOW Verification workflow stages (3 stages)
const sowStages = [
  { id: 'sow-upload', name: 'Document Upload', description: 'Upload SOW and supporting documents' },
  { id: 'sow-results', name: 'Suggestions', description: 'Review verification results and annotations' },
  { id: 'sow-edit', name: 'Manual Edit', description: 'Edit and finalize SOW document. Returning to the Suggestions stage will overwrite all manual edits.' },
];

// Wage Determination workflow stages (single page)
const wdStages = [
  { id: 'wd', name: 'Wage Determination', description: 'Upload, review location & type, and search' },
];

// PNM (Price Negotiation Memorandum) workflow stages (2 stages)
const pnmStages = [
  { id: 'pnm', name: 'IGE vs Vendor Comparison', description: 'Upload documents, compare, and review results' },
];

// FOPR (Fair Opportunity Proposal Request) workflow stages (3 stages)
const foprStages = [
  { id: 'fopr-upload', name: 'Document Upload', description: 'Upload source documents for FOPR extraction' },
  { id: 'fopr-form', name: 'FOPR Form', description: 'Review extracted fields and generate FOPR DOCX' },
  { id: 'construction-abstract', name: 'Abstract of Proposals', description: 'Extract proposal bids and generate the Abstract of Proposals/Quotations PDF' },
];

// Services workflow stages (7 stages) — imported from ./constants/workflowStages.

// Services COMBO (Combined Synopsis/Solicitation) workflow stages (2 stages)
const servicesComboStages = [
  { id: 'services-combo-upload', name: 'Document Upload', description: 'Upload SOW, wage determination, CLS XML, and optional attachments' },
  { id: 'services-combo-form', name: 'Combined Synopsis Form', description: 'Review extracted fields, complete manual fields, and export the fillable PDF' },
];

// GPC (Government Purchase Card) workflow stages.
//
// The first 3 stages (UI indices 0/1/2) are FIXED: item-only questions → salient
// chars → product/price discovery (auto price search → agent buy type → quantity
// → threshold). After Product Discovery the stepper ALWAYS shows EVERY per-form
// stage (S4–S11) in canonical order — a FIXED-length list (3 base + 9 form = 12).
// Inapplicable form stages (computed from the Stage-3 outputs via
// `applicableGpcFormStages(...)`) are greyed/disabled IN PLACE and auto-skipped
// during navigation; they are never removed, so a stage's UI index never shifts
// between Path A and Path B. See `gpcStages` (the fixed append) +
// `gpcDisabledFormStageIndices` (the greyed set) and `GPC_FORM_STAGE_META` below.
const GPC_STAGES = [
  { id: 'gpc-questions', name: 'Clarify Requirements', description: 'Describe the GPC requirement and answer clarifying questions' },
  { id: 'gpc-salient-chars', name: 'Salient Characteristics', description: 'Synthesize and review the salient characteristics for the buy' },
  { id: 'gpc-product-discovery', name: 'Product Discovery', description: 'Search open-market prices, determine the buy type, enter quantity, and compute the threshold' },
];

// Per-form stage display metadata, keyed by the `GpcFormStageKey`. Used to map an
// applicable stage key (from `applicableGpcFormStages`) to a stepper entry. eCARS
// reuses the existing GPCApprovalRouting (stage code 803).
const GPC_FORM_STAGE_META: Record<GpcFormStageKey, { id: string; name: string; description: string }> = {
  naics_psc: { id: 'gpc-naics-psc', name: 'NAICS / PSC Selection', description: 'Confirm a single PSC and NAICS and resolve the size standard' },
  mandatory_source: { id: 'gpc-mandatory-source', name: 'Mandatory Source Determination', description: 'Determine the mandatory source and run the scoped source search (Path B)' },
  quotes: { id: 'gpc-quotes', name: 'Competition Quotes', description: 'Capture the ≥3 competing quotes for price reasonableness (Path B)' },
  '889': { id: 'gpc-889', name: '889 Representation', description: 'Capture the vendor 889 covered-telecom representation' },
  abstract: { id: 'gpc-abstract', name: 'GPC Abstract', description: 'Complete the GPC Abstract of Proposals' },
  dd2579: { id: 'gpc-dd2579', name: 'DD/DAF 2579', description: 'Document small-business coordination (DD/DAF 2579)' },
  af_form9: { id: 'gpc-af9', name: 'AF Form 9', description: 'Prepare the AF Form 9 request for purchase (local CONS)' },
  ecars: { id: 'gpc-ecars', name: 'Approval / eCARS', description: 'Route the prepared package to the approving official via eCARS' },
  tax_exempt: { id: 'gpc-tax-exempt', name: 'Tax-Exempt Certificate', description: 'Provide the tax-exempt certificate to the vendor' },
  receipt: { id: 'gpc-receipt', name: 'Receipt', description: 'Record receipt and a receiver signature' },
  iuid: { id: 'gpc-iuid', name: 'IUID / Disposition', description: 'Track the accountable-property IUID record' },
};


function NotificationStageMounted({ id, onMount }: { id: number; onMount: (id: number) => void }) {
  useEffect(() => { onMount(id); }, [id, onMount]);
  return null;
}

function App() {
  const documentAssistant = useDocumentAssistant();
  const { mode } = useColorMode();
  const theme = React.useMemo(() => createAppTheme(mode), [mode]);
  const marketResearchBrandNameJaFeatureEnabled = isMarketResearchBrandNameJaEnabled();
  const [currentStage, setCurrentStage] = useDocumentNavigationState(0);
  const [maxStageReached, setMaxStageReached] = useState(0);
  // A live Stage 2 completion arms one automatic Stage 3 search. Reopening an
  // existing flow never arms it merely because its saved Stage 3 output is empty.
  const [firstStage3AutoStartFlowId, setFirstStage3AutoStartFlowId] = useState<string | null>(null);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState<'success' | 'info' | 'error'>('info');
  const [notificationAction, setNotificationAction] = useState<{
    label: string;
    onClick: () => void;
  } | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [accountAnchorEl, setAccountAnchorEl] = useState<null | HTMLElement>(null);
  const [workflowNotificationsOpen, setWorkflowNotificationsOpen] = useState(false);
  const [workflowNotificationFlow, setWorkflowNotificationFlow] = useState<{ id: string; name: string } | null>(null);
  const [unreadWorkflowNotificationCount, setUnreadWorkflowNotificationCount] = useState(0);
  const [notificationBrandNameJaProjection, setNotificationBrandNameJaProjection] = useState<{
    flowId: string;
    projection: BrandNameJaProjection;
  } | null>(null);
  const notificationStageReadinessRef = useRef<{
    id: number;
    flowId: string;
    stageCode: 1 | 14;
    stageIndex: number;
    authGeneration: number;
    workflowOperationGeneration: number;
    resolve: () => void;
    reject: (error: Error) => void;
    timeout: number;
  } | null>(null);
  const notificationStageReadinessIdRef = useRef(0);
  const [notificationStageReadinessEpoch, setNotificationStageReadinessEpoch] = useState(0);
  const [notificationMountedStageId, setNotificationMountedStageId] = useState(0);
  const accountButtonRef = useRef<HTMLButtonElement>(null);
  const [backNavDialog, setBackNavDialog] = useState<{ open: boolean; targetStage: number }>({
    open: false,
    targetStage: 0,
  });
  const [marketResearchRefreshRequirement, setMarketResearchRefreshRequirement] =
    useState<MarketResearchRefreshRequirement | null>(null);
  const [marketResearchClientUpgradeRequired, setMarketResearchClientUpgradeRequired] =
    useState(false);

  // Email authentication state
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [showEmailGate, setShowEmailGate] = useState(true);
  // True while the initial /api/auth/me check is in flight. Prevents the
  // login screen from flashing on load when the user has an active session.
  // Use a localStorage hint so first-time/logged-out users skip the blank
  // screen entirely — only returning users (hint present) wait for the check.
  const [authChecking, setAuthChecking] = useState(
    () => localStorage.getItem('farchat_has_session') === '1'
  );
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEffectiveSuperAdmin, setIsEffectiveSuperAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isUserFeedbackStaff, setIsUserFeedbackStaff] = useState(false);
  const [adminStatusRole, setAdminStatusRole] = useState<RolePreview | null>(null);
  // The backend validates this role against the real Super Admin session.
  const [rolePreview, setRolePreview] = useState<RolePreview>('super_admin');
  const [rolePreviewDialogOpen, setRolePreviewDialogOpen] = useState(false);
  const [rolePreviewBusy, setRolePreviewBusy] = useState(false);
  const [rolePreviewSyncError, setRolePreviewSyncError] = useState(false);
  const [rolePreviewRetry, setRolePreviewRetry] = useState(0);
  const [pendingRolePreview, setPendingRolePreview] = useState<RolePreview>('super_admin');
  const [pendingCount, setPendingCount] = useState(0);
  const [unreadFeedbackCount, setUnreadFeedbackCount] = useState(0);
  const [unreadSupportIssueCount, setUnreadSupportIssueCount] = useState(0);
  const [allowedWorkflowTypes, setAllowedWorkflowTypes] = useState<string[] | null>(null);
  const [workflowPermissionsLoaded, setWorkflowPermissionsLoaded] = useState(false);
  const [permissionsLoadFailed, setPermissionsLoadFailed] = useState(false);
  const [permissionsScope, setPermissionsScope] = useState<string | null>(null);
  const permissionsRequestGenerationRef = useRef(0);
  const permissionsRequestScopeRef = useRef<string | null>(null);
  const [permissionsRefreshVersion, setPermissionsRefreshVersion] = useState(0);
  const [permissionsAppliedVersion, setPermissionsAppliedVersion] = useState(0);
  const [userCanUseChatbot, setUserCanUseChatbot] = useState<boolean>(true);
  const [homeChatMode, setHomeChatMode] = useState<HomeChatMode>('none');
  const [workflowOnlyAudience, setWorkflowOnlyAudience] = useState<'ra' | 'ce' | null>(null);
  const [allowTraining, setAllowTraining] = useState<boolean>(false);
  const [allowRaTraining, setAllowRaTraining] = useState(false);
  const [allowDcmaPbp, setAllowDcmaPbp] = useState<boolean>(false);
  const [allowDcmaCrr, setAllowDcmaCrr] = useState<boolean>(false);
  const [canUseStandalonePriceEstimator, setCanUseStandalonePriceEstimator] = useState(false);
  const [projectTrackerCapability, setProjectTrackerCapability] = useState<{
    allowed: boolean; revision: string; unit: string;
  }>({ allowed: false, revision: '', unit: '' });
  const trackerScopeRef = useRef('');
  const refreshProjectTrackerScope = useCallback(() => {
    setPermissionsRefreshVersion(version => version + 1);
  }, []);
  const [allowChangelog, setAllowChangelog] = useState<boolean>(false);
  // Permissions API projection of the authenticated caller's MR surface.
  const [marketResearchCallerView, setMarketResearchCallerView] =
    useState<'customer' | 'contracting' | 'unknown'>('unknown');
  const [customerMarketResearchEnabled, setCustomerMarketResearchEnabled] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showManageUsers, setShowManageUsers] = useState(false);
  const [supportIssueView, setSupportIssueView] = useState<
    'customer' | 'admin' | null
  >(null);
  const refreshSupportIssueUnreadCount = useCallback(() => {
    fetchSupportIssueUnreadCount()
      .then(setUnreadSupportIssueCount)
      .catch(() => setUnreadSupportIssueCount(0));
  }, []);
  const refreshWorkflowNotificationUnreadCount = useCallback(() => {
    fetchWorkflowNotificationUnreadCount()
      .then(setUnreadWorkflowNotificationCount)
      .catch(() => setUnreadWorkflowNotificationCount(0));
  }, []);

  useEffect(() => {
    if (!userEmail) {
      setUnreadSupportIssueCount(0);
      return;
    }

    refreshSupportIssueUnreadCount();
    const intervalId = window.setInterval(
      refreshSupportIssueUnreadCount,
      60_000
    );
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshSupportIssueUnreadCount();
      }
    };
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [refreshSupportIssueUnreadCount, userEmail]);

  useEffect(() => {
    if (!userEmail) {
      setUnreadWorkflowNotificationCount(0);
      return;
    }
    refreshWorkflowNotificationUnreadCount();
    const intervalId = window.setInterval(refreshWorkflowNotificationUnreadCount, 60_000);
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refreshWorkflowNotificationUnreadCount();
    };
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [refreshWorkflowNotificationUnreadCount, userEmail]);

  // View-only ("read-only example") mode for curated training flows. Derived
  // from the backend's computed `metadata.read_only` when a flow is opened, and
  // threaded into every StageWrapper via the shared `stageRatingProps` spread so
  // all 8 workflow types receive it. Defaults to false; reset on every pipeline
  // reset so opening a normal flow never inherits a prior view-only state.
  const [isViewOnly, setIsViewOnly] = useState<boolean>(false);
  const [isTrainingExample, setIsTrainingExample] = useState(false);
  const [readOnlyReason, setReadOnlyReason] = useState<'similar_requirements' | 'super_admin_view' | 'training' | null>(null);

  // Flow management state
  const [flowId, setFlowId] = useDocumentNavigationState<string | null>(null);
  const activeFlowRoleRef = useRef<RolePreview | null>(null);
  const activeFlowRouteRef = useRef<{ flowId: string; signature: string } | null>(null);
  const workflowWriteContextRef = useRef<{ flowId: string | null; allowed: boolean }>({ flowId: null, allowed: false });
  // Verification changes a render barrier, so its key must trigger a render
  // even when React batches checking -> cleared into an unchanged null status.
  const [checkedFlowRefreshKey, setCheckedFlowRefreshKey] = useState<string | null>(null);
  const [flowRevalidation, setFlowRevalidation] = useState<WorkflowAccessState | null>(null);
  const [flowRevalidationRetryVersion, setFlowRevalidationRetryVersion] = useState(0);
  const backgroundAccessRefreshVersionRef = useRef<number | null>(null);
  const [flowName, setFlowName] = useState<string>('');
  const [otherTransactionsDisplayRoute, setOtherTransactionsDisplayRoute] = useState<{
    flowId: string;
    stages: OtherTransactionsDisplayStage[];
  } | null>(null);
  const handleOtherTransactionsStagesChange = useCallback((
    routeFlowId: string, routeStages: OtherTransactionsDisplayStage[],
  ) => setOtherTransactionsDisplayRoute({flowId: routeFlowId, stages: routeStages}), []);
  const activeOtherTransactionsStages = otherTransactionsDisplayRoute?.flowId === flowId
    ? otherTransactionsDisplayRoute.stages : [];
  // Kept separate from flowId so a newly allocated chooser flow does not try
  // to restore before it has a Stage-1000 workspace snapshot.
  const [postAwardResumeFlowId, setPostAwardResumeFlowId] = useState<string | null>(null);
  type AppPage = 'type_selector' | 'flow_selector' | 'workflow' | 'training' | 'ra_training' | 'people_chats';
  const [appPage, setAppPage] = useDocumentNavigationState<AppPage>('type_selector');
  const [peopleConversationId, setPeopleConversationId] = useState<string | null>(null);
  useEffect(() => {
    // Older RA links return to the main app; training uses in-app navigation.
    if (/^#ra-training(?:\/|$)/.test(window.location.hash)) {
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
    }
  }, []);
  const trainingEntryTrackerRef = useRef(new TrainingEntryTracker(recordTrainingEntry));
  const [trainingOpeningId, setTrainingOpeningId] = useState<string | null>(null);
  const [raTrainingOpeningId, setRaTrainingOpeningId] = useState(0);
  const [flowSelectorScope, setFlowSelectorScope] = useState<WorkflowType | null>(null);
  const [openCreateRequest, setOpenCreateRequest] = useState<Readonly<{
    workflowType: WorkflowType;
    workflowEntryMode?: WorkflowRecommendationEntryMode;
  }> | null>(null);
  const [workflowChatSessionId, setWorkflowChatSessionId] =
    useState<string | null>(null);
  const [workflowChatVisible, setWorkflowChatVisible] = useState(false);
  const [workflowChatNavigationPending, setWorkflowChatNavigationPending] =
    useState(false);
  const [workflowChatMountRevision, setWorkflowChatMountRevision] = useState(0);
  const [pendingWorkflowChatSessionId, setPendingWorkflowChatSessionId] =
    useState<string | null>(null);
  const [pendingWorkflowHandoffReceipt, setPendingWorkflowHandoffReceipt] =
    useState<string | null>(null);
  const activeWorkflowFlowIdRef = useRef<string | null>(flowId);
  activeWorkflowFlowIdRef.current = flowId;
  const workflowChatSessionsByFlowRef = useRef<Map<string, string>>(new Map());
  const workflowChatRevisionByFlowRef = useRef<Map<string, number>>(new Map());
  const workflowChatVisibilityRevisionByFlowRef =
    useRef<Map<string, number>>(new Map());
  const attributedWorkflowChatSessionsByFlowRef =
    useRef<Map<string, string>>(new Map());
  const authGenerationRef = useRef(0);
  const workflowCompanionWriterRef = useRef<ReturnType<
    typeof createSerializedWorkflowCompanionWriter
  > | null>(null);
  if (workflowCompanionWriterRef.current === null) {
    workflowCompanionWriterRef.current = createSerializedWorkflowCompanionWriter(
      setWorkflowCompanionSession,
      (error, failedFlowId, failedSessionId) => {
        console.error('Failed to save workflow chat companion:', error);
        const failedAuthGeneration = authGenerationRef.current;
        const applyRestoredSession = (sessionId: string | null) => {
          if (
            authGenerationRef.current !== failedAuthGeneration
            || activeWorkflowFlowIdRef.current !== failedFlowId
            || (
              failedSessionId
                ? workflowChatSessionsByFlowRef.current.get(failedFlowId)
                  !== failedSessionId
                : workflowChatSessionsByFlowRef.current.has(failedFlowId)
            )
          ) return;
          const revision =
            workflowChatRevisionByFlowRef.current.get(failedFlowId) ?? 0;
          workflowChatRevisionByFlowRef.current.set(
            failedFlowId,
            revision + 1,
          );
          if (sessionId) {
            workflowChatSessionsByFlowRef.current.set(failedFlowId, sessionId);
          } else {
            workflowChatSessionsByFlowRef.current.delete(failedFlowId);
          }
          setWorkflowChatSessionId(sessionId);
          setWorkflowChatMountRevision((current) => current + 1);
          setNotificationMessage(
            USER_FACING_ERROR_MESSAGES.workflow_companion_not_saved,
          );
          setNotificationType('error');
          setShowNotification(true);
        };
        const attributedSessionId =
          attributedWorkflowChatSessionsByFlowRef.current.get(failedFlowId)
          ?? null;
        void getWorkflowCompanionSession(failedFlowId)
          .then((persistedSessionId) => applyRestoredSession(
            persistedSessionId === undefined
              ? attributedSessionId
              : persistedSessionId,
          ))
          .catch((restoreError) => {
            console.error(
              'Failed to restore workflow chat companion:',
              restoreError,
            );
            applyRestoredSession(attributedSessionId);
          });
      },
    );
  }
  const workflowOperationGenerationRef = useRef(0);

  const clearWorkflowChatIdentityState = useCallback(() => {
    authGenerationRef.current += 1;
    setRolePreviewBusy(false);
    workflowOperationGenerationRef.current += 1;
    activeFlowRoleRef.current = null;
    workflowChatSessionsByFlowRef.current.clear();
    workflowChatRevisionByFlowRef.current.clear();
    workflowChatVisibilityRevisionByFlowRef.current.clear();
    attributedWorkflowChatSessionsByFlowRef.current.clear();
    setWorkflowChatSessionId(null);
    setWorkflowChatVisible(false);
    setWorkflowChatNavigationPending(false);
    setPendingWorkflowChatSessionId(null);
    setPendingWorkflowHandoffReceipt(null);
    setOpenCreateRequest(null);
    setFlowId(null);
    setFlowName('');
    setWorkflowType(null);
    setFlowSelectorScope(null);
    setAppPage('type_selector');
  }, []);

  const rememberWorkflowChatSession = useCallback((
    selectedFlowId: string,
    sessionId: string | null,
  ) => {
    if (sessionId) {
      workflowChatSessionsByFlowRef.current.set(selectedFlowId, sessionId);
    } else {
      workflowChatSessionsByFlowRef.current.delete(selectedFlowId);
    }
  }, []);

  const handleWorkflowChatSessionChange = useCallback((
    sessionId: string | null,
    options?: WorkflowChatSessionChangeOptions,
  ) => {
    setWorkflowChatSessionId(sessionId);
    if (flowId) {
      // A completed question often reports the existing session again. Its
      // binding is already durable, so another write can only add a failure.
      if (sessionId !== null && workflowChatSessionsByFlowRef.current.get(flowId) === sessionId) return;
      const revision = workflowChatRevisionByFlowRef.current.get(flowId) ?? 0;
      workflowChatRevisionByFlowRef.current.set(flowId, revision + 1);
      const visibilityRevision =
        workflowChatVisibilityRevisionByFlowRef.current.get(flowId) ?? 0;
      workflowChatVisibilityRevisionByFlowRef.current.set(
        flowId,
        visibilityRevision + 1,
      );
      rememberWorkflowChatSession(flowId, sessionId);
      if (options?.bindingManagedByServer) return;
      const authGeneration = authGenerationRef.current;
      void workflowCompanionWriterRef.current?.(
        flowId,
        sessionId,
        () => (
          authGenerationRef.current === authGeneration
          && (
            sessionId
              ? workflowChatSessionsByFlowRef.current.get(flowId) === sessionId
              : !workflowChatSessionsByFlowRef.current.has(flowId)
          )
        ),
      );
    }
  }, [flowId, rememberWorkflowChatSession]);

  const handleWorkflowChatTurnStart = useCallback(() => {
    if (!flowId) return;
    const revision = workflowChatRevisionByFlowRef.current.get(flowId) ?? 0;
    workflowChatRevisionByFlowRef.current.set(flowId, revision + 1);
    const visibilityRevision =
      workflowChatVisibilityRevisionByFlowRef.current.get(flowId) ?? 0;
    workflowChatVisibilityRevisionByFlowRef.current.set(
      flowId,
      visibilityRevision + 1,
    );
  }, [flowId]);

  const handleWorkflowChatClose = useCallback(() => {
    if (flowId) {
      const visibilityRevision =
        workflowChatVisibilityRevisionByFlowRef.current.get(flowId) ?? 0;
      workflowChatVisibilityRevisionByFlowRef.current.set(
        flowId,
        visibilityRevision + 1,
      );
    }
    setWorkflowChatVisible(false);
  }, [flowId]);

  const handleWorkflowChatOpen = useCallback(() => {
    if (flowId) {
      const revision = workflowChatRevisionByFlowRef.current.get(flowId) ?? 0;
      workflowChatRevisionByFlowRef.current.set(flowId, revision + 1);
      const visibilityRevision =
        workflowChatVisibilityRevisionByFlowRef.current.get(flowId) ?? 0;
      workflowChatVisibilityRevisionByFlowRef.current.set(
        flowId,
        visibilityRevision + 1,
      );
    }
    setWorkflowChatVisible(true);
  }, [flowId]);

  const realIsAdmin = adminStatusRole === rolePreview && (isAdmin || isEffectiveSuperAdmin);
  const realIsSuperAdmin = isSuperAdmin;
  const effectiveIsSuperAdmin = adminStatusRole === rolePreview && isEffectiveSuperAdmin;
  const currentPermissionsScope = `${userEmail ?? ''}\u0000${rolePreview}`;
  useLayoutEffect(() => {
    setStartupReadScope(currentPermissionsScope);
  }, [currentPermissionsScope]);
  const currentPermissionsLoaded = workflowPermissionsLoaded
    && permissionsScope === currentPermissionsScope;
  const presentationCapabilities = useMemo(() => getPresentationCapabilities(rolePreview, {
    isAdmin: realIsAdmin,
    isSuperAdmin: effectiveIsSuperAdmin,
    isUserFeedbackStaff: adminStatusRole === rolePreview && isUserFeedbackStaff,
    permissions: currentPermissionsLoaded ? {
      allowed_workflow_types: allowedWorkflowTypes,
      allow_chatbot: userCanUseChatbot,
      allow_training: allowTraining,
      allow_ra_training: allowRaTraining,
      allow_dcma_pbp: allowDcmaPbp,
      allow_dcma_crr: allowDcmaCrr,
      can_use_standalone_price_estimator: canUseStandalonePriceEstimator,
      allow_changelog: allowChangelog,
      market_research_view: marketResearchCallerView,
      customer_market_research_enabled: customerMarketResearchEnabled,
    } : null,
  }), [
    allowChangelog,
    allowDcmaPbp,
    allowDcmaCrr,
    canUseStandalonePriceEstimator,
    allowTraining,
    allowRaTraining,
    allowedWorkflowTypes,
    adminStatusRole,
    customerMarketResearchEnabled,
    currentPermissionsLoaded,
    effectiveIsSuperAdmin,
    isUserFeedbackStaff,
    marketResearchCallerView,
    realIsAdmin,
    rolePreview,
    userCanUseChatbot,
  ]);
  const presentationAllowedWorkflowTypes = presentationCapabilities.allowedWorkflowTypes;
  const isRolePreview = presentationCapabilities.isRolePreview;
  const isDcmaOnly = isDcmaOnlyWorkflowAccess(presentationAllowedWorkflowTypes);
  // This predicate is shared by the direct navigation control and the route
  // guard below. An authorized learner therefore keeps the same entry after
  // opening Training, while a restricted user never receives it.
  const canAccessTraining = !isDcmaOnly && presentationCapabilities.allowTraining;
  const canAccessRaTraining = !isDcmaOnly && presentationCapabilities.allowRaTraining;
  const missionPartnerResearchUnavailable = (presentationCapabilities.marketResearchView === 'customer' || workflowOnlyAudience === 'ra')
    && !customerMarketResearchEnabled;
  // The landing tile belongs to the RA view; Super Admin keeps header access.
  const showRaTrainingTile = canAccessRaTraining && workflowOnlyAudience === 'ra'
    && !presentationCapabilities.showSuperAdminControls;
  const isTrainingPage = appPage === 'training' || appPage === 'ra_training';
  const canAccessActiveTraining = appPage === 'ra_training' ? canAccessRaTraining : canAccessTraining;
  const openRaTraining = () => {
    if (!canAccessRaTraining) return;
    closeAccountMenu();
    setSupportIssueView(null);
    setShowDashboard(false);
    setShowManageUsers(false);
    setFlowRevalidation(null);
    setRaTrainingOpeningId(value => value + 1);
    setAppPage('ra_training');
  };
  const contactChatScope = `${userEmail || ''}:${rolePreview}:${authGenerationRef.current}`;
  const contactChatInbox = useContactChatInbox(contactChatScope, Boolean(userEmail && !showEmailGate && !authChecking));
  const openPeopleChats = (conversationId?: string | null) => {
    if (appPage === 'workflow' && !confirmLeaveWorkflow('savedWorkflows')) return;
    closeAccountMenu();
    setWorkflowNotificationsOpen(false);
    setSupportIssueView(null);
    setShowDashboard(false);
    setShowManageUsers(false);
    setFlowRevalidation(null);
    setPeopleConversationId(conversationId || null);
    setAppPage('people_chats');
  };
  usePeopleChatNavigation(Boolean(userEmail && !showEmailGate && !authChecking), openPeopleChats);
  const [trainingFeedbackContext, setTrainingFeedbackContext] = useState<TrainingFeedbackContext | null>(null);
  const openTraining = (source: TrainingEntrySource) => {
    if (!canAccessTraining) return;
    const trainingVisible = appPage === 'training' && !showDashboard && !showManageUsers && !supportIssueView;
    if (trainingVisible) return;
    // Admin and support screens are early render branches. They can cover the
    // Training page while appPage still says "training"; that is a new entry.
    if (appPage === 'training') trainingEntryTrackerRef.current.leave();
    const opening = trainingEntryTrackerRef.current.open(source);
    if (!opening) return;
    closeAccountMenu();
    setSupportIssueView(null);
    setShowDashboard(false);
    setShowManageUsers(false);
    setTrainingOpeningId(opening.eventId);
    setTrainingFeedbackContext(null);
    setAppPage('training');
  };
  const leaveTraining = () => {
    trainingEntryTrackerRef.current.leave();
    setTrainingOpeningId(null);
    setTrainingFeedbackContext(null);
    setFlowRevalidation(null);
    if (rolePreviewSyncError) setAuthChecking(true);
    setAppPage('type_selector');
  };
  useEffect(() => {
    if (appPage !== 'training' || showDashboard || showManageUsers || supportIssueView) {
      trainingEntryTrackerRef.current.leave();
      setTrainingFeedbackContext(null);
    }
  }, [appPage, showDashboard, showManageUsers, supportIssueView]);
  // A non-null list is authoritative. The separate DCMA capability exists so
  // unrestricted super administrators can receive it without a roster entry,
  // but it must not override an explicit list that omits DCMA.
  const canUseDcmaCrr = presentationCapabilities.allowDcmaCrr === true && (!presentationAllowedWorkflowTypes || presentationAllowedWorkflowTypes.includes('dcma_crr'));
  const canUseDcmaPbp = presentationCapabilities.allowDcmaPbp && (
    !presentationAllowedWorkflowTypes || presentationAllowedWorkflowTypes.includes('dcma_pbp')
  );
  const canUseWorkflowType = useCallback((type: string | null | undefined) => {
    if (!type) return false;
    if (type === 'dcma_crr') return canUseDcmaCrr;
    if (type === 'dcma_pbp') return canUseDcmaPbp;
    return !presentationAllowedWorkflowTypes || presentationAllowedWorkflowTypes.includes(type);
  }, [presentationAllowedWorkflowTypes, canUseDcmaPbp, canUseDcmaCrr]);

  useEffect(() => {
    if (
      appPage !== 'flow_selector' ||
      showDashboard ||
      showManageUsers ||
      supportIssueView
    ) {
      setPendingWorkflowChatSessionId(null);
      setPendingWorkflowHandoffReceipt(null);
    }
  }, [
    appPage,
    showDashboard,
    showManageUsers,
    supportIssueView,
  ]);

  const handleStartWorkflowTypeFromHomeChat = useCallback((
    type: WorkflowRecommendationType,
    context?: Partial<HomeChatHandoffContext>,
  ) => {
    // A valid server-issued receipt is the authority for the dedicated create
    // route. Do not discard it because this page still has an older permission
    // snapshot; without a receipt, the normal client-side filter still applies.
    if (context?.workflowEntryMode === 'standalone_price_estimator' && !canUseWorkflowType(type)) return;
    if (!context?.workflowHandoffReceipt && !canUseWorkflowType(type)) return;
    setPendingWorkflowChatSessionId(context?.homeChatSessionId ?? null);
    setPendingWorkflowHandoffReceipt(
      context?.workflowHandoffReceipt ?? null,
    );
    setSupportIssueView(null);
    setFlowSelectorScope(type);
    setOpenCreateRequest({
      workflowType: type,
      ...(context?.workflowEntryMode
        ? { workflowEntryMode: context.workflowEntryMode }
        : {}),
    });
    setAppPage('flow_selector');
  }, [canUseWorkflowType]);

  // CLS workflow state
  const [workflowType, setWorkflowType] = useDocumentNavigationState<WorkflowType | null>(null);
  const workflowChatLayoutRef = useRef<{
    flowId: string;
    enabled: boolean;
  } | null>(null);
  if (appPage !== 'workflow' || !flowId) {
    workflowChatLayoutRef.current = null;
  } else if (currentPermissionsLoaded
    && workflowChatLayoutRef.current?.flowId !== flowId) {
    workflowChatLayoutRef.current = {
      flowId,
      enabled: currentPermissionsLoaded && !isDcmaOnly
        && presentationCapabilities.allowChatbot,
    };
  }
  const verifiedDcmaFlowRef = useRef<string | null>(null);
  if (appPage !== 'workflow' || workflowType !== 'dcma_pbp') {
    verifiedDcmaFlowRef.current = null;
  } else if (flowId && canUseDcmaPbp) {
    verifiedDcmaFlowRef.current = flowId;
  }
  const activeWorkflowMountedRef = useRef(false);
  activeWorkflowMountedRef.current = appPage === 'workflow' && !!flowId && !!workflowType;
  const activeTrainingMountedRef = useRef(false);
  activeTrainingMountedRef.current = isTrainingPage;

  // Resolve notification navigation only after React has committed the exact
  // authorized flow and target stage. Stage-specific preflight happens before
  // this request is installed, so this effect is the final mounted-view
  // handshake before the panel may terminalize the notification.
  useEffect(() => {
    const pending = notificationStageReadinessRef.current;
    if (!pending || pending.id !== notificationStageReadinessEpoch) return;
    if (
      authGenerationRef.current !== pending.authGeneration
      || workflowOperationGenerationRef.current
        !== pending.workflowOperationGeneration
    ) {
      window.clearTimeout(pending.timeout);
      notificationStageReadinessRef.current = null;
      pending.reject(new Error('Workflow notification navigation was superseded'));
      return;
    }
    if (
      flowId !== pending.flowId
      || workflowType !== 'market_research'
      || currentStage !== pending.stageIndex
      || notificationMountedStageId !== pending.id
    ) return;
    if (
      pending.stageCode === 14
      && notificationBrandNameJaProjection?.flowId !== pending.flowId
    ) return;
    window.clearTimeout(pending.timeout);
    notificationStageReadinessRef.current = null;
    pending.resolve();
  }, [
    currentStage,
    flowId,
    notificationBrandNameJaProjection,
    notificationStageReadinessEpoch,
    notificationMountedStageId,
    workflowType,
  ]);

  useEffect(() => () => {
    const pending = notificationStageReadinessRef.current;
    if (!pending) return;
    window.clearTimeout(pending.timeout);
    notificationStageReadinessRef.current = null;
    pending.reject(new Error('Workflow notification navigation was interrupted'));
  }, []);
  const [constructionSowOrigin, setConstructionSowOrigin] = useState<'existing_sow' | 'requirements_generation'>('existing_sow');
  const [constructionPriceEstimatorApplicable, setConstructionPriceEstimatorApplicable] = useState(false);
  const [servicesPwsOrigin, setServicesPwsOrigin] = useState<'existing_pws' | 'requirements_generation'>('existing_pws');
  const [complexAcqMode, setComplexAcqMode] = useState<AcquisitionMode | null>(null);
  const [complexPop, setComplexPop] = useState<PopStructure | null>(null);
  const [complexSatThreshold, setComplexSatThreshold] = useState<SatThreshold | null>(null);
  // Persisted payloads for the complex-only stages that hydrate PURELY from the
  // `initialData` prop (i.e. do NOT self-fetch): ige, pws_diff, options,
  // solicitation, clearance, record. Fetched once when a complex flow loads so
  // these stages can restore their prior work on reload/restore. (Compliance,
  // reqval, qasp, dd2579 self-hydrate from the backend and are left untouched.)
  const [complexStageData, setComplexStageData] = useState<Partial<Record<ComplexStageKey, unknown>>>({});
  const [complexStageDataLoaded, setComplexStageDataLoaded] = useState(false);
  const showConstructionPriceEstimator = constructionPriceEstimatorApplicable;
  const activeOneTimeConstructionStages = oneTimeConstructionStageList(
    constructionSowOrigin,
    showConstructionPriceEstimator,
  );
  // The final Market Research DD/DAF 2579 stage is conditional. Its
  // applicability is derived exclusively from the persisted Stage-6 selection
  // and rationale state returned by the coordination endpoint.
  const [marketResearchDd2579Coordination, setMarketResearchDd2579Coordination] =
    useState<MarketResearchDd2579Coordination | null>(null);
  const [marketResearchDd2579CoordinationStatus, setMarketResearchDd2579CoordinationStatus] =
    useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const marketResearchDd2579CoordinationRequestRef = useRef(0);
  const marketResearchDd2579CoordinationInFlightRef = useRef<{
    flowId: string;
    requestId: number;
    promise: Promise<MarketResearchDd2579Coordination>;
  } | null>(null);
  // Normal flow metadata is the only initial-load source for this presentation
  // hint. It deliberately remains separate from detailed coordination: a
  // saved earlier-stage revisit must retain its conditional step without
  // fetching vendor/rationale details on the critical path.
  const [marketResearchDd2579MetadataRequired, setMarketResearchDd2579MetadataRequired] =
    useState(false);
  // Applicability can change on autosave; an entered document must not vanish
  // or shift the visual indices underneath the current page.
  const [marketResearchDd2579Visited, setMarketResearchDd2579Visited] = useState(false);
  const [marketResearchDd2579, setMarketResearchDd2579] =
    useState<MarketResearchDd2579Form | null>(null);
  const [marketResearchDd2579RestoredState, setMarketResearchDd2579RestoredState] =
    useState<RestoredMarketResearchDd2579State | null>(null);
  const marketResearchDd2579FormHydrationFlowRef = useRef<string | null>(null);
  const marketResearchDd2579FormHydrationRequestRef = useRef(0);
  const [marketResearchDd2579FormHydrationStatus, setMarketResearchDd2579FormHydrationStatus] =
    useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [marketResearchBrandNameDecision, setMarketResearchBrandNameDecision] =
    useState<ProductSelectionBrandNameRecord | null>(null);
  const [marketResearchBrandNameStatus, setMarketResearchBrandNameStatus] =
    useState<'draft' | 'ready' | 'needs_review' | 'inactive' | 'attested' | null>(null);
  // An open flow carries its own authenticated presentation projection. Keep
  // it separate from the account-level projection so a late permissions
  // response cannot expose flow-scoped CO/CS actions before this flow is
  // confirmed as contracting.
  const [marketResearchFlowView, setMarketResearchFlowView] =
    useState<'customer' | 'contracting' | 'unknown'>('unknown');
  // Set only from freshly authorized flow metadata. This gates the contextual
  // share recovery after a zero-recipient Notify result.
  const [selectedFlowIsOwner, setSelectedFlowIsOwner] = useState(false);
  const [marketResearchShareDialogContext, setMarketResearchShareDialogContext] =
    useState<MarketResearchNotificationContext | null>(null);
  const [marketResearchCustomerTerminal, setMarketResearchCustomerTerminal] = useState(false);
  // The unnumbered progress view and the editable Section C terminal are
  // distinct presentations. Returning to Section C must restore its handoff
  // controls without keeping the progress view mounted.
  const [marketResearchCustomerTerminalStageView, setMarketResearchCustomerTerminalStageView] =
    useState(false);
  // CO/CS presentation-only destination. It is intentionally distinct from
  // currentStage so it cannot change canonical MR stage state or completion.
  const [marketResearchRaCollaborationOpen, setMarketResearchRaCollaborationOpen] = useState(false);
  const [marketResearchContractingHandoffState, setMarketResearchContractingHandoffState] =
    useState<MarketResearchHandoffState>('not_handed_off');
  const [marketResearchContractingHandoffVerifiedFlowId, setMarketResearchContractingHandoffVerifiedFlowId] =
    useState<string | null>(null);
  // The server returns this compact, viewer-specific projection only after it
  // has applied ordinary workflow and stage access. A failed/late request is
  // deliberately indistinguishable from no update in the UI.
  const [marketResearchEditAwareness, setMarketResearchEditAwareness] =
    useState<MarketResearchEditAwarenessSummary | null>(null);
  const marketResearchEditAcknowledgingRef = useRef(new Set<string>());
  // A stage becomes eligible for acknowledgement only after its ordinary
  // authorized UI projection successfully loads. Retain no payload: this is
  // merely the flow/stage/revision cursor we may acknowledge when navigating
  // away from that same stage.
  const marketResearchLoadedStageEditRef = useRef<LoadedMarketResearchStageEdit | null>(null);
  // The existing stage renderer may finish its independent UI hydration before
  // this narrow authorization/readiness request returns. Keep that request's
  // cursor so a real, same-flow departure can wait for it instead of silently
  // losing the acknowledgement because the user navigated promptly.
  const marketResearchStageEditReadinessRef = useRef<{
    candidate: LoadedMarketResearchStageEdit;
    ready: boolean;
  } | null>(null);
  const marketResearchPendingStageEditExitRef = useRef<{
    departing: MarketResearchStageRoute;
    next: MarketResearchStageRoute;
    candidate: LoadedMarketResearchStageEdit;
  } | null>(null);
  const marketResearchPreviousStageRouteRef = useRef<MarketResearchStageRoute | null>(null);
  const marketResearchDd2579Enabled = marketResearchDd2579Visited || (marketResearchDd2579CoordinationStatus === 'ready'
    ? isMarketResearchDd2579StageEnabled(marketResearchDd2579Coordination)
    : marketResearchDd2579MetadataRequired);
  const marketResearchVendorBidsAction = getMarketResearchVendorBidsAction(
    marketResearchDd2579Coordination,
  );
  const marketResearchDd2579Products = React.useMemo<MarketResearchCoordinationProduct[]>(
    () => (marketResearchDd2579Coordination?.products ?? [])
      .map((product: any) => ({
        productId: product.product_id,
        productName: product.product_name,
        selectedVendors: product.selected_vendors ?? [],
        rationale: product.rationale ?? '',
      })),
    [marketResearchDd2579Coordination],
  );
  const marketResearchBrandNameRequired = marketResearchBrandNameJaFeatureEnabled
    && marketResearchBrandNameDecision?.requested === true;
  const isCustomerMarketResearch = workflowType === 'market_research'
    && marketResearchFlowView === 'customer';
  const hasConfirmedContractingMarketResearchFlow = workflowType === 'market_research'
    && presentationCapabilities.marketResearchView === 'contracting'
    && marketResearchFlowView === 'contracting';
  const marketResearchNavigation = React.useMemo(
    () => {
      if (isCustomerMarketResearch) {
        const descriptors = getCustomerMarketResearchStages(marketResearchBrandNameRequired);
        const stages = descriptors.map((stage, index) => ({
          id: stage.id,
          name: stage.name,
          description: stage.description,
          backendCode: stage.backendCode,
          prevStage: descriptors[index - 1]?.id,
          nextStage: descriptors[index + 1]?.id,
        }));
        return { stages, disabledStageIndices: [], disabledStageReasons: {} };
      }
      return buildMarketResearchNavigation({
        brandNameJaRequired: marketResearchBrandNameRequired,
        dd2579Required: marketResearchDd2579Enabled,
      }, marketResearchBrandNameJaFeatureEnabled);
    },
    [
      isCustomerMarketResearch,
      marketResearchBrandNameJaFeatureEnabled,
      marketResearchBrandNameRequired,
      marketResearchDd2579Enabled,
    ],
  );
  const marketResearchStages = marketResearchNavigation.stages;
  // DD/DAF 2579 is a conditional final stage; the remaining MR stages use
  // tighter arrow insets to keep the full flow on one row when space permits.
  const compactMarketResearchNavigation = !marketResearchStages.some(
    (stage) => stage.backendCode === 13,
  );
  // This is a Customer-only visual destination.  Keep it out of
  // `marketResearchStages`, whose indices and count are canonical persisted
  // workflow state, while still rendering it consistently in every Customer
  // stepper (including the editable terminal-stage presentation).
  const marketResearchPostHandoffProgressIndex = isCustomerMarketResearch
    ? marketResearchStages.length
    : null;
  const marketResearchRaCollaborationIndex = hasConfirmedContractingMarketResearchFlow
    ? marketResearchStages.length
    : null;
  const marketResearchStepperStages = isCustomerMarketResearch
    ? [...marketResearchStages.map((stage) => stage.name), 'Handoff & contracting progress']
    : hasConfirmedContractingMarketResearchFlow
      ? [...marketResearchStages.map((stage) => stage.name), 'Chat with Mission Partner']
      : marketResearchStages.map((stage) => stage.name);
  const marketResearchStepperDisabledStageIndices = isCustomerMarketResearch
    && !marketResearchCustomerTerminal
    && marketResearchPostHandoffProgressIndex !== null
    ? [...marketResearchNavigation.disabledStageIndices, marketResearchPostHandoffProgressIndex]
    : hasConfirmedContractingMarketResearchFlow
      && marketResearchRaCollaborationIndex !== null
      && !isMarketResearchHandoffAvailable(marketResearchContractingHandoffState)
        ? [...marketResearchNavigation.disabledStageIndices, marketResearchRaCollaborationIndex]
        : marketResearchNavigation.disabledStageIndices;
  const marketResearchStepperDisabledStageReasons = isCustomerMarketResearch
    && !marketResearchCustomerTerminal
    && marketResearchPostHandoffProgressIndex !== null
    ? {
        ...marketResearchNavigation.disabledStageReasons,
        [marketResearchPostHandoffProgressIndex]: 'Complete Section C before viewing contracting progress.',
      }
    : hasConfirmedContractingMarketResearchFlow
      && marketResearchRaCollaborationIndex !== null
      && !isMarketResearchHandoffAvailable(marketResearchContractingHandoffState)
        ? {
            ...marketResearchNavigation.disabledStageReasons,
            [marketResearchRaCollaborationIndex]: 'Chat with Mission Partner opens after handoff.',
          }
        : marketResearchNavigation.disabledStageReasons;
  const marketResearchUpdatedStageIndices = React.useMemo(() => {
    if (!marketResearchEditAwareness) return [];
    return marketResearchEditAwareness.stages.flatMap((summary) => {
      if (!summary.has_unreviewed_other_edit) return [];
      const stageIndex = marketResearchStages.findIndex((stage) => stage.backendCode === summary.stage_code);
      // The compact server projection is already stage-authorized. This local
      // check keeps conditional/disabled and Customer-hidden stages from
      // becoming an availability signal while React transitions topology.
      if (stageIndex < 0 || marketResearchNavigation.disabledStageIndices.includes(stageIndex)) return [];
      return [stageIndex];
    });
  }, [marketResearchEditAwareness, marketResearchNavigation.disabledStageIndices, marketResearchStages]);
  const marketResearchCurrentStageInlineEdits = React.useMemo<MarketResearchInlineEdit[]>(() => {
    const backendCode = marketResearchStages[currentStage]?.backendCode;
    if (!backendCode || !marketResearchEditAwareness) return [];
    // These are server-filtered to other-party edits and survive the local
    // acknowledgement state change for this mounted stage, so a viewer can
    // still inspect the marker after its navigation dot clears. A later normal
    // summary refresh remains authoritative when they return to the stage.
    return marketResearchEditAwareness.stages.find((entry) => entry.stage_code === backendCode)
      ?.unreviewed_other_edits ?? [];
  }, [currentStage, marketResearchEditAwareness, marketResearchStages]);
  const marketResearchStageIndexForBackend = useCallback((backendCode: number): number => {
    const exactIndex = marketResearchStages.findIndex((stage) => stage.backendCode === backendCode);
    if (exactIndex >= 0) return exactIndex;
    // A stale downstream index can only be repaired for the limited Customer
    // route after the server has projected the caller view. Do not request or
    // render the skipped stage while doing so.
    if (isCustomerMarketResearch) return Math.max(0, marketResearchStages.length - 1);
    return marketResearchBackendCodeToUiIndex(
      backendCode,
      marketResearchDd2579Enabled,
      marketResearchBrandNameJaFeatureEnabled,
    );
  }, [
    isCustomerMarketResearch,
    marketResearchBrandNameJaFeatureEnabled,
    marketResearchDd2579Enabled,
    marketResearchStages,
  ]);
  // The ordinary flow load does not carry receipt details. Re-authorize via
  // the dedicated minimal availability projection so the unnumbered CO/CS
  // row can be enabled without adding receipt data to generic flow metadata.
  useEffect(() => {
    let cancelled = false;
    if (isViewOnly || !hasConfirmedContractingMarketResearchFlow || !flowId) {
      setMarketResearchContractingHandoffState('not_handed_off');
      setMarketResearchContractingHandoffVerifiedFlowId(null);
      return () => { cancelled = true; };
    }
    setMarketResearchContractingHandoffState('not_handed_off');
    setMarketResearchContractingHandoffVerifiedFlowId(null);
    void getMarketResearchHandoffAvailability(flowId)
      .then((conversation) => {
        if (!cancelled) {
          setMarketResearchContractingHandoffState(conversation.handoff.state);
          setMarketResearchContractingHandoffVerifiedFlowId(flowId);
        }
      })
      .catch(() => {
        // Prior handoff/no-access is deliberately indistinguishable in the
        // stepper. The dedicated route repeats authorization before rendering.
        if (!cancelled) {
          setMarketResearchContractingHandoffState('not_handed_off');
          setMarketResearchContractingHandoffVerifiedFlowId(null);
        }
      });
    return () => { cancelled = true; };
  }, [flowId, hasConfirmedContractingMarketResearchFlow, isViewOnly]);
  useEffect(() => {
    let cancelled = false;
    if (isViewOnly || workflowType !== 'market_research' || !flowId
      || activeFlowRouteRef.current?.flowId !== flowId || marketResearchFlowView === 'unknown') {
      setMarketResearchEditAwareness(null);
      return () => { cancelled = true; };
    }
    setMarketResearchEditAwareness(null);
    void getMarketResearchEditAwarenessSummary(flowId)
      .then((summary) => {
        if (!cancelled) setMarketResearchEditAwareness(summary);
      })
      .catch(() => {
        // Edit awareness must never block normal stage navigation or render a
        // false update cue when its private projection is unavailable.
        if (!cancelled) setMarketResearchEditAwareness(null);
      });
    return () => { cancelled = true; };
  }, [flowId, workflowType, isViewOnly, marketResearchFlowView]);
  const acknowledgeLoadedMarketResearchStageEdit = useCallback((candidate: LoadedMarketResearchStageEdit) => {
    if (!workflowWriteContextRef.current.allowed || workflowWriteContextRef.current.flowId !== candidate.flowId) return;
    const acknowledgementKey = `${candidate.flowId}:${(userEmail ?? '').trim().toLowerCase()}:${candidate.stageCode}:${candidate.revision}`;
    if (marketResearchEditAcknowledgingRef.current.has(acknowledgementKey)) return;
    marketResearchEditAcknowledgingRef.current.add(acknowledgementKey);
    void acknowledgeMarketResearchStageEdit(candidate.flowId, candidate.stageCode, candidate.revision)
      .then(() => {
        if (!workflowWriteContextRef.current.allowed || workflowWriteContextRef.current.flowId !== candidate.flowId) return;
        setMarketResearchEditAwareness((current) => {
          if (!current) return current;
          return {
            ...current,
            stages: current.stages.map((entry) => (
              entry.stage_code === candidate.stageCode && entry.revision === candidate.revision
                ? { ...entry, has_unreviewed_other_edit: false, unreviewed_other_edits: [] }
                : entry
            )),
          };
        });
        if (
          marketResearchLoadedStageEditRef.current?.flowId === candidate.flowId
          && marketResearchLoadedStageEditRef.current.stageCode === candidate.stageCode
          && marketResearchLoadedStageEditRef.current.revision === candidate.revision
        ) {
          marketResearchLoadedStageEditRef.current = null;
        }
      })
      .catch(() => {
        // A failed acknowledgement is intentionally retryable on the next
        // successful visit and exit; leave local projection untouched.
      })
      .finally(() => {
        marketResearchEditAcknowledgingRef.current.delete(acknowledgementKey);
      });
  }, [userEmail]);
  // The existing stage renderer owns content hydration. This narrow readiness
  // check simply prevents a stepper click, failed request, or stale route from
  // being treated as a completed view. It deliberately does *not* acknowledge
  // yet: the indicator remains visible while the viewer is in the stage.
  useEffect(() => {
    if (isViewOnly || workflowType !== 'market_research' || !flowId || !marketResearchEditAwareness) return undefined;
    const selectedStage = marketResearchStages[currentStage];
    if (!selectedStage || marketResearchNavigation.disabledStageIndices.includes(currentStage)) return undefined;
    const update = marketResearchEditAwareness.stages.find((entry) => (
      entry.stage_code === selectedStage.backendCode && entry.has_unreviewed_other_edit
    ));
    if (!update) return undefined;

    const readiness = {
      candidate: {
        flowId,
        stageCode: selectedStage.backendCode,
        revision: update.revision,
      },
      ready: false,
    };
    // A prior quick departure can still be waiting on its readiness request.
    // Re-entering the same stage starts a new review visit, so that older
    // departure must not clear the marker while this new visit is still open.
    const pendingExit = marketResearchPendingStageEditExitRef.current;
    if (
      pendingExit
      && pendingExit.candidate.flowId === readiness.candidate.flowId
      && pendingExit.candidate.stageCode === readiness.candidate.stageCode
      && pendingExit.candidate.revision === readiness.candidate.revision
    ) {
      marketResearchPendingStageEditExitRef.current = null;
    }
    marketResearchStageEditReadinessRef.current = readiness;
    void (async () => {
      try {
        // Do not clear an update merely because the stepper was clicked. The
        // existing stage UI projection must first be successfully fetched for
        // this exact authorized stage. Its content remains owned by the normal
        // stage renderer; this narrow request is the readiness boundary for
        // the acknowledgement and carries no acknowledgement identity.
        const response = await fetchWithChatFeedback(
          `${API_BASE_URL}/api/flows/${encodeURIComponent(flowId)}/stage/${selectedStage.backendCode}/ui`,
          { credentials: 'include', headers: { Accept: 'application/json' } },
        );
        if (!response.ok) return;
        if (!workflowWriteContextRef.current.allowed || workflowWriteContextRef.current.flowId !== flowId) return;
        // A successful UI route is the authorization/load boundary.  ``null``
        // is also a valid response: it means this stage has no derived UI
        // cache yet, not that the authorized request failed. This is common
        // for custom Market Research stages (notably Brand-name J&A) whose
        // visible data is loaded by their own stage API. Requiring an object
        // here therefore made those edit indicators impossible to acknowledge.
        // Do not parse the body: a successful no-content response is equally
        // valid, while 4xx/5xx responses still retain the indicator.
        readiness.ready = true;
        // Do not let an old response become the active-stage cursor after a
        // later stage has already mounted. It may still complete a queued,
        // same-flow exit below.
        if (marketResearchStageEditReadinessRef.current === readiness) {
          marketResearchLoadedStageEditRef.current = readiness.candidate;
        }
        const pendingExit = marketResearchPendingStageEditExitRef.current;
        if (pendingExit && shouldAcknowledgeMarketResearchStageOnExit(
          pendingExit.departing,
          pendingExit.next,
          readiness.candidate,
        )) {
          marketResearchPendingStageEditExitRef.current = null;
          acknowledgeLoadedMarketResearchStageEdit(readiness.candidate);
        }
      } catch {
        // A failed readiness request leaves the dot in place. Edit awareness
        // must never disturb the normal stage or acknowledge a failed load.
      }
    })();
    return undefined;
  }, [
    acknowledgeLoadedMarketResearchStageEdit,
    currentStage,
    flowId,
    marketResearchEditAwareness,
    isViewOnly,
    marketResearchNavigation.disabledStageIndices,
    marketResearchStages,
    workflowType,
  ]);
  // Acknowledge only when the viewer navigates from a ready, dotted stage to
  // another stage in the *same* MR flow. Flow switches, page exits, a failed
  // projection, and topology refreshes intentionally leave the indicator
  // pending so a later genuine visit can review it.
  useEffect(() => {
    const selectedStage = marketResearchStages[currentStage];
    const nextRoute: MarketResearchStageRoute = {
      flowId,
      workflowType,
      appPage,
      stageIndex: currentStage,
      stageCode: selectedStage?.backendCode ?? null,
    };
    const departingRoute = marketResearchPreviousStageRouteRef.current;
    const readiness = marketResearchStageEditReadinessRef.current;
    const candidate = marketResearchLoadedStageEditRef.current
      ?? readiness?.candidate
      ?? null;
    marketResearchPreviousStageRouteRef.current = nextRoute;
    // The first rendered route has no departure. Keep the ref current for the
    // next navigation, but never queue an acknowledgement without a concrete
    // same-flow departure route.
    if (!departingRoute) return;
    if (!shouldAcknowledgeMarketResearchStageOnExit(departingRoute, nextRoute, candidate)) return;
    if (readiness?.candidate === candidate && !readiness.ready) {
      // The viewer has actually left this same stage, but the proof that its
      // authorized UI projection loaded is still in flight. Preserve only the
      // safe route/cursor tuple; failures and flow switches never acknowledge.
      marketResearchPendingStageEditExitRef.current = {
        departing: departingRoute,
        next: nextRoute,
        candidate,
      };
      return;
    }
    acknowledgeLoadedMarketResearchStageEdit(candidate);
  }, [
    acknowledgeLoadedMarketResearchStageEdit,
    appPage,
    currentStage,
    flowId,
    marketResearchStages,
    workflowType,
  ]);
  // GPC (Government Purchase Card) cross-stage data. GPC is a new vertical slice
  // sharing no state with market_research (see gpc-workflow implplan §0).
  // Phase 8 ([NOTES-3]): Stage 1 is item-only (no buy params); Stage 3 carries
  // the searched price history + derived unit price + the threshold result.
  const [gpcRequirementText, setGpcRequirementText] = useState<string>('');
  const [gpcNavigationLoading, setGpcNavigationLoading] = useState(false);
  const [gpcServerAvailableFormStages, setGpcServerAvailableFormStages] = useState<GpcFormStageKey[] | null>(null);
  const [gpcPriceSearchInvalidated, setGpcPriceSearchInvalidated] = useState(false);
  const [gpcRequiredFieldsValidation, setGpcRequiredFieldsValidation] = useState<Record<string, unknown> | null>(null);
  const [gpcQuestions, setGpcQuestions] = useState<Question[]>([]);
  const [gpcAnswers, setGpcAnswers] = useState<Record<string, string>>({});
  const [gpcMfr, setGpcMfr] = useState<MFR | null>(null);
  const [gpcPriceQuotes, setGpcPriceQuotes] = useState<GpcPriceQuote[]>([]);
  // Stage 802 may legitimately complete with no price quotes. Retain that
  // completion fact separately so reopening Product Discovery does not treat
  // the empty list as an unsearched stage and repeat the agent search.
  const [gpcPriceSearchComplete, setGpcPriceSearchComplete] = useState(false);
  const [gpcSearchGeneration, setGpcSearchGeneration] = useState<string | null>(null);
  const [gpcUnitPrice, setGpcUnitPrice] = useState<number | null>(null);
  const [gpcThreshold, setGpcThreshold] = useState<GpcThresholdResult | null>(null);
  // Buy params restored from a reopened flow (carries the agent buy type + quantity).
  const [gpcBuyParams, setGpcBuyParams] = useState<GpcBuyParams>({});
  // Path-B mandatory-source decision. Product Discovery's threshold result seeds
  // a base decision; the dedicated Mandatory Source stage (803) confirms the
  // chosen sources. Lifted + restored so it persists across navigation/restore.
  const [gpcDecision, setGpcDecision] = useState<GpcSourceDecision | null>(null);
  // Per-source result status belongs to the mandatory-source search result,
  // not just the live component, so restore it with the products on re-entry.
  const [gpcSourceStatus, setGpcSourceStatus] = useState<Record<string, GpcSourceStatus>>({});
  const [gpcCarpetIiCheck, setGpcCarpetIiCheck] = useState<GpcCarpetIiCheck | null>(null);
  // Scoped mandatory-source product/quote results produced by the Mandatory
  // Source stage (803). Fed back as `initialProducts` on restore.
  const [gpcProducts, setGpcProducts] = useState<Product[]>([]);
  // Selected Mandatory Source product IDs (804) — used to pre-seed the
  // Competition Quotes stage (805) from the chosen products.
  const [gpcSelectedProductIds, setGpcSelectedProductIds] = useState<string[]>([]);
  // The EXPLICIT award pick (the Mandatory Source "Award" radio) + its override
  // reason (required when the award is not the lowest-priced checked product).
  // These route the chosen vendor/product to every downstream form and are
  // persisted onto stage 804. Empty award id ⇒ backend falls back to lowest.
  const [gpcAwardProductId, setGpcAwardProductId] = useState<string>('');
  const [gpcAwardOverrideReason, setGpcAwardOverrideReason] = useState<string>('');
  // NAICS/PSC selection (803) + the single award vendor (805) — both paths.
  const [gpcNaicsPsc, setGpcNaicsPsc] = useState<GpcNaicsPsc | null>(null);
  const [gpcVendor, setGpcVendor] = useState<GpcVendor | null>(null);
  // The award vendor's confirmed small-business flag, lifted from the Mandatory
  // Source stage (null = unknown/unconfirmed). Gates DD 2579.
  const [gpcAwardIsSmall, setGpcAwardIsSmall] = useState<boolean | null>(null);
  // Competition quotes (806, Path B) documenting price reasonableness.
  const [gpcCompetitionQuotes, setGpcCompetitionQuotes] = useState<GpcPriceQuote[]>([]);

  // Per-form (S4–S11) cross-stage data. Each is fed back to its stage as the
  // `initial*` restore prop and lifted on completion. eCARS (S8) reuses the
  // existing approval model.
  const [gpc889, setGpc889] = useState<Gpc889RepresentationData | null>(null);
  const [gpcAbstract, setGpcAbstract] = useState<GpcAbstractData | null>(null);
  const [gpcDd2579, setGpcDd2579] = useState<GpcDd2579Data | null>(null);
  const [gpcAfForm9, setGpcAfForm9] = useState<GpcAfForm9Data | null>(null);
  const [gpcApproval, setGpcApproval] = useState<GpcApproval | null>(null);
  const [gpcTaxExempt, setGpcTaxExempt] = useState<GpcTaxExemptData | null>(null);
  const [gpcReceipt, setGpcReceipt] = useState<GpcReceiptData | null>(null);
  const [gpcIuid, setGpcIuid] = useState<GpcIuidData | null>(null);

  // The REAL vendor_is_small: the selected vendor's derived small-business flag.
  // Unknown (null/no vendor) defaults to `true` (small) so DD 2579 stays OFF
  // until a non-small vendor is actually confirmed.
  // Prefer the explicitly-confirmed award small-business flag; fall back to the
  // restored vendor's SAM flag; default true (no DD-2579) only when fully unknown.
  const gpcVendorIsSmall =
    gpcAwardIsSmall ?? gpcVendor?.is_small_business ?? true;

  // The resolved AWARD product: the product whose id == the explicit award pick
  // (`gpcAwardProductId`); else the lowest-priced CHECKED product (backward
  // compatible). Mirrors the backend `_resolve_award_product` precedence so the
  // form vendor names match the chosen vendor on both the client and server.
  const gpcAwardProduct = React.useMemo<Product | null>(() => {
    if (gpcProducts.length === 0) return null;
    const byId = new Map<string, Product>();
    gpcProducts.forEach((p, i) => byId.set(p.id ?? `gpc-product-${i}`, p));
    // 1) explicit award pick, when it is still in the checked competition set.
    if (
      gpcAwardProductId &&
      gpcSelectedProductIds.includes(gpcAwardProductId) &&
      byId.has(gpcAwardProductId)
    ) {
      return byId.get(gpcAwardProductId) ?? null;
    }
    // 2) else the lowest-priced CHECKED product.
    const checkedIds = gpcSelectedProductIds.filter((id) => byId.has(id));
    if (checkedIds.length === 0) return null;
    let bestId = checkedIds[0];
    let bestPrice = Infinity;
    for (const id of checkedIds) {
      const raw = byId.get(id)?.price;
      const cleaned = raw == null ? '' : String(raw).replace(/[^0-9.]/g, '');
      const n = cleaned ? Number(cleaned) : NaN;
      if (Number.isFinite(n) && n > 0 && n < bestPrice) {
        bestPrice = n;
        bestId = id;
      }
    }
    return byId.get(bestId) ?? null;
  }, [gpcProducts, gpcAwardProductId, gpcSelectedProductIds]);

  // The award vendor NAME routed to the per-form stages (889 / abstract /
  // tax-exempt / receipt). Prefer the resolved award product's real vendor /
  // contract holder; fall back to the persisted award `GpcVendor` legal name,
  // then the decision's recommended source key (the prior behavior).
  const gpcAwardVendorName =
    (gpcAwardProduct as any)?.vendor ||
    gpcAwardProduct?.manufacturer ||
    gpcVendor?.legal_name ||
    gpcDecision?.recommended_source ||
    '';

  const gpcFormStageArgs = React.useMemo(() => {
    const isPathB = gpcThreshold?.sourcingPath === 'mandatory_source';
    // Path B "sources obtained" = the number of DISTINCT GSA Schedule / contract
    // numbers among ALL discovered mandatory-source products (FY26 deck Slide 42:
    // the ≥3 rule requires 3 vendors each on a DIFFERENT schedule — "no
    // exceptions"). The per-row checkboxes were removed, so the competition set
    // is ALWAYS the full product list; count distinct schedules across every
    // product. Path A still uses the open-market priced-quote history.
    const sourceCount = isPathB
      ? gpcDistinctSourceCount(gpcProducts)
      : gpcPriceQuotes.filter((q) => q.price !== null && q.price !== undefined).length;
    return {
      sourcingPath: gpcThreshold?.sourcingPath ?? null,
      dollarAmount: gpcThreshold?.total ?? gpcBuyParams.dollar_amount ?? null,
      unitPrice: gpcUnitPrice ?? gpcBuyParams.unit_price ?? null,
      buyType: gpcThreshold?.buyParams?.buy_type ?? gpcBuyParams.buy_type ?? null,
      sourceCount,
      vendorIsSmall: gpcVendorIsSmall,
      category: gpcDecision?.category ?? '',
      pilferable: false,
      taxable: null,
    };
  }, [
    gpcThreshold,
    gpcBuyParams,
    gpcUnitPrice,
    gpcPriceQuotes,
    gpcProducts,
    gpcVendorIsSmall,
    gpcDecision,
  ]);

  const gpcAvailableFormStages = React.useMemo<GpcFormStageKey[]>(
    () => gpcPriceSearchInvalidated ? [] :
      (gpcServerAvailableFormStages ?? applicableGpcFormStages(gpcFormStageArgs)),
    [gpcFormStageArgs, gpcServerAvailableFormStages, gpcPriceSearchInvalidated],
  );

  const gpcAf9Requirement = React.useMemo(
    () => resolveGpcStageRequirement('af_form9', gpcFormStageArgs, gpcRequiredFieldsValidation),
    [gpcFormStageArgs, gpcRequiredFieldsValidation],
  );
  const gpcDd2579Requirement = React.useMemo(
    () => resolveGpcStageRequirement('dd2579', gpcFormStageArgs, gpcRequiredFieldsValidation),
    [gpcFormStageArgs, gpcRequiredFieldsValidation],
  );
  const gpcAvailableSet = React.useMemo(
    () => new Set<GpcFormStageKey>(gpcAvailableFormStages),
    [gpcAvailableFormStages],
  );
  const gpcQuotesInitial = React.useMemo(
    () => reconcileSeededQuotes(gpcCompetitionQuotes, gpcProducts, gpcSelectedProductIds),
    [gpcCompetitionQuotes, gpcProducts, gpcSelectedProductIds],
  );
  // The GPC stepper ALWAYS shows the 3 base stages followed by EVERY form stage
  // in canonical order (fixed length). Inapplicable form stages are greyed in
  // place (see `gpcDisabledFormStageIndices`) and auto-skipped during nav, but
  // never removed — so a stage's UI index never shifts between Path A and B.
  const gpcStages = React.useMemo(
    () => [
      ...GPC_STAGES,
      ...allGpcFormStages().map((key) => GPC_FORM_STAGE_META[key]),
    ],
    [],
  );
  // Stepper indices to grey out: the form stages whose key is NOT applicable.
  const gpcDisabledFormStageIndices = React.useMemo(
    () => gpcDisabledStageIndices(gpcAvailableSet),
    [gpcAvailableSet],
  );

  // Per-stage tooltip reasons (stepper index → specific "why inapplicable"
  // text), mirroring the same gates that produce `gpcAvailableFormStages`.
  // Recomputed from the same inputs so each greyed stage explains itself.
  const gpcDisabledFormStageReasons = React.useMemo(
    () => gpcServerAvailableFormStages === null ? gpcDisabledStageReasonMap(gpcFormStageArgs) : {},
    [gpcFormStageArgs, gpcServerAvailableFormStages],
  );
  const complexApplicableStages = React.useMemo<ComplexStageKey[]>(
    () => applicableComplexStages(complexAcqMode, complexSatThreshold, complexPop, false),
    [complexAcqMode, complexSatThreshold, complexPop],
  );
  const complexApplicableSet = React.useMemo(
    () => new Set<ComplexStageKey>(complexApplicableStages),
    [complexApplicableStages],
  );
  // A generated PWS replaces the four uploaded-PWS review stages.  It hands
  // directly into the shared downstream/complex acquisition work just as the
  // generated One-time Construction branch does.
  const activeComplexApplicableSet = React.useMemo(
    () => servicesPwsOrigin === 'requirements_generation'
      ? new Set<ComplexStageKey>(
        complexApplicableStages.filter((key) => ![
          'sow_upload', 'context_questions', 'suggestions', 'manual_edit',
        ].includes(key)),
      )
      : complexApplicableSet,
    [complexApplicableStages, complexApplicableSet, servicesPwsOrigin],
  );
  const complexDisabledIndices = React.useMemo(
    () => complexDisabledStageIndices(activeComplexApplicableSet),
    [activeComplexApplicableSet],
  );
  const complexDisabledReasons = React.useMemo(
    () => complexDisabledStageReasonMap(complexAcqMode, complexSatThreshold, complexPop, false),
    [complexAcqMode, complexSatThreshold, complexPop],
  );
  // Hydrate the persisted payloads for the complex-only stages that rely solely
  // on `initialData` (they don't self-fetch). Runs once per complex flow so a
  // reload / restore lands on the stage with its prior work already seeded. A
  // load miss is not an error — the stage falls back to an empty form.
  useEffect(() => {
    if (workflowType !== 'complex_services' || !flowId) {
      return undefined;
    }
    let cancelled = false;
    setComplexStageDataLoaded(false);
    setComplexStageData({});
    (async () => {
      const [mrr, ige, igd, pwsDiff, options, solicitation, clearance, record, form9] = await Promise.all([
        getComplexServicesMrr(flowId).catch(() => null),
        getComplexServicesIge(flowId).catch(() => null),
        getComplexServicesIgd(flowId).catch(() => null),
        getComplexServicesPwsDiff(flowId).catch(() => null),
        getComplexServicesOptions(flowId).catch(() => null),
        getComplexServicesSolicitation(flowId).catch(() => null),
        getComplexServicesClearance(flowId).catch(() => null),
        getComplexServicesRecord(flowId).catch(() => null),
        getComplexServicesForm9(flowId).catch(() => null),
      ]);
      if (cancelled) return;
      setComplexStageData({
        mrr,
        ige,
        igd,
        pws_diff: pwsDiff,
        options,
        solicitation,
        clearance,
        record,
        form9,
      });
      setComplexStageDataLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [workflowType, flowId]);
  const [stageRatings, setStageRatings] = useState<Record<string, RatingValue>>({});
  const [clsCorrectionResult, setClsCorrectionResult] = useState<CLSCorrectionResult | null>(null);
  const [clsIsProcessing, setClsIsProcessing] = useState(false);
  const [sowIsProcessing, setSowIsProcessing] = useState(false);
  const [foprData, setFoprData] = useState<FOPRData | null>(null);
  const [servicesComboData, setServicesComboData] = useState<ServicesComboData | null>(null);

  const [clsUserAnswers, setClsUserAnswers] = useState<Record<string, string> | null>(null);
  const [clsExtractedParams, setClsExtractedParams] = useState<CLSContractParams | null>(null);
  const [clsUserOverrides, setClsUserOverrides] = useState<Map<string, 'added' | 'removed'>>(new Map());
  const [clsHiddenClauses, setClsHiddenClauses] = useState<Set<string>>(new Set());

  const [clsManuallyAddedClauses, setClsManuallyAddedClauses] = useState<MappedClause[]>([]);
  const [clsSectionOverrides, setClsSectionOverrides] = useState<Map<string, string>>(new Map());
  const [showSowBackDialog, setShowSowBackDialog] = useState(false);
  const [sowBackTarget, setSowBackTarget] = useState<0 | 1>(1);
  const [showServicesBackDialog, setShowServicesBackDialog] = useState(false);
  const [enteredEditFromSuggestions, setEnteredEditFromSuggestions] = useState(false);
  const [sowEditDirty, setSowEditDirty] = useState(false);
  const sowEditDirtyRef = useRef(false);
  const sowEditRevisionRef = useRef(0);

  const handleSowEditDirtyChange = useCallback((dirty: boolean) => {
    sowEditDirtyRef.current = dirty;
    setSowEditDirty(dirty);
  }, []);

  const handleSowDocumentEdit = useCallback(() => {
    // Set this synchronously with the editor mutation. Waiting for the child's
    // dirty-state effect would leave a microtask-sized window where a pending
    // handoff response could mistake the new edit for already-saved work.
    sowEditDirtyRef.current = true;
    sowEditRevisionRef.current += 1;
  }, []);

  const [standaloneMode, setStandaloneMode] = useState<boolean>(false);
  // Backend-marked training samples remain read-only in every role. Ordinary
  // workflows use the selected role's server-enforced action permissions.
  const effectiveIsViewOnly = isViewOnly || presentationCapabilities.readOnly;
  const canWriteCurrentWorkflow = (targetFlowId = flowId): boolean => (
    !!targetFlowId && workflowWriteContextRef.current.flowId === targetFlowId
    && workflowWriteContextRef.current.allowed
    && activeFlowRouteRef.current?.flowId === targetFlowId
  );
  
  // Pipeline data state
  const [questions, setQuestions] = useState<any[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  useEffect(() => {
    console.log('[App] answers state updated:', answers);
    (window as any).__APP_ANSWERS = answers;
  }, [answers]);
  const [mfr, setMfr] = useState<MFR | null>(null);
  const [initialCommodityDescription, setInitialCommodityDescription] = useState<string>('');
  // Safe display metadata only. Original bytes, paths, digest, and document
  // text remain server-side and are never placed in React state.
  const [initialRequirementDocument, setInitialRequirementDocument] = useState<RequirementDocumentMetadata | null>(null);
  // Full current Stage-3 listing for Product Discovery display. This must stay
  // separate from `products`, which is the selected downstream subset.
  const [productDiscoveryResults, setProductDiscoveryResults] = useState<Product[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [stage3SelectedIds, setStage3SelectedIds] = useState<string[]>([]);
  const [stage3SelectedKeys, setStage3SelectedKeys] = useState<string[]>([]);
  const [productsWithPSC, setProductsWithPSC] = useState<ProductWithPSC[]>([]);
  const [pscMethodUsed, setPscMethodUsed] = useState<CodeMethod>('unknown');
  const [productsWithCodes, setProductsWithCodes] = useState<ProductWithCodes[]>([]);
  const [naicsMethodUsed, setNaicsMethodUsed] = useState<CodeMethod>('unknown');
  const [vendors, setVendors] = useState<ProductWithVendors[]>([]);
  const [sectionBDocument, setSectionBDocument] = useState<string>('');
  const [sectionCDocument, setSectionCDocument] = useState<string>('');
  const [sectionDDocument, setSectionDDocument] = useState<string>('');
  const [sectionEDocument, setSectionEDocument] = useState<string>('');
  // True only for the first ordinary-workflow mount after an MR chat handoff.
  // Its completed canonical records must be reviewed, not regenerated by
  // stage components before their caches hydrate.
  const [preserveMrChatResults, setPreserveMrChatResults] = useState(false);
  const [questionsUseCachedData, setQuestionsUseCachedData] = useState<boolean>(true);
  const [mfrUseCachedData, setMfrUseCachedData] = useState<boolean>(true);
  // Stage 1 multi-product detection count. Set from Stage 1 output —
  // 1 means a single product (default), >1 means the customer's raw input
  // describes multiple distinct products. Surfaced via
  // <MultiItemWarningBanner /> rendered inside StageWrapper for every
  // downstream stage (banner appears only when count > 1).
  const [numItemsDetected, setNumItemsDetected] = useState<number>(1);

  useEffect(() => {
    (window as any).__APP_STATE__ = {
      flowId,
      currentStage,
      questions,
      answers,
      questionsUseCachedData,
      mfrUseCachedData,
      products,
      productsWithPSC,
      productsWithCodes,
      vendors,
    };
    console.log('[App] State updated - vendors.length:', vendors.length);
  }, [flowId, currentStage, questions, answers, questionsUseCachedData, mfrUseCachedData, products, productsWithPSC, productsWithCodes, vendors]);

  const normalizeRequirementDocument = (value: any): RequirementDocumentMetadata | null => {
    if (!value || typeof value !== 'object') return null;
    if (typeof value.filename !== 'string' || typeof value.content_type !== 'string' ||
        typeof value.upload_timestamp !== 'string' || typeof value.revision !== 'number') {
      return null;
    }
    return {
      filename: value.filename,
      content_type: value.content_type,
      upload_timestamp: value.upload_timestamp,
      revision: value.revision,
    };
  };

  useEffect(() => {
    setMaxStageReached(prev => Math.max(prev, currentStage));
  }, [currentStage]);

  const advanceToStage = (stageIndex: number) => {
    setCurrentStage(stageIndex);
    setMaxStageReached(prev => Math.max(prev, stageIndex));
  };

  const confirmCurrentStageAdvance = async (stageNumber: number): Promise<boolean> => {
    if (!canWriteCurrentWorkflow()) return false;
    const targetFlowId = flowId;
    const operationGeneration = workflowOperationGenerationRef.current;
    try {
      await persistMarketResearchCurrentStage(targetFlowId, stageNumber);
    } catch {
      showNotificationMessage('Could not confirm the next stage. Your work remains here; retry to continue.', 'error');
      setPermissionsRefreshVersion((version) => version + 1);
      return false;
    }
    return canWriteCurrentWorkflow(targetFlowId) && operationGeneration === workflowOperationGenerationRef.current;
  };

  // Stage completion handlers
  const resetPipelineState = () => {
    setGpcNavigationLoading(false);
    setQuestions([]);
    setAnswers({});
    setMfr(null);
    setInitialCommodityDescription('');
    setInitialRequirementDocument(null);
    setProductDiscoveryResults([]);
    setProducts([]);
    setProductsWithPSC([]);
    setPscMethodUsed('unknown');
    setProductsWithCodes([]);
    setVendors([]);
    setSectionBDocument('');
    setSectionCDocument('');
    setSectionDDocument('');
    setSectionEDocument('');
    setPreserveMrChatResults(false);
    setIsComplete(false);
    setCurrentStage(0);
    setMaxStageReached(0);
    setStandaloneMode(false);
    setIsViewOnly(false);
    setIsTrainingExample(false);
    setReadOnlyReason(null);
    setConstructionPriceEstimatorApplicable(false);
    setGpcServerAvailableFormStages(null);
    setGpcPriceSearchInvalidated(false);
    setGpcRequiredFieldsValidation(null);
    setMarketResearchDd2579Coordination(null);
    marketResearchDd2579CoordinationRequestRef.current += 1;
    marketResearchDd2579CoordinationInFlightRef.current = null;
    setMarketResearchDd2579CoordinationStatus('idle');
    setMarketResearchDd2579MetadataRequired(false);
    setMarketResearchDd2579Visited(false);
    setMarketResearchDd2579(null);
    setMarketResearchDd2579RestoredState(null);
    marketResearchDd2579FormHydrationFlowRef.current = null;
    marketResearchDd2579FormHydrationRequestRef.current += 1;
    setMarketResearchDd2579FormHydrationStatus('idle');
    setMarketResearchBrandNameDecision(null);
    setMarketResearchBrandNameStatus(null);
    setNotificationBrandNameJaProjection(null);
    setMarketResearchRefreshRequirement(null);
    setMarketResearchClientUpgradeRequired(false);
    setGpcReceipt(null);
    setQuestionsUseCachedData(true);
    setMfrUseCachedData(true);
    setNumItemsDetected(1);
    setClsCorrectionResult(null);
    setClsUserAnswers(null);
    setClsExtractedParams(null);
    setClsUserOverrides(new Map());
    localStorage.removeItem('farchat-session');
  };

  const clearStageDataFrom = (stageIndex: number) => {
    setMaxStageReached(stageIndex);
    switch (stageIndex) {
      case 0:
        setMfr(null);
        setProductDiscoveryResults([]);
        setProducts([]);
        setProductsWithPSC([]);
        setPscMethodUsed('unknown');
        setProductsWithCodes([]);
        setVendors([]);
        setSectionBDocument('');
        setSectionCDocument('');
        setSectionDDocument('');
        setSectionEDocument('');
        setIsComplete(false);
        break;
      case 1:
        setProductDiscoveryResults([]);
        setProducts([]);
        setProductsWithPSC([]);
        setPscMethodUsed('unknown');
        setProductsWithCodes([]);
        setVendors([]);
        setSectionBDocument('');
        setSectionCDocument('');
        setSectionDDocument('');
        setSectionEDocument('');
        setIsComplete(false);
        break;
      case 2:
        setProductsWithPSC([]);
        setPscMethodUsed('unknown');
        setProductsWithCodes([]);
        setVendors([]);
        setSectionBDocument('');
        setSectionCDocument('');
        setSectionDDocument('');
        setSectionEDocument('');
        setIsComplete(false);
        break;
      case 3:
        setProductsWithCodes([]);
        setVendors([]);
        setSectionBDocument('');
        setSectionCDocument('');
        setSectionDDocument('');
        setSectionEDocument('');
        setIsComplete(false);
        break;
      case 4:
        setVendors([]);
        setSectionBDocument('');
        setSectionCDocument('');
        setSectionDDocument('');
        setSectionEDocument('');
        setIsComplete(false);
        break;
      case 5:
        setSectionBDocument('');
        setSectionCDocument('');
        setSectionDDocument('');
        setSectionEDocument('');
        setIsComplete(false);
        break;
      case 6:
        setSectionCDocument('');
        setSectionDDocument('');
        setSectionEDocument('');
        setIsComplete(false);
        break;
      case 7:
        setSectionDDocument('');
        setSectionEDocument('');
        setIsComplete(false);
        break;
      case 8:
        setSectionEDocument('');
        setIsComplete(false);
        break;
      case 9:
        setIsComplete(false);
        break;
      default:
        break;
    }
  };

  const questionsCompletionPendingRef = useRef(false);
  const handleQuestionsComplete = async (
    questionData: any[],
    answerData: Record<string, string>,
    commodityDesc: string,
    useCachePreference: boolean,
  ) => {
    if (!canWriteCurrentWorkflow() || questionsCompletionPendingRef.current) return;
    questionsCompletionPendingRef.current = true;
    console.log('[App] handleQuestionsComplete called');
    const completingFlowId = flowId;
    const operationGeneration = workflowOperationGenerationRef.current;
    try {
      if (completingFlowId) {
        const saved = await saveStageAnswers(completingFlowId, questionData, answerData, commodityDesc);
        if (!saved.success) throw new Error('Could not save your answers. Your edits remain here; retry to continue.');
        if (!canWriteCurrentWorkflow(completingFlowId) || operationGeneration !== workflowOperationGenerationRef.current) return;
        const stageUpdated = await updateCurrentStage(completingFlowId, 2);
        if (!stageUpdated.success) throw new Error('Could not confirm the next stage. Your answers were saved; retry to continue.');
      }
    } catch (error) {
      showNotificationMessage(error instanceof Error ? error.message : 'Could not save your answers. Retry to continue.', 'error');
      setPermissionsRefreshVersion((version) => version + 1);
      return;
    } finally {
      questionsCompletionPendingRef.current = false;
    }
    if (!canWriteCurrentWorkflow(completingFlowId) || operationGeneration !== workflowOperationGenerationRef.current) return;
    // Try to derive commodity description from first question or existing input if missing
    const prevSig = normalizeStage0Signature(questions, answers);
    const nextSig = normalizeStage0Signature(questionData, answerData);
    if (prevSig !== nextSig) {
      clearStageDataFrom(0);
    }
    setQuestions(questionData);
    setAnswers(answerData);
    setQuestionsUseCachedData(useCachePreference);
    setMfrUseCachedData(useCachePreference);
    // Set commodity directly from Stage 1 input for clean architecture
    if (commodityDesc && commodityDesc.trim()) {
      console.log('[App] Setting initialCommodityDescription from Stage 1 input:', commodityDesc);
      setInitialCommodityDescription(commodityDesc);
    } else {
      console.log('[App] Empty commodityDesc passed; keeping existing initialCommodityDescription:', initialCommodityDescription);
    }
    advanceToStage(1);
    showNotificationMessage('Questions answered! Moving to Salient Characteristics.', 'success');
    if (flowId) {
      // Business Impact auto-gen (spec §2.6.3 trigger #1) — fire-and-forget,
      // concurrent with the downstream Salient Characteristics generation.
      const biQuestionIds = new Set<string>();
      (questionData || []).forEach((q: any, idx: number) => {
        if (q?.category === 'business_impact') {
          biQuestionIds.add(String(q?.id ?? q?.question_id ?? idx));
        }
      });
      const biHasAnswer = hasNonEmptyBIAnswer(answerData, biQuestionIds);
      void tryFireBusinessImpact(flowId, biHasAnswer);
    }
    saveSession({
      questions: questionData,
      answers: answerData,
      commodityDescription: commodityDesc,
      currentStage: 1,
      questionsUseCachedData: useCachePreference,
    });
  };

  const handleRequirementDocumentChange = (
    document: RequirementDocumentMetadata | null,
    update?: { commodityDescription?: string; invalidateDependentState?: boolean },
  ) => {
    setInitialRequirementDocument(document);
    // Retaining/replacing/removing source provenance never alters the editable
    // requirement. Only a completed extraction changes source text and makes
    // existing Stage 1/2 results stale.
    if (!update?.invalidateDependentState) return;
    if (typeof update.commodityDescription === 'string') {
      setInitialCommodityDescription(update.commodityDescription);
    }
    setQuestions([]);
    setAnswers({});
    setQuestionsUseCachedData(false);
    setMfrUseCachedData(false);
    clearStageDataFrom(0);
  };

  const handleMFRComplete = async (mfrData: MFR) => {
    if (!(await confirmCurrentStageAdvance(3))) return;
    console.log('[App] handleMFRComplete called with mfrData:', mfrData);
    console.log('[App] mfrData.rawText length:', mfrData?.rawText?.length);
    const prevSig = normalizeStage1Signature(mfr);
    const nextSig = normalizeStage1Signature(mfrData);
    setFirstStage3AutoStartFlowId(
      shouldArmFirstProductDiscoveryEntry(flowId, maxStageReached) ? flowId : null,
    );
    if (prevSig !== nextSig) {
      clearStageDataFrom(1);
    }
    setMfr(mfrData);
    advanceToStage(2);
    showNotificationMessage('Salient Characteristics generated! Moving to product discovery.', 'success');
    saveSession({
      mfr: mfrData,
      currentStage: 2,
    });
    console.log('[App] saveSession called with mfrData, checking localStorage...');
    setTimeout(() => {
      const saved = localStorage.getItem('farchat-session');
      if (saved) {
        const session = JSON.parse(saved);
        console.log('[App] Verified localStorage after save - MFR is:', session.stages?.mfr?.mfr);
      }
    }, 100);
  };

  const projectProductDestination = (
    selectedProducts: Product[],
    destination: 4 | 5 | 6,
  ) => {
    // Backend stage codes are durable identities, while the Customer/RA
    // presentation omits the optional Brand-name J&A row unless it is
    // required. Resolve through the active navigation rather than the full
    // contracting topology: otherwise code 6 (Vendor Discovery) becomes the
    // visual index for Section B when J&A is absent.
    const destinationIndex = marketResearchStageIndexForBackend(destination);
    if (destination === 6) {
      const productsWithBothCodes = selectedProducts.map(p => ({
        ...p,
        psc_codes: (p as any).psc_codes || [],
        psc_search_keywords: (p as any).psc_search_keywords || [],
        naics_from_psc: (p as any).naics_from_psc || [],
        naicsCode: (p as any).naicsCode || (p as any).naics_from_psc?.[0],
      }));
      setProductsWithPSC(productsWithBothCodes);
      setProductsWithCodes(productsWithBothCodes);
      advanceToStage(destinationIndex);
      showNotificationMessage(
        effectiveIsViewOnly
          ? 'Navigated to Vendor Discovery'
          : `${selectedProducts.length} products selected. Skipping to Vendor Discovery (manual codes provided).`,
        effectiveIsViewOnly ? 'info' : 'success',
      );
      saveSession({
        products: selectedProducts,
        productsWithPSC: productsWithBothCodes,
        productsWithCodes: productsWithBothCodes,
        currentStage: destinationIndex,
      });
      return;
    }
    if (destination === 5) {
      const productsWithPsc = selectedProducts.map(p => ({
        ...p,
        psc_codes: (p as any).psc_codes || [],
        psc_search_keywords: (p as any).psc_search_keywords || [],
      }));
      setProductsWithPSC(productsWithPsc);
      advanceToStage(destinationIndex);
      showNotificationMessage(
        effectiveIsViewOnly
          ? 'Navigated to NAICS Code Mapping'
          : `${selectedProducts.length} products selected. Skipping to NAICS Mapping (manual PSC codes provided).`,
        effectiveIsViewOnly ? 'info' : 'success',
      );
      saveSession({
        products: selectedProducts,
        productsWithPSC: productsWithPsc,
        currentStage: destinationIndex,
      });
      return;
    }
    advanceToStage(destinationIndex);
    showNotificationMessage(
      effectiveIsViewOnly
        ? 'Navigated to PSC Code Assignment'
        : `${selectedProducts.length} products selected. Proceeding to PSC assignment.`,
      effectiveIsViewOnly ? 'info' : 'success',
    );
    saveSession({ products: selectedProducts, currentStage: destinationIndex });
  };

  const handleProductsComplete = (
    selectedProducts: Product[],
    acknowledgedSelection?: ProductSelectionResponse,
    allDiscoveredProducts?: Product[],
  ) => {
    const destination = marketResearchBrandNameJaFeatureEnabled
      ? resolveMarketResearchProductDestination(acknowledgedSelection)
      : (() => {
          const skipPsc = localStorage.getItem(`skip-psc-${flowId}`) === 'true';
          const skipNaics = localStorage.getItem(`skip-naics-${flowId}`) === 'true';
          return skipPsc ? (skipNaics ? 6 : 5) : 4;
        })();
    if (destination === null) {
      showNotificationMessage(USER_FACING_ERROR_MESSAGES.wait_and_retry, 'error');
      return;
    }
    const prevSig = normalizeStage2Signature(products);
    const nextSig = normalizeStage2Signature(selectedProducts);
    if (prevSig !== nextSig) {
      clearStageDataFrom(2);
    }
    // Product Discovery currently performs an automatic bounded handoff, but
    // retain the full listing explicitly so returning from downstream stages
    // never relies on cache validity to repaint Stage 3.
    setProductDiscoveryResults(allDiscoveredProducts || selectedProducts);
    setProducts(selectedProducts);
    try {
      setStage3SelectedIds(selectedProducts.map(p => String(p.id)));
      const s = (x: any) => (typeof x === 'string' ? x.trim().toLowerCase() : String(x || '').trim().toLowerCase());
      const keys = selectedProducts.map((p:any) => {
        const piid = s(p.specifications?.piid || p.specifications?.award_id || '');
        return `${s(p.product_name || p.name)}|${s(p.manufacturer)}|${s(p.model)}|${piid}`;
      });
      setStage3SelectedKeys(keys);
    } catch {}
    if (marketResearchBrandNameJaFeatureEnabled) {
      setMarketResearchBrandNameDecision(acknowledgedSelection?.brand_name ?? null);
      setMarketResearchBrandNameStatus(
        acknowledgedSelection?.brand_name?.requested ? 'draft' : 'inactive',
      );
    }
    if (destination === 14) {
      // The acknowledgement is authoritative. The Customer navigation still
      // reflects the pre-save no-J&A topology until React commits the new
      // decision, so resolve against the known next Customer topology rather
      // than its stale active list. Contracting navigation already contains
      // the optional J&A row and can use the ordinary active resolver.
      const stageIndex = isCustomerMarketResearch
        ? getCustomerMarketResearchStages(true).findIndex((stage) => stage.backendCode === 14)
        : marketResearchStageIndexForBackend(14);
      setMarketResearchCustomerTerminal(false);
      setMarketResearchCustomerTerminalStageView(false);
      advanceToStage(stageIndex);
      showNotificationMessage(
        effectiveIsViewOnly
          ? 'Navigated to Brand-name J&A'
          : 'Product selection saved. Continue to Brand-name J&A.',
        effectiveIsViewOnly ? 'info' : 'success',
      );
      saveSession({ products: selectedProducts, currentStage: stageIndex });
      return;
    }
    projectProductDestination(selectedProducts, destination);
  };

  const handleMarketResearchBrandNameJaComplete = (completion: BrandNameJaCompletion) => {
    const downstreamProducts = completion.downstream_products as unknown as Product[];
    setMarketResearchBrandNameStatus('ready');
    setProducts(downstreamProducts);
    projectProductDestination(downstreamProducts, completion.post_ja_destination_stage);
  };

  // An uncapped legacy selection is historical evidence only. Return the user
  // to Product Discovery without silently replacing its saved 42-product set;
  // a successful new discovery will create the new capped downstream path.
  const handleRerunProductDiscovery = () => {
    if (!canWriteCurrentWorkflow()) return;
    setMarketResearchCustomerTerminal(false);
    setMarketResearchCustomerTerminalStageView(false);
    setMarketResearchRaCollaborationOpen(false);
    setMarketResearchContractingHandoffState('not_handed_off');
    setMarketResearchContractingHandoffVerifiedFlowId(null);
    clearStageDataFrom(2);
    setProductDiscoveryResults([]);
    setProducts([]);
    setStage3SelectedIds([]);
    setStage3SelectedKeys([]);
    advanceToStage(2);
    if (flowId) {
      updateCurrentStage(flowId, 3).catch((error) => {
        console.error('Failed to return legacy flow to Product Discovery:', error);
      });
    }
  };

  const handlePSCComplete = async (productsWithPSCData: ProductWithPSC[]) => {
    if (!canWriteCurrentWorkflow()) return;
    // Persisted Stage-3 route evidence is authoritative. The local flag is a
    // compatibility fallback only for flows created before the decision record.
    const skipNaics = marketResearchBrandNameDecision
      // Older Stage-3 decision records did not always carry the manual-code
      // arrays. Treat an absent/malformed value as no manual bypass instead
      // of crashing the PSC handoff or skipping an intermediate stage.
      ? Array.isArray(marketResearchBrandNameDecision.manual_naics_codes)
        && marketResearchBrandNameDecision.manual_naics_codes.length > 0
      : localStorage.getItem(`skip-naics-${flowId}`) === 'true';
    const destinationBackendCode: 5 | 6 = skipNaics ? 6 : 5;

    try {
      await persistMarketResearchCurrentStage(flowId, destinationBackendCode);
    } catch (error) {
      if (marketResearchBrandNameJaFeatureEnabled
        && error instanceof MarketResearchRefreshRequiredError) {
        setMarketResearchRefreshRequirement(error.requirement);
      } else if (error instanceof MarketResearchRefreshRequiredError) {
        setMarketResearchClientUpgradeRequired(true);
      } else {
        showNotificationMessage(USER_FACING_ERROR_MESSAGES.wait_and_retry, 'error');
      }
      return;
    }

    // Only clear downstream (Stage 5+) if the Stage 4 inputs actually changed in a way
    // that affects NAICS mapping. Compare a canonical signature of PSC inputs.
    const prevSig = normalizeStage4Signature(productsWithPSC);
    const nextSig = normalizeStage4Signature(productsWithPSCData);
    const stage4Changed = prevSig !== nextSig;

    if (stage4Changed) {
      clearStageDataFrom(3);
    }

    setProductsWithPSC(productsWithPSCData);

    if (skipNaics) {
      // NAICS codes were provided manually - skip to Vendor Discovery
      const productsWithNaics = productsWithPSCData.map(p => ({
        ...p,
        psc_search_keywords: p.psc_search_keywords || [],
        naics_from_psc: (p as any).naics_from_psc || [],
        naicsCode: (p as any).naicsCode || (p as any).naics_from_psc?.[0],
      }));
      setProductsWithCodes(productsWithNaics);
      const destinationIndex = marketResearchStageIndexForBackend(6);
      advanceToStage(destinationIndex);
      showNotificationMessage('PSC codes assigned. Skipping to Vendor Discovery (manual NAICS codes provided).', 'success');
      saveSession({
        productsWithPSC: productsWithPSCData,
        productsWithCodes: productsWithNaics,
        currentStage: destinationIndex,
      });
    } else {
      // Normal flow - go to NAICS Mapping
      const destinationIndex = marketResearchStageIndexForBackend(5);
      advanceToStage(destinationIndex);
      showNotificationMessage('PSC codes assigned. Moving to NAICS mapping.', 'success');
      saveSession({
        productsWithPSC: productsWithPSCData,
        currentStage: destinationIndex,
      });
    }
  };

  const handleNAICSComplete = async (productsWithCodesData: ProductWithCodes[], methodUsed: CodeMethod = 'unknown') => {
    if (!canWriteCurrentWorkflow()) return;
    // The stage component has already persisted the selection. Confirm that
    // the flow's durable current-stage marker can advance before changing the
    // in-memory UI/session, otherwise a refresh can silently put the user
    // back on NAICS despite the UI having moved to Vendor Discovery.
    try {
      await persistNAICSCurrentStage(flowId);
    } catch (error) {
      if (marketResearchBrandNameJaFeatureEnabled
        && error instanceof MarketResearchRefreshRequiredError) {
        setMarketResearchRefreshRequirement(error.requirement);
        return;
      }
      if (error instanceof MarketResearchRefreshRequiredError) {
        setMarketResearchClientUpgradeRequired(true);
        return;
      }
      showNotificationMessage(USER_FACING_ERROR_MESSAGES.wait_and_retry, 'error');
      return;
    }
    const prevSig = normalizeStage5Signature(productsWithCodes);
    const nextSig = normalizeStage5Signature(productsWithCodesData);
    if (prevSig !== nextSig) {
      clearStageDataFrom(4);
    }
    setProductsWithCodes(productsWithCodesData);
    setNaicsMethodUsed(methodUsed);
    const destinationIndex = marketResearchStageIndexForBackend(6);
    advanceToStage(destinationIndex);
    showNotificationMessage('NAICS codes mapped. Proceeding to vendor discovery.', 'success');
    saveSession({
      productsWithCodes: productsWithCodesData,
      currentStage: destinationIndex,
    });
  };

  const handleNAICSSelectionChange = (updatedProducts: ProductWithCodes[], methodUsed: CodeMethod = 'unknown') => {
    setProductsWithCodes(updatedProducts);
    setNaicsMethodUsed(methodUsed);
    saveSession({ productsWithCodes: updatedProducts });
  };

  const handleVendorsComplete = async (productsWithVendors: ProductWithVendors[]) => {
    if (!(await confirmCurrentStageAdvance(7))) return;
    // Store the products with vendors (new structure from golden script)
    const prevSig = normalizeStage6Signature(vendors);
    const nextSig = normalizeStage6Signature(productsWithVendors);
    if (prevSig !== nextSig) {
      clearStageDataFrom(5);
    }
    const normalizedVendors = normalizeProductsWithVendors(productsWithVendors);
    setVendors(normalizedVendors);
    // Vendor Discovery lifts its latest server-authoritative coordination
    // response into App. Do not erase it here: doing so made the DD step vanish
    // until a later Stage-12 remount even after the rationale was durably saved.
    const totalVendors = normalizedVendors.reduce((sum, p) => sum + (p.vendors?.length || 0), 0);
    showNotificationMessage(`${totalVendors} vendors identified. Proceeding to Section B generation.`, 'success');
    // Stage code 7 is Section B in both projections, but its visual index is
    // different for RAs because their stepper omits the conditional J&A row.
    // Resolve against the active navigation so Vendor Discovery never skips
    // Section B when it is rendered in the Customer projection.
    const destinationIndex = marketResearchStageIndexForBackend(7);
    advanceToStage(destinationIndex);
    saveSession({
      vendors: normalizedVendors,
      currentStage: destinationIndex,
    });
  };

  const loadMarketResearchDd2579Coordination = useCallback((
    targetFlowId: string,
    options: { force?: boolean } = {},
  ): Promise<MarketResearchDd2579Coordination> => {
    const existing = marketResearchDd2579CoordinationInFlightRef.current;
    if (!options.force && existing?.flowId === targetFlowId) {
      return existing.promise;
    }
    const requestId = ++marketResearchDd2579CoordinationRequestRef.current;
    setMarketResearchDd2579Coordination(null);
    setMarketResearchDd2579CoordinationStatus('loading');
    const promise = getMarketResearchDd2579Coordination(targetFlowId)
      .then((response) => {
        if (!response.success || !response.data) {
          throw new Error(response.error || 'Unable to verify the Market Research coordination status.');
        }
        // A flow switch or a newer selection/status check supersedes this
        // response. Never let an older request re-enable Stage 13.
        if (requestId === marketResearchDd2579CoordinationRequestRef.current) {
          setMarketResearchDd2579Coordination(response.data);
          setMarketResearchDd2579MetadataRequired(isMarketResearchDd2579StageEnabled(response.data));
          setMarketResearchDd2579CoordinationStatus('ready');
        }
        return response.data;
      })
      .catch((error) => {
        if (requestId === marketResearchDd2579CoordinationRequestRef.current) {
          setMarketResearchDd2579Coordination(null);
          setMarketResearchDd2579CoordinationStatus('error');
        }
        throw error;
      })
      .finally(() => {
        const active = marketResearchDd2579CoordinationInFlightRef.current;
        if (active?.flowId === targetFlowId && active.requestId === requestId) {
          marketResearchDd2579CoordinationInFlightRef.current = null;
        }
      });
    marketResearchDd2579CoordinationInFlightRef.current = { flowId: targetFlowId, requestId, promise };
    return promise;
  }, []);

  const refreshMarketResearchDd2579Coordination = useCallback(async (): Promise<MarketResearchDd2579Coordination | null> => {
    if (!flowId || workflowType !== 'market_research') return null;
    return loadMarketResearchDd2579Coordination(flowId);
  }, [flowId, loadMarketResearchDd2579Coordination, workflowType]);

  const handleMarketResearchDd2579CoordinationChange = useCallback((
    coordination: MarketResearchDd2579Coordination | null,
  ) => {
    // Invalidate any older in-flight read whenever Vendor Discovery changes
    // the selection/signature. A non-null value is the response from the
    // authoritative save/rationale endpoint; null is a fail-closed local edit.
    marketResearchDd2579CoordinationRequestRef.current += 1;
    marketResearchDd2579CoordinationInFlightRef.current = null;
    setMarketResearchDd2579Coordination(coordination);
    setMarketResearchDd2579MetadataRequired(isMarketResearchDd2579StageEnabled(coordination));
    setMarketResearchDd2579CoordinationStatus(coordination ? 'ready' : 'idle');
  }, []);

  const hydrateMarketResearchDd2579 = useCallback(async (targetFlowId: string, force = false) => {
    if (!force && marketResearchDd2579FormHydrationFlowRef.current === targetFlowId) return true;
    const requestId = ++marketResearchDd2579FormHydrationRequestRef.current;
    marketResearchDd2579FormHydrationFlowRef.current = targetFlowId;
    setMarketResearchDd2579FormHydrationStatus('loading');
    try {
      const stage13 = await getRawStageData(targetFlowId, 13);
      if (marketResearchDd2579FormHydrationRequestRef.current !== requestId) return false;
      // The raw stage endpoint returns an empty input/output record for a new
      // draft. A null result therefore means both reads failed and must never
      // unlock an empty editable form.
      if (stage13 === null) {
        throw new Error('DD/DAF 2579 draft refresh failed');
      }
      const restored = restoreMarketResearchDd2579StateFromStageOutput(stage13?.output);
      if (restored === null) {
        throw new Error('DD/DAF 2579 draft refresh returned an invalid payload');
      }
      setMarketResearchDd2579(restored.form);
      setMarketResearchDd2579RestoredState(restored);
      setMarketResearchDd2579FormHydrationStatus('ready');
      return true;
    } catch (error) {
      if (marketResearchDd2579FormHydrationRequestRef.current !== requestId) return false;
      // The DD stage owns its prefill/error affordances. A draft-cache miss
      // must not turn a successfully verified final route back into a fresh
      // workflow shell.
      marketResearchDd2579FormHydrationFlowRef.current = null;
      setMarketResearchDd2579FormHydrationStatus('error');
      console.warn('Failed to restore Market Research DD 2579 draft:', error);
      return false;
    }
  }, []);

  const openMarketResearchDd2579 = async () => {
    setMarketResearchDd2579Visited(true);
    if (flowId) {
      await hydrateMarketResearchDd2579(flowId);
    }
    if (!(await confirmCurrentStageAdvance(13))) return;
    advanceToStage(marketResearchBackendCodeToUiIndex(13, true, marketResearchBrandNameJaFeatureEnabled));
    showNotificationMessage('Resolve the applicable small-business coordination before the solicitation decision.', 'info');
  };

  useEffect(() => {
    const backendStage = marketResearchStages[currentStage]?.backendCode;
    if (
      workflowType === 'market_research'
      && backendStage === 13
      && flowId
      && marketResearchDd2579CoordinationStatus === 'ready'
      && marketResearchDd2579Enabled
    ) {
      setMarketResearchDd2579Visited(true);
      void hydrateMarketResearchDd2579(flowId);
    } else {
      // Leaving Stage 13 invalidates the entry snapshot. A later same-flow
      // entry must re-read the durable draft before mounting an editable form.
      marketResearchDd2579FormHydrationRequestRef.current += 1;
      marketResearchDd2579FormHydrationFlowRef.current = null;
      setMarketResearchDd2579FormHydrationStatus('idle');
    }
  }, [
    currentStage,
    flowId,
    hydrateMarketResearchDd2579,
    marketResearchDd2579CoordinationStatus,
    marketResearchDd2579Enabled,
    marketResearchStages,
    workflowType,
  ]);

  const handleVendorBidsDd2579Continue = async () => {
    try {
      const coordination = await refreshMarketResearchDd2579Coordination();
      if ((coordination?.applicability && coordination.applicability.status !== 'exempt') || getMarketResearchVendorBidsAction(coordination) === 'continue-dd2579') {
        await openMarketResearchDd2579();
        return;
      }
      showNotificationMessage(
        'Review the acquisition coordination checkpoint before continuing.',
        'error',
      );
    } catch (error) {
      console.error('Failed to determine Market Research DD 2579 eligibility:', error);
      showNotificationMessage(toUserFacingError(error), 'error');
    }
  };

  const handleVendorBidsComplete = async () => {
    try {
      const coordination = await refreshMarketResearchDd2579Coordination();
      const action = getMarketResearchVendorBidsAction(coordination);
      if (action !== 'complete-workflow') {
        showNotificationMessage(
          action === 'continue-dd2579'
            ? 'DD/DAF 2579 is required. Use Continue to DD/DAF 2579 to proceed.'
            : 'Vendor coordination must be resolved before this workflow can be completed.',
          'error',
        );
        return;
      }
      setIsComplete(true);
      showNotificationMessage('Procurement Process Complete!', 'success');
    } catch (error) {
      console.error('Failed to determine Market Research DD 2579 eligibility:', error);
      showNotificationMessage(toUserFacingError(error), 'error');
    }
  };

  const handleMarketResearchDd2579Complete = async (
    _record: MarketResearchDd2579Form,
    resolved?: MarketResearchDd2579Coordination,
  ) => {
    // The checkpoint passes its fresh, post-save server decision. Draft
    // navigation is deliberately separate from actual coordination approval.
    const coordination = resolved ?? marketResearchDd2579Coordination;
    const canContinue = coordination?.can_continue_to_synopsis === undefined
      ? coordination?.applicability?.status === 'exempt' || coordination?.dd2579_eligible
      : coordination.can_continue_to_synopsis;
    if (!canContinue) {
      showNotificationMessage('Complete the required DD2579 fields before continuing.', 'error');
      return;
    }
    const destination = 11;
    if (!(await confirmCurrentStageAdvance(destination))) return;
    const destinationIndex = marketResearchStageIndexForBackend(destination);
    advanceToStage(destinationIndex);
    saveSession({ currentStage: destinationIndex });
    showNotificationMessage('DD2579 draft saved. Continuing to Synopsis & MRR.', 'success');
  };

  const handleSectionBComplete = async (finalDocument: string) => {
    if (!canWriteCurrentWorkflow()) return;
    // Section C self-hydrates through its own protected endpoints. Persist its
    // durable code before mounting it: otherwise a direct Next navigation can
    // race those reads while the server still considers this flow to be on
    // Section B. The Customer projection has its own index topology, so derive
    // the visual target from the active navigation rather than the canonical
    // CO/CS list (which includes the optional J&A row).
    const destinationIndex = marketResearchStageIndexForBackend(8);
    try {
      await persistMarketResearchCurrentStage(flowId, 8);
    } catch (error) {
      console.error('Failed to enter Section C:', error);
      if (marketResearchBrandNameJaFeatureEnabled
        && error instanceof MarketResearchRefreshRequiredError) {
        setMarketResearchRefreshRequirement(error.requirement);
      } else if (error instanceof MarketResearchRefreshRequiredError) {
        setMarketResearchClientUpgradeRequired(true);
      } else {
        showNotificationMessage(USER_FACING_ERROR_MESSAGES.wait_and_retry, 'error');
      }
      return;
    }

    setSectionBDocument(finalDocument);
    showNotificationMessage('Section B complete! Proceeding to Section C generation.', 'info');
    advanceToStage(destinationIndex);
    saveSession({
      sectionBDocument: finalDocument,
      currentStage: destinationIndex,
    });
  };

  const handleSectionCComplete = async (finalDocument: string) => {
    if (!canWriteCurrentWorkflow()) return;
    if (isCustomerMarketResearch) {
      setSectionCDocument(finalDocument);
      // Saving Section C is the authoritative completion mutation. Do not
      // attempt to advance an RA to code 9: the terminal controls and the
      // narrow progress endpoint are both authorized from the server's
      // Section-C-complete projection.
      setMarketResearchCustomerTerminal(true);
      setMarketResearchCustomerTerminalStageView(true);
      saveSession({
        sectionCDocument: finalDocument,
        currentStage: marketResearchStageIndexForBackend(8),
      });
      showNotificationMessage('Section C is complete and ready to hand off to CO/CS.', 'success');
      return;
    }
    if (!(await confirmCurrentStageAdvance(9))) return;
    setSectionCDocument(finalDocument);
    showNotificationMessage('Section C complete! Proceeding to Section D.', 'info');
    const destinationIndex = marketResearchStageIndexForBackend(9);
    advanceToStage(destinationIndex);
    saveSession({
      sectionCDocument: finalDocument,
      currentStage: destinationIndex,
    });
  };

  const handleSectionDComplete = async (finalDocument: string) => {
    if (!(await confirmCurrentStageAdvance(10))) return;
    setSectionDDocument(finalDocument);
    showNotificationMessage('Section D complete! Proceeding to Section E.', 'info');
    const destinationIndex = marketResearchStageIndexForBackend(10);
    advanceToStage(destinationIndex);
    saveSession({
      sectionDDocument: finalDocument,
      currentStage: destinationIndex,
    });
  };

  const handleSectionEComplete = async (finalDocument: string) => {
    setSectionEDocument(finalDocument);
    let coordination: MarketResearchDd2579Coordination | null = null;
    try {
      coordination = await refreshMarketResearchDd2579Coordination();
      const canContinue = coordination?.can_continue_to_synopsis === undefined
        ? coordination?.applicability?.status === 'exempt' || coordination?.dd2579_eligible
        : coordination.can_continue_to_synopsis;
      if (coordination?.applicability && !canContinue) {
        saveSession({ sectionEDocument: finalDocument });
        await openMarketResearchDd2579();
        return;
      }
    } catch (error) {
      showNotificationMessage(toUserFacingError(error), 'error');
      return;
    }
    if (!(await confirmCurrentStageAdvance(11))) return;
    showNotificationMessage('Section E complete! Proceeding to Synopsis & MRR...', 'success');
    const destinationIndex = coordination
      ? marketResearchBackendCodeToUiIndex(11, marketResearchDd2579Visited || isMarketResearchDd2579StageEnabled(coordination), marketResearchBrandNameJaFeatureEnabled)
      : marketResearchStageIndexForBackend(11);
    saveSession({ sectionEDocument: finalDocument, currentStage: destinationIndex });
    // Move to Synopsis stage
    advanceToStage(destinationIndex);
  };

  const handleSynopsisComplete = async (synopsis: any) => {
    if (!(await confirmCurrentStageAdvance(12))) return;
    // Advance to Vendor Bids stage (index 11)
    const destinationIndex = marketResearchStageIndexForBackend(12);
    advanceToStage(destinationIndex);
    showNotificationMessage('Synopsis & MRR complete! Proceeding to Vendor Bids.', 'success');
    saveSession({ currentStage: destinationIndex });
  };

  const showNotificationMessage = (
    message: string, 
    type: 'success' | 'info' | 'error' = 'info',
    action: { label: string; onClick: () => void } | null = null,
  ) => {
    setNotificationMessage(message);
    setNotificationType(type);
    setNotificationAction(action);
    setShowNotification(true);
  };

  useEffect(() => {
    if (workflowType !== 'gpc') return;
    const formIndex = currentStage - GPC_FORM_STAGE_BASE_OFFSET;
    const key = allGpcFormStages()[formIndex];
    if (!key || gpcAvailableSet.has(key)) return;

    const nextStage = nextEnabledGpcStage(currentStage + 1, gpcAvailableSet);
    if (nextStage === null) {
      setNotificationMessage('Unable to verify GPC completion.');
      setNotificationType('error');
      setNotificationAction(null);
      setShowNotification(true);
      return;
    }
    localStorage.setItem(`gpc_stage_${flowId}`, String(nextStage));
    setCurrentStage(nextStage);
  }, [currentStage, flowId, gpcAvailableSet, workflowType]);

  useEffect(() => {
    // Stage 13 is after Vendor Bids, so code 12 has the same index regardless
    // of whether the conditional final stage is currently enabled.
    const backendStage = marketResearchStages[currentStage]?.backendCode;
    if (workflowType !== 'market_research' || backendStage !== 12 || !flowId) return;
    // Stage 6 may have changed since the flow was opened. Clear any prior
    // decision while Stage 12 refreshes so stale eligibility never exposes a
    // navigation or completion action, including on legacy/reloaded flows.
    void refreshMarketResearchDd2579Coordination().catch((error) => {
      console.error('Failed to refresh Market Research DD 2579 coordination on Vendor Bids:', error);
    });
  }, [
    currentStage,
    flowId,
    marketResearchStages,
    refreshMarketResearchDd2579Coordination,
    workflowType,
  ]);

  useEffect(() => {
    if (
      workflowType === 'market_research' &&
      marketResearchDd2579Enabled &&
      currentStage >= 11
    ) {
      // Backend codes are not visual indices: the optional Brand-name J&A
      // sits between Product Discovery and PSC. Resolve the final DD/DAF 2579
      // step from its durable backend code so it is selectable when eligible.
      const dd2579Index = marketResearchStageIndexForBackend(13);
      setMaxStageReached((previous) => Math.max(previous, dd2579Index));
    }
  }, [
    currentStage,
    marketResearchDd2579Enabled,
    marketResearchStageIndexForBackend,
    workflowType,
  ]);

  const handleNext = () => {
    // Navigation handled by individual stage completion handlers
    showNotificationMessage('Please complete the current stage to proceed', 'info');
  };

  const commitMarketResearchStageSelection = async (stageIndex: number): Promise<boolean> => {
    const target = marketResearchStages[stageIndex];
    if (!target) return false;
    const selectionGeneration = workflowOperationGenerationRef.current;
    const selectedFlowId = flowId;
    const isCurrentSelection = () => workflowOperationGenerationRef.current === selectionGeneration
      && activeWorkflowFlowIdRef.current === selectedFlowId;
    if (workflowWriteContextRef.current.flowId !== flowId) return false;
    if (canWriteCurrentWorkflow()) {
      try {
        const result = await updateCurrentStage(flowId!, target.backendCode);
        if (!isCurrentSelection()) return false;
        if (!isCurrentStageUpdateAcknowledged(result)) {
          const refreshRequirement = getMarketResearchRefreshRequirement(result);
          if (marketResearchBrandNameJaFeatureEnabled && refreshRequirement) {
            setMarketResearchRefreshRequirement(refreshRequirement);
            return false;
          }
          if (refreshRequirement) {
            setMarketResearchClientUpgradeRequired(true);
            return false;
          }
          showNotificationMessage(USER_FACING_ERROR_MESSAGES.wait_and_retry, 'error');
          return false;
        }
      } catch (error) {
        if (!isCurrentSelection()) return false;
        console.error('Failed to update current stage:', error);
        showNotificationMessage(USER_FACING_ERROR_MESSAGES.wait_and_retry, 'error');
        return false;
      }
    }
    if (!isCurrentSelection()) return false;
    if (target.backendCode === 13) {
      setMarketResearchDd2579Visited(true);
      if (flowId && marketResearchDd2579CoordinationStatus !== 'ready') {
        void loadMarketResearchDd2579Coordination(flowId).catch((error) => {
          console.error('Failed to load Market Research DD 2579 coordination on entry:', error);
        });
      }
    }
    setCurrentStage(stageIndex);
    showNotificationMessage(`Navigated to ${target.name}`, 'info');
    return true;
  };

  const handleBack = () => {
    if (currentStage > 0) {
      const currentBackendCode = marketResearchStages[currentStage]?.backendCode;
      if (currentBackendCode === undefined) return;
      const previousBackendCode = resolveMarketResearchBackDestination(
        currentBackendCode,
        marketResearchBrandNameJaFeatureEnabled ? marketResearchBrandNameDecision : null,
        {
          brandNameJaRequired: marketResearchBrandNameRequired,
          dd2579Required: marketResearchDd2579Enabled,
        },
      );
      if (previousBackendCode === undefined) return;
      const targetStage = marketResearchStageIndexForBackend(previousBackendCode);
      if (effectiveIsViewOnly) {
        void commitMarketResearchStageSelection(targetStage);
        return;
      }
      setBackNavDialog({ open: true, targetStage });
    }
  };

  const handleBackNavConfirm = async () => {
    const target = backNavDialog.targetStage;
    setBackNavDialog({ open: false, targetStage: 0 });
    await commitMarketResearchStageSelection(target);
  };

  const handleBackNavCancel = () => {
    setBackNavDialog({ open: false, targetStage: 0 });
  };

  const handleStageSelect = async (stageIndex: number) => {
    if (!documentAssistant.allowNavigation()) { showNotificationMessage('Finish the current edit before leaving this stage.', 'info'); return; }
    if (marketResearchNavigation.disabledStageIndices.includes(stageIndex)) return;
    const selectedBackendCode = marketResearchStages[stageIndex]?.backendCode;
    if (!selectedBackendCode) return;
    // View-only / training flows are fully read-only and have all stage data
    // present in storage, so the stepper must always allow jumping to any
    // stage. Bypass the standalone gate, the back-nav confirmation dialog, and
    // the cache/hash/`maxStageReached` completion gate below — there is nothing
    // to lose and no stage to "complete first". Normal flows are unaffected.
    if (effectiveIsViewOnly) {
      await commitMarketResearchStageSelection(stageIndex);
      return;
    }

    // Block navigation to earlier stages in standalone mode
    const standaloneMinIndex = (standaloneMode && workflowType === 'fopr') ? 2 : 11;
    if (standaloneMode && stageIndex < standaloneMinIndex) {
      showNotificationMessage(
        'Previous stages are not available in standalone mode.',
        'info'
      );
      return;
    }

    // Show warning when navigating to an earlier stage
    if (stageIndex < currentStage) {
      setBackNavDialog({ open: true, targetStage: stageIndex });
      return;
    }

    // Allow navigation if user has completed up to that stage or it's a previous stage
    const hasData =
      (selectedBackendCode === 1) ||
      (selectedBackendCode === 2 && questions.length > 0) ||
      (selectedBackendCode === 3 && mfr) ||
      (selectedBackendCode === 14 && marketResearchBrandNameRequired && products.length > 0) ||
      (selectedBackendCode === 4 && products.length > 0) ||
      (selectedBackendCode === 5 && productsWithPSC.length > 0) ||
      (selectedBackendCode === 6 && productsWithCodes.length > 0) ||
      (selectedBackendCode === 7 && vendors.length > 0) ||
      (selectedBackendCode === 8 && sectionBDocument.length > 0) ||
      (selectedBackendCode === 9 && sectionCDocument.length > 0) ||
      (selectedBackendCode === 10 && sectionDDocument.length > 0);

    if (hasData || stageIndex <= maxStageReached) {
      const navigated = await commitMarketResearchStageSelection(stageIndex);
      if (!navigated) return;

      // If navigating back to Stage 1 (Questions), rehydrate commodity from backend UI snapshot
      if (flowId && stageIndex === 0) {
          (async () => {
            try {
              const API_BASE_URL = process.env.REACT_APP_API_URL || '';
              const headers: Record<string, string> = {};
              const resp = await fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${flowId}/stage/1/ui`, { headers, credentials: 'include' });
              if (resp.ok) {
                const data = await resp.json();
                const cd = data?.input?.commodityDescription || '';
                if (cd && cd.trim()) {
                  setInitialCommodityDescription(cd);
                }
              }
            } catch (e) {
              console.warn('Failed to hydrate Stage 1 commodity from backend:', e);
            }
          })();
      }
    } else {
      showNotificationMessage('Please complete the previous stages first', 'info');
    }
  };

  // Legacy manual save removed; session persists automatically where needed

  interface SessionOverrides {
    commodityDescription?: string;
    questions?: Question[];
    answers?: Record<string, string>;
    currentStage?: number;
    questionsUseCachedData?: boolean;
    mfr?: MFR | null;
    mfrUseCachedData?: boolean;
    products?: Product[];
    productsWithPSC?: ProductWithPSC[];
    productsWithCodes?: ProductWithCodes[];
    vendors?: ProductWithVendors[];
    sectionBDocument?: string;
    sectionCDocument?: string;
    sectionDDocument?: string;
    sectionEDocument?: string;
  }

  const saveSession = (overrides: SessionOverrides = {}) => {
    const sessionQuestions = overrides.questions ?? questions;
    const sessionAnswers = overrides.answers ?? answers;
    const sessionCommodity = overrides.commodityDescription ?? initialCommodityDescription;
    const sessionCurrentStage = overrides.currentStage ?? currentStage;
    const sessionQuestionsUseCache = overrides.questionsUseCachedData ?? questionsUseCachedData;
    const sessionMfr = overrides.mfr ?? mfr;
    console.log('[saveSession] overrides.mfr:', overrides.mfr);
    console.log('[saveSession] current mfr state:', mfr);
    console.log('[saveSession] sessionMfr resolved to:', sessionMfr);
    const sessionMfrUseCache = overrides.mfrUseCachedData ?? mfrUseCachedData;
    const sessionProducts = overrides.products ?? products;
    const sessionProductsWithPSC = overrides.productsWithPSC ?? productsWithPSC;
    const sessionProductsWithCodes = overrides.productsWithCodes ?? productsWithCodes;
    const sessionVendors = normalizeProductsWithVendors(overrides.vendors ?? vendors);
    const sessionSectionB = overrides.sectionBDocument ?? sectionBDocument;
    const sessionSectionC = overrides.sectionCDocument ?? sectionCDocument;
    const sessionSectionD = overrides.sectionDDocument ?? sectionDDocument;
    const sessionSectionE = overrides.sectionEDocument ?? sectionEDocument;

    const toQA = (sourceQuestions: Question[], answerMap: Record<string, string>) =>
      Object.entries(answerMap).map(([questionId, answer]) => ({
        questionId,
        answer,
        question: sourceQuestions.find(q => q.id === questionId)?.question || '',
        category: sourceQuestions.find(q => q.id === questionId)?.category || '',
      }));

    const session: PipelineSession = {
      id: 'session-1',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      currentStage: sessionCurrentStage,
      maxStageReached: Math.max(maxStageReached, sessionCurrentStage),
      stages: {
        questions: {
          commodityDescription: sessionCommodity,
          questions: sessionQuestions,
          answers: toQA(sessionQuestions, sessionAnswers),
          useCachedData: sessionQuestionsUseCache,
        },
        mfr: {
          questions: sessionQuestions,
          answers: toQA(sessionQuestions, sessionAnswers),
          mfr: sessionMfr,
          useCachedData: sessionMfrUseCache,
        },
        products: {
          mfr: sessionMfr,
          products: sessionProducts,
          selectedProducts: sessionProducts.map(p => p.id),
        },
        psc: {
          products: sessionProductsWithPSC,
        },
        naics: {
          products: sessionProductsWithCodes,
        },
        vendors: {
          productsWithVendors: sessionVendors,
          selectedVendors: sessionVendors.map(v => v.id),
        },
        sectionB: {
          document: sessionSectionB,
          finalOutput: sessionSectionB,
        },
        sectionC: {
          document: sessionSectionC,
          finalOutput: sessionSectionC,
        },
        sectionD: {
          document: sessionSectionD,
          finalOutput: sessionSectionD,
        },
        sectionE: {
          document: sessionSectionE,
          finalOutput: sessionSectionE,
        },
      },
    };
    localStorage.setItem('farchat-session', JSON.stringify(session));
  };

  const handleMfrCachePreferenceChange = (useCache: boolean) => {
    setMfrUseCachedData(useCache);
    saveSession({ mfrUseCachedData: useCache });
  };
  const handleRestart = () => {
    if (window.confirm('Are you sure you want to start over? All progress will be lost.')) {
      resetPipelineState();
      showNotificationMessage('Pipeline reset. Starting fresh.', 'info');
    }
  };
  // Change Email (logout) handler
  const handleChangeEmail = async () => {
    if (!window.confirm('Sign out and switch email? Your local session will be cleared.')) {
      return;
    }
    invalidateStartupReads();
    clearUsageDashboardAuthenticationState();
    // Invalidate every workflow operation before the logout request begins.
    // A flow load or create that resolves while sign-out is in progress must
    // never restore the departing account's workflow or companion chat.
    clearWorkflowChatIdentityState();
    try {
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {}
    invalidateStartupReads();
    localStorage.removeItem('farchat_has_session');
    clearDcmaPbpBrowserDrafts();
    resetPipelineState();
    setUserEmail(null);
    setAllowedWorkflowTypes([]);
    setUserCanUseChatbot(false);
    setAllowTraining(false);
    setAllowRaTraining(false);
    setAllowDcmaPbp(false);
    setAllowDcmaCrr(false);
    setCanUseStandalonePriceEstimator(false);
    setAllowChangelog(false);
    setShowChangelog(false);
    setShowDashboard(false);
    setShowManageUsers(false);
    setSupportIssueView(null);
    setIsAdmin(false);
    setIsEffectiveSuperAdmin(false);
    setIsSuperAdmin(false);
    setIsUserFeedbackStaff(false);
    setAdminStatusRole(null);
    setPermissionsScope(null);
    setPendingCount(0);
    setUnreadFeedbackCount(0);
    setShowEmailGate(true);
    showNotificationMessage('Signed out.', 'info');
  };

  // Home button handler (from within a workflow) — reuses the "Change Workflow" confirm logic
  // Navbar logo doubles as Home. It always returns to the category cards: on the
  // homepage it resets the in-page category selection (via the page's imperative
  // handle); elsewhere it navigates back to the homepage, which mounts fresh on
  // the category cards.
  const typeSelectorRef = useRef<WorkflowTypeSelectorPageHandle>(null);
  const gpcSalientRef = useRef<GPCSalientCharacteristicsHandle>(null);
  const handleLogoHome = () => {
    if (!documentAssistant.allowNavigation()) { showNotificationMessage('Finish the current edit before leaving this stage.', 'info'); return; }
    // The Usage Dashboard is rendered as an early branch that takes precedence
    // over `appPage`, so it must be dismissed explicitly (same exit the
    // dashboard's "Back" button uses) before the homepage can show.
    if (showDashboard) {
      setShowDashboard(false);
    }
    if (showManageUsers) {
      setShowManageUsers(false);
    }
    if (supportIssueView) {
      setSupportIssueView(null);
    }
    if (appPage === 'type_selector') {
      typeSelectorRef.current?.resetToCategories();
    } else if (appPage === 'flow_selector') {
      setAppPage('type_selector');
      setFlowSelectorScope(null);
    } else if (isTrainingPage) {
      // Training has no unsaved-workflow progress to confirm; go straight home.
      leaveTraining();
    } else {
      handleHomeFromWorkflow();
    }
  };

  // Shared "leave the active workflow" confirmation. Returns true if the user
  // confirms. Both the Home button and the Saved Workflows button route through
  // this so there is a single source of truth for the popup text/behavior.
  const confirmLeaveWorkflow = (
    destination: 'home' | 'savedWorkflows' | 'workflow',
    overrideMessage?: string,
  ): boolean => {
    if (!documentAssistant.allowNavigation()) { showNotificationMessage('Finish the current edit before leaving this stage.', 'info'); return false; }
    const viewOnlyMessage = destination === 'savedWorkflows'
      ? 'Leave this view-only workflow and return to Manage Workflows?'
      : destination === 'workflow'
      ? 'Leave this view-only workflow and open another workflow?'
      : 'Leave this view-only workflow and return to the homepage?';
    const defaultMsg = overrideMessage ?? (effectiveIsViewOnly ? viewOnlyMessage : destination === 'savedWorkflows'
      ? 'Return to Manage Workflows page? Current progress will be saved.'
      : destination === 'workflow'
      ? 'Open another workflow? Current progress will be saved.'
      : 'Return to homepage? Current progress will be saved.');
    const dirtyDestination = destination === 'savedWorkflows'
      ? 'Return to Manage Workflows'
      : destination === 'workflow'
      ? 'Open another workflow'
      : 'Return to homepage';
    const msg = (
      !effectiveIsViewOnly && (workflowType === 'sow_verification'
        || workflowType === 'one_time_construction'
        || workflowType === 'construction_sow_generation')
      && currentStage === 2
      && (sowEditDirty || sowEditDirtyRef.current)
    )
      ? `You have unsaved changes in the SOW editor. ${dirtyDestination}? `
        + 'Unsaved edits will be lost, but previous checkpoints are still '
        + 'available in History.'
      : defaultMsg;
    return window.confirm(msg);
  };

  const confirmWorkflowNavigation = (
    previous?: WorkflowNavigationCheckpoint,
  ): WorkflowNavigationCheckpoint | false => {
    const editRevision = sowEditRevisionRef.current;
    if (previous?.editRevision === editRevision) return previous;
    // A mutation that was saved before the request completed is no longer at
    // risk, so update the checkpoint without showing a second confirmation.
    if (previous && !sowEditDirtyRef.current) return { editRevision };
    return confirmLeaveWorkflow('workflow') ? { editRevision } : false;
  };

  const handleHomeFromWorkflow = () => {
    if (confirmLeaveWorkflow('home')) {
      workflowOperationGenerationRef.current += 1;
      resetPipelineState();
      setWorkflowType(null);
      setClsCorrectionResult(null);
      setFlowName('');
      setAppPage('type_selector');
      setFlowSelectorScope(null);
    }
  };

  // Account menu handlers
  const openAccountMenu = (e: React.MouseEvent<HTMLElement>) => {
    void refreshSupportIssueUnreadCount();
    setAccountAnchorEl(e.currentTarget);
  };
  const closeAccountMenu = () => setAccountAnchorEl(null);

  const openRolePreviewDialog = () => {
    if (!canShowRolePreviewControl({ isSuperAdmin: realIsSuperAdmin })) return;
    setPendingRolePreview(rolePreview);
    closeAccountMenu();
    setRolePreviewDialogOpen(true);
  };

  const closeRolePreviewDialog = () => setRolePreviewDialogOpen(false);

  const changeRolePreview = async (nextPreview: RolePreview): Promise<boolean> => {
    if (!canShowRolePreviewControl({ isSuperAdmin: realIsSuperAdmin })) return false;
    if (rolePreviewBusy) return false;
    if (nextPreview === rolePreview) {
      closeAccountMenu();
      return true;
    }
    // Reopen workflows under the selected role so their server-owned stage
    // path and metadata are fetched with the current permissions.
    if (appPage === 'workflow' && !confirmLeaveWorkflow('home')) return false;

    const authGeneration = authGenerationRef.current;
    setRolePreviewBusy(true);
    if (appPage === 'workflow' && workflowType === 'gpc' && currentStage === 1 && gpcSalientRef.current) {
      let saved = false;
      try {
        saved = await gpcSalientRef.current.flushPendingSaves();
      } catch {
        // An unexpected save error must leave the editor and current role intact.
      }
      if (authGeneration !== authGenerationRef.current) return false;
      if (!saved) {
        showNotificationMessage(USER_FACING_ERROR_MESSAGES.gpc_salient_not_saved, 'error');
        setRolePreviewBusy(false);
        return false;
      }
    }
    try {
      if (process.env.REACT_APP_USE_MOCK !== 'true') {
        await setServerRolePreview(nextPreview);
      }
      if (authGeneration !== authGenerationRef.current) return false;
      setStartupReadScope(`${userEmail ?? ''}\u0000${nextPreview}`);
      permissionsRequestGenerationRef.current += 1;
      setPermissionsScope(null);
      setWorkflowPermissionsLoaded(false);
      setAdminStatusRole(null);
    } catch (error) {
      if (authGeneration !== authGenerationRef.current) return false;
      try {
        const serverRole = await getServerRolePreview();
        if (authGeneration !== authGenerationRef.current) return false;
        if (serverRole !== rolePreview) {
          setStartupReadScope(`${userEmail}\u0000${serverRole}`);
          permissionsRequestGenerationRef.current += 1;
          workflowOperationGenerationRef.current += 1;
          resetPipelineState();
          setMarketResearchFlowView('unknown');
          setMarketResearchRaCollaborationOpen(false);
          setFlowId(null);
          setFlowName('');
          setWorkflowType(null);
          setFlowSelectorScope(null);
          setAppPage('type_selector');
          setPermissionsScope(null);
          setWorkflowPermissionsLoaded(false);
          setAdminStatusRole(null);
          setRolePreview(serverRole);
        }
      } catch {
        if (authGeneration !== authGenerationRef.current) return false;
        // Unknown server state: keep the browser on the least privileged view.
        permissionsRequestGenerationRef.current += 1;
        workflowOperationGenerationRef.current += 1;
        resetPipelineState();
        setMarketResearchFlowView('unknown');
        setMarketResearchRaCollaborationOpen(false);
        setFlowId(null);
        setFlowName('');
        setWorkflowType(null);
        setFlowSelectorScope(null);
        setAppPage('type_selector');
        setPermissionsScope(null);
        setWorkflowPermissionsLoaded(false);
        setAdminStatusRole(null);
        setStartupReadScope(`${userEmail}\u0000regular`);
        setRolePreview('regular');
      }
      showNotificationMessage(error instanceof Error ? error.message : 'Could not change the active role view.', 'error');
      setRolePreviewBusy(false);
      return false;
    }
    const refreshedStatus = await checkAdminStatus();
    if (authGeneration !== authGenerationRef.current) return false;
    setIsAdmin(refreshedStatus.isAdmin);
    setIsEffectiveSuperAdmin(refreshedStatus.isSuperAdmin);
    setIsUserFeedbackStaff(refreshedStatus.isUserFeedbackStaff);
    setAdminStatusRole(nextPreview);
    if (refreshedStatus.isAdmin) refreshPendingCount();
    if (refreshedStatus.isUserFeedbackStaff) refreshUnreadFeedbackCount();
    setRolePreviewBusy(false);

    // Supersede pending flow work before changing the client-side role
    // projection so an earlier operation cannot restore inaccessible data.
    workflowOperationGenerationRef.current += 1;
    setGpcNavigationLoading(false);

    // Dismiss state built from the previous role. The new permissions response
    // will own every workflow card and menu after the switch.
    closeAccountMenu();
    setWorkflowNotificationsOpen(false);
    setWorkflowNotificationFlow(null);
    setShowChangelog(false);
    setFeedbackOpen(false);
    setOpenCreateRequest(null);
    setMarketResearchFlowView('unknown');
    setMarketResearchRaCollaborationOpen(false);
    setMarketResearchContractingHandoffState('not_handed_off');
    setMarketResearchContractingHandoffVerifiedFlowId(null);
    setMarketResearchCustomerTerminal(false);
    setMarketResearchCustomerTerminalStageView(false);
    setMarketResearchEditAwareness(null);
    setPermissionsScope(null);
    setWorkflowPermissionsLoaded(false);
    setRolePreview(nextPreview);
    resetPipelineState();
    setShowDashboard(false);
    setShowManageUsers(false);
    setSupportIssueView(null);
    setFlowId(null);
    setFlowName('');
    setWorkflowType(null);
    setFlowSelectorScope(null);
    setAppPage('type_selector');
    showNotificationMessage(
      nextPreview === 'super_admin'
        ? 'Returned to Super Admin view.'
        : `Viewing as: ${ROLE_PREVIEW_OPTIONS.find((option) => option.value === nextPreview)?.label} — preview mode.`,
      'info',
    );
    return true;
  };

  const applyRolePreview = async () => {
    if (await changeRolePreview(pendingRolePreview)) closeRolePreviewDialog();
  };

  const openSupportIssues = (view: 'customer' | 'admin') => {
    if (view === 'admin' && !presentationCapabilities.showSuperAdminControls) return;
    closeAccountMenu();
    setShowDashboard(false);
    setShowManageUsers(false);
    setSupportIssueView(view);
  };

  // Feedback dialog state
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackContext, setFeedbackContext] = useState<{
    workflow: string;
    stage: string;
    source?: FeedbackSourceTuple;
    question?: string;
  } | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackAttachments, setFeedbackAttachments] = useState<File[]>([]);
  const [feedbackAttachmentPickerKey, setFeedbackAttachmentPickerKey] = useState(0);
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [feedbackExpanded, setFeedbackExpanded] = useState(false); // compact ↔ large box
  const feedbackSubmissionIdRef = useRef<string | null>(null);
  const feedbackSourceTrackerRef = useRef(new FeedbackSourceTracker());
  const feedbackSourcePage = supportIssueView === 'admin'
    ? 'user_feedback'
    : appPage === 'type_selector' && !showDashboard && !showManageUsers && !supportIssueView
      ? 'homepage'
      : null;

  const feedbackSourceNavigationKey = feedbackSourcePage ?? 'untracked';
  useEffect(() => {
    feedbackSourceTrackerRef.current.reset();
  }, [feedbackSourceNavigationKey, userEmail]);

  const recordHomepageFeedbackSource = useCallback((
    area: Exclude<HomepageSourceArea, 'general'>,
    confidence: Exclude<FeedbackSourceConfidence, 'fallback'>,
  ) => {
    feedbackSourceTrackerRef.current.record('homepage', area, confidence);
  }, []);

  const recordUserFeedbackSource = useCallback((
    area: Exclude<UserFeedbackSourceArea, 'general'>,
    confidence: Exclude<FeedbackSourceConfidence, 'fallback'>,
  ) => {
    feedbackSourceTrackerRef.current.record('user_feedback', area, confidence);
  }, []);

  const newFeedbackSubmissionId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
      const value = Math.floor(Math.random() * 16);
      return (char === 'x' ? value : (value & 0x3) | 0x8).toString(16);
    });
  };

  const openFeedbackDialog = useCallback(() => {
    if (!feedbackSubmissionIdRef.current) feedbackSubmissionIdRef.current = newFeedbackSubmissionId();
    // Account overlays retain the underlying appPage. Capture the visible
    // surface now so feedback cannot be attributed to a hidden parent page.
    const context = getFeedbackContext();
    setFeedbackContext({
      ...context,
      ...(feedbackSourcePage
        ? { source: feedbackSourceTrackerRef.current.snapshot(feedbackSourcePage) }
        : {}),
    });
    setFeedbackOpen(true);
  }, [feedbackSourcePage, getFeedbackContext]);

  const clearFeedbackDialog = useCallback(() => {
    setFeedbackOpen(false);
    setFeedbackText('');
    setFeedbackAttachments([]);
    setFeedbackAttachmentPickerKey((key) => key + 1);
    setFeedbackExpanded(false);
    setFeedbackContext(null);
    feedbackSubmissionIdRef.current = null;
  }, []);

  // Maps the current page/workflow/stage into friendly labels for the feedback
  // email context line. Reuses the existing per-workflow stage-name arrays.
  function getFeedbackContext(): { workflow: string; stage: string; source?: FeedbackSourceTuple; question?: string } {
    // These branches mirror the visible render precedence below. An overlay
    // is a first-class feedback surface, never a workflow-stage child.
    if (supportIssueView === 'admin') return { workflow: 'User Feedback', stage: '' };
    if (supportIssueView === 'customer') return { workflow: 'My feedback', stage: '' };
    if (showDashboard) return { workflow: 'Usage Dashboard', stage: '' };
    if (showManageUsers) return { workflow: 'Manage Users', stage: '' };
    if (appPage === 'ra_training' && canAccessRaTraining) return { workflow: 'Warfighter Training', stage: 'Warfighter Training' };
    if (appPage === 'training' && canAccessTraining) {
      const visible = trainingFeedbackContext;
      return {
        workflow: 'Training',
        stage: visible?.stage ?? 'Overview',
        source: trainingFeedbackSource(visible?.sourceArea ?? 'overview'),
        ...(visible?.question ? { question: visible.question.slice(0, 2000) } : {}),
      };
    }
    const STAGE_ARRAYS: Record<string, { name: string }[]> = {
      market_research: stages,
      clause_logic: clsStages,
      sow_verification: sowStages,
      wage_determination: wdStages,
      pnm: pnmStages,
      fopr: foprStages,
      services: servicesPwsOrigin === 'requirements_generation' ? generatedServicesStages : servicesStages,
      one_time_construction: activeOneTimeConstructionStages,
      services_combo: servicesComboStages,
      gpc: gpcStages,
      complex_services: servicesPwsOrigin === 'requirements_generation' && currentStage < 3
        ? servicesPwsGenerationStages : complexServicesStages,
      other_transactions: activeOtherTransactionsStages,
      post_award_actions: postAwardStages,
    };
    if (appPage === 'workflow' && workflowType) {
      const workflow = getWorkflowDisplayLabel(workflowType);
      const arr = STAGE_ARRAYS[workflowType];
      const stage =
        arr && currentStage >= 0 && currentStage < arr.length
          ? arr[currentStage].name
          : '';
      return { workflow, stage };
    }
    if (appPage === 'type_selector') {
      return { workflow: 'Homepage', stage: '' };
    }
    if (appPage === 'flow_selector') {
      return { workflow: 'Saved Workflows', stage: '' };
    }
    return { workflow: '', stage: '' };
  }

  const workflowChatPageContext = React.useMemo<HomeChatPageContext | undefined>(
    () => {
      if (appPage !== 'workflow' || !workflowType) return undefined;
      if (workflowType === 'dcma_pbp') {
        return {
          workflowType,
          workflowLabel: getWorkflowDisplayLabel(workflowType),
          ...(flowName ? { workflowName: flowName } : {}),
          stageName: 'PBP milestone schedule review',
          stageDescription: (
            'Upload, map, validate, and review performance-based payment '
            + 'milestones.'
          ),
        };
      }
      if (workflowType === 'post_award_actions') {
        return getPostAwardWorkflowChatPageContext(currentStage, flowName);
      }
      if (workflowType === 'market_research') {
        return getMarketResearchWorkflowChatPageContext(marketResearchStages[currentStage], flowId, flowName);
      }
      const stageArrays: Record<string, { name: string; description: string }[]> = {
        market_research: stages,
        clause_logic: clsStages,
        sow_verification: sowStages,
        wage_determination: wdStages,
        pnm: pnmStages,
        fopr: foprStages,
        services: servicesPwsOrigin === 'requirements_generation' ? generatedServicesStages : servicesStages,
        one_time_construction: activeOneTimeConstructionStages,
        services_combo: servicesComboStages,
        gpc: gpcStages,
        complex_services: servicesPwsOrigin === 'requirements_generation' && currentStage < 3
          ? servicesPwsGenerationStages : complexServicesStages,
        other_transactions: activeOtherTransactionsStages,
      };
      const stage = stageArrays[workflowType]?.[currentStage];
      if (!stage) return undefined;
      return {
        workflowType,
        workflowLabel: getWorkflowDisplayLabel(workflowType),
        ...(flowName ? { workflowName: flowName } : {}),
        stageName: stage.name,
        ...(stage.description ? { stageDescription: stage.description } : {}),
      };
    },
    [activeOneTimeConstructionStages, activeOtherTransactionsStages, appPage, currentStage, flowId, flowName, gpcStages, marketResearchStages, servicesPwsOrigin, workflowType],
  );

  const handleSubmitFeedback = async () => {
    setFeedbackSending(true);
    const { workflow, stage } = feedbackContext ?? getFeedbackContext();
    const res = await submitFeedback(
      feedbackText.trim(),
      workflow,
      stage,
      feedbackAttachments,
      feedbackSubmissionIdRef.current ?? undefined,
      feedbackContext?.source,
      feedbackContext?.question,
    );
    setFeedbackSending(false);
    if (res.success) {
      clearFeedbackDialog();
      // Delivery-channel details remain server-side for operators. From the
      // user's perspective, any successfully captured submission is simply
      // feedback submitted.
      showNotificationMessage(
        'Feedback received. Track updates in',
        'success',
        {
          label: 'My feedback',
          onClick: () => {
            setShowNotification(false);
            openSupportIssues('customer');
          },
        },
      );
    } else {
      // Resolve the canonical user-facing message from the error_code (e.g.
      // feedback_rate_limit) so the snackbar shows the specific guidance instead
      // of collapsing to the generic "maintainers alerted" fallback.
      const friendly = res.error_code
        ? toUserFacingError(Object.assign(new Error(res.error || ''), { error_code: res.error_code }))
        : (res.error || 'Failed to send feedback');
      showNotificationMessage(friendly, 'error');
    }
  };

  useEffect(() => {
    const handleAuthExpired = () => {
      // The expiry event is itself an authentication boundary. Purge protected
      // Dashboard state synchronously, before the confirming request can
      // yield, so no cached rows remain displayable during verification.
      clearUsageDashboardAuthenticationState();
      verifySessionOrShowGate(() => {
        invalidateStartupReads();
        localStorage.removeItem('farchat_has_session');
        clearDcmaPbpBrowserDrafts();
        setAllowedWorkflowTypes([]);
        setMarketResearchCallerView('unknown');
        setCustomerMarketResearchEnabled(false);
        setMarketResearchFlowView('unknown');
        setUserCanUseChatbot(false);
        setAllowDcmaPbp(false);
    setAllowDcmaCrr(false);
        setCanUseStandalonePriceEstimator(false);
        setUserEmail(null);
        setShowDashboard(false);
        setShowManageUsers(false);
        setSupportIssueView(null);
        setIsAdmin(false);
        setIsEffectiveSuperAdmin(false);
        setIsSuperAdmin(false);
        setIsUserFeedbackStaff(false);
        setAdminStatusRole(null);
        setPermissionsScope(null);
        setPendingCount(0);
        setUnreadFeedbackCount(0);
        setAllowTraining(false);
        setAllowRaTraining(false);
        clearWorkflowChatIdentityState();
        setAppPage((p) => (p === 'training' || p === 'ra_training' ? 'type_selector' : p));
        setShowEmailGate(true);
      });
    };
    const handleDcmaAccessRevoked = () => setAllowDcmaPbp(false);
    const handleCrrAccessRevoked = () => setAllowDcmaCrr(false);
    window.addEventListener('auth-expired', handleAuthExpired);
    window.addEventListener('dcma-access-revoked', handleDcmaAccessRevoked);
    window.addEventListener('dcma-crr-access-revoked', handleCrrAccessRevoked);
    return () => {
      window.removeEventListener('auth-expired', handleAuthExpired);
      window.removeEventListener('dcma-access-revoked', handleDcmaAccessRevoked);
      window.removeEventListener('dcma-crr-access-revoked', handleCrrAccessRevoked);
    };
  }, [clearWorkflowChatIdentityState]);

  useEffect(() => {
    if (isTrainingPage && currentPermissionsLoaded && !permissionsLoadFailed
      && !canAccessActiveTraining && flowRevalidation?.kind !== 'changed') {
      setFlowRevalidation({
        kind: 'changed',
        message: 'Training access changed. Leave and reopen Training before continuing.',
      });
    }
  }, [isTrainingPage, canAccessActiveTraining, currentPermissionsLoaded, permissionsLoadFailed,
    flowRevalidation?.kind]);

  useEffect(() => {
    if (isTrainingPage && currentPermissionsLoaded && permissionsLoadFailed
      && flowRevalidation?.kind !== 'changed') {
      setFlowRevalidation({
        kind: 'retry',
        message: 'Could not confirm Training access. Retry before continuing.',
      });
    }
  }, [isTrainingPage, currentPermissionsLoaded, permissionsLoadFailed,
    flowRevalidation?.kind]);

  useEffect(() => {
    if (isTrainingPage && currentPermissionsLoaded && !permissionsLoadFailed
      && canAccessActiveTraining && !rolePreviewSyncError
      && flowRevalidation?.kind !== 'changed') {
      setFlowRevalidation(null);
    }
  }, [isTrainingPage, currentPermissionsLoaded, permissionsLoadFailed,
    canAccessActiveTraining, rolePreviewSyncError, flowRevalidation?.kind]);

  // Keep the local projection in sync with the signed server cookie.
  useEffect(() => {
    if (!realIsSuperAdmin && rolePreview !== 'super_admin') {
      setRolePreview('super_admin');
    }
    if (!realIsSuperAdmin) setRolePreviewDialogOpen(false);
  }, [realIsSuperAdmin, rolePreview]);

  useEffect(() => {
    if (!userEmail || rolePreviewBusy || process.env.REACT_APP_USE_MOCK === 'true') return;
    let active = true;
    let latestSync = 0;
    const sync = async (onReturn = false) => {
      let readGeneration = getStartupReadGeneration();
      const syncId = ++latestSync;
      const authGeneration = authGenerationRef.current;
      try {
        const serverRole = realIsSuperAdmin
          ? await getServerRolePreview({ fresh: onReturn || rolePreviewRetry > 0 }) : rolePreview;
        if (!active || syncId !== latestSync || readGeneration !== getStartupReadGeneration()
          || authGeneration !== authGenerationRef.current) return;
        setRolePreviewSyncError(false);
        setAuthChecking(false);
        if (serverRole !== rolePreview) {
          setStartupReadScope(`${userEmail}\u0000${serverRole}`);
          readGeneration = getStartupReadGeneration();
          permissionsRequestGenerationRef.current += 1;
          setPermissionsScope(null);
          setWorkflowPermissionsLoaded(false);
          setAdminStatusRole(null);
          workflowOperationGenerationRef.current += 1;
          if (activeWorkflowMountedRef.current || activeTrainingMountedRef.current) {
            // A role changed in another tab. Keep unsaved draft components
            // mounted behind the blocking dialog until the user leaves.
            setWorkflowChatNavigationPending(false);
            setFlowRevalidation({
              kind: 'changed',
              message: 'The active role changed in another tab. This workflow must be reopened under the new role before you can continue.',
            });
          } else {
            resetPipelineState();
            setMarketResearchFlowView('unknown');
            setMarketResearchRaCollaborationOpen(false);
            setFlowId(null);
            setFlowName('');
            setWorkflowType(null);
            setFlowSelectorScope(null);
            setShowDashboard(false);
            setShowManageUsers(false);
            setSupportIssueView(null);
            setAppPage('type_selector');
          }
          setRolePreview(serverRole);
        } else if (onReturn || rolePreviewSyncError) {
          setPermissionsRefreshVersion((version) => {
            backgroundAccessRefreshVersionRef.current = onReturn
              && (activeWorkflowMountedRef.current || activeTrainingMountedRef.current)
              ? version + 1 : null;
            return version + 1;
          });
        }
        const status = await checkAdminStatus();
        if (!active || syncId !== latestSync || readGeneration !== getStartupReadGeneration()
          || authGeneration !== authGenerationRef.current) return;
        setIsAdmin(status.isAdmin);
        setIsEffectiveSuperAdmin(status.isSuperAdmin);
        setIsUserFeedbackStaff(status.isUserFeedbackStaff);
        setAdminStatusRole(serverRole);
      } catch {
        if (active && syncId === latestSync && readGeneration === getStartupReadGeneration()
          && authGeneration === authGenerationRef.current) {
          setRolePreviewSyncError(true);
          if (activeWorkflowMountedRef.current || activeTrainingMountedRef.current) {
            setFlowRevalidation((current) => current?.kind === 'changed' ? current : {
              kind: 'retry',
              message: 'Could not confirm the active role. Retry before continuing.',
            });
          } else {
            setAuthChecking(true);
          }
        }
      }
    };
    // A mounted workflow already resolved its ownership and route when it was
    // opened. Rechecking on every tab return briefly projects a transient
    // read-only editor while permission and flow metadata requests complete.
    // Server authorization still applies to subsequent writes.
    const onFocus = () => {
      if (!activeWorkflowMountedRef.current) void sync(true);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && !activeWorkflowMountedRef.current) {
        void sync(true);
      }
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    if (realIsSuperAdmin) void sync();
    return () => {
      active = false;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [userEmail, realIsSuperAdmin, rolePreview, rolePreviewBusy, rolePreviewRetry]);

  useLayoutEffect(() => {
    // A policy refresh is pending; wait for its verified result before
    // deciding whether the currently open workflow must be closed.
    // An open workflow has unsaved edits and uses the blocking revalidation
    // dialog; it must never be discarded by a transient permission failure.
    if (appPage === 'workflow' || isTrainingPage || !currentPermissionsLoaded
      || !workflowType || canUseWorkflowType(workflowType)) return;
    setFlowId(null);
    setFlowName('');
    setWorkflowType(null);
    setFlowSelectorScope(null);
    setAppPage('type_selector');
  }, [appPage, isTrainingPage, canUseWorkflowType, currentPermissionsLoaded, workflowType]);

  useEffect(() => {
    const handler = (e: Event) => {
      if ((e as CustomEvent).detail) openFeedbackDialog();
      else if (!feedbackSending) clearFeedbackDialog();
    };
    window.addEventListener('tour:set-feedback', handler);
    return () => window.removeEventListener('tour:set-feedback', handler);
  }, [feedbackSending, openFeedbackDialog, clearFeedbackDialog]);

  useEffect(() => {
    if (process.env.REACT_APP_USE_MOCK === 'true') {
      clearUsageDashboardAuthenticationState();
      setUserEmail('preview@waypoint.demo');
      const mockAdmin = process.env.REACT_APP_MOCK_ADMIN === 'true';
      setIsAdmin(mockAdmin);
      setIsEffectiveSuperAdmin(mockAdmin);
      setIsSuperAdmin(mockAdmin);
      setIsUserFeedbackStaff(mockAdmin);
      setAdminStatusRole('super_admin');
      setShowEmailGate(false);
      setAuthChecking(false);
      setAllowTraining(true);
      setAllowChangelog(true);
      return;
    }
    const controller = new AbortController();
    let readGeneration = getStartupReadGeneration();
    const current = () => !controller.signal.aborted
      && readGeneration === getStartupReadGeneration();
    fetchStartupRead(`${API_BASE_URL}/api/auth/me`, { credentials: 'include', signal: controller.signal })
      .then(async (resp) => {
        if (!current()) return;
        if (!resp.ok) {
          clearUsageDashboardAuthenticationState();
          localStorage.removeItem('farchat_has_session');
          setAllowedWorkflowTypes([]);
          setMarketResearchCallerView('unknown');
          setCustomerMarketResearchEnabled(false);
          setMarketResearchFlowView('unknown');
          setUserCanUseChatbot(false);
          setAllowDcmaPbp(false);
    setAllowDcmaCrr(false);
          setCanUseStandalonePriceEstimator(false);
          setUserEmail(null);
          setIsAdmin(false);
          setIsEffectiveSuperAdmin(false);
          setIsSuperAdmin(false);
          setIsUserFeedbackStaff(false);
          setAdminStatusRole(null);
          setPermissionsScope(null);
          setPendingCount(0);
          setUnreadFeedbackCount(0);
          setShowEmailGate(true);
          setAuthChecking(false);
          return;
        }
        const data = await resp.json();
        if (!current()) return;
        setStartupReadScope(`${data.email}\u0000${rolePreview}`);
        readGeneration = getStartupReadGeneration();
        clearUsageDashboardAuthenticationState();
        localStorage.setItem('farchat_has_session', '1');
        setAllowedWorkflowTypes([]);
        setUserCanUseChatbot(false);
        setAllowDcmaPbp(false);
    setAllowDcmaCrr(false);
        setCanUseStandalonePriceEstimator(false);
        setUserEmail(data.email);
        setAuthChecking(true);
        setShowEmailGate(false);
        checkAdminStatus()
          .then(async ({ isAdmin, isSuperAdmin, isActorSuperAdmin, isUserFeedbackStaff }) => {
            if (!current()) return;
            setIsAdmin(isAdmin);
            setIsEffectiveSuperAdmin(isSuperAdmin);
            setIsSuperAdmin(isActorSuperAdmin === true);
            setIsUserFeedbackStaff(isUserFeedbackStaff);
            if (isActorSuperAdmin === true) {
              try {
                const serverRole = await getServerRolePreview();
                if (!current()) return;
                setStartupReadScope(`${data.email}\u0000${serverRole}`);
                readGeneration = getStartupReadGeneration();
                setRolePreview(serverRole);
                setAdminStatusRole(serverRole);
                setRolePreviewSyncError(false);
                setAuthChecking(false);
              } catch {
                if (!current()) return;
                // Keep the app behind its loading screen until a retry
                // confirms the server's active role.
                setRolePreviewSyncError(true);
              }
            } else {
              setAdminStatusRole('super_admin');
              setAuthChecking(false);
            }
            if (isAdmin) refreshPendingCount();
            if (isSuperAdmin) refreshUnreadFeedbackCount();
          })
          .catch(() => {
            if (!current()) return;
            setIsAdmin(false);
            setIsEffectiveSuperAdmin(false);
            setIsSuperAdmin(false);
            setIsUserFeedbackStaff(false);
            setAdminStatusRole(null);
            setAuthChecking(false);
          });
      })
      .catch(() => {
        if (!current()) return;
        clearUsageDashboardAuthenticationState();
        localStorage.removeItem('farchat_has_session');
        setAllowedWorkflowTypes([]);
        setMarketResearchCallerView('unknown');
        setCustomerMarketResearchEnabled(false);
        setMarketResearchFlowView('unknown');
        setUserCanUseChatbot(false);
        setAllowDcmaPbp(false);
    setAllowDcmaCrr(false);
        setUserEmail(null);
        setIsAdmin(false);
        setIsEffectiveSuperAdmin(false);
        setIsSuperAdmin(false);
        setIsUserFeedbackStaff(false);
        setAdminStatusRole(null);
        setPermissionsScope(null);
        setPendingCount(0);
        setUnreadFeedbackCount(0);
        setShowEmailGate(true);
        setAuthChecking(false);
      });
    return () => controller.abort();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!userEmail) {
      permissionsRequestScopeRef.current = null;
      setPermissionsScope(null);
      setMarketResearchCallerView('unknown');
      setCustomerMarketResearchEnabled(false);
      setHomeChatMode('none');
      setWorkflowOnlyAudience(null);
      setWorkflowPermissionsLoaded(false);
      return;
    }
    // A response from the previous role cannot authorize even one render of
    // the new role. Keep the verified scope visible during same-role refreshes.
    const scope = `${userEmail}\u0000${rolePreview}`;
    const scopeChanged = permissionsRequestScopeRef.current !== scope;
    permissionsRequestScopeRef.current = scope;
    if (scopeChanged) {
      clearProjectTrackerProtectedState();
      setProjectTrackerCapability({allowed:false,revision:'',unit:''});
      setPermissionsScope(null);
      setAllowedWorkflowTypes([]);
      setUserCanUseChatbot(false);
      setAllowTraining(false);
      setAllowRaTraining(false);
      setAllowDcmaPbp(false);
    setAllowDcmaCrr(false);
      setCanUseStandalonePriceEstimator(false);
      setAllowChangelog(false);
      setMarketResearchCallerView('unknown');
      setCustomerMarketResearchEnabled(false);
      setHomeChatMode('none');
      setWorkflowOnlyAudience(null);
      setWorkflowPermissionsLoaded(false);
      setPermissionsLoadFailed(false);
    }
    if (process.env.REACT_APP_USE_MOCK === 'true') {
      // The static mock preview has no permissions endpoint; model its
      // intentionally unrestricted demo account explicitly rather than
      // treating the missing response as a production authorization grant.
      setAllowedWorkflowTypes(null);
      setUserCanUseChatbot(true);
      setHomeChatMode('full');
      setWorkflowOnlyAudience(null);
      setAllowTraining(true);
      setAllowChangelog(true);
      setCustomerMarketResearchEnabled(true);
      setPermissionsScope(scope);
      setPermissionsAppliedVersion(permissionsRefreshVersion);
      setWorkflowPermissionsLoaded(true);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    const readGeneration = getStartupReadGeneration();
    const requestGeneration = permissionsRequestGenerationRef.current;
    (async () => {
      try {
        const headers: Record<string, string> = { 'X-User-Email': userEmail };
        // Background revalidation is a new request generation, never a reuse
        // of an earlier pending permission projection.
        const read = permissionsRefreshVersion > 0 ? fetch : fetchStartupRead;
        const resp = await read(`${API_BASE_URL}/api/user/permissions`, { headers, credentials: 'include', signal: controller.signal });
        if (!resp.ok) throw new Error('Could not load role permissions');
        if (resp.ok) {
          const payload: unknown = await resp.json();
          if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
            throw new Error('Invalid role permissions');
          }
          const data = payload as Record<string, unknown>;
          const allowed = data.allowed_workflow_types;
          if (allowed !== null && (!Array.isArray(allowed)
            || !allowed.every((type) => typeof type === 'string' && type.length > 0))) {
            throw new Error('Invalid allowed workflow types');
          }
          if (!cancelled && readGeneration === getStartupReadGeneration()
            && requestGeneration === permissionsRequestGenerationRef.current) {
            const tracker = {
              allowed: data.can_use_project_tracker === true,
              revision: typeof data.project_tracker_scope_revision === 'string' ? data.project_tracker_scope_revision : '',
              unit: typeof data.project_tracker_unit === 'string' ? data.project_tracker_unit : '',
            };
            const trackerScope = `${scope}:${tracker.allowed}:${tracker.revision}:${tracker.unit}:${JSON.stringify(allowed)}`;
            if (trackerScopeRef.current !== trackerScope) {
              clearProjectTrackerProtectedState();
              trackerScopeRef.current = trackerScope;
            }
            setProjectTrackerCapability(tracker);
            setAllowedWorkflowTypes(allowed);
            setMarketResearchCallerView(
              data.market_research_view === 'customer' || data.market_research_view === 'contracting'
                ? data.market_research_view
                : 'unknown',
            );
            setCustomerMarketResearchEnabled(data.customer_market_research_enabled === true);
            setUserCanUseChatbot(data.allow_chatbot === true);
            setHomeChatMode(parseHomeChatMode(data.home_chat_mode, data.allow_chatbot === true));
            setWorkflowOnlyAudience(data.workflow_only_audience === 'ra' || data.workflow_only_audience === 'ce'
              ? data.workflow_only_audience : null);
            setAllowTraining(data.allow_training === true);
            setAllowRaTraining(data.allow_ra_training === true);
            setAllowDcmaPbp(data.allow_dcma_pbp === true);
            setAllowDcmaCrr(data.allow_dcma_crr === true);
            setCanUseStandalonePriceEstimator(data.can_use_standalone_price_estimator === true);
            setAllowChangelog(data.allow_changelog === true);
            setPermissionsLoadFailed(false);
            setPermissionsScope(scope);
          }
        }
      } catch {
        if (!cancelled && readGeneration === getStartupReadGeneration()
          && requestGeneration === permissionsRequestGenerationRef.current) {
          clearProjectTrackerProtectedState();
          setProjectTrackerCapability({allowed:false,revision:'',unit:''});
          setAllowedWorkflowTypes([]);
          setUserCanUseChatbot(false);
          setHomeChatMode('none');
          setWorkflowOnlyAudience(null);
          setAllowTraining(false);
          setAllowRaTraining(false);
          setAllowDcmaPbp(false);
    setAllowDcmaCrr(false);
          setCanUseStandalonePriceEstimator(false);
          setAllowChangelog(false);
          setMarketResearchCallerView('unknown');
          setCustomerMarketResearchEnabled(false);
          setPermissionsLoadFailed(true);
          setPermissionsScope(scope);
        }
      } finally {
        if (!cancelled && readGeneration === getStartupReadGeneration()
          && requestGeneration === permissionsRequestGenerationRef.current) {
          setPermissionsAppliedVersion(permissionsRefreshVersion);
          setWorkflowPermissionsLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [userEmail, rolePreview, permissionsRefreshVersion]);

  // Fetch per-user stage ratings once auth is established. Ratings are scoped
  // per-user (not per-flow), so a single best-effort fetch per session suffices.
  useEffect(() => {
    if (!userEmail) return;
    let cancelled = false;
    fetchStageRatings()
      .then((map) => {
        if (cancelled) return;
        const next: Record<string, RatingValue> = {};
        for (const [stageId, entry] of Object.entries(map)) {
          next[stageId] = entry.value;
        }
        setStageRatings(next);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [userEmail]);

  // Refresh the pending access-request count (any admin; advisory red dot).
  // The backend returns the unit-scoped list per role, so the count is correct
  // for non-super admins (their unit only) and super admins (everything).
  const refreshPendingCount = useCallback(() => {
    fetchPendingRequests('pending')
      .then((rows) => setPendingCount(rows.length))
      .catch(() => setPendingCount(0));
  }, []);

  // Refresh the unread chat-feedback count (super admins only; advisory badge).
  // Event-driven (no polling); fail-safe → 0, so the badge simply hides on error.
  const refreshUnreadFeedbackCount = useCallback(() => {
    fetchUnreadFeedbackCount()
      .then(setUnreadFeedbackCount)
      .catch(() => setUnreadFeedbackCount(0));
  }, []);

  // Aggregate badge for the account icon without exposing stale admin counts.
  const accountBadgeCount =
    (presentationCapabilities.showAdminNavigation ? pendingCount : 0) +
    (presentationCapabilities.showSuperAdminControls ? unreadFeedbackCount : 0) +
    unreadSupportIssueCount +
    // Workflow requests share the existing numeric Account badge.  Nesting a
    // second dot badge here would obscure the count and make a newly received
    // notification look disconnected from the existing pending work total.
    unreadWorkflowNotificationCount + contactChatInbox.unreadCount;
  const handleAuthSuccess = (email: string) => {
    invalidateStartupReads();
    setStartupReadScope(`${email}\u0000super_admin`);
    const readGeneration = getStartupReadGeneration();
    clearUsageDashboardAuthenticationState();
    clearWorkflowChatIdentityState();
    setAllowedWorkflowTypes([]);
    setUserCanUseChatbot(false);
    setAllowTraining(false);
    setAllowRaTraining(false);
    setAllowDcmaPbp(false);
    setAllowDcmaCrr(false);
    setCanUseStandalonePriceEstimator(false);
    setAllowChangelog(false);
    setIsAdmin(false);
    setIsEffectiveSuperAdmin(false);
    setIsSuperAdmin(false);
    setIsUserFeedbackStaff(false);
    setAdminStatusRole(null);
    setPermissionsScope(null);
    setRolePreview('super_admin');
    setRolePreviewDialogOpen(false);
    setPendingCount(0);
    setUnreadFeedbackCount(0);
    clearDcmaPbpBrowserDrafts();
    setUserEmail(email);
    setShowEmailGate(false);
    checkAdminStatus()
      .then(({ isAdmin, isSuperAdmin, isActorSuperAdmin, isUserFeedbackStaff }) => {
        if (readGeneration !== getStartupReadGeneration()) return;
        setIsAdmin(isAdmin);
        setIsEffectiveSuperAdmin(isSuperAdmin);
        setIsSuperAdmin(isActorSuperAdmin === true);
        setIsUserFeedbackStaff(isUserFeedbackStaff);
        setAdminStatusRole('super_admin');
        if (isAdmin) refreshPendingCount();
        if (isSuperAdmin) refreshUnreadFeedbackCount();
      })
      .catch(() => {
        if (readGeneration !== getStartupReadGeneration()) return;
        setIsAdmin(false);
        setIsEffectiveSuperAdmin(false);
        setIsSuperAdmin(false);
        setIsUserFeedbackStaff(false);
        setAdminStatusRole(null);
        setRolePreview('super_admin');
        setRolePreviewDialogOpen(false);
      });
  };

  const failFlowLoad = (message: string): void => {
    activeFlowRoleRef.current = null;
    setGpcNavigationLoading(false);
    setWorkflowChatSessionId(null);
    setWorkflowChatVisible(false);
    setWorkflowChatNavigationPending(false);
    setFlowId(null);
    setFlowName('');
    setWorkflowType(null);
    setIsViewOnly(false);
    setIsTrainingExample(false);
    setReadOnlyReason(null);
    setAppPage('flow_selector');
    showNotificationMessage(message, 'error');
  };

  // Revalidate the caller-specific route without remounting unchanged drafts.
  useEffect(() => {
    if (appPage !== 'workflow' || !flowId || !workflowType
      || !currentPermissionsLoaded
      || permissionsAppliedVersion !== permissionsRefreshVersion
      || workflowChatNavigationPending || rolePreviewSyncError) return;
    const refreshKey = `${rolePreview}\u0000${flowId}\u0000${permissionsRefreshVersion}`;
    if (activeFlowRoleRef.current && activeFlowRoleRef.current !== rolePreview) {
      setFlowRevalidation({
        kind: 'changed',
        message: 'The active role changed in another tab. This workflow must be reopened under the new role before you can continue.',
      });
      return;
    }
    if (!canUseWorkflowType(workflowType)) {
      setFlowRevalidation(permissionsLoadFailed
        ? {
          kind: 'retry',
          message: 'Could not confirm your current workflow permissions. Retry before continuing.',
        }
        : {
          kind: 'changed',
          message: 'This workflow type is no longer available to the current role. Leave the workflow or retry after access is restored.',
        });
      return;
    }
    if (checkedFlowRefreshKey === refreshKey) return;
    const chatCapability = !isDcmaOnly && presentationCapabilities.allowChatbot;
    if (workflowChatLayoutRef.current?.flowId === flowId
      && workflowChatLayoutRef.current.enabled
      && !chatCapability) {
      setFlowRevalidation({
        kind: 'changed',
        message: 'Workflow chat access changed. Reopen this workflow before continuing.',
      });
      return;
    }
    if (workflowChatLayoutRef.current?.flowId === flowId
      && !workflowChatLayoutRef.current.enabled
      && chatCapability) {
      setFlowRevalidation({
        kind: 'changed',
        message: 'Workflow chat access changed. Reopen this workflow before continuing.',
      });
      return;
    }
    if (backgroundAccessRefreshVersionRef.current !== permissionsRefreshVersion) {
      setFlowRevalidation({ kind: 'checking' });
    }
    const operationGeneration = workflowOperationGenerationRef.current;
    const authGeneration = authGenerationRef.current;
    let cancelled = false;
    const isCurrent = () => !cancelled
      && authGenerationRef.current === authGeneration
      && workflowOperationGenerationRef.current === operationGeneration
      && activeWorkflowFlowIdRef.current === flowId;
    const blockChangedFlow = (message: string) => {
      if (!isCurrent()) return;
      setFlowRevalidation({ kind: 'changed', message });
    };
    void (async () => {
      try {
        const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${encodeURIComponent(flowId)}`, {
          credentials: 'include',
        });
        if (!isCurrent()) return;
        if (response.status === 401) {
          window.dispatchEvent(new Event('auth-expired'));
          return;
        }
        if (response.status === 403 || response.status === 404) {
          blockChangedFlow(USER_FACING_ERROR_MESSAGES.workflow_access_revoked);
          return;
        }
        if (!response.ok) throw new Error('Could not refresh workflow access');
        const data = await response.json();
        if (!isCurrent()) return;
        const metadata = data?.metadata as ActiveFlowRouteMetadata | undefined;
        if (!metadata || typeof metadata !== 'object') {
          blockChangedFlow(USER_FACING_ERROR_MESSAGES.workflow_unavailable);
          return;
        }
        const signature = activeFlowRouteSignature(metadata);
        const baseline = activeFlowRouteRef.current;
        const matchesCurrentView = (metadata.workflow_type ?? 'market_research') === workflowType
          && (workflowType !== 'one_time_construction' || (
            metadata.standalone_mode === standaloneMode
            && (standaloneMode || resolveOneTimeConstructionOrigin(metadata) === constructionSowOrigin)
            && constructionHasStage(metadata, 'price_estimator') === constructionPriceEstimatorApplicable
          ))
          && (workflowType !== 'market_research'
            || metadata.market_research_view === marketResearchFlowView);
        if ((baseline?.flowId === flowId && baseline.signature !== signature) || !matchesCurrentView) {
          blockChangedFlow('Workflow access or stage design changed. Reopen it from Manage Workflows.');
          return;
        }
        activeFlowRouteRef.current = { flowId, signature };
        setCheckedFlowRefreshKey(refreshKey);
        setFlowRevalidation(null);
      } catch {
        if (isCurrent()) {
          setFlowRevalidation({
            kind: 'retry',
            message: 'Could not confirm this workflow’s current access and stage path. Retry before continuing.',
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [
    appPage,
    canUseWorkflowType,
    checkedFlowRefreshKey,
    constructionPriceEstimatorApplicable,
    constructionSowOrigin,
    currentPermissionsLoaded,
    flowId,
    isDcmaOnly,
    marketResearchFlowView,
    permissionsRefreshVersion,
    permissionsAppliedVersion,
    permissionsLoadFailed,
    presentationCapabilities.allowChatbot,
    flowRevalidationRetryVersion,
    rolePreview,
    rolePreviewSyncError,
    standaloneMode,
    workflowType,
    workflowChatNavigationPending,
  ]);

  const handleSelectFlow = async (
    selectedFlowId: string,
    selectedFlowName?: string,
    navigation?: {
      gpcTargetStageHint?: GpcFormStageKey;
      preserveMrChatResults?: boolean;
      /** Redeemed only by the construction chat's owner-bound 206 receipt. */
      constructionSowGenerationManualEdit?: boolean;
      /** Redeemed only by the Services chat's owner-bound 622 receipt. */
      servicesPwsGenerationManualEdit?: boolean;
      knownWorkflowType?: WorkflowType;
      /** Fail closed when a server-authorized navigation requires this type. */
      requiredWorkflowType?: WorkflowType;
      /** Keep the calling panel mounted until notification target preflight succeeds. */
      deferPageTransitionUntilReady?: boolean;
      homeChatSessionId?: string | null;
    },
  ) => {
    if (!documentAssistant.allowNavigation()) { showNotificationMessage('Finish the current edit before leaving this stage.', 'info'); return; }
    // Selecting a workflow only reads its server-authorized metadata and
    // stage state. In a role preview, effectiveIsViewOnly keeps the opened
    // workflow non-mutating; do not block Super Admins from inspecting a
    // workflow that the simulated role can access.
    // Saved-workflow rows normally carry their type. Reject a stale or
    // incompatible known type before fetching it; the response type is still
    // checked below because this metadata is only a client-side preflight.
    if (navigation?.knownWorkflowType && !canUseWorkflowType(navigation.knownWorkflowType)) {
      showNotificationMessage(USER_FACING_ERROR_MESSAGES.workflow_access_revoked, 'info');
      return false;
    }
    const pendingNotificationReadiness = notificationStageReadinessRef.current;
    if (pendingNotificationReadiness) {
      window.clearTimeout(pendingNotificationReadiness.timeout);
      notificationStageReadinessRef.current = null;
      pendingNotificationReadiness.reject(
        new Error('Workflow notification navigation was superseded'),
      );
    }
    const authGeneration = authGenerationRef.current;
    const operationGeneration = ++workflowOperationGenerationRef.current;
    activeFlowRouteRef.current = null;
    setCheckedFlowRefreshKey(null);
    setFlowRevalidation(null);
    activeFlowRoleRef.current = rolePreview;
    const isCurrentWorkflowOperation = () => (
      authGenerationRef.current === authGeneration
      && workflowOperationGenerationRef.current === operationGeneration
    );
    setSupportIssueView(null);
    resetPipelineState();
    // The existing shell still describes the workflow being left. Keep the
    // incoming companion hidden and clear that context until this load has
    // applied the authorized workflow type and its restored stage.
    setWorkflowChatNavigationPending(true);
    setWorkflowType(null);
    setPendingWorkflowChatSessionId(null);
    setPendingWorkflowHandoffReceipt(null);
    const requestedHomeChatSessionId = navigation?.homeChatSessionId ?? null;
    const rememberedHomeChatSessionId =
      workflowChatSessionsByFlowRef.current.get(selectedFlowId) ?? null;
    const companionRevisionAtLoad =
      workflowChatRevisionByFlowRef.current.get(selectedFlowId) ?? 0;
    const companionVisibilityRevisionAtLoad =
      workflowChatVisibilityRevisionByFlowRef.current.get(selectedFlowId) ?? 0;
    const initialHomeChatSessionId = resolveWorkflowChatSessionId({
      requestedSessionId: requestedHomeChatSessionId,
      rememberedSessionId: rememberedHomeChatSessionId,
    });
    if (initialHomeChatSessionId) {
      rememberWorkflowChatSession(selectedFlowId, initialHomeChatSessionId);
    }
    setWorkflowChatSessionId(initialHomeChatSessionId);
    setWorkflowChatVisible(Boolean(initialHomeChatSessionId));
    setPreserveMrChatResults(navigation?.preserveMrChatResults === true);
    // Never carry a previous flow's presentation projection into a newly
    // selected flow while its server metadata is loading.
    setMarketResearchFlowView('unknown');
    setSelectedFlowIsOwner(false);
    setMarketResearchCustomerTerminal(false);
    setMarketResearchCustomerTerminalStageView(false);
    setMarketResearchRaCollaborationOpen(false);
    setMarketResearchContractingHandoffState('not_handed_off');
    setMarketResearchContractingHandoffVerifiedFlowId(null);
    setGpcNavigationLoading(true);
    setFlowId(selectedFlowId);
    setPostAwardResumeFlowId(null);
    setClsCorrectionResult(null);
    setClsExtractedParams(null);
    setClsUserAnswers(null);
    if (selectedFlowName) {
      setFlowName(selectedFlowName);
    } else {
      setFlowName('');
    }
    if (!navigation?.deferPageTransitionUntilReady) {
      setAppPage('workflow');
    }
    try {
      let persistedSelectedIds: string[] = [];
      const headers: Record<string, string> = {};
      const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${selectedFlowId}`, { headers, credentials: 'include' });
      if (!isCurrentWorkflowOperation()) return false;
      if (response.status === 401) {
        window.dispatchEvent(new Event('auth-expired'));
        return false;
      }
      if (!response.ok) {
        // Saved-flow navigation supplies the known type. A 403 for a known
        // DCMA flow means the roster grant is stale, so remove DCMA from the
        // active shell immediately rather than leaving its entry points live.
        if (response.status === 403 && navigation?.knownWorkflowType === 'dcma_pbp') {
          setAllowDcmaPbp(false);
        }
        failFlowLoad(response.status === 403
          ? USER_FACING_ERROR_MESSAGES.workflow_access_revoked
          : response.status === 404
          ? USER_FACING_ERROR_MESSAGES.workflow_no_longer_available
          : USER_FACING_ERROR_MESSAGES.workflow_unavailable);
        return false;
      }
      const data = await response.json();
      if (!isCurrentWorkflowOperation()) return false;
      const metadata = data.metadata;
      // Training-flow metadata controls the shared read-only rendering path.
      const targetReadOnly = isWorkflowReadOnly(metadata);
      setIsViewOnly(targetReadOnly);
      setReadOnlyReason(metadata.read_only_reason ?? (metadata.is_training_example === true ? 'training' : null));
      setIsTrainingExample(metadata.is_training_example ?? (metadata.read_only === true && metadata.read_only_reason === 'training'));
        setSelectedFlowIsOwner(metadata.is_owner !== false);
        const flowWorkflowType = metadata.workflow_type || 'market_research';
        if (navigation?.requiredWorkflowType && flowWorkflowType !== navigation.requiredWorkflowType) {
          failFlowLoad(USER_FACING_ERROR_MESSAGES.workflow_unavailable);
          return false;
        }
        const flowMarketResearchView = flowWorkflowType === 'market_research'
          && (metadata.market_research_view === 'customer'
            || metadata.market_research_view === 'contracting')
          ? metadata.market_research_view
          : 'unknown';
        const isCustomerFlowView = flowMarketResearchView === 'customer';
        setMarketResearchFlowView(flowMarketResearchView);
        setMarketResearchCustomerTerminal(
          isCustomerFlowView
          && metadata.market_research_section_c_complete === true,
        );
        // A reopened Customer flow returns to its editable Section C terminal;
        // progress is an explicit unnumbered destination, never a default route.
        setMarketResearchCustomerTerminalStageView(true);
        if (!canUseWorkflowType(flowWorkflowType)) {
          failFlowLoad(USER_FACING_ERROR_MESSAGES.workflow_access_revoked);
          return false;
        }
        activeFlowRouteRef.current = {
          flowId: selectedFlowId,
          signature: activeFlowRouteSignature(metadata),
        };
        setWorkflowType(flowWorkflowType);
        if (flowWorkflowType !== 'gpc') setGpcNavigationLoading(false);
        if (!targetReadOnly && currentPermissionsLoaded && !isDcmaOnly && presentationCapabilities.allowChatbot) {
          const attributedSessionId = attributedHomeChatSessionId(metadata);
          if (attributedSessionId) {
            attributedWorkflowChatSessionsByFlowRef.current.set(
              selectedFlowId,
              attributedSessionId,
            );
          } else {
            attributedWorkflowChatSessionsByFlowRef.current.delete(
              selectedFlowId,
            );
          }
          const applyCompanionRestore = (
            persistedHomeChatSessionId: string | null | undefined,
          ) => {
            if (!isCurrentWorkflowOperation()) return;
            const companionRevisionNow =
              workflowChatRevisionByFlowRef.current.get(selectedFlowId) ?? 0;
            const restoredHomeChatSessionId = resolveWorkflowChatSessionId({
              requestedSessionId: requestedHomeChatSessionId,
              metadata,
              persistedSessionId: persistedHomeChatSessionId,
              rememberedSessionId: rememberedHomeChatSessionId,
            });
            if (companionRevisionNow === companionRevisionAtLoad) {
              const companionNeedsRemount =
                restoredHomeChatSessionId !== initialHomeChatSessionId;
              setWorkflowChatSessionId(restoredHomeChatSessionId);
              rememberWorkflowChatSession(
                selectedFlowId,
                restoredHomeChatSessionId,
              );
              if (companionNeedsRemount) {
                setWorkflowChatMountRevision((current) => current + 1);
              }
            }
            const visibilityRevisionNow =
              workflowChatVisibilityRevisionByFlowRef.current.get(
                selectedFlowId,
              ) ?? 0;
            if (
              visibilityRevisionNow === companionVisibilityRevisionAtLoad
            ) {
              setWorkflowChatVisible(Boolean(restoredHomeChatSessionId));
            }
          };
          void getWorkflowCompanionSession(selectedFlowId)
            .then(applyCompanionRestore)
            .catch((error) => {
              console.error(
                'Failed to restore workflow chat companion:',
                error,
              );
              applyCompanionRestore(undefined);
            });
        }
        if (flowWorkflowType === 'other_transactions') {
          if (!selectedFlowName && metadata.name) setFlowName(metadata.name);
          setCurrentStage(getOtherTransactionsResumeStage(metadata));
          setMaxStageReached(9);
          return;
        }

        if (flowWorkflowType === 'dcma_pbp' || flowWorkflowType === 'dcma_crr') {
          setCurrentStage(0);
          if (!selectedFlowName && metadata.name) setFlowName(metadata.name);
          return;
        }

        // Post-award uses the canonical 1000–1002 sequence. Its workspace
        // owns the content; App owns the shared stepper state and unlocks each
        // following stage from persisted completion, like other workflows.
        if (flowWorkflowType === 'post_award_actions') {
          if (!selectedFlowName && metadata.name) {
            setFlowName(metadata.name);
          }
          const { canHydrate, maxAvailableStage } = getPostAwardResumeState(metadata);
          setPostAwardResumeFlowId(canHydrate ? selectedFlowId : null);
          const savedStage = localStorage.getItem(`post_award_stage_${selectedFlowId}`);
          const parsedSavedStage = savedStage === null ? NaN : parseInt(savedStage, 10);
          const targetStage = !isNaN(parsedSavedStage) && parsedSavedStage >= 0 && parsedSavedStage <= maxAvailableStage
            ? parsedSavedStage
            : maxAvailableStage;
          localStorage.setItem(`post_award_stage_${selectedFlowId}`, String(targetStage));
          setCurrentStage(targetStage);
          setMaxStageReached(maxAvailableStage);
          showNotificationMessage(`Loaded Post-award Actions workflow: ${metadata.name}`, 'info');
          return;
        }

        // For SOW Verification workflows, restore stage based on cached data
        if (flowWorkflowType === 'sow_verification') {
          setWorkflowType('sow_verification');

          // Check if stage 201 is currently processing
          try {
            const stage201Status = await getStageStatus(selectedFlowId, 201);
            if (!isCurrentWorkflowOperation()) return;
            if (stage201Status?.status === 'processing') {
              console.log('[App] SOW stage 201 is processing, showing spinner');
              setSowIsProcessing(true);
              setCurrentStage(1); // Suggestions stage
              if (!selectedFlowName && metadata.name) {
                setFlowName(metadata.name);
              }
              showNotificationMessage(`Loaded Construction SOW Verification workflow: ${metadata.name} (processing in progress)`, 'info');
              return;
            }
          } catch (e) {
            if (!isCurrentWorkflowOperation()) return;
            console.warn('[App] Failed to check SOW stage 201 status:', e);
          }
          setSowIsProcessing(false);

          let maxAvailableStage = 0;

          try {
            const stage201Response = await fetchWithChatFeedback(
              `${API_BASE_URL}/api/flows/${selectedFlowId}/stage/201`,
              { headers, credentials: 'include' }
            );
            if (!isCurrentWorkflowOperation()) return;
            if (stage201Response.ok) {
              const stage201Data = await stage201Response.json();
              if (!isCurrentWorkflowOperation()) return;

              if (stage201Data?.output?.sow_edited_docx) {
                maxAvailableStage = 2;
              } else {
                const cachedJobId = stage201Data?.output?.job_id;
                const annotationStatuses = stage201Data?.output?.annotation_statuses;
                if (cachedJobId || (annotationStatuses && Object.keys(annotationStatuses).length > 0)) {
                  maxAvailableStage = 1;
                }
              }
            }
          } catch (e) {
            if (!isCurrentWorkflowOperation()) return;
            console.warn('[App] Failed to load stage 201 cache:', e);
          }

          // Use the user's last active stage if saved, otherwise use max available
          const savedStageStr = localStorage.getItem(`sow_stage_${selectedFlowId}`);
          let targetStage = maxAvailableStage;
          if (savedStageStr !== null) {
            const savedStage = parseInt(savedStageStr, 10);
            if (!isNaN(savedStage) && savedStage >= 0 && savedStage <= maxAvailableStage) {
              targetStage = savedStage;
            }
          }

          console.log(`[App] SOW resume: maxAvailable=${maxAvailableStage}, saved=${savedStageStr}, target=${targetStage}`);

          if (targetStage === 2) {
            setEnteredEditFromSuggestions(false);
          }
          localStorage.setItem(`sow_stage_${selectedFlowId}`, String(targetStage));
          setCurrentStage(targetStage);
          setMaxStageReached(Math.max(maxAvailableStage, targetStage));

          if (!selectedFlowName && metadata.name) {
            setFlowName(metadata.name);
          }
          const stageNames = ['upload', 'suggestions', 'editor'];
          showNotificationMessage(`Loaded Construction SOW Verification workflow: ${metadata.name} (${stageNames[targetStage] || 'upload'})`, 'info');
          return;
        }

        // For CLS workflows, load cached stage data and restore state
        if (flowWorkflowType === 'clause_logic') {
          setWorkflowType('clause_logic');

          // Check if stage 102 (CLS correction) is currently processing
          // If so, don't jump to results from stale cached data - stay on questions stage
          const [stage102Status, stage103Status] = await Promise.all([
            getStageStatus(selectedFlowId, 102),
            getStageStatus(selectedFlowId, 103),
          ]);
          if (!isCurrentWorkflowOperation()) return;
          if (stage102Status?.status === 'processing' || stage103Status?.status === 'processing') {
            console.log('[App] Stage 102/103 is still processing, staying on questions stage');
            setClsIsProcessing(true);
            setCurrentStage(1); // CLSResults — will show loading spinner since no data is passed
            if (!selectedFlowName && metadata.name) {
              setFlowName(metadata.name);
            }
            showNotificationMessage(`Loaded CLS workflow: ${metadata.name} (processing in progress)`, 'info');
            return;
          }

          setClsIsProcessing(false);

          // Try to load CLS stage 3 (correction results) from backend
          const stage3Data = await getCLSStageData(selectedFlowId, 3);
          if (!isCurrentWorkflowOperation()) return;
          // Only restore results if there are actual clauses (not just empty arrays or questions-only data)
          const hasActualResults = stage3Data?.data && (
            (Array.isArray(stage3Data.data.clauses_to_add) && stage3Data.data.clauses_to_add.length > 0) ||
            (Array.isArray(stage3Data.data.clauses_to_remove) && stage3Data.data.clauses_to_remove.length > 0) ||
            (Array.isArray(stage3Data.data.clauses_unchanged) && stage3Data.data.clauses_unchanged.length > 0)
          );
          if (hasActualResults) {
            setClsCorrectionResult({
              clauses_to_add: stage3Data.data.clauses_to_add || [],
              clauses_to_remove: stage3Data.data.clauses_to_remove || [],
              clauses_to_update: stage3Data.data.clauses_to_update || [],
              clauses_unchanged: stage3Data.data.clauses_unchanged || [],
              diff_report: stage3Data.data.diff_report || '',
              corrected_xml_path: stage3Data.data.corrected_xml_path || 'draft_corrected.xml',
              html_viewer_path: stage3Data.data.comparison_html_path || 'draft_corrected.html',
              // Include questions and answers so they can be displayed alongside results
              questions: stage3Data.data.questions || [],
              answers: stage3Data.data.answers || {},
              has_questions: !!(stage3Data.data.questions && stage3Data.data.questions.length > 0),
              dodaac_questions: stage3Data.data.dodaac_questions || {},
              flags: stage3Data.data.flags || null,
            });

            // Restore user overrides if they exist
            if (stage3Data.data.user_overrides) {
              const overridesMap = new Map<string, 'added' | 'removed'>();
              Object.entries(stage3Data.data.user_overrides).forEach(([key, value]) => {
                if (value === 'added' || value === 'removed') {
                  overridesMap.set(key, value);
                }
              });
              setClsUserOverrides(overridesMap);
            }

            // Load manual clause changes from cache
            try {
              const userEmail = localStorage.getItem('userEmail') || '';
              const manualChangesRes = await fetchWithChatFeedback(
                `${API_BASE_URL}/api/cls/${selectedFlowId}/manual-changes`,
                {
                  credentials: 'include',
                }
              );
              if (!isCurrentWorkflowOperation()) return;
              if (manualChangesRes.ok) {
                const manualChangesData = await manualChangesRes.json();
                if (!isCurrentWorkflowOperation()) return;
                if (manualChangesData?.success && manualChangesData.manual_changes) {
                  console.log('[App] Restoring manual changes:', manualChangesData.manual_changes);
                  const { userOverrides: savedOverrides, hiddenClauses: savedHidden, manuallyAddedClauses: savedManual } = manualChangesData.manual_changes;
                  
                  if (savedOverrides && Object.keys(savedOverrides).length > 0) {
                    setClsUserOverrides(new Map(Object.entries(savedOverrides) as [string, 'added' | 'removed'][]));
                  }
                  if (savedHidden && savedHidden.length > 0) {
                    setClsHiddenClauses(new Set(savedHidden));
                  }
                  if (savedManual && savedManual.length > 0) {
                    setClsManuallyAddedClauses(savedManual);
                  }
                }
              }
            } catch (manualErr) {
              if (!isCurrentWorkflowOperation()) return;
              console.error('[App] Error loading manual changes:', manualErr);
            }

            // Determine which stage to go to
            // Priority: metadata.currentStage > user_overrides existence
            if (metadata.currentStage === 2) {
              setCurrentStage(2); // Final Review stage (index 2)
            } else if (stage3Data.data.user_overrides) {
              setCurrentStage(2); // Final Review stage (index 2)
            } else {
              setCurrentStage(1); // Results stage (index 1)
            }

            if (!selectedFlowName && metadata.name) {
              setFlowName(metadata.name);
            }
            showNotificationMessage(`Loaded CLS workflow: ${metadata.name} (correction complete)`, 'info');
            return;
          } else if (stage3Data?.data?.questions && Array.isArray(stage3Data.data.questions) && stage3Data.data.questions.length > 0) {
            // Have cached questions but no results yet - go to stage 1 to show questions
            // Pass questions via clsCorrectionResult so CLSResults can display them
            console.log('[App] Found cached questions but no results, going to stage 1');
            // Clear any stale user overrides since we are starting fresh with questions
            setClsUserOverrides(new Map());
            setClsHiddenClauses(new Set());
            setClsManuallyAddedClauses([]);
            setClsCorrectionResult({
              clauses_to_add: [],
              clauses_to_remove: [],
              clauses_to_update: [],
              clauses_unchanged: [],
              diff_report: '',
              corrected_xml_path: '',
              html_viewer_path: '',
              // Pass questions so CLSResults shows them
              questions: stage3Data.data.questions,
              answers: stage3Data.data.answers || {},
              has_questions: true,
              status: stage3Data.data.status || 'questions_pending',
              dodaac_questions: stage3Data.data.dodaac_questions || {},
              flags: stage3Data.data.flags || null,
            });
            setCurrentStage(1); // Results/Questions stage (index 1)
            if (!selectedFlowName && metadata.name) {
              setFlowName(metadata.name);
            }
            showNotificationMessage(`Loaded CLS workflow: ${metadata.name} (questions ready)`, 'info');
            return;
          }

          // Try to load CLS stage 2 (extract-params) - deprecated, skip to results
          const stage2Data = await getCLSStageData(selectedFlowId, 2);
          if (!isCurrentWorkflowOperation()) return;
          if (stage2Data?.data?.cls_params) {
            setClsExtractedParams(stage2Data.data.cls_params);
            setCurrentStage(1); // Go to Results stage (index 1)
            if (!selectedFlowName && metadata.name) {
              setFlowName(metadata.name);
            }
            showNotificationMessage(`Loaded CLS workflow: ${metadata.name}`, 'info');
            return;
          }

          // No cached data, start from upload stage
          setCurrentStage(0);
          if (!selectedFlowName && metadata.name) {
            setFlowName(metadata.name);
          }
          showNotificationMessage(`Loaded CLS workflow: ${metadata.name}`, 'info');
          return;
        }


        // For Wage Determination workflows, restore stage based on cached data
        // Stages: 0=Upload, 1=Extraction Review, 2=Search Results (with export)
        if (flowWorkflowType === 'wage_determination') {
          setWorkflowType('wage_determination');

          // Check if extraction is currently processing
          try {
            const stage302Status = await getStageStatus(selectedFlowId, 302);
            if (!isCurrentWorkflowOperation()) return;
            if (stage302Status?.status === 'processing') {
              console.log('[App] Wage Det stage 302 is processing, skipping cached data');
              setCurrentStage(0); // single-page WD
              if (!selectedFlowName && metadata.name) {
                setFlowName(metadata.name);
              }
              showNotificationMessage(`Loaded Wage Determination workflow: ${metadata.name} (processing in progress)`, 'info');
              return;
            }
          } catch (e) {
            if (!isCurrentWorkflowOperation()) return;
            console.warn('[App] Failed to check Wage Det stage 302 status:', e);
          }

          // Check for search results (stage 2 - Search with export)
          try {
            const searchResultsResponse = await fetchWithChatFeedback(
              `${API_BASE_URL}/api/flows/${selectedFlowId}/wage-det/search-results`,
              { credentials: 'include', headers }
            );
            if (!isCurrentWorkflowOperation()) return;
            if (searchResultsResponse.ok) {
              const searchData = await searchResultsResponse.json();
              if (!isCurrentWorkflowOperation()) return;
              // Check if search was performed (has search_params OR has results)
              // Even if no wage determinations were found, we should restore to Search stage
              if (searchData?.search_params || (searchData?.wage_determinations && searchData.wage_determinations.length > 0)) {
                console.log('[App] Found search results, restoring to Search stage (stage 2)');
                setCurrentStage(0); // Go to Search stage (index 2)
                if (!selectedFlowName && metadata.name) {
                  setFlowName(metadata.name);
                }
                showNotificationMessage(`Loaded Wage Determination workflow: ${metadata.name} (search complete)`, 'info');
                return;
              }
            }
          } catch (e) {
            if (!isCurrentWorkflowOperation()) return;
            console.warn('[App] Failed to load search results cache:', e);
          }

          // Check for extraction data (stage 1 - Extraction Review)
          try {
            const extractionResponse = await fetchWithChatFeedback(
              `${API_BASE_URL}/api/flows/${selectedFlowId}/wage-det/extraction`,
              { credentials: 'include', headers }
            );
            if (!isCurrentWorkflowOperation()) return;
            if (extractionResponse.ok) {
              const extractionData = await extractionResponse.json();
              if (!isCurrentWorkflowOperation()) return;
              if (extractionData?.location?.state_abbrev || extractionData?.construction_type) {
                console.log('[App] Found extraction data, restoring to Extraction stage (stage 1)');
                setCurrentStage(0); // Go to Extraction stage (index 1)
                if (!selectedFlowName && metadata.name) {
                  setFlowName(metadata.name);
                }
                showNotificationMessage(`Loaded Wage Determination workflow: ${metadata.name} (extraction complete)`, 'info');
                return;
              }
            }
          } catch (e) {
            if (!isCurrentWorkflowOperation()) return;
            console.warn('[App] Failed to load extraction cache:', e);
          }

          // Check for uploaded document - if exists with extraction, go to stage 1; otherwise stage 0
          try {
            const documentResponse = await fetchWithChatFeedback(
              `${API_BASE_URL}/api/flows/${selectedFlowId}/wage-det/document`,
              { credentials: 'include', headers }
            );
            if (!isCurrentWorkflowOperation()) return;
            if (documentResponse.ok) {
              const docData = await documentResponse.json();
              if (!isCurrentWorkflowOperation()) return;
              if (docData?.filename) {
                console.log('[App] Found uploaded document, restoring to Extraction stage (stage 1)');
                // Document exists - go to Extraction stage where auto-extract can happen
                setCurrentStage(0);
                if (!selectedFlowName && metadata.name) {
                  setFlowName(metadata.name);
                }
                showNotificationMessage(`Loaded Wage Determination workflow: ${metadata.name} (document uploaded)`, 'info');
                return;
              }
            }
          } catch (e) {
            if (!isCurrentWorkflowOperation()) return;
            console.warn('[App] Failed to load document cache:', e);
          }

          // No cached data, start from upload stage (stage 0)
          setCurrentStage(0);
          if (!selectedFlowName && metadata.name) {
            setFlowName(metadata.name);
          }
          showNotificationMessage(`Loaded Wage Determination workflow: ${metadata.name}`, 'info');
          return;
        }

        // For PNM workflows, restore stage based on cached data
        // Single unified stage (stage 0)
        if (flowWorkflowType === 'pnm') {
          setWorkflowType('pnm');

          // Check for comparison results (stage 1 - Comparison Table)
          try {
            const comparisonResponse = await fetchWithChatFeedback(
              `${API_BASE_URL}/api/flows/${selectedFlowId}/stage/401/ui`,
              { credentials: 'include', headers }
            );
            if (!isCurrentWorkflowOperation()) return;
            if (comparisonResponse.ok) {
              const comparisonData = await comparisonResponse.json();
              if (!isCurrentWorkflowOperation()) return;
              if (comparisonData?.output?.discrepancies) {
                console.log('[App] Found PNM comparison results, restoring PNM stage');
                setCurrentStage(0);
                if (!selectedFlowName && metadata.name) {
                  setFlowName(metadata.name);
                }
                showNotificationMessage(`Loaded PNM workflow: ${metadata.name} (comparison results available)`, 'info');
                return;
              }
            }
          } catch (e) {
            if (!isCurrentWorkflowOperation()) return;
            console.warn('[App] Failed to load PNM comparison cache:', e);
          }

          // Check for uploaded documents
          try {
            const docsResponse = await fetchWithChatFeedback(
              `${API_BASE_URL}/api/flows/${selectedFlowId}/pnm/documents`,
              { credentials: 'include', headers }
            );
            if (!isCurrentWorkflowOperation()) return;
            if (docsResponse.ok) {
              const docsData = await docsResponse.json();
              if (!isCurrentWorkflowOperation()) return;
              if (docsData?.vendor_quote || docsData?.ige) {
                console.log('[App] Found PNM documents, restoring to Upload stage (stage 0)');
                setCurrentStage(0);
                if (!selectedFlowName && metadata.name) {
                  setFlowName(metadata.name);
                }
                showNotificationMessage(`Loaded PNM workflow: ${metadata.name} (documents uploaded)`, 'info');
                return;
              }
            }
          } catch (e) {
            if (!isCurrentWorkflowOperation()) return;
            console.warn('[App] Failed to load PNM documents cache:', e);
          }

          // No cached data, start from upload stage
          setCurrentStage(0);
          if (!selectedFlowName && metadata.name) {
            setFlowName(metadata.name);
          }
          showNotificationMessage(`Loaded PNM workflow: ${metadata.name}`, 'info');
          return;
        }
        // For FOPR workflows, check if extraction data exists and skip to form if so
        if (flowWorkflowType === 'fopr') {
          setWorkflowType('fopr');
          if (!selectedFlowName && metadata.name) {
            setFlowName(metadata.name);
          }

          // Check for standalone mode flag. Prefer the value persisted on the
          // backend (metadata.standalone_mode) so duplicated flows — which get
          // a fresh flow_id and therefore have no localStorage entry — still
          // route correctly. Fall back to the legacy localStorage flag for
          // older flows that predate the metadata field.
          const isStandalone =
            metadata.standalone_mode === true ||
            localStorage.getItem(`standalone-mode-${selectedFlowId}`) === 'true';
          // Mirror the resolved value into localStorage so subsequent loads
          // (and any code still reading the legacy flag) stay consistent.
          if (isStandalone) {
            localStorage.setItem(`standalone-mode-${selectedFlowId}`, 'true');
          }
          setStandaloneMode(isStandalone);

          if (isStandalone) {
            // FOPR standalone: map backend stage → React index (0-indexed within foprStages)
            const backendStage = metadata.currentStage || 502;
            const foprIndex = backendStage >= 502 ? 2 : backendStage >= 501 ? 1 : 0;
            const clamped = Math.max(foprIndex, 2);  // standalone minimum is Abstract (index 2)
            setCurrentStage(clamped);
            setMaxStageReached(clamped);
            showNotificationMessage(`Loaded FOPR workflow: ${metadata.name}`, 'info');
            return;
          }

          try {
            const savedData = await getFOPRData(selectedFlowId);
            if (!isCurrentWorkflowOperation()) return;
            setFoprData(savedData);
            // Determine resume stage from completedStages
            const foprCompletedStages: number[] = metadata.completedStages || metadata.completed_stages || [];
            let foprResumeStage = 1; // default: FOPR Form
            if (foprCompletedStages.includes(502)) {
              foprResumeStage = 2; // Abstract of Proposals
            } else if (foprCompletedStages.includes(501) || foprCompletedStages.includes(500)) {
              foprResumeStage = 1; // FOPR Form
            }
            setCurrentStage(foprResumeStage);
            showNotificationMessage(`Loaded FOPR workflow: ${metadata.name}`, 'info');
          } catch {
            if (!isCurrentWorkflowOperation()) return;
            setCurrentStage(0);  // no saved data, start at upload
            showNotificationMessage(`Loaded FOPR workflow: ${metadata.name}`, 'info');
          }
          return;
        }

        // For Services COMBO workflows, check if extraction data exists and skip to form if so
        if (flowWorkflowType === 'services_combo') {
          setWorkflowType('services_combo');
          if (!selectedFlowName && metadata.name) {
            setFlowName(metadata.name);
          }
          try {
            const savedCombo = await getServicesComboData(selectedFlowId);
            if (!isCurrentWorkflowOperation()) return;
            setServicesComboData(savedCombo);
            setMaxStageReached(1);
            setCurrentStage(1);  // resume on the Form stage
            showNotificationMessage(`Loaded Combined Synopsis workflow: ${metadata.name}`, 'info');
          } catch {
            if (!isCurrentWorkflowOperation()) return;
            setCurrentStage(0);  // no saved data, start at upload
            showNotificationMessage(`Loaded Combined Synopsis workflow: ${metadata.name}`, 'info');
          }
          return;
        }

        // For Services workflows, restore stage based on completed stages
        if (flowWorkflowType === 'services') {
          setWorkflowType('services');
          const origin = resolveServicesPwsOrigin(metadata);
          setServicesPwsOrigin(origin);
          if (!selectedFlowName && metadata.name) {
            setFlowName(metadata.name);
          }
          const completedStages: number[] = metadata.completedStages || metadata.completed_stages || [];
          if (origin === 'requirements_generation') {
            const maxAvailableStage = resumeGeneratedServicesStage(metadata);
            const savedServicesStage = localStorage.getItem(`services_stage_${selectedFlowId}`);
            // A redeemed Home Chat receipt names stage 622. Do not discard
            // that server-authorized destination by resuming at the next
            // Services authoring stage from generic progress.
            const servicesChatGeneratePwsStage = resumeGeneratedServicesChatHandoffStage(
              navigation?.servicesPwsGenerationManualEdit === true,
              completedStages,
            );
            let targetStage = servicesChatGeneratePwsStage ?? maxAvailableStage;
            if (servicesChatGeneratePwsStage === null && savedServicesStage !== null) {
              const parsed = parseInt(savedServicesStage, 10);
              if (!isNaN(parsed) && parsed >= 0 && parsed <= maxAvailableStage) {
                targetStage = parsed;
              }
            }
            localStorage.setItem(`services_stage_${selectedFlowId}`, String(targetStage));
            setCurrentStage(targetStage);
            setMaxStageReached(Math.max(maxAvailableStage, targetStage));
            showNotificationMessage(`Loaded generated One-time Services workflow: ${metadata.name}`, 'info');
            return;
          }
          let maxAvailableStage = 0;
          if (completedStages.includes(606)) {
            maxAvailableStage = 6;
          } else if (completedStages.includes(605)) {
            maxAvailableStage = 6;
          } else if (completedStages.includes(604)) {
            maxAvailableStage = 5;
          } else if (completedStages.includes(603)) {
            maxAvailableStage = 4;
          } else if (completedStages.includes(602)) {
            maxAvailableStage = 3;
          } else if (completedStages.includes(601)) {
            maxAvailableStage = 2;
          } else if (completedStages.includes(600)) {
            maxAvailableStage = 1;
          }
          // Check localStorage for a saved stage position
          const savedServicesStage = localStorage.getItem(`services_stage_${selectedFlowId}`);
          let targetStage = maxAvailableStage;
          if (savedServicesStage !== null) {
            const parsed = parseInt(savedServicesStage, 10);
            if (!isNaN(parsed) && parsed >= 0 && parsed <= maxAvailableStage) {
              targetStage = parsed;
            }
          }
          localStorage.setItem(`services_stage_${selectedFlowId}`, String(targetStage));
          setCurrentStage(targetStage);
          setMaxStageReached(Math.max(maxAvailableStage, targetStage));
          showNotificationMessage(`Loaded ${getWorkflowDisplayLabel('services')} workflow: ${metadata.name}`, 'info');
          return;
        }

        // One-time Construction reuses the construction verifier (200–202),
        // then enters the shared 604–606 authoring components. Do not infer
        // readiness from Services 600–603 cache data: stage 202 is the
        // explicit edited-SOW handoff persisted by the construction editor.
        if (flowWorkflowType === 'one_time_construction') {
          setWorkflowType('one_time_construction');
          const isStandaloneEstimator = metadata.standalone_mode === true
            && metadata.workflow_path?.path_id === 'one_time_construction/price_estimator_standalone';
          if (isStandaloneEstimator) {
            // The server owns this one-stage path. Never recover it from a
            // browser stage cache, which could accidentally expose upstream UI.
            setStandaloneMode(true);
            setConstructionPriceEstimatorApplicable(constructionHasStage(metadata, 'price_estimator'));
            setCurrentStage(0);
            setMaxStageReached(0);
            if (!selectedFlowName && metadata.name) setFlowName(metadata.name);
            showNotificationMessage(`Loaded Price realism estimator workflow: ${metadata.name}`, 'info');
            return;
          }
          setStandaloneMode(false);
          const origin = resolveOneTimeConstructionOrigin(metadata);
          setConstructionSowOrigin(origin);
          const hasPriceEstimator = constructionHasStage(metadata, 'price_estimator');
          setConstructionPriceEstimatorApplicable(hasPriceEstimator);
          if (!selectedFlowName && metadata.name) {
            setFlowName(metadata.name);
          }
          const completedStages: number[] = metadata.completedStages || metadata.completed_stages || [];
          let existingSowVerificationReady = false;
          if (
            origin === 'existing_sow'
            && (completedStages.includes(200) || completedStages.includes(201))
          ) {
            // Stage 200 records independent SOW/document/context mutations.
            // Do not treat that marker as Suggestions readiness: Results
            // starts /run on mount. The authoritative GETs must confirm both
            // the named SOW and the saved planned vehicle first.
            try {
              const [documents, vehicleContext] = await Promise.all([
                getSOWDocuments(selectedFlowId),
                getSOWVehicleContext(selectedFlowId),
              ]);
              if (!isCurrentWorkflowOperation()) return;
              existingSowVerificationReady = Boolean(
                documents.sow
                && typeof vehicleContext.planned_vehicle === 'string'
                && vehicleContext.planned_vehicle.trim(),
              );
            } catch {
              if (!isCurrentWorkflowOperation()) return;
              // Fail closed to Document Upload. Its own requests show the
              // actionable recovery state without mounting an unsafe auto-run.
              existingSowVerificationReady = false;
            }
          }
          const maxAvailableStage = resumeOneTimeConstructionProjectedStage(
            metadata,
            origin !== 'existing_sow' || existingSowVerificationReady,
          );
          const savedConstructionStage = localStorage.getItem(`one_time_construction_stage_${selectedFlowId}`);
          // This is a server-redeemed construction chat receipt. After the
          // freshly-owner-authorized metadata proves its generated origin and
          // stage-206 draft, force Manual Edit (UI index 2).
          const constructionChatManualEdit = navigation?.constructionSowGenerationManualEdit === true
            && origin === 'requirements_generation'
            && metadata.workflow_progress?.completed_keys?.includes('manual_edit');
          let targetStage = constructionChatManualEdit
            ? Math.max(0, constructionPathKeys(metadata).indexOf('manual_edit'))
            : maxAvailableStage;
          if (!constructionChatManualEdit && savedConstructionStage !== null) {
            const parsed = parseInt(savedConstructionStage, 10);
            if (!isNaN(parsed) && parsed >= 0 && parsed <= maxAvailableStage) {
              targetStage = parsed;
            }
          }
          localStorage.setItem(`one_time_construction_stage_${selectedFlowId}`, String(targetStage));
          setCurrentStage(targetStage);
          setMaxStageReached(Math.max(maxAvailableStage, targetStage));
          showNotificationMessage(`Loaded One-time Construction workflow: ${metadata.name}`, 'info');
          return;
        }

        if (flowWorkflowType === 'construction_sow_generation') {
          setWorkflowType('construction_sow_generation');
          setConstructionSowOrigin('requirements_generation');
          if (!selectedFlowName && metadata.name) setFlowName(metadata.name);
          const completedStages: number[] = metadata.completedStages || metadata.completed_stages || [];
          const maxAvailableStage = completedStages.includes(206) ? 2
            : completedStages.includes(205) ? 2
            : completedStages.includes(204) ? 1 : 0;
          const saved = Number(localStorage.getItem(`construction_sow_generation_stage_${selectedFlowId}`));
          const targetStage = Number.isInteger(saved) && saved >= 0 && saved <= maxAvailableStage ? saved : maxAvailableStage;
          localStorage.setItem(`construction_sow_generation_stage_${selectedFlowId}`, String(targetStage));
          setCurrentStage(targetStage); setMaxStageReached(Math.max(maxAvailableStage, targetStage));
          showNotificationMessage(`Loaded Construction SOW Generation workflow: ${metadata.name}`, 'info');
          return;
        }

        // Standalone Services PWS generation is deliberately separate from
        // the existing 600–606 review workflow. Its three stage codes are
        // opaque backend identifiers; this index only selects the displayed
        // logical path after the server has authorized the flow metadata.
        if (flowWorkflowType === 'services_pws_generation') {
          setWorkflowType('services_pws_generation');
          if (!selectedFlowName && metadata.name) setFlowName(metadata.name);
          const maxAvailableStage = resumeServicesPwsGenerationStage(metadata);
          // Do not restore a client-selected local stage: this standalone
          // generation path is resumed solely from server-owned progress.
          const targetStage = maxAvailableStage;
          localStorage.setItem(`services_pws_generation_stage_${selectedFlowId}`, String(targetStage));
          setCurrentStage(targetStage); setMaxStageReached(Math.max(maxAvailableStage, targetStage));
          showNotificationMessage(`Loaded Services PWS Generation workflow: ${metadata.name}`, 'info');
          return;
        }

        if (flowWorkflowType === 'complex_services') {
          setWorkflowType('complex_services');
          const origin = resolveServicesPwsOrigin(metadata);
          setServicesPwsOrigin(origin);
          if (!selectedFlowName && metadata.name) {
            setFlowName(metadata.name);
          }
          const mode = (metadata.acquisition_mode ?? null) as AcquisitionMode | null;
          const pop = (metadata.pop_structure ?? null) as PopStructure | null;
          const sat = (metadata.sat_threshold ?? null) as SatThreshold | null;
          setComplexAcqMode(mode);
          setComplexPop(pop);
          setComplexSatThreshold(sat);
          const applicable = applicableComplexStages(mode, sat, pop, false);
          const applicableSet = new Set<ComplexStageKey>(
            origin === 'requirements_generation'
              ? applicable.filter((key) => ![
                'sow_upload', 'context_questions', 'suggestions', 'manual_edit',
              ].includes(key))
              : applicable,
          );
          const completedStages: number[] = metadata.completedStages || metadata.completed_stages || [];
          const generatedPrefixStage = origin === 'requirements_generation'
            ? resumeGeneratedComplexServicesPrefixStage(metadata)
            : null;
          const servicesChatGeneratePwsStage = resumeGeneratedServicesChatHandoffStage(
            navigation?.servicesPwsGenerationManualEdit === true,
            completedStages,
          );
          if (origin === 'requirements_generation' && (servicesChatGeneratePwsStage !== null || generatedPrefixStage !== null)) {
            const prefixStage = servicesChatGeneratePwsStage ?? generatedPrefixStage!;
            localStorage.setItem(`complex_services_stage_${selectedFlowId}`, String(prefixStage));
            setCurrentStage(prefixStage);
            setMaxStageReached(prefixStage);
            showNotificationMessage(`Loaded generated Complex Services workflow: ${metadata.name}`, 'info');
            return;
          }
          // Stage 609 is the backend's branch-independent terminal marker. A
          // completed flow reopens on its Contract Record with its full stepper
          // available for review, rather than showing a terminal-only page.
          if (isComplexServicesFlowComplete(completedStages)) {
            const completion = completeComplexServicesRecord(0);
            localStorage.setItem(
              `complex_services_stage_${selectedFlowId}`,
              String(completion.currentStage),
            );
            setCurrentStage(completion.currentStage);
            setMaxStageReached(completion.maxStageReached);
            setIsComplete(completion.isComplete);
            showNotificationMessage(`Loaded completed Complex Services workflow: ${metadata.name}`, 'info');
            return;
          }
          // Highest reachable enabled stepper index: for each saved+enabled
          // stage, its NEXT enabled stage becomes reachable.
          let maxAvailableStage = origin === 'requirements_generation'
            ? (nextEnabledComplexStage(4, applicableSet) ?? 4)
            : 0;
          for (let i = 0; i < COMPLEX_SERVICES_STAGE_ORDER.length; i += 1) {
            const key = COMPLEX_SERVICES_STAGE_ORDER[i];
            if (applicableSet.has(key) && completedStages.includes(COMPLEX_SERVICES_STAGE_CODE[key])) {
              const nxt = nextEnabledComplexStage(i + 1, applicableSet);
              maxAvailableStage = Math.max(maxAvailableStage, nxt ?? i);
            }
          }
          if (!isComplexStageEnabled(maxAvailableStage, applicableSet)) {
            maxAvailableStage = lastEnabledComplexStageAtOrBelow(maxAvailableStage, applicableSet);
          }
          const savedComplexStage = localStorage.getItem(`complex_services_stage_${selectedFlowId}`);
          let targetStage = maxAvailableStage;
          if (savedComplexStage !== null) {
            const parsed = parseInt(savedComplexStage, 10);
            if (!isNaN(parsed) && parsed >= 0 && parsed <= maxAvailableStage) {
              targetStage = parsed;
            }
          }
          // Never land on a greyed stage.
          if (!isComplexStageEnabled(targetStage, applicableSet)) {
            targetStage = lastEnabledComplexStageAtOrBelow(targetStage, applicableSet);
          }
          localStorage.setItem(`complex_services_stage_${selectedFlowId}`, String(targetStage));
          setCurrentStage(targetStage);
          setMaxStageReached(Math.max(maxAvailableStage, targetStage));
          showNotificationMessage(`Loaded Complex Services workflow: ${metadata.name}`, 'info');
          return;
        }

        // GPC restoration owns mapping and landing, not the shared App shell.
        if (flowWorkflowType === 'gpc') {
          setWorkflowType('gpc');
          const completion = resolveGpcCompletionMetadata(metadata);
          setGpcServerAvailableFormStages(completion.availableStages);
          setGpcRequiredFieldsValidation(completion.requiredFieldsValidation);
          if (!selectedFlowName && metadata.name) setFlowName(metadata.name);
          const restored = await restoreGpcWorkflow({
            flowId: selectedFlowId,
            metadata,
            savedStage: localStorage.getItem(`gpc_stage_${selectedFlowId}`),
            stageHint: navigation?.gpcTargetStageHint,
            isCurrentOperation: isCurrentWorkflowOperation,
            onPatch: (patch) => {
              if (patch.rep889 !== undefined) setGpc889(patch.rep889);
              if (patch.questions !== undefined) setGpcQuestions(patch.questions);
              if (patch.answers !== undefined) setGpcAnswers(patch.answers);
              if (patch.requirementText !== undefined) setGpcRequirementText(patch.requirementText);
              if (patch.mfr !== undefined) setGpcMfr(patch.mfr);
              if (patch.priceSearchComplete !== undefined) setGpcPriceSearchComplete(patch.priceSearchComplete);
              if (patch.searchGeneration !== undefined) setGpcSearchGeneration(patch.searchGeneration);
              if (patch.priceQuotes !== undefined) setGpcPriceQuotes(patch.priceQuotes);
              if (patch.unitPrice !== undefined) setGpcUnitPrice(patch.unitPrice);
              if (patch.threshold !== undefined) setGpcThreshold(patch.threshold);
              if (patch.buyParams !== undefined) setGpcBuyParams(patch.buyParams);
              if (patch.decision !== undefined) setGpcDecision(patch.decision);
              if (patch.naicsPsc !== undefined) setGpcNaicsPsc(patch.naicsPsc);
              if (patch.products !== undefined) setGpcProducts(patch.products);
              if (patch.sourceStatus !== undefined) setGpcSourceStatus(patch.sourceStatus);
              if (patch.carpetIiCheck !== undefined) setGpcCarpetIiCheck(patch.carpetIiCheck);
              if (patch.selectedProductIds !== undefined) setGpcSelectedProductIds(patch.selectedProductIds);
              if (patch.awardProductId !== undefined) setGpcAwardProductId(patch.awardProductId);
              if (patch.awardOverrideReason !== undefined) setGpcAwardOverrideReason(patch.awardOverrideReason);
              if (patch.vendor !== undefined) setGpcVendor(patch.vendor);
              if (patch.awardIsSmall !== undefined) setGpcAwardIsSmall(patch.awardIsSmall);
              if (patch.competitionQuotes !== undefined) setGpcCompetitionQuotes(patch.competitionQuotes);
              if (patch.abstract !== undefined) setGpcAbstract(patch.abstract);
              if (patch.dd2579 !== undefined) setGpcDd2579(patch.dd2579);
              if (patch.afForm9 !== undefined) setGpcAfForm9(patch.afForm9);
              if (patch.approval !== undefined) setGpcApproval(patch.approval);
              if (patch.taxExempt !== undefined) setGpcTaxExempt(patch.taxExempt);
              if (patch.receipt !== undefined) setGpcReceipt(patch.receipt);
              if (patch.iuid !== undefined) setGpcIuid(patch.iuid);
            },
          });
          if (!isCurrentWorkflowOperation() || !restored) return;
          localStorage.setItem(`gpc_stage_${selectedFlowId}`, String(restored.targetStage));
          setCurrentStage(restored.targetStage);
          setMaxStageReached(restored.maxStageReached);
          setGpcNavigationLoading(false);
          showNotificationMessage(`Loaded GPC workflow: ${metadata.name}`, 'info');
          return;
        }

        // Historical chat-authored Stage 1 records stored only questions;
        // metadata retains the requirement used by that chat. Hydrate it
        // before mounting Stage 2, then let a saved Stage-1 draft override it.
        if (flowWorkflowType === 'market_research' && typeof metadata.description === 'string') {
          setInitialCommodityDescription(metadata.description);
        }
        const mrCurrentStage = metadata.currentStage || 1;
        // Restore the conditional stepper from the server-projected metadata
        // immediately. Detailed coordination contains vendor/rationale data
        // and remains off the Stage 1–11 critical path. The final stages start
        // one authoritative check in parallel with their normal hydration.
        let restoredMarketResearchDd2579Required =
          metadata.market_research_dd2579_required === true;
        if (flowWorkflowType === 'market_research' && !isCustomerFlowView) {
          const completedDdStageCodes = Array.isArray(metadata.completedStages)
            ? metadata.completedStages
            : Array.isArray(metadata.completed_stages) ? metadata.completed_stages : [];
          const enteredDd2579 = metadata.market_research_dd2579_visited === true
            || mrCurrentStage === 13 || completedDdStageCodes.includes(13);
          setMarketResearchDd2579Visited(enteredDd2579);
          restoredMarketResearchDd2579Required = restoredMarketResearchDd2579Required || enteredDd2579;
          setMarketResearchDd2579MetadataRequired(restoredMarketResearchDd2579Required);
          // `currentStage` is server-owned durable navigation state. Apply it
          // before the secondary processing-status and cache hydration reads:
          // otherwise resetPipelineState leaves the workflow shell on Generate
          // Questions until those reads finish, which briefly makes a saved
          // Salient Characteristics (or later) workflow look brand new.
          // Do not consult browser-local state here; it may be stale or belong
          // to an older revision of this flow.
          const metadataStageIndex = Math.max(0, marketResearchBackendCodeToUiIndex(
            mrCurrentStage,
            restoredMarketResearchDd2579Required,
            marketResearchBrandNameJaFeatureEnabled,
          ));
          const completedStageCodes = Array.isArray(metadata.completedStages)
            ? metadata.completedStages
            : Array.isArray(metadata.completed_stages) ? metadata.completed_stages : [];
          const metadataMaxStageIndex = completedStageCodes.reduce(
            (maxStageIndex: number, stageCode: unknown) => (
              typeof stageCode === 'number' && Number.isFinite(stageCode)
                ? Math.max(maxStageIndex, marketResearchBackendCodeToUiIndex(
                  stageCode,
                  restoredMarketResearchDd2579Required,
                  marketResearchBrandNameJaFeatureEnabled,
                ))
                : maxStageIndex
            ),
            metadataStageIndex,
          );
          setCurrentStage(metadataStageIndex);
          setMaxStageReached(metadataMaxStageIndex);
          if (mrCurrentStage === 12 || mrCurrentStage === 13) {
            void loadMarketResearchDd2579Coordination(selectedFlowId).catch((error) => {
              // The final-stage local pending/error UI owns recovery. Do not
              // fail the saved workflow shell because a secondary read failed.
              console.warn('Failed to load Market Research DD 2579 coordination:', error);
            });
          }
        }
        // Check if the current stage is processing (cross-tab detection)
        const flagOffRestoreRequiresUpgrade = !marketResearchBrandNameJaFeatureEnabled
          && (
            mrCurrentStage === 14
            || metadata.market_research_brand_name_required === true
          );
        if (flagOffRestoreRequiresUpgrade) {
          // Never reinterpret backend Stage 14 as one of the legacy visual
          // indices. A mixed-version client cannot safely expose or bypass the
          // required gate, regardless of whether the saved J&A is ready.
          setMarketResearchClientUpgradeRequired(true);
          const productIndex = marketResearchBackendCodeToUiIndex(
            3,
            restoredMarketResearchDd2579Required,
            false,
          );
          setCurrentStage(productIndex);
          setMaxStageReached(productIndex);
          if (!selectedFlowName && metadata.name) setFlowName(metadata.name);
          showNotificationMessage(`Loaded flow: ${metadata.name}`, 'info');
          return false;
        }

        // A saved flow response authorizes both the completed-stage list and
        // the stage endpoints for this flow. Start those independent reads
        // before awaiting the current-stage processing probe: previously that
        // probe added its full round-trip ahead of every cache request, and
        // the cache requests then ran one-at-a-time. Apply the resolved data
        // below in stage order so Stage 3 selection still precedes Stage 4's
        // derived filtering. A processing result still wins and prevents any
        // cached payload from being applied.
        const stagesToRestore = new Set<number>(
          Array.isArray(metadata.completedStages)
            ? metadata.completedStages
            : Array.isArray(metadata.completed_stages) ? metadata.completed_stages : [],
        );
        // `completedStages` is a completion ledger, not an exhaustive index of
        // persisted stage state.  In particular, an author may have a saved
        // editable result at the server's current stage (for example Vendor
        // Discovery selections) before taking that stage's explicit Continue
        // action.  Always restore that current record as well, but only after
        // applying the same Customer projection used for normal stage reads.
        // This keeps a Customer from using a stale contracting cursor to probe
        // Section D--DD/DAF 2579 while ensuring an authorized saved page does
        // not look blank after a transition or reload.
        const customerRestorableStageCodes = metadata.market_research_brand_name_required === true
          ? [1, 2, 3, 4, 5, 6, 7, 8, 14]
          : [1, 2, 3, 4, 5, 6, 7, 8];
        if (isCustomerFlowView) {
          // The Customer shell restores only its authoring ceiling. It must
          // never probe CO/CS generic-stage data (codes 9–13).
          Array.from(stagesToRestore).forEach((stageCode) => {
            if (!customerRestorableStageCodes.includes(stageCode)) stagesToRestore.delete(stageCode);
          });
        }
        if (
          flowWorkflowType === 'market_research'
          && Number.isInteger(mrCurrentStage)
          && mrCurrentStage >= 1
          && mrCurrentStage <= 14
          && (!isCustomerFlowView || customerRestorableStageCodes.includes(mrCurrentStage))
        ) {
          stagesToRestore.add(mrCurrentStage);
        }
        // Stage 13 cache-only drafts are intentionally not marked complete.
        // Its form cache is restored only after direct coordination succeeds;
        // before then the dedicated pending shell must not load or expose
        // form/prefill/export data.
        if (flowWorkflowType === 'market_research' && mrCurrentStage === 13) {
          stagesToRestore.delete(13);
        }
        const completedStagesInCanonicalOrder = Array.from(stagesToRestore)
          .sort((a: number, b: number) => a - b);
        const orderedStagesToRestore = flowWorkflowType === 'market_research'
          ? prioritizeMarketResearchRestoreStages(
            completedStagesInCanonicalOrder,
            mrCurrentStage,
          )
          : completedStagesInCanonicalOrder;
        const stageRestoreRequests = new Map(
          orderedStagesToRestore.map((stageNum) => [
            stageNum,
            loadRestorableStageData(
              () => fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${selectedFlowId}/stage/${stageNum}/ui`, { headers, credentials: 'include' }),
              () => fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${selectedFlowId}/stage/${stageNum}`, { headers, credentials: 'include' }),
            ),
          ]),
        );
        // A Customer's stale local/legacy current-stage value must not cause a
        // probe of a contracting-only stage before the Customer route clamps
        // it to the safe server-projected surface below. Stage components
        // perform their own guarded reads for the allowed Customer stages.
        if (!isCustomerFlowView) {
          try {
            const mrStageStatus = await getStageStatus(selectedFlowId, mrCurrentStage);
            if (!isCurrentWorkflowOperation()) return false;
            if (mrStageStatus?.status === 'processing') {
              console.log(`[App] MR stage ${mrCurrentStage} is processing, skipping cached data`);
              const stageIndex = marketResearchBackendCodeToUiIndex(
                mrCurrentStage,
                restoredMarketResearchDd2579Required,
                marketResearchBrandNameJaFeatureEnabled,
              );
              setCurrentStage(Math.max(0, stageIndex));
              setMaxStageReached(Math.max(0, stageIndex));
              if (!selectedFlowName && metadata.name) {
                setFlowName(metadata.name);
              }
              showNotificationMessage(`Loaded flow: ${metadata.name} (processing in progress)`, 'info');
              return true;
            }
          } catch (e) {
            if (!isCurrentWorkflowOperation()) return false;
            console.warn('[App] Failed to check MR stage status:', e);
          }
        }

        // Load stage data if available (Market Research workflow). Requests
        // were issued above alongside the status probe, but application stays
        // ordered to retain cross-stage restoration dependencies.
        if (orderedStagesToRestore.length > 0) {
          let hasHydratedProductsWithCodes = false;
          let restoredProducts: any[] = [];
          let restoredBrandNameDecision: ProductSelectionBrandNameRecord | null = null;
          // Iterate ascending so the Stage-3 (Products) selection is restored
          // into persistedSelectedIds BEFORE the Stage-4 (PSC) branch reads it.
          for (const stageNum of orderedStagesToRestore) {
            (window as any).__LOADED_STAGE_NUMS = (window as any).__LOADED_STAGE_NUMS || [];
            (window as any).__LOADED_STAGE_NUMS.push(stageNum);
            const restoredStage = await stageRestoreRequests.get(stageNum)!;
            if (!isCurrentWorkflowOperation()) return false;

            if (restoredStage.data) {
              // `loadRestorableStageData` has already established that input
              // and output are records or null. The legacy hydration branches
              // below intentionally retain their existing field mappings.
              const stageData: any = restoredStage.data;
              const isUIFormat = restoredStage.isUIFormat;
              // Never log restored payloads: Market Research caches can contain
              // product identities, acquisition narratives, signer names, and
              // signature text. Stage identity/format is sufficient diagnostics.
              console.log(`Restoring stage ${stageNum} (${isUIFormat ? 'UI format' : 'raw format'})`);

              // A file-only Stage 1 flow may have input but no question output
              // yet. Hydrate it before the output-only restoration branch.
              if (stageNum === 1 && stageData.input) {
                const commodityDesc = stageData.input.commodityDescription || stageData.input.commodity_description;
                if (commodityDesc) setInitialCommodityDescription(commodityDesc);
                setInitialRequirementDocument(
                  normalizeRequirementDocument(stageData.input.requirement_document),
                );
              }
              
              // Populate state based on stage output
              if (stageData.output) {
                // Multi-item warning count is mirrored into every stage's
                // UI JSON by the backend transformer. Pick it up from
                // whichever stage loads — Stage 1 is canonical, but
                // downstream stages also carry it so the banner survives
                // mid-flow reloads.
                if (typeof stageData.output.num_items_detected === 'number') {
                  setNumItemsDetected(stageData.output.num_items_detected);
                }
                switch (stageNum) {
                  case 1: // Generate Questions
                    // Load the original commodity description from input
                    if (stageData.input) {
                      const commodityDesc = stageData.input.commodityDescription || stageData.input.commodity_description;
                      if (commodityDesc) {
                        setInitialCommodityDescription(commodityDesc);
                      }
                      // UI transformer returns only filename/type/timestamp/revision.
                      // Deliberately project the known-safe shape rather than retain
                      // arbitrary raw stage input in browser state.
                      setInitialRequirementDocument(
                        normalizeRequirementDocument(stageData.input.requirement_document),
                      );
                    }

                    if (stageData.output.questions) {
                      console.log(`Setting ${stageData.output.questions.length} questions`);
                      setQuestions(stageData.output.questions);
                    }
                    // Stage 1 owns the canonical ID-keyed answer map.
                    const stage1Answers = restoredAnswersForStage(1, stageData);
                    if (Object.keys(stage1Answers).length > 0) {
                      console.log('Setting answers from stage 1:', stage1Answers);
                      setAnswers(stage1Answers);
                    }
                    break;

                  case 2: // Generate MFR
                    if (stageData.output.minimum_requirements) {
                      const mfrData = mapMfrStageOutput(stageData.output, {
                        id: `mfr-${Date.now()}`,
                        commodityDescription: stageData.output.original_spec || '',
                        questionsAnswered: stageData.output.answers || [],
                      });
                      console.log('Setting MFR from backend structured data:', mfrData);
                      setMfr(mfrData);
                    }
                    // Stage 2 raw output may contain only a positional array of
                    // values. Restore from its durable Q&A input instead so a
                    // cache miss never wipes the Stage 1 ID-keyed answer map.
                    const stage2Answers = restoredAnswersForStage(2, stageData);
                    if (Object.keys(stage2Answers).length > 0) {
                      console.log('Derived answers from stage 2:', stage2Answers);
                      (window as any).__DERIVED_STAGE2_ANSWERS = stage2Answers;
                      setAnswers(stage2Answers);
                    }
                    break;
                  
                  case 3: { // Products
                    // A completed Stage 3 is durable historical output, not a
                    // cache-reuse decision. Prefer its canonical full products
                    // array; support the older selected-only shape only when no
                    // canonical list exists. Explicitly stale output must never
                    // become current display state after an upstream change.
                    const rawProducts = stageData.output.stale === true
                      ? []
                      : Array.isArray(stageData.output.products)
                        ? stageData.output.products
                        : Array.isArray(stageData.output.selected_products)
                          ? stageData.output.selected_products
                          : [];
                    if (stageData.output.stale !== true
                      && stageData.output.brand_name
                      && typeof stageData.output.brand_name === 'object') {
                      const decision = stageData.output.brand_name as ProductSelectionBrandNameRecord;
                      restoredBrandNameDecision = decision;
                      setMarketResearchBrandNameDecision(decision);
                      setMarketResearchBrandNameStatus(
                        decision.requested
                          ? (metadata.market_research_brand_name_ready === true ? 'ready' : 'draft')
                          : 'inactive',
                      );
                    }
                    if (rawProducts.length > 0) {
                      console.log(`Setting ${rawProducts.length} products`);
                      // Ensure products have a stable deterministic ID
                      const makeId = (p: any, idx: number) => {
                        const slug = (s: string) => (s || '').toString().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
                        const base = `${slug(p.product_name || p.name || 'product')}-${slug(p.manufacturer || '')}-${slug(p.model || '')}`;
                        const piid = (p.specifications?.piid || p.specifications?.award_id) ? `-${slug(p.specifications?.piid || p.specifications?.award_id)}` : '';
                        return `${base}${piid || '-' + idx}`;
                      };
                      const allProductsWithIds = rawProducts.map((p: any, idx: number) => ({
                        ...p,
                        id: p.id || makeId(p, idx),
                      }));
                      setProductDiscoveryResults(allProductsWithIds);
                      // Persist selection (ids/keys) for Stage 3 UI
                      const selectedIds: string[] = (stageData.output.selected_product_ids || []).map(String);
                      const selectedKeys: string[] = (stageData.output.selected_product_keys || []).map(String);
                      setStage3SelectedIds(selectedIds);
                      setStage3SelectedKeys(selectedKeys);
                      // Expose the persisted Stage-3 selection to the Stage-4 (PSC)
                      // restoration branch below so it can filter the PSC rows the
                      // same way (previously this stayed [] and the filter never ran).
                      persistedSelectedIds = selectedIds;
                      // Compute selected subset for downstream stages (do not filter the full list for Stage 3 UI)
                      const s = (x: any) => (typeof x === 'string' ? x.trim().toLowerCase() : String(x || '').trim().toLowerCase());
                      const keyOf = (p: any) => {
                        const piid = s(p.specifications?.piid || p.specifications?.award_id || '');
                        return `${s(p.product_name || p.name)}|${s(p.manufacturer)}|${s(p.model)}|${piid}`;
                      };
                      // Resolve the selected subset, preferring IDs but falling back
                      // to stable keys when the ID scheme stored at selection time no
                      // longer matches the regenerated IDs. Without the key fallback,
                      // an ID mismatch left selectedSubset empty -> setProducts([]) ->
                      // deep-linking straight into PSC showed "0 products selected"
                      // until the user visited Product Discovery (which already does
                      // this key-based reconciliation) and came back.
                      let selectedSubset = allProductsWithIds;
                      if (selectedIds.length > 0) {
                        const idSet = new Set(selectedIds);
                        const byId = allProductsWithIds.filter((p: any) => idSet.has(String(p.id)));
                        if (byId.length > 0) {
                          selectedSubset = byId;
                        } else if (selectedKeys.length > 0) {
                          const keySet = new Set(selectedKeys);
                          const byKey = allProductsWithIds.filter((p: any) => keySet.has(keyOf(p)));
                          if (byKey.length > 0) {
                            selectedSubset = byKey;
                            console.log(`Stage 3 selection IDs did not match; recovered ${byKey.length} products via stable keys`);
                          }
                        }
                      } else if (selectedKeys.length > 0) {
                        const keySet = new Set(selectedKeys);
                        const byKey = allProductsWithIds.filter((p: any) => keySet.has(keyOf(p)));
                        if (byKey.length > 0) selectedSubset = byKey;
                      }
                      setProducts(selectedSubset);
                      restoredProducts = selectedSubset;
                    }
                    break;
                  }

                  case 4: // PSC Codes
                    if (stageData.output.productsWithPSC || stageData.output.products_with_psc || stageData.output.products) {
                      let pscData = stageData.output.productsWithPSC || stageData.output.products_with_psc || stageData.output.products;
                      console.log(`Setting ${pscData.length} products with PSC`);
                      // Ensure all products have stable IDs
                      const makeId = (p: any, idx: number) => {
                        const slug = (s: string) => (s || '').toString().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
                        const base = `${slug(p.product_name || p.name || 'product')}-${slug(p.manufacturer || '')}-${slug(p.model || '')}`;
                        const piid = (p.specifications?.piid || p.specifications?.award_id) ? `-${slug(p.specifications?.piid || p.specifications?.award_id)}` : '';
                        return `${base}${piid || '-' + idx}`;
                      };
                      let pscDataWithIds = pscData.map((p: any, idx: number) => ({
                        ...p,
                        id: p.id || makeId(p, idx),
                      }));
                      // If a persisted selection exists, filter Stage 4 output to match
                      if (Array.isArray(persistedSelectedIds) && persistedSelectedIds.length > 0) {
                        const selSet = new Set(persistedSelectedIds.map(String));
                        const filtered = pscDataWithIds.filter((p: any) => selSet.has(String(p.id)));
                        if (filtered.length > 0) {
                          pscDataWithIds = filtered;
                          console.log(`Filtered PSC data to ${pscDataWithIds.length} selected products`);
                        } else {
                          console.log('Selection IDs did not match PSC rows; showing unfiltered PSC to allow UI hydration');
                        }
                      } else if (Array.isArray(stageData.output.selected_product_keys) && stageData.output.selected_product_keys.length > 0) {
                        const keyOf = (p: any) => {
                          const s = (x: any) => (typeof x === 'string' ? x.trim().toLowerCase() : String(x || '').trim().toLowerCase());
                          const piid = s(p.specifications?.piid || p.specifications?.award_id || '');
                          return `${s(p.product_name || p.name)}|${s(p.manufacturer)}|${s(p.model)}|${piid}`;
                        };
                        const selKeys = new Set(stageData.output.selected_product_keys.map(String));
                        const filtered = pscDataWithIds.filter((p: any) => selKeys.has(keyOf(p)));
                        if (filtered.length > 0) {
                          pscDataWithIds = filtered;
                          console.log(`Filtered PSC data by keys to ${pscDataWithIds.length} selected products`);
                        } else {
                          console.log('Selection keys did not match PSC rows; showing unfiltered PSC to allow UI hydration');
                        }
                      }
                      setProductsWithPSC(pscDataWithIds);
                      setPscMethodUsed(
                        stageData.output.method_used === 'ai' || stageData.output.method_used === 'manual'
                          ? stageData.output.method_used
                          : 'unknown',
                      );
                    }
                    break;
                  
                  case 5: // NAICS Codes
                    if (stageData.output.productsWithNAICS || stageData.output.products_with_naics || stageData.output.products) {
                      const naicsData = stageData.output.productsWithNAICS || stageData.output.products_with_naics || stageData.output.products;
                      console.log(`[App.tsx] PATH 0: Setting ${naicsData.length} products with NAICS from flow restore`);
                      // Ensure all products have IDs
                      const naicsDataWithIds = naicsData.map((p: any, idx: number) => {
                        if (!p.id) {
                          return {
                            ...p,
                            id: `${p.product_name || 'product'}-${p.manufacturer || ''}-${idx}-${Date.now()}`
                          };
                        }
                        return p;
                      });
                      // NOTE: This sets productsWithCodes state in App.tsx, which is passed as initialProducts
                      // to NAICSCodeMapping. The component should then call handleNAICSMapped to normalize.
                      console.log(`[App.tsx] PATH 0: Setting productsWithCodes state (will be passed as initialProducts)`);
                      setProductsWithCodes(naicsDataWithIds);
                      setNaicsMethodUsed(
                        stageData.output.method_used === 'ai' || stageData.output.method_used === 'manual'
                          ? stageData.output.method_used
                          : 'unknown',
                      );
                      hasHydratedProductsWithCodes = true;
                    }
                    break;
                  
                  case 6: { // Vendors
                    // A Stage-2 source change preserves historical artifacts
                    // for audit but marks them stale. Do not repopulate the
                    // Vendor Discovery UI from that historical output; the
                    // Stage-5 NAICS mutation path removes Stage 6 entirely.
                    if (stageData.output.stale === true) {
                      console.warn('Skipping explicitly stale Stage 6 vendor data');
                      break;
                    }
                    const vendorData =
                      stageData.output.productsWithVendors ||
                      stageData.output.products_with_vendors ||
                      stageData.output.products;

                    if (Array.isArray(vendorData)) {
                      const normalizedVendors = normalizeProductsWithVendors(vendorData);
                      console.log(`Vendors array length: ${normalizedVendors.length}`);
                      setVendors(normalizedVendors);
                      if (!hasHydratedProductsWithCodes && normalizedVendors.length > 0) {
                        setProductsWithCodes(normalizedVendors);
                        hasHydratedProductsWithCodes = true;
                      }
                    } else {
                      console.warn('Stage 6 output has no vendor data');
                    }
                    break;
                  }
                  
                  case 7: // Section B
                    // Stage 7 doesn't need state, it's the final output
                    console.log('Stage 7 (Section B) output exists but doesn\'t need state');
                    break;

                  case 13: { // Conditional final Market Research DD 2579
                    const restoredDd2579 = restoreMarketResearchDd2579StateFromStageOutput(stageData.output);
                    if (restoredDd2579) {
                      setMarketResearchDd2579(restoredDd2579.form);
                      setMarketResearchDd2579RestoredState(restoredDd2579);
                      if (restoredDd2579.hasSavedOrCachedDraft) {
                        setMarketResearchDd2579Visited(true);
                        restoredMarketResearchDd2579Required = true;
                      }
                    }
                    break;
                  }
                }
              } else {
                console.warn(`Stage ${stageNum} has no output data`);
              }
            } else {
              console.warn(`[App] Skipping stage ${stageNum}: no restorable UI cache or raw stage data`);
            }
          }
          
          const storedBackendStage = metadata.currentStage || 1;
          const restoredDecisionRequiresUpgrade = !marketResearchBrandNameJaFeatureEnabled
            && restoredBrandNameDecision?.requested === true;
          if (restoredDecisionRequiresUpgrade) {
            setMarketResearchClientUpgradeRequired(true);
          }
          const backendStage = storedBackendStage === 14 && (
            restoredBrandNameDecision?.requested !== true
          )
            ? (restoredBrandNameDecision?.post_ja_destination_stage ?? 3)
            : restoredDecisionRequiresUpgrade ? 3 : storedBackendStage;
          const stageIndex = isCustomerFlowView
            ? Math.max(0, getCustomerMarketResearchStages(
              restoredBrandNameDecision?.requested === true
                || metadata.market_research_brand_name_required === true,
            ).findIndex((stage) => stage.backendCode === backendStage))
            : marketResearchBackendCodeToUiIndex(
              backendStage,
              restoredMarketResearchDd2579Required,
              marketResearchBrandNameJaFeatureEnabled,
            );

          if (!isCustomerFlowView && !hasHydratedProductsWithCodes && stageIndex >= 5 && restoredProducts.length > 0) {
            const skipPscRaw = localStorage.getItem(`skip-psc-${selectedFlowId}`);
            const skipNaicsRaw = localStorage.getItem(`skip-naics-${selectedFlowId}`);
            const manualPsc = skipPscRaw && skipPscRaw !== 'true' ? (() => { try { return JSON.parse(skipPscRaw); } catch { return null; } })() : null;
            const manualNaics = skipNaicsRaw && skipNaicsRaw !== 'true' ? (() => { try { return JSON.parse(skipNaicsRaw); } catch { return null; } })() : null;
            const derived = restoredProducts.map((p) => ({
              ...p,
              pscCode: p.pscCode ?? manualPsc?.[p.id] ?? null,
              naicsCode: p.naicsCode ?? manualNaics?.[p.id] ?? null,
            }));
            setProductsWithCodes(derived);
          }

          // Check for standalone mode flag. Prefer the value persisted on the
          // backend (metadata.standalone_mode) so duplicated flows — which get
          // a fresh flow_id and therefore have no localStorage entry — still
          // route correctly. Fall back to the legacy localStorage flag for
          // older flows that predate the metadata field.
          const isStandalone = !isCustomerFlowView && (
            metadata.standalone_mode === true ||
            localStorage.getItem(`standalone-mode-${selectedFlowId}`) === 'true'
          );
          if (isStandalone) {
            localStorage.setItem(`standalone-mode-${selectedFlowId}`, 'true');
          }
          setStandaloneMode(isStandalone);

          // If standalone, ensure we're at least at stage 11
          if (isStandalone && stageIndex < 11) {
            setCurrentStage(11);
            setMaxStageReached(11);
          } else {
            setCurrentStage(Math.max(0, stageIndex));
            const completedIndices = (metadata.completedStages ?? [])
              .filter((stageCode: number) => !isCustomerFlowView || [1, 2, 3, 4, 5, 6, 7, 8, 14].includes(stageCode))
              .map((stageCode: number) => isCustomerFlowView
                ? getCustomerMarketResearchStages(
                  restoredBrandNameDecision?.requested === true
                    || metadata.market_research_brand_name_required === true,
                ).findIndex((stage) => stage.backendCode === stageCode)
                : marketResearchBackendCodeToUiIndex(
                  stageCode,
                  restoredMarketResearchDd2579Required,
                  marketResearchBrandNameJaFeatureEnabled,
                ))
              .filter((stageIndex: number) => stageIndex >= 0);
            const maxCompleted = completedIndices.length ? Math.max(...completedIndices) : 0;
            setMaxStageReached(Math.max(maxCompleted, stageIndex));
          }

          if (!selectedFlowName && metadata.name) {
            setFlowName(metadata.name);
          }
          showNotificationMessage(`Loaded flow: ${metadata.name}`, 'info');
        } else if (flowWorkflowType === 'market_research') {
          // A Stage-13 draft may be current without being complete, so it can
          // legitimately have no completed-stage marker. Restore navigation
          // from the server's durable backend code even when there is no
          // output list to hydrate. The visual order includes the conditional
          // Brand-name J&A (backend Stage 14) after Product Discovery, so
          // numeric backend-code-minus-one would incorrectly reopen a later
          // stage such as DD/DAF 2579 as Vendor Bids.
          const backendStage = metadata.currentStage || 1;
          const stageIndex = isCustomerFlowView
            ? Math.max(0, getCustomerMarketResearchStages(
              metadata.market_research_brand_name_required === true,
            ).findIndex((stage) => stage.backendCode === backendStage))
            : marketResearchBackendCodeToUiIndex(
              backendStage,
              restoredMarketResearchDd2579Required,
              marketResearchBrandNameJaFeatureEnabled,
            );
          setCurrentStage(stageIndex);
          setMaxStageReached(stageIndex);
          setStandaloneMode(Boolean(metadata.standalone_mode));
          if (!selectedFlowName && metadata.name) {
            setFlowName(metadata.name);
          }
          showNotificationMessage(`Loaded flow: ${metadata.name}`, 'info');
        }
        return true;
    } catch (err) {
      if (!isCurrentWorkflowOperation()) return false;
      console.error('Error loading flow:', err);
      failFlowLoad(toUserFacingError(err));
      return false;
    } finally {
      if (isCurrentWorkflowOperation()) {
        setWorkflowChatNavigationPending(false);
      }
    }
  };

  // Target resolution is non-mutating. This adapter must confirm that the
  // authorized Market Research flow loaded before the panel terminalizes the
  // notification; it never writes the shared workflow cursor itself.
  const handleOpenWorkflowNotificationStage = async (
    target: {
      flowId: string;
      stageCode: number | 'ra_collaboration';
      /** Server-issued only for stale target fallback navigation. */
      navigationMode?: 'current_workflow';
      /** Generic shares must open their actual workflow, not an MR handoff. */
      kind?: WorkflowNotificationKind;
    },
  ) => {
    const notificationAuthGeneration = authGenerationRef.current;
    if (target.kind === 'workflow_shared' || target.navigationMode === 'current_workflow') {
      // A generic share may be for any workflow type and uses that workflow's
      // own durable resume logic. Its target stage is an audit cursor, not a
      // Market Research handoff instruction, so do not force an MR type or
      // reinterpret opaque non-MR stage codes as MR presentation indexes.
      const loaded = await handleSelectFlow(target.flowId);
      if (loaded === false) {
        throw new Error('Workflow notification target failed to load');
      }
      if (authGenerationRef.current !== notificationAuthGeneration) {
        throw new Error('Workflow notification navigation was superseded');
      }
      return;
    }
    if (target.stageCode === 'ra_collaboration') {
      const loaded = await handleSelectFlow(
        target.flowId,
        undefined,
        { requiredWorkflowType: 'market_research', deferPageTransitionUntilReady: true },
      );
      if (loaded !== true || authGenerationRef.current !== notificationAuthGeneration) {
        throw new Error('Workflow notification target failed to load');
      }
      // The receipt is checked again by the destination's minimal endpoint;
      // this notification target is navigation-only and cannot grant access.
      setAppPage('workflow');
      setMarketResearchRaCollaborationOpen(true);
      return;
    }
    if (target.stageCode !== 1 && target.stageCode !== 14) {
      showNotificationMessage('This request is no longer active.', 'info');
      throw new Error('Unsupported workflow notification stage');
    }
    const targetStageCode = target.stageCode as 1 | 14;
    if (targetStageCode === 14 && !marketResearchBrandNameJaFeatureEnabled) {
      showNotificationMessage('This request is no longer active.', 'info');
      throw new Error('Inactive workflow notification stage');
    }
    const loaded = await handleSelectFlow(
      target.flowId,
      undefined,
      {
        requiredWorkflowType: 'market_research',
        deferPageTransitionUntilReady: true,
      },
    );
    if (loaded !== true) {
      throw new Error('Workflow notification target failed to load');
    }
    const notificationWorkflowOperationGeneration =
      workflowOperationGenerationRef.current;
    const assertCurrentNotificationAttempt = () => {
      if (
        authGenerationRef.current !== notificationAuthGeneration
        || workflowOperationGenerationRef.current
          !== notificationWorkflowOperationGeneration
      ) {
        throw new Error('Workflow notification navigation was superseded');
      }
    };
    assertCurrentNotificationAttempt();
    if (targetStageCode === 14) {
      const projection = await getMarketResearchBrandNameJa(target.flowId);
      assertCurrentNotificationAttempt();
      if (!isUsableBrandNameJaProjection(projection, target.flowId)) {
        throw new Error('Workflow notification J&A projection was invalid');
      }
      setNotificationBrandNameJaProjection({ flowId: target.flowId, projection });
    } else {
      const stageData = await getRawStageData(target.flowId, 1);
      assertCurrentNotificationAttempt();
      if (!isUsableQuestionsStageData(stageData)) {
        throw new Error('Workflow notification target stage failed to load');
      }
      setNotificationBrandNameJaProjection(null);
    }
    // Stage codes are durable server concepts; their presentation index is
    // conditional (Customer projection, Brand-name J&A, DD2579). Never rely
    // on an incidental array position when opening a notification.
    const notificationStageIndex = marketResearchStageIndexForBackend(targetStageCode);
    const priorReadiness = notificationStageReadinessRef.current;
    if (priorReadiness) {
      window.clearTimeout(priorReadiness.timeout);
      notificationStageReadinessRef.current = null;
      priorReadiness.reject(new Error('Workflow notification navigation was superseded'));
    }
    const readinessId = ++notificationStageReadinessIdRef.current;
    const stageMounted = new Promise<void>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        if (notificationStageReadinessRef.current?.id !== readinessId) return;
        notificationStageReadinessRef.current = null;
        reject(new Error('Workflow notification target stage did not become ready'));
      }, 5000);
      notificationStageReadinessRef.current = {
        id: readinessId,
        flowId: target.flowId,
        stageCode: targetStageCode,
        stageIndex: notificationStageIndex,
        authGeneration: notificationAuthGeneration,
        workflowOperationGeneration: notificationWorkflowOperationGeneration,
        resolve,
        reject,
        timeout,
      };
    });
    setNotificationStageReadinessEpoch(readinessId);
    setAppPage('workflow');
    setCurrentStage(notificationStageIndex);
    setMaxStageReached((previous) => Math.max(previous, notificationStageIndex));
    await stageMounted;
    // The readiness effect resolves asynchronously. Recheck the captured
    // operation immediately before returning control to the panel, which is
    // the boundary after which it may redeem the notification.
    assertCurrentNotificationAttempt();
  };

  // Home Chat never chooses a UI index. Its optional key is emitted only from
  // the controller's workflow metadata, then `handleSelectFlow` cross-checks
  // it against the freshly fetched owner-authorized flow before landing there.
  const handleOpenGpcWorkflowFromChat = (
    selectedFlowId: string,
    targetStageHint?: GpcFormStageKey,
    context?: HomeChatHandoffContext,
  ) => handleSelectFlow(
    selectedFlowId,
    undefined,
    {
      gpcTargetStageHint: targetStageHint,
      homeChatSessionId: context?.homeChatSessionId ?? null,
    },
  );

  const handleOpenMrWorkflowFromChat = (
    selectedFlowId: string,
    context?: HomeChatHandoffContext,
  ) => handleSelectFlow(
    selectedFlowId,
    undefined,
    {
      preserveMrChatResults: true,
      homeChatSessionId: context?.homeChatSessionId ?? null,
    },
  );

  const handleOpenGeneratedConstructionSowFromChat = (
    selectedFlowId: string,
    context?: HomeChatHandoffContext,
  ) => handleSelectFlow(
    selectedFlowId,
    undefined,
    {
      constructionSowGenerationManualEdit: true,
      homeChatSessionId: context?.homeChatSessionId ?? null,
    },
  );

  const handleOpenGeneratedServicesPwsFromChat = (
    selectedFlowId: string,
    context?: HomeChatHandoffContext,
  ) => handleSelectFlow(
    selectedFlowId,
    undefined,
    {
      servicesPwsGenerationManualEdit: true,
      homeChatSessionId: context?.homeChatSessionId ?? null,
    },
  );

  const handleOpenSimilarRequirementsNavigation = async (
    navigationHandle: string,
    context?: HomeChatHandoffContext,
  ) => {
    // The Home Chat card/transcript never sees a flow ID. This closed App
    // adapter consumes the one-time server token, then immediately hands the
    // freshly reauthorized ID to the existing generic SPA selection path.
    // Let the open chat surface handle a rejected token so it remains visible.
    await navigateFromHomeChat(
      navigationHandle,
      async (selectedFlowId) => {
        await handleSelectFlow(
          selectedFlowId,
          undefined,
          { homeChatSessionId: context?.homeChatSessionId ?? null },
        );
      },
    );
  };

  const handleOpenCreatedWorkflowFromChat = async (
    selectedFlowId: string,
    context?: HomeChatHandoffContext,
  ) => {
    const opened = await handleSelectFlow(selectedFlowId, undefined, {
      homeChatSessionId: context?.homeChatSessionId ?? null,
    });
    if (opened === false) throw new Error('This workflow is no longer available.');
  };

  const handleCreateNewFlow = async (
    newFlowId: string,
    newFlowName?: string,
    _description?: string,
    standaloneMode?: boolean,
    workflowTypeOverride?: WorkflowType,
    createAuthGeneration?: number,
  ) => {
    if (presentationCapabilities.readOnly) {
      showNotificationMessage('Creating workflows is unavailable in this view.', 'info');
      return;
    }
    const authGeneration = createAuthGeneration ?? authGenerationRef.current;
    if (
      createAuthGeneration !== undefined
      && createAuthGeneration !== authGenerationRef.current
    ) {
      return;
    }
    const operationGeneration = ++workflowOperationGenerationRef.current;
    const isCurrentWorkflowOperation = () => (
      authGenerationRef.current === authGeneration
      && workflowOperationGenerationRef.current === operationGeneration
    );
    resetPipelineState();
    activeFlowRouteRef.current = null;
    setCheckedFlowRefreshKey(null);
    setFlowRevalidation(null);
    activeFlowRoleRef.current = rolePreview;
    setWorkflowChatNavigationPending(false);
    const inheritedHomeChatSessionId = pendingWorkflowChatSessionId;
    setWorkflowChatSessionId(inheritedHomeChatSessionId);
    setWorkflowChatVisible(Boolean(inheritedHomeChatSessionId));
    if (inheritedHomeChatSessionId) {
      rememberWorkflowChatSession(newFlowId, inheritedHomeChatSessionId);
    }
    setPendingWorkflowChatSessionId(null);
    setPendingWorkflowHandoffReceipt(null);
    setFlowId(newFlowId);
    setPostAwardResumeFlowId(null);
    setFlowName(newFlowName || '');
    setClsCorrectionResult(null);
    setAppPage('workflow');

    // These creation paths return before the ordinary metadata read below.
    // Capture their server-owned route now so the first focus refresh has a
    // real baseline instead of treating any changed design as the initial one.
    let earlyMetadata: ActiveFlowRouteMetadata | null = null;
    if (workflowTypeOverride === 'dcma_pbp' || workflowTypeOverride === 'dcma_crr' || standaloneMode) {
      try {
        const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${encodeURIComponent(newFlowId)}`, {
          credentials: 'include',
        });
        if (!isCurrentWorkflowOperation()) return;
        if (response.status === 401) {
          window.dispatchEvent(new Event('auth-expired'));
          return;
        }
        if (!response.ok) throw new Error('The new workflow could not be opened');
        const detail = await response.json();
        if (!isCurrentWorkflowOperation()) return;
        earlyMetadata = detail?.metadata as ActiveFlowRouteMetadata | null;
        if (!earlyMetadata || typeof earlyMetadata !== 'object'
          || earlyMetadata.workflow_type !== (workflowTypeOverride ?? 'market_research')) {
          throw new Error('The new workflow route was invalid');
        }
        activeFlowRouteRef.current = {
          flowId: newFlowId,
          signature: activeFlowRouteSignature(earlyMetadata),
        };
      } catch {
        if (isCurrentWorkflowOperation()) {
          failFlowLoad('The new workflow could not be opened. Reopen it from Manage Workflows.');
        }
        return;
      }
    }

    if (workflowTypeOverride === 'dcma_pbp' || workflowTypeOverride === 'dcma_crr') {
      setStandaloneMode(false);
      setWorkflowType(workflowTypeOverride);
      setCurrentStage(0);
      showNotificationMessage(workflowTypeOverride === 'dcma_crr' ? 'New contract receipt review created.' : 'New DCMA PBP review created.', 'success');
      return;
    }

    if (standaloneMode && workflowTypeOverride === 'one_time_construction') {
      // The standalone Price Estimator is a server-owned one-stage path. The
      // successful create response has already passed the server's access check.
      setStandaloneMode(true);
      setWorkflowType('one_time_construction');
      setConstructionPriceEstimatorApplicable(constructionHasStage(earlyMetadata, 'price_estimator'));
      setCurrentStage(0);
      setMaxStageReached(0);
      showNotificationMessage('New Price realism estimator created.', 'success');
      return;
    }

    if (standaloneMode) {
      setStandaloneMode(true);
      localStorage.setItem(`standalone-mode-${newFlowId}`, 'true');
      const wfType = (workflowTypeOverride || 'market_research') as WorkflowType;
      setWorkflowType(wfType);
      if (wfType === 'market_research') {
        setMarketResearchFlowView(
          earlyMetadata?.market_research_view === 'customer'
            || earlyMetadata?.market_research_view === 'contracting'
            ? earlyMetadata.market_research_view
            : 'unknown',
        );
      }
      if (wfType === 'fopr') {
        setCurrentStage(2);                     // foprStages[2] = construction-abstract
        if (earlyMetadata && !isWorkflowReadOnly(earlyMetadata) && isCurrentWorkflowOperation()) updateCurrentStage(newFlowId, 502);     // backend stage number
        showNotificationMessage('Standalone mode: Starting with Abstract of Proposals.', 'info');
      } else {
        setCurrentStage(11);  // Vendor Bids (React index)
        if (earlyMetadata && !isWorkflowReadOnly(earlyMetadata) && isCurrentWorkflowOperation()) updateCurrentStage(newFlowId, 12);  // Backend stage number
        showNotificationMessage('Standalone mode: Starting with Vendor Bids.', 'info');
      }
      return;
    }                                                                                                                       

    setStandaloneMode(false);                                                                                               

    try {                                                                                                                   
      const headers: Record<string, string> = {};
      const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/flows/${newFlowId}`, { headers, credentials: 'include' });
      if (!isCurrentWorkflowOperation()) return;
      if (response.status === 401) {
        window.dispatchEvent(new Event('auth-expired'));
        return;
      }
      if (response.ok) {                                                                                                    
        const data = await response.json();                                                                                 
        if (!isCurrentWorkflowOperation()) return;
        const flowWorkflowType = workflowTypeOverride
          || data.metadata?.workflow_type
          || 'market_research';
        activeFlowRouteRef.current = {
          flowId: newFlowId,
          signature: activeFlowRouteSignature(data.metadata ?? {}),
        };
        setWorkflowType(flowWorkflowType);                                                                                  
        if (flowWorkflowType === 'market_research') {
          setMarketResearchFlowView(
            data.metadata?.market_research_view === 'customer'
              || data.metadata?.market_research_view === 'contracting'
              ? data.metadata.market_research_view
              : 'unknown',
          );
        }
        if (flowWorkflowType === 'post_award_actions') {
          // A new selector flow has no workspace to resume yet. Reset any
          // prior workflow's stage so the first interaction is always intake.
          setCurrentStage(0);
          setMaxStageReached(0);
          localStorage.setItem(`post_award_stage_${newFlowId}`, '0');
        }
        if (flowWorkflowType === 'one_time_construction') {
          // The create dialog has already persisted this server-owned choice.
          // Restore it before the workflow shell renders, rather than leaving
          // the prior/default existing-SOW branch active for a generated flow.
          setConstructionSowOrigin(resolveOneTimeConstructionOrigin(data.metadata));
          setConstructionPriceEstimatorApplicable(
            constructionHasStage(data.metadata, 'price_estimator'),
          );
        }
        if (flowWorkflowType === 'services' || flowWorkflowType === 'complex_services') {
          setServicesPwsOrigin(resolveServicesPwsOrigin(data.metadata));
        }
        if (flowWorkflowType === 'complex_services') {
          setComplexAcqMode((data.metadata?.acquisition_mode ?? null) as AcquisitionMode | null);
          setComplexPop((data.metadata?.pop_structure ?? null) as PopStructure | null);
          setComplexSatThreshold((data.metadata?.sat_threshold ?? null) as SatThreshold | null);
        }

        if (flowWorkflowType === 'clause_logic') {                                                                          
          showNotificationMessage('New CLS workflow created. Upload your documents to begin.', 'success');                  
        } else {                                                                                                            
          showNotificationMessage('New workflow created. Starting with requirements.', 'success');                          
        }                                                                                                                   
      } else {                                                                                                              
        setWorkflowType(workflowTypeOverride || 'market_research');
        showNotificationMessage('New workflow created. Starting with requirements.', 'success');                            
      }                                                                                                                     
    } catch (err) {                                                                                                         
      if (!isCurrentWorkflowOperation()) return;
      console.error('Error fetching flow details:', err);                                                                   
      setWorkflowType(workflowTypeOverride || 'market_research');
      showNotificationMessage('New workflow created. Starting with requirements.', 'success');                              
    }                                                                                                                       
  };

  // Create default workflow on mount if not showing selector
  const createDefaultWorkflow = async () => {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/flows`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          name: `Workflow ${new Date().toISOString().split('T')[0]}`,
          description: 'Auto-created workflow',
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setFlowId(data.flow_id);
        setFlowName(`Workflow ${new Date().toISOString().split('T')[0]}`);
        console.log('Created default workflow:', data.flow_id);
      }
    } catch (err) {
      console.error('Error creating default workflow:', err);
    }
  };

  // Removed JSON export of full package for non-technical audiences

  // Load saved session on mount (unless auto-clear is enabled)
  useEffect(() => {
    // Check if we should skip the flow selector
    const skipFlowSelector = process.env.REACT_APP_SKIP_FLOW_SELECTOR === 'true';

    if (skipFlowSelector) {
      // Create default workflow and skip selector
      setAppPage('workflow');
      createDefaultWorkflow();
    }

    if (process.env.REACT_APP_AUTO_CLEAR_SESSION === 'true') {
      localStorage.removeItem('farchat-session');
      console.log('Session auto-cleared based on environment setting');
    } else {
      // Inline loadSession logic
      const saved = localStorage.getItem('farchat-session');
      if (saved) {
        try {
          const session: PipelineSession = JSON.parse(saved);
          if (session.stages.questions?.questions) setQuestions(session.stages.questions.questions);
          if (session.stages.questions?.answers) {
            // Convert QA[] to Record<string, string>
            const answersObj: Record<string, string> = {};
            session.stages.questions.answers.forEach((qa: any) => {
              if (qa.questionId) {
                answersObj[qa.questionId] = qa.answer || '';
              }
            });
            setAnswers(answersObj);
          }
          if (session.stages.questions?.useCachedData !== undefined) {
            setQuestionsUseCachedData(session.stages.questions.useCachedData);
          } else {
            setQuestionsUseCachedData(true);
          }
          if (session.stages.mfr?.mfr) setMfr(session.stages.mfr.mfr);
          if (session.stages.products?.products) setProducts(session.stages.products.products);
          if (session.stages.psc?.products) setProductsWithPSC(session.stages.psc.products);
          if (session.stages.naics?.products) setProductsWithCodes(session.stages.naics.products);
          if (session.stages.vendors?.productsWithVendors) {
            setVendors(normalizeProductsWithVendors(session.stages.vendors.productsWithVendors));
          }
          if (session.stages.sectionB?.document) setSectionBDocument(session.stages.sectionB.document);
          if (session.stages.sectionC?.document) setSectionCDocument(session.stages.sectionC.document);
          if (session.stages.sectionD?.document) setSectionDDocument(session.stages.sectionD.document);
          if (session.stages.sectionE?.document) setSectionEDocument(session.stages.sectionE.document);
          if (session.stages.mfr?.useCachedData !== undefined) {
            setMfrUseCachedData(session.stages.mfr.useCachedData);
          } else {
            setMfrUseCachedData(true);
          }
          // Ensure currentStage is within valid bounds
          setCurrentStage(Math.min(session.currentStage, stages.length - 1));
          setMaxStageReached(session.maxStageReached ?? session.currentStage ?? 0);
          showNotificationMessage('Previous session restored', 'info');
        } catch (e) {
          console.error('Failed to load session:', e);
        }
      }
    }
  }, []);

  const canProceed = () => {
    // Each stage manages its own progression
    return false;
  };

  const renderStage = () => {
    const currentBackendStage = marketResearchStages[currentStage]?.backendCode;
    if (!shouldRenderMarketResearchStage(isComplete, currentBackendStage, marketResearchDd2579Enabled)) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Procurement Process Complete!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Successfully identified {vendors.length} qualified vendors for your requirements.
          </Typography>
          <Box display="flex" gap={2} justifyContent="center">
            {/* Removed JSON export of full package */}
            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={handleRestart}
              size="large"
            >
              Start New Process
            </Button>
          </Box>
        </Paper>
      );
    }

    switch (currentBackendStage) {
      case 1:
        return (
          <>
            <GenerateQuestions
              onComplete={handleQuestionsComplete}
              initialDescription={initialCommodityDescription}
              initialRequirementDocument={initialRequirementDocument}
              flowId={flowId}
              initialQuestions={questions}
              initialAnswers={answers}
              onNumItemsDetected={setNumItemsDetected}
              onRequirementDocumentChange={handleRequirementDocumentChange}
              isCustomerMarketResearch={isCustomerMarketResearch}
              supplementalActions={hasConfirmedContractingMarketResearchFlow && flowId ? (
                <MarketResearchNotifyControls
                  flowId={flowId}
                  context="questions"
                  isOwner={selectedFlowIsOwner}
                  onOpenShare={setMarketResearchShareDialogContext}
                />
              ) : undefined}
            />
            <NotificationStageMounted id={notificationStageReadinessEpoch} onMount={setNotificationMountedStageId} />
          </>
        );
      case 2:
        console.log('[App] Render GenerateMFR with commodity:', initialCommodityDescription);
        console.log('[App] Passing initialMfr:', mfr);
        return questions.length > 0 || isTrainingExample ? (
          <GenerateMFR
            commodityDescription={initialCommodityDescription}
            questions={questions}
            onComplete={handleMFRComplete}
            flowId={flowId}
            initialAnswers={answers}
            initialMfr={mfr}
            initialUseCachedData={mfrUseCachedData}
            onUseCachePreferenceChange={handleMfrCachePreferenceChange}
            onOpenFlow={handleSelectFlow}
            currentUserEmail={userEmail ?? undefined}
          />
        ) : (
          <Alert severity="error">No questions available. Please complete question generation first.</Alert>
        );
      case 3:
        // Allow rendering if we have MFR OR if there's a flowId (loading from backend)
        return (mfr || flowId) ? (
          <>
            <EnhancedProductDiscovery
              mfr={mfr || { id: '', rawText: '', commodityDescription: '', requirements: { functional: [], performance: [] }, generatedAt: '', questionsAnswered: [] }}
              onComplete={handleProductsComplete}
              flowId={flowId}
              initialProducts={productDiscoveryResults}
              initialSelectedIds={stage3SelectedIds}
              initialSelectedKeys={stage3SelectedKeys}
              initialBrandNameDecision={marketResearchBrandNameDecision}
              initialBrandNameStatus={marketResearchBrandNameStatus}
              sourceRevision={marketResearchBrandNameDecision?.source_revision}
              autoStartOnFirstEntry={Boolean(flowId && firstStage3AutoStartFlowId === flowId)}
              onFirstEntryHandled={() => setFirstStage3AutoStartFlowId((current) => current === flowId ? null : current)}
              brandNameJaEnabled={marketResearchBrandNameJaFeatureEnabled}
              showBrandNamePostJaHandoff={!isCustomerMarketResearch}
              isCustomerMarketResearch={isCustomerMarketResearch}
            />
          </>
        ) : (
          <Alert severity="error">No MFR data available. Please complete MFR generation first.</Alert>
        );
      case 14:
        return marketResearchBrandNameJaFeatureEnabled && flowId ? (
          <>
            <MarketResearchBrandNameJa
              flowId={flowId}
              initialProjection={
                notificationBrandNameJaProjection?.flowId === flowId
                  ? notificationBrandNameJaProjection.projection
                  : null
              }
              loadOnMount={notificationBrandNameJaProjection?.flowId !== flowId}
              viewOnly={sourceWorkflowReadOnly}
              onBack={async () => {
                const productIndex = marketResearchStageIndexForBackend(3);
                if (!canWriteCurrentWorkflow()) {
                  setCurrentStage(productIndex);
                  return;
                }
                const result = await updateCurrentStage(flowId, 3);
                if (!isCurrentStageUpdateAcknowledged(result)) {
                  const refreshRequirement = getMarketResearchRefreshRequirement(result);
                  if (refreshRequirement) {
                    setMarketResearchRefreshRequirement(refreshRequirement);
                    return;
                  }
                  showNotificationMessage(USER_FACING_ERROR_MESSAGES.wait_and_retry, 'error');
                  return;
                }
                setMarketResearchCustomerTerminal(false);
                setMarketResearchCustomerTerminalStageView(false);
                setCurrentStage(productIndex);
              }}
              onComplete={handleMarketResearchBrandNameJaComplete}
            />
            <NotificationStageMounted id={notificationStageReadinessEpoch} onMount={setNotificationMountedStageId} />
          </>
        ) : null;
      case 4:
                    // Allow rendering if we have products OR if there's a flowId (loading from backend)
                    return (products.length > 0 || flowId) ? (
                      <PSCCodeAssignment
            products={products}
            onComplete={handlePSCComplete}
            flowId={flowId}
            initialProducts={productsWithPSC}
            initialMethodUsed={pscMethodUsed}
            onRerunProductDiscovery={handleRerunProductDiscovery}
          />
        ) : (
          <Alert severity="error">No products selected. Please complete product discovery first.</Alert>
        );
      case 5:
        // Allow rendering if we have products OR if there's a flowId (loading from backend)
        return (productsWithPSC.length > 0 || flowId) ? (
          <NAICSCodeMapping
            products={productsWithPSC}
            onComplete={handleNAICSComplete}
            flowId={flowId}
            initialProducts={productsWithCodes}
            initialMethodUsed={naicsMethodUsed}
            onSelectionChange={handleNAICSSelectionChange}
            onRerunProductDiscovery={handleRerunProductDiscovery}
          />
        ) : (
          <Alert severity="error">No PSC codes assigned. Please complete PSC assignment first.</Alert>
        );
      case 6:
        // Allow rendering if we have products OR if there's a flowId (which means we're loading from backend)
        return (productsWithCodes.length > 0 || flowId) ? (
          <VendorDiscovery
            products={productsWithCodes}
            onComplete={handleVendorsComplete}
            onViewOnlyContinue={() => { void handleStageSelect(marketResearchStageIndexForBackend(7)); }}
            flowId={flowId}
            initialVendors={vendors}
            mfr={mfr}
            onRerunProductDiscovery={handleRerunProductDiscovery}
            marketResearchDd2579Enabled
            isCustomerMarketResearch={isCustomerMarketResearch}
            onDd2579CoordinationChange={handleMarketResearchDd2579CoordinationChange}
          />
        ) : (
          <Alert severity="error">No NAICS codes mapped. Please complete NAICS mapping first.</Alert>
        );
      case 7:
        return vendors.length > 0 || isTrainingExample ? (
          <SectionBGeneration
            productsWithVendors={vendors}
            onComplete={handleSectionBComplete}
            onViewOnlyContinue={() => { void handleStageSelect(marketResearchStageIndexForBackend(8)); }}
            flowId={flowId}
          />
        ) : (
          <Alert severity="error">No vendor data available. Please complete vendor discovery first.</Alert>
        );
      case 8:
        return vendors.length > 0 || isTrainingExample ? (
          <SectionCGeneration
            productsWithVendors={vendors}
            onComplete={handleSectionCComplete}
            onViewOnlyContinue={() => { void handleStageSelect(marketResearchStageIndexForBackend(9)); }}
            flowId={flowId}
            isCustomerMarketResearch={isCustomerMarketResearch}
            customerTerminalAction={isCustomerMarketResearch && marketResearchCustomerTerminal && flowId ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button
                  data-testid="open-post-handoff-progress-section-c"
                  variant="outlined"
                  onClick={() => setMarketResearchCustomerTerminalStageView(false)}
                >
                  View contracting progress
                </Button>
                <MarketResearchNotifyControls
                  flowId={flowId}
                  context="section_c_handoff"
                  isOwner={selectedFlowIsOwner}
                  onOpenShare={setMarketResearchShareDialogContext}
                />
              </Stack>
            ) : undefined}
          />
        ) : (
          <Alert severity="error">No vendor data available. Please complete vendor discovery first.</Alert>
        );
      case 9:
        return vendors.length > 0 || isTrainingExample ? (
          <SectionDGeneration
            productsWithVendors={vendors}
            onComplete={handleSectionDComplete}
            onViewOnlyContinue={() => { void handleStageSelect(marketResearchStageIndexForBackend(10)); }}
            flowId={flowId}
          />
        ) : (
          <Alert severity="error">No vendor data available.</Alert>
        );
      case 10:
        return vendors.length > 0 || isTrainingExample ? (
          <SectionEGeneration
            productsWithVendors={vendors}
            onComplete={handleSectionEComplete}
            onViewOnlyContinue={() => { void handleStageSelect(marketResearchStageIndexForBackend(11)); }}
            flowId={flowId}
            preserveSavedResult={preserveMrChatResults}
          />
        ) : (
          <Alert severity="error">No vendor data available.</Alert>
        );
      case 11:
        return (
          <SynopsisGeneration
            onComplete={handleSynopsisComplete}
            flowId={flowId}
            preserveSavedResult={preserveMrChatResults}
          />
        );
      case 12:
        return (
          <VendorBidsStage
            flowId={flowId}
            dd2579Required={
              marketResearchVendorBidsAction === 'continue-dd2579' ||
              marketResearchVendorBidsAction === 'blocked'
                ? true
                : marketResearchVendorBidsAction === 'complete-workflow'
                  ? false
                  : undefined
            }
            dd2579Eligible={
              marketResearchVendorBidsAction === 'loading'
                ? undefined
                : marketResearchVendorBidsAction === 'continue-dd2579'
            }
            onContinueToDd2579={handleVendorBidsDd2579Continue}
            onComplete={handleVendorBidsComplete}
          />
        );
      case 13:
        if (marketResearchDd2579CoordinationStatus !== 'ready') {
          const retry = () => {
            if (!flowId) return;
            void loadMarketResearchDd2579Coordination(flowId, { force: true }).catch((error) => {
              console.error('Failed to retry Market Research DD 2579 coordination:', error);
            });
          };
          return (
            <Paper
              data-testid={marketResearchDd2579CoordinationStatus === 'error'
                ? 'market-research-dd2579-error'
                : 'market-research-dd2579-pending'}
              variant="outlined"
              sx={{ p: 3 }}
            >
              {marketResearchDd2579CoordinationStatus === 'error' ? (
                <Alert
                  severity="warning"
                  action={<Button color="inherit" size="small" onClick={retry}>Retry DD/DAF 2579 check</Button>}
                >
                  DD/DAF 2579 eligibility could not be verified. Retry the check before reviewing or exporting this form.
                </Alert>
              ) : (
                <Alert severity="info" icon={<CircularProgress size={20} />}>
                  Verifying the saved DD/DAF 2579 coordination status…
                </Alert>
              )}
              {isTrainingExample && <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Button onClick={handleBack}>Back</Button>
                <Button variant="contained" onClick={onSampleContinue}>Continue to Synopsis &amp; MRR</Button>
              </Box>}
            </Paper>
          );
        }
        if (marketResearchDd2579FormHydrationStatus !== 'ready') {
          const retryDraft = () => {
            if (!flowId) return;
            void hydrateMarketResearchDd2579(flowId, true);
          };
          return (
            <Paper
              data-testid={marketResearchDd2579FormHydrationStatus === 'error'
                ? 'market-research-dd2579-draft-error'
                : 'market-research-dd2579-draft-pending'}
              variant="outlined"
              sx={{ p: 3 }}
            >
              {marketResearchDd2579FormHydrationStatus === 'error' ? (
                <Alert
                  severity="warning"
                  action={<Button color="inherit" size="small" onClick={retryDraft}>Retry DD/DAF 2579 draft</Button>}
                >
                  The saved DD/DAF 2579 draft could not be refreshed. Retry before editing.
                </Alert>
              ) : (
                <Alert severity="info" icon={<CircularProgress size={20} />}>
                  Loading the latest saved DD/DAF 2579 draft…
                </Alert>
              )}
              {isTrainingExample && <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Button onClick={handleBack}>Back</Button>
                <Button variant="contained" onClick={onSampleContinue}>Continue to Synopsis &amp; MRR</Button>
              </Box>}
            </Paper>
          );
        }
        return marketResearchDd2579Enabled ? (
          <MarketResearchDd2579
            flowId={flowId}
            coordinationProducts={marketResearchDd2579Products}
            initialDd2579={marketResearchDd2579}
            initialState={marketResearchDd2579RestoredState}
            coordinationState={marketResearchDd2579Coordination}
            onCoordinationChange={handleMarketResearchDd2579CoordinationChange}
            onComplete={handleMarketResearchDd2579Complete}
          />
        ) : null;
      default:
        return null;
    }
  };

  // Memoize onResultLoaded callback to prevent unnecessary re-renders
  const handleResultLoaded = useCallback((result: CLSCorrectionResult) => {
    setClsCorrectionResult(result);
  }, []);

  // Render CLS workflow stage
  const renderCLSStage = () => {
    switch (currentStage) {
      case 0:
        return (
          <CLSDocumentUpload
            flowId={flowId!}
            onComplete={() => setCurrentStage(1)}
            onBack={() => { setAppPage('type_selector'); setFlowSelectorScope(null); }}
            onOpenFlow={handleSelectFlow}
            currentUserEmail={userEmail ?? undefined}
          />
        );
      case 1:
        return (
          <CLSResults
            flowId={flowId!}
            result={clsCorrectionResult}
            isProcessing={clsIsProcessing}
            onProcessingComplete={() => setClsIsProcessing(false)}
            initialUserOverrides={clsUserOverrides}
            initialHiddenClauses={clsHiddenClauses}
            initialManuallyAddedClauses={clsManuallyAddedClauses}
            initialSectionOverrides={clsSectionOverrides}
            onBack={() => setCurrentStage(0)}
            onStartOver={() => {
              setClsCorrectionResult(null);
              setClsUserAnswers(null);
              setClsExtractedParams(null);
              setClsUserOverrides(new Map());
              setClsHiddenClauses(new Set());
              setClsManuallyAddedClauses([]);
              setClsSectionOverrides(new Map());
              setCurrentStage(0);
            }}
            onResultLoaded={handleResultLoaded}
            onContinue={(result, userOverrides, hiddenClauses, manuallyAddedClauses, sectionOverrides) => {
              setClsCorrectionResult(result);
              setClsUserOverrides(userOverrides);
              setClsHiddenClauses(hiddenClauses);
              setClsManuallyAddedClauses(manuallyAddedClauses);
              setClsSectionOverrides(sectionOverrides);
              setCurrentStage(2);
              // Persist stage change to backend for flow reload
              if (canWriteCurrentWorkflow()) {
                updateCurrentStage(flowId!, 2).catch((error) => {
                  console.error('Failed to update current stage:', error);
                });
                // Save manual clause changes to cache
                const userEmail = localStorage.getItem('userEmail') || '';
                fetchWithChatFeedback(`${API_BASE_URL}/api/cls/${flowId}/save-manual-changes`, {
                  method: 'POST',
                  credentials: 'include',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    userOverrides: Object.fromEntries(userOverrides),
                    hiddenClauses: Array.from(hiddenClauses),
                    manuallyAddedClauses: manuallyAddedClauses,
                  }),
                }).catch((error) => {
                  console.error('Failed to save manual changes:', error);
                });
              }
            }}
          />
        );
      case 2:
        if (!clsCorrectionResult) {
          return (
            <Box>
              <Alert severity="info">No saved clause results are available for this stage.</Alert>
              <Button onClick={() => setCurrentStage(1)}>Back</Button>
            </Box>
          );
        }
        return (
          <CLSFinalStep
            flowId={flowId!}
            result={clsCorrectionResult}
            userOverrides={clsUserOverrides}
            hiddenClauses={clsHiddenClauses}
            manuallyAddedClauses={clsManuallyAddedClauses}
            sectionOverrides={clsSectionOverrides}
            onBack={() => setCurrentStage(1)}
            onStartOver={() => {
              setClsCorrectionResult(null);
              setClsUserAnswers(null);
              setClsExtractedParams(null);
              setClsUserOverrides(new Map());
              setClsHiddenClauses(new Set());
              setClsManuallyAddedClauses([]);
              setClsSectionOverrides(new Map());
              setCurrentStage(0);
            }}
          />
        );
      default:
        return null;
    }
  };

  // Render SOW Verification workflow stage
  const renderSOWStage = () => {
    switch (currentStage) {
      case 0:
        return (
          <SOWVerificationUpload
            flowId={flowId!}
            workflowType="sow_verification"
            onComplete={() => {
              localStorage.setItem(`sow_stage_${flowId}`, '1');
              setCurrentStage(1);
            }}
            onOpenFlow={handleSelectFlow}
            currentUserEmail={userEmail ?? undefined}
          />
        );
      case 1:
        return (
          <SOWVerificationResults
            flowId={flowId!}
            workflowType="sow_verification"
            isProcessing={sowIsProcessing}
            onProcessingComplete={() => setSowIsProcessing(false)}
            onStartOver={() => {
              localStorage.setItem(`sow_stage_${flowId}`, '0');
              setCurrentStage(0);
            }}
            onContinueToEdit={() => {
              localStorage.setItem(`sow_stage_${flowId}`, '2');
              setEnteredEditFromSuggestions(true);
              setCurrentStage(2);
            }}
          />
        );
      case 2:
        return (
          <SOWVerificationEdit
            flowId={flowId!}
            workflowType="sow_verification"
            onBack={() => {
              localStorage.setItem(`sow_stage_${flowId}`, '1');
              setCurrentStage(1);
            }}
            fromSuggestions={enteredEditFromSuggestions}
            onSuggestionsConsumed={() => setEnteredEditFromSuggestions(false)}
            onDirtyChange={handleSowEditDirtyChange}
            onDocumentEdit={handleSowDocumentEdit}
          />
        );
      default:
        return null;
    }
  };

  // One-time Construction keeps the typed construction upload/review/editor
  // intact, while the requirements path passes its stage-206 edited-SOW handoff
  // to the shared 604–606 components. The optional workflowType prop is display-only;
  // downstream APIs derive their trusted profile from persisted flow metadata.
  const renderOneTimeConstructionStage = () => {
    if (standaloneMode) {
      return constructionPriceEstimatorApplicable
        ? <ConstructionPriceEstimator flowId={flowId!} onComplete={() => setMaxStageReached(0)} />
        : <Alert severity="error">Price Estimator is unavailable for this workflow.</Alert>;
    }
    if (constructionSowOrigin === 'requirements_generation') {
      switch (currentStage) {
        case 0: return <ConstructionRequirementIntake flowId={flowId!} onComplete={() => { localStorage.setItem(`one_time_construction_stage_${flowId}`, '1'); setMaxStageReached((v) => Math.max(v, 1)); setCurrentStage(1); }} />;
        case 1: return <ConstructionContextQuestions flowId={flowId!} onComplete={() => { localStorage.setItem(`one_time_construction_stage_${flowId}`, '2'); setMaxStageReached((v) => Math.max(v, 2)); setCurrentStage(2); }} />;
        case 2:
          return (
            <SOWVerificationEdit
              flowId={flowId!}
              workflowType="one_time_construction"
              generatedDraft
              onBack={() => setCurrentStage(1)}
              onDirtyChange={handleSowEditDirtyChange}
              onDocumentEdit={handleSowDocumentEdit}
              onContinueToAuthoring={() => {
                localStorage.setItem(
                  `one_time_construction_stage_${flowId}`,
                  '3',
                );
                setMaxStageReached((value) => Math.max(value, 3));
                setCurrentStage(3);
              }}
            />
          );
        case 3: return <ServicesSalientCharacteristics flowId={flowId!} workflowType="one_time_construction" entryProfile="construction_requirements_generation" onComplete={() => { localStorage.setItem(`one_time_construction_stage_${flowId}`, '4'); setMaxStageReached((v) => Math.max(v, 4)); setCurrentStage(4); }} />;
        case 4: return <ServicesCodeSelection flowId={flowId!} workflowType="one_time_construction" onComplete={() => { localStorage.setItem(`one_time_construction_stage_${flowId}`, '5'); setMaxStageReached((v) => Math.max(v, 5)); setCurrentStage(5); }} />;
        case 5: return <ServicesVendorDiscovery flowId={flowId!} workflowType="one_time_construction" onComplete={showConstructionPriceEstimator ? () => { localStorage.setItem(`one_time_construction_stage_${flowId}`, '6'); setMaxStageReached((v) => Math.max(v, 6)); setCurrentStage(6); } : undefined} />;
        case 6: return showConstructionPriceEstimator ? <ConstructionPriceEstimator flowId={flowId!} onComplete={() => setMaxStageReached((v) => Math.max(v, 6))} /> : null;
        default: return null;
      }
    }
    switch (currentStage) {
      case 0:
        return (
          <SOWVerificationUpload
            flowId={flowId!}
            workflowType="one_time_construction"
            onComplete={() => {
              localStorage.setItem(`one_time_construction_stage_${flowId}`, '1');
              setMaxStageReached((prev) => Math.max(prev, 1));
              setCurrentStage(1);
            }}
            onOpenFlow={handleSelectFlow}
            currentUserEmail={userEmail ?? undefined}
          />
        );
      case 1:
        return (
          <SOWVerificationResults
            flowId={flowId!}
            workflowType="one_time_construction"
            isProcessing={sowIsProcessing}
            onProcessingComplete={() => setSowIsProcessing(false)}
            onStartOver={() => {
              localStorage.setItem(`one_time_construction_stage_${flowId}`, '0');
              setCurrentStage(0);
            }}
            onContinueToEdit={() => {
              localStorage.setItem(`one_time_construction_stage_${flowId}`, '2');
              setEnteredEditFromSuggestions(true);
              setMaxStageReached((prev) => Math.max(prev, 2));
              setCurrentStage(2);
            }}
          />
        );
      case 2:
        return (
          <SOWVerificationEdit
            flowId={flowId!}
            workflowType="one_time_construction"
            onBack={() => {
              localStorage.setItem(`one_time_construction_stage_${flowId}`, '1');
              setCurrentStage(1);
            }}
            fromSuggestions={enteredEditFromSuggestions}
            onSuggestionsConsumed={() => setEnteredEditFromSuggestions(false)}
            onDirtyChange={handleSowEditDirtyChange}
            onDocumentEdit={handleSowDocumentEdit}
            onContinueToAuthoring={() => {
              localStorage.setItem(`one_time_construction_stage_${flowId}`, '3');
              setMaxStageReached((prev) => Math.max(prev, 3));
              setCurrentStage(3);
            }}
          />
        );
      case 3:
        return (
          <ServicesSalientCharacteristics
            flowId={flowId!}
            workflowType="one_time_construction"
            entryProfile="construction_existing_sow"
            onComplete={() => {
              localStorage.setItem(`one_time_construction_stage_${flowId}`, '4');
              setMaxStageReached((prev) => Math.max(prev, 4));
              setCurrentStage(4);
            }}
          />
        );
      case 4:
        return (
          <ServicesCodeSelection
            flowId={flowId!}
            workflowType="one_time_construction"
            onComplete={() => {
              localStorage.setItem(`one_time_construction_stage_${flowId}`, '5');
              setMaxStageReached((prev) => Math.max(prev, 5));
              setCurrentStage(5);
            }}
          />
        );
      case 5:
        return <ServicesVendorDiscovery flowId={flowId!} workflowType="one_time_construction" onComplete={showConstructionPriceEstimator ? () => { localStorage.setItem(`one_time_construction_stage_${flowId}`, '6'); setMaxStageReached((prev) => Math.max(prev, 6)); setCurrentStage(6); } : undefined} />;
      case 6:
        return showConstructionPriceEstimator ? <ConstructionPriceEstimator flowId={flowId!} onComplete={() => setMaxStageReached((prev) => Math.max(prev, 6))} /> : null;
      default:
        return null;
    }
  };

  const renderConstructionSowGenerationStage = () => {
    switch (currentStage) {
      case 0: return <ConstructionRequirementIntake flowId={flowId!} onComplete={() => { localStorage.setItem(`construction_sow_generation_stage_${flowId}`, '1'); setMaxStageReached((v) => Math.max(v, 1)); setCurrentStage(1); }} />;
      case 1: return <ConstructionContextQuestions flowId={flowId!} onComplete={() => { localStorage.setItem(`construction_sow_generation_stage_${flowId}`, '2'); setMaxStageReached((v) => Math.max(v, 2)); setCurrentStage(2); }} />;
      case 2: return <SOWVerificationEdit flowId={flowId!} workflowType="construction_sow_generation" generatedDraft onBack={() => setCurrentStage(1)} onDirtyChange={handleSowEditDirtyChange} onDocumentEdit={handleSowDocumentEdit} />;
      default: return null;
    }
  };

  const renderServicesPwsGenerationStage = () => {
    switch (currentStage) {
      case 0:
        return <ServicesPwsRequirementIntake flowId={flowId!} onComplete={() => {
          localStorage.setItem(`services_pws_generation_stage_${flowId}`, '1');
          setMaxStageReached((value) => Math.max(value, 1)); setCurrentStage(1);
        }} />;
      case 1:
        return <ServicesPwsContextQuestions flowId={flowId!} onComplete={() => {
          localStorage.setItem(`services_pws_generation_stage_${flowId}`, '2');
          setMaxStageReached((value) => Math.max(value, 2)); setCurrentStage(2);
        }} />;
      case 2:
        return <ServicesPwsGenerationEdit flowId={flowId!} onBack={() => {
          localStorage.setItem(`services_pws_generation_stage_${flowId}`, '1'); setCurrentStage(1);
        }} />;
      default:
        return null;
    }
  };

  // Render Wage Determination workflow stage (single page)
  const renderWageDetStage = () => {
    return (
      <WageDeterminationStage
        flowId={flowId!}
        onComplete={() => setIsComplete(true)}
        onOpenFlow={handleSelectFlow}
        currentUserEmail={userEmail ?? undefined}
      />
    );
  };

  // Render PNM workflow stage
  const renderPNMStage = () => {
    return (
      <PNMStage
        flowId={flowId!}
        onComplete={() => setIsComplete(true)}
        onOpenFlow={handleSelectFlow}
        currentUserEmail={userEmail ?? undefined}
      />
    );
  };

  // Render FOPR workflow stage
  const renderFOPRStage = () => {
    switch (currentStage) {
      case 0:
        return (
          <FOPRUpload
            flowId={flowId!}
            onComplete={(data) => {
              setFoprData(data);
              setCurrentStage(1);
            }}
            onOpenFlow={handleSelectFlow}
            currentUserEmail={userEmail ?? undefined}
          />
        );
      case 1:
        if (!foprData) {
          return <Box><Alert severity="info">No saved FOPR form is available for this stage.</Alert><Button onClick={() => setCurrentStage(0)}>Back</Button>{isTrainingExample && <Button onClick={() => setCurrentStage(2)}>Continue</Button>}</Box>;
        }
        return (
          <FOPRForm
            flowId={flowId!}
            foprData={foprData}
            onComplete={() => setIsComplete(true)}
            onAdvanceToStage={(stage) => setCurrentStage(stage - 500)}
          />
        );
      case 2:
        return (
          <ConstructionAbstractStage
            flowId={flowId!}
            onComplete={() => setIsComplete(true)}
          />
        );
      default:
        return null;
    }
  };

  // Render Services COMBO (Combined Synopsis/Solicitation) workflow stage
  const renderServicesComboStage = () => {
    switch (currentStage) {
      case 0:
        return (
          <ServicesComboUpload
            flowId={flowId!}
            onComplete={(data) => {
              setServicesComboData(data);
              setMaxStageReached((prev) => Math.max(prev, 1));
              setCurrentStage(1);
            }}
            onOpenFlow={handleSelectFlow}
            currentUserEmail={userEmail ?? undefined}
          />
        );
      case 1:
        if (!servicesComboData) {
          return <Box><Alert severity="info">No saved synopsis form is available for this stage.</Alert><Button onClick={() => setCurrentStage(0)}>Back</Button></Box>;
        }
        return (
          <ServicesComboForm
            flowId={flowId!}
            comboData={servicesComboData}
            onComplete={() => setIsComplete(true)}
          />
        );
      default:
        return null;
    }
  };

  // Render Services workflow stage
  const renderGeneratedServicesStage = () => {
    switch (currentStage) {
      case 0:
        return <ServicesPwsRequirementIntake flowId={flowId!} onComplete={() => {
          localStorage.setItem(`services_stage_${flowId}`, '1');
          setMaxStageReached((value) => Math.max(value, 1));
          setCurrentStage(1);
        }} />;
      case 1:
        return <ServicesPwsContextQuestions flowId={flowId!} onComplete={() => {
          localStorage.setItem(`services_stage_${flowId}`, '2');
          setMaxStageReached((value) => Math.max(value, 2));
          setCurrentStage(2);
        }} />;
      case 2:
        return <ServicesPwsGenerationEdit flowId={flowId!} onBack={() => {
          localStorage.setItem(`services_stage_${flowId}`, '1');
          setCurrentStage(1);
        }} onContinueToAuthoring={() => {
          localStorage.setItem(`services_stage_${flowId}`, '3');
          setMaxStageReached((value) => Math.max(value, 3));
          setCurrentStage(3);
        }} />;
      case 3:
        return <ServicesSalientCharacteristics flowId={flowId!} entryProfile="services_requirements_generation" onComplete={() => {
          localStorage.setItem(`services_stage_${flowId}`, '4');
          setMaxStageReached((value) => Math.max(value, 4));
          setCurrentStage(4);
        }} />;
      case 4:
        return <ServicesCodeSelection flowId={flowId!} onComplete={() => {
          localStorage.setItem(`services_stage_${flowId}`, '5');
          setMaxStageReached((value) => Math.max(value, 5));
          setCurrentStage(5);
        }} />;
      case 5:
        return <ServicesVendorDiscovery flowId={flowId!} />;
      default:
        return null;
    }
  };

  const renderServicesStage = () => {
    switch (currentStage) {
      case 0:
        // Stage 600: SOW Upload
        return (
          <ServicesSOWUpload
            flowId={flowId!}
            onComplete={() => { localStorage.setItem(`services_stage_${flowId}`, '1'); setCurrentStage(1); }}
            onOpenFlow={handleSelectFlow}
            currentUserEmail={userEmail ?? undefined}
          />
        );
      case 1:
        // Stage 601: Context Questions
        return (
          <ServicesContextQuestions
            flowId={flowId!}
            onComplete={() => { localStorage.setItem(`services_stage_${flowId}`, '2'); setCurrentStage(2); }}
          />
        );
      case 2:
        // Stage 602: Suggestions
        return (
          <ServicesSuggestions
            flowId={flowId!}
            onComplete={() => { localStorage.setItem(`services_stage_${flowId}`, '3'); setCurrentStage(3); }}
          />
        );
      case 3:
        // Stage 603: Manual Edit
        return (
          <ServicesManualEdit
            flowId={flowId!}
            onComplete={() => { localStorage.setItem(`services_stage_${flowId}`, '4'); setCurrentStage(4); }}
          />
        );
      case 4:
        // Stage 604: Salient Characteristics
        return (
          <ServicesSalientCharacteristics
            flowId={flowId!}
            onComplete={() => { localStorage.setItem(`services_stage_${flowId}`, '5'); setCurrentStage(5); }}
          />
        );
      case 5:
        // Stage 605: Code Selection (NAICS + PSC)
        return (
          <ServicesCodeSelection
            flowId={flowId!}
            onComplete={() => { localStorage.setItem(`services_stage_${flowId}`, '6'); setCurrentStage(6); }}
          />
        );
      case 6:
        // Stage 606: Vendor Discovery
        return (
          <ServicesVendorDiscovery
            flowId={flowId!}
          />
        );
      default:
        return null;
    }
  };

  const renderGeneratedComplexServicesPrefixStage = () => {
    switch (currentStage) {
      case 0:
        return <ServicesPwsRequirementIntake flowId={flowId!} showComplexTour onComplete={() => {
          localStorage.setItem(`complex_services_stage_${flowId}`, '1');
          setMaxStageReached((value) => Math.max(value, 1));
          setCurrentStage(1);
        }} />;
      case 1:
        return <ServicesPwsContextQuestions flowId={flowId!} showComplexTour onComplete={() => {
          localStorage.setItem(`complex_services_stage_${flowId}`, '2');
          setMaxStageReached((value) => Math.max(value, 2));
          setCurrentStage(2);
        }} />;
      case 2:
        return <ServicesPwsGenerationEdit flowId={flowId!} showComplexTour onBack={() => {
          localStorage.setItem(`complex_services_stage_${flowId}`, '1');
          setCurrentStage(1);
        }} onContinueToAuthoring={() => {
          const firstAuthoringStage = nextEnabledComplexStage(4, activeComplexApplicableSet) ?? 4;
          localStorage.setItem(`complex_services_stage_${flowId}`, String(firstAuthoringStage));
          setMaxStageReached((value) => Math.max(value, firstAuthoringStage));
          setCurrentStage(firstAuthoringStage);
        }} />;
      default:
        return null;
    }
  };

  const renderComplexServicesStage = () => {
    const key = COMPLEX_SERVICES_STAGE_ORDER[currentStage];
    if (!key) return null;
    // Defensive: if we somehow land on a greyed stage, skip forward to the next
    // enabled one (or complete). Normal nav never reaches here.
    if (!activeComplexApplicableSet.has(key)) {
      const fwd = nextEnabledComplexStage(currentStage + 1, activeComplexApplicableSet);
      if (fwd === null) setIsComplete(true);
      else setCurrentStage(fwd);
      return null;
    }
    const nextEnabled = nextEnabledComplexStage(currentStage + 1, activeComplexApplicableSet);
    // `advance` doubles as each stage's `onComplete(data)`: it captures the
    // saved payload so intra-session back-nav re-seeds the stage with the
    // just-entered work (the App-hydrated stages read `complexStageData[key]`).
    const advance = (data?: unknown) => {
      if (data !== undefined) {
        setComplexStageData((prev) => ({ ...prev, [key]: data }));
      }
      if (nextEnabled === null && key === 'record') {
        const completion = completeComplexServicesRecord(maxStageReached);
        localStorage.setItem(`complex_services_stage_${flowId}`, String(completion.currentStage));
        setCurrentStage(completion.currentStage);
        setMaxStageReached(completion.maxStageReached);
        setIsComplete(completion.isComplete);
        showNotificationMessage(completion.notification.message, completion.notification.type);
        return;
      }
      if (nextEnabled === null) { setIsComplete(true); return; }
      localStorage.setItem(`complex_services_stage_${flowId}`, String(nextEnabled));
      setMaxStageReached((prev) => Math.max(prev, nextEnabled));
      setCurrentStage(nextEnabled);
    };
    // Seed an initialData object from a persisted payload, tolerating null/empty.
    const seedFor = (v: unknown): Record<string, unknown> =>
      v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
    // The complex-only stages below hydrate PURELY from `initialData` (no
    // self-fetch), so hold their render until the persisted payloads have been
    // fetched — otherwise a reload would seed them with an empty form.
    const APP_HYDRATED_KEYS: ComplexStageKey[] = [
      'mrr', 'ige', 'igd', 'pws_diff', 'options', 'solicitation', 'clearance', 'record', 'form9',
    ];
    if (APP_HYDRATED_KEYS.includes(key) && !complexStageDataLoaded) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      );
    }
    switch (key) {
      // Reused Services authoring stages (600–606).
      case 'sow_upload':
        return <ServicesSOWUpload flowId={flowId!} onComplete={advance} onOpenFlow={handleSelectFlow} currentUserEmail={userEmail ?? undefined} />;
      case 'context_questions':
        return <ServicesContextQuestions flowId={flowId!} onComplete={advance} />;
      case 'suggestions':
        return <ServicesSuggestions flowId={flowId!} onComplete={advance} />;
      case 'manual_edit':
        return <ServicesManualEdit flowId={flowId!} onComplete={advance} />;
      case 'salient_chars':
        return <ServicesSalientCharacteristics
          flowId={flowId!}
          entryProfile={servicesPwsOrigin === 'requirements_generation'
            ? 'services_requirements_generation' : 'services'}
          onComplete={advance}
        />;
      case 'code_selection':
        return <ServicesCodeSelection flowId={flowId!} onComplete={advance} />;
      case 'vendor_discovery':
        // ServicesVendorDiscovery takes only `flowId` (no onComplete); provide a
        // wrapper Continue button so the complex flow can advance past it.
        return (
          <Box>
            <ServicesVendorDiscovery flowId={flowId!} />
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" size="large" onClick={isViewOnly ? () => {
                const next = nextEnabledComplexStage(currentStage + 1, activeComplexApplicableSet);
                if (next !== null) setCurrentStage(next);
                else setAppPage('flow_selector');
              } : advance} data-testid="complex-services-vendor-discovery-continue">Continue</Button>
            </Box>
          </Box>
        );
      case 'mrr':
      case 'igd':
      case 'form9': {
        // Artifact stages use the shared registry so they receive the same
        // flow/data/advance contract as the existing App-hydrated stages.
        const ArtifactStage = COMPLEX_SERVICES_ARTIFACT_STAGE_COMPONENTS[key];
        return ArtifactStage
          ? <ArtifactStage flowId={flowId!} initialData={seedFor(complexStageData[key])} onComplete={advance} />
          : null;
      }
      // Complex-only stages (607–616). The App-hydrated stages receive their
      // persisted payload plus the flow's branch flags (which they read out of
      // `initialData`). Compliance/reqval/qasp/dd2579 self-hydrate and are left
      // to fetch their own persisted data.
      case 'ige': return <ComplexServicesIge flowId={flowId} initialData={{ acquisition_mode: complexAcqMode, pop_structure: complexPop, sat_threshold: complexSatThreshold, ...seedFor(complexStageData.ige) }} onComplete={advance} onSatThresholdChange={setComplexSatThreshold} />;
      case 'compliance': return <ComplexServicesCompliance flowId={flowId} initialData={{ sat_threshold: complexSatThreshold }} onComplete={advance} />;
      case 'pws_diff': return <ComplexServicesPwsDiff flowId={flowId} initialData={seedFor(complexStageData.pws_diff)} onComplete={advance} />;
      case 'options': return <ComplexServicesOptions flowId={flowId} initialData={{ acquisition_mode: complexAcqMode, ...seedFor(complexStageData.options) }} onComplete={advance} />;
      case 'reqval': return <ComplexServicesReqval flowId={flowId} onComplete={advance} />;
      case 'qasp': return <ComplexServicesQasp flowId={flowId} onComplete={advance} />;
      case 'dd2579': return <ComplexServicesDd2579 flowId={flowId} onComplete={advance} />;
      case 'solicitation': return <ComplexServicesSolicitation flowId={flowId} initialData={seedFor(complexStageData.solicitation)} onComplete={advance} />;
      case 'clearance': return <ComplexServicesClearance flowId={flowId} initialData={seedFor(complexStageData.clearance)} onComplete={advance} />;
      case 'record': return <ComplexServicesRecord flowId={flowId} initialData={{ acquisition_mode: complexAcqMode, ...seedFor(complexStageData.record) }} onComplete={advance} />;
      default: return null;
    }
  };

  const renderGPCStage = () => {
    switch (currentStage) {
      case 0:
        return (
          <GPCGenerateQuestions
            flowId={flowId!}
            initialDescription={gpcRequirementText}
            initialQuestions={gpcQuestions}
            initialAnswers={gpcAnswers}
            onComplete={(questions, answers, requirementText) => {
              setGpcQuestions(questions);
              setGpcAnswers(answers);
              setGpcRequirementText(requirementText);
              localStorage.setItem(`gpc_stage_${flowId}`, '1');
              setMaxStageReached((prev) => Math.max(prev, 1));
              setCurrentStage(1);
            }}
          />
        );
      case 1:
        return (
          <GPCSalientCharacteristics
            ref={gpcSalientRef}
            flowId={flowId!}
            requirementText={gpcRequirementText}
            questions={gpcQuestions}
            answers={gpcAnswers}
            initialMfr={gpcMfr}
            onComplete={(mfr) => {
              setGpcMfr(mfr);
              localStorage.setItem(`gpc_stage_${flowId}`, '2');
              setMaxStageReached((prev) => Math.max(prev, 2));
              setCurrentStage(2);
            }}
          />
        );
      case 2:
        return (
          <GPCProductDiscovery
            flowId={flowId!}
            mfr={gpcMfr}
            requirementText={gpcRequirementText}
            initialBuyParams={gpcBuyParams}
            initialPriceQuotes={gpcPriceQuotes}
            initialSearchComplete={gpcPriceSearchComplete}
            initialSearchGeneration={gpcSearchGeneration}
            initialUnitPrice={gpcUnitPrice}
            initialThreshold={gpcThreshold}
            onStateChange={(state) => {
              setGpcPriceQuotes(state.priceQuotes);
              setGpcPriceSearchComplete(state.searchComplete);
              setGpcSearchGeneration(state.searchGeneration);
              setGpcUnitPrice(state.unitPrice);
              setGpcThreshold(state.threshold);
              if (!state.threshold && !isViewOnly) {
                setGpcPriceSearchInvalidated(true);
                setGpcServerAvailableFormStages(null);
                setMaxStageReached((prev) => Math.min(prev, 2));
              }
              setGpcBuyParams((prev) => {
                const nextBuyType = state.buyType || null;
                if (
                  prev.buy_type === nextBuyType &&
                  prev.buy_type_agent_source === state.buyTypeAgentSource &&
                  prev.quantity === state.quantity
                ) {
                  return prev;
                }
                return {
                  ...prev,
                  buy_type: nextBuyType,
                  buy_type_agent_source: state.buyTypeAgentSource,
                  quantity: state.quantity,
                };
              });
            }}
            onComplete={async (result) => {
              setGpcThreshold(result);
              setGpcBuyParams(result.buyParams);
              const localAvailable = applicableGpcFormStages({
                sourcingPath: result.sourcingPath ?? null,
                dollarAmount: result.total ?? null,
                unitPrice: result.buyParams?.unit_price ?? null,
                buyType: result.buyParams?.buy_type ?? null,
                sourceCount: gpcPriceQuotes.filter(
                  (q) => q.price !== null && q.price !== undefined,
                ).length,
                vendorIsSmall: gpcVendorIsSmall,
                category: gpcDecision?.category ?? '',
                pilferable: false,
                taxable: null,
              });
              const authGeneration = authGenerationRef.current;
              const operationGeneration = workflowOperationGenerationRef.current;
              const isCurrent = () => (
                authGenerationRef.current === authGeneration
                && workflowOperationGenerationRef.current === operationGeneration
              );
              setGpcNavigationLoading(true);
              try {
                const completion = await fetchGpcCompletion(flowId!);
                if (!isCurrent()) return;
                setGpcRequiredFieldsValidation(completion.requiredFieldsValidation);
                setGpcServerAvailableFormStages(completion.availableStages);
                const available = completion.availableStages ?? localAvailable;
                if (available.length === 0) {
                  showNotificationMessage('Unable to determine the remaining GPC steps.', 'error');
                  return;
                }
                setGpcPriceSearchInvalidated(false);
                const target = nextEnabledGpcStage(GPC_FORM_STAGE_BASE_OFFSET, available);
                if (target === null) return;
                localStorage.setItem(`gpc_stage_${flowId}`, String(target));
                setMaxStageReached((prev) => Math.max(prev, target));
                setCurrentStage(target);
              } catch {
                if (!isCurrent()) return;
                showNotificationMessage(
                  'Unable to determine the remaining GPC steps.',
                  'error',
                );
              } finally {
                if (isCurrent()) setGpcNavigationLoading(false);
              }
            }}
          />
        );
      default:
        return renderGPCFormStage();
    }
  };

  const renderGPCFormStage = () => {
    const formIndex = currentStage - GPC_FORM_STAGE_BASE_OFFSET;
    const key = allGpcFormStages()[formIndex];
    if (!key) return null;
    const requiredFieldsValidationFor = (stageKey: GpcFormStageKey): GpcRequiredFieldsValidation | null => {
      const stages = gpcRequiredFieldsValidation?.stages;
      if (!stages || typeof stages !== 'object') return null;
      const entry = (stages as Record<string, unknown>)[stageKey];
      return entry && typeof entry === 'object' ? entry as GpcRequiredFieldsValidation : null;
    };
    if (!gpcAvailableSet.has(key)) {
      return null;
    }
    const nextEnabled = nextEnabledGpcStage(currentStage + 1, gpcAvailableSet);
    const isLast = nextEnabled === null;
    const advance = async (refreshProjection = false) => {
      if (!refreshProjection && !isLast && nextEnabled !== null) {
        localStorage.setItem(`gpc_stage_${flowId}`, String(nextEnabled));
        setMaxStageReached((prev) => Math.max(prev, nextEnabled));
        setCurrentStage(nextEnabled);
        return;
      }
      if (!flowId) {
        showNotificationMessage('Unable to verify GPC completion.', 'error');
        return;
      }
      const authGeneration = authGenerationRef.current;
      const operationGeneration = workflowOperationGenerationRef.current;
      const isCurrentCompletion = () => (
        authGenerationRef.current === authGeneration
        && workflowOperationGenerationRef.current === operationGeneration
      );
      setGpcNavigationLoading(true);
      try {
        const completion = await fetchGpcCompletion(flowId);
        if (!isCurrentCompletion()) return;
        setGpcRequiredFieldsValidation(completion.requiredFieldsValidation);
        setGpcServerAvailableFormStages(completion.availableStages);
        if (refreshProjection && !isLast) {
          const next = nextEnabledGpcStage(
            currentStage + 1, completion.availableStages ?? gpcAvailableSet,
          );
          if (next !== null) {
            localStorage.setItem(`gpc_stage_${flowId}`, String(next));
            setMaxStageReached((prev) => Math.max(prev, next));
            setCurrentStage(next);
            return;
          }
        }
        if (completion.ready) {
          setIsComplete(true);
          return;
        }
        if (completion.firstIncompleteStage !== null) {
          localStorage.setItem(
            `gpc_stage_${flowId}`,
            String(completion.firstIncompleteStage),
          );
          setCurrentStage(completion.firstIncompleteStage);
        }
        showNotificationMessage(
          'Complete the remaining required GPC fields before finishing.',
          'info',
        );
      } catch {
        if (!isCurrentCompletion()) return;
        showNotificationMessage(
          'We could not verify GPC completion. Please try Continue again.',
          'error',
        );
      } finally {
        if (isCurrentCompletion()) setGpcNavigationLoading(false);
      }
    };

    switch (key) {
      case 'naics_psc':
        return (
          <GPCNaicsPscSelection
            flowId={flowId}
            requirement={gpcRequirementText || gpcBuyParams.requirement_title || ''}
            products={gpcProducts}
            initialNaicsPsc={gpcNaicsPsc}
            onStateChange={(nps) => setGpcNaicsPsc(nps)}
            onComplete={(nps) => {
              setGpcNaicsPsc(nps);
              advance();
            }}
          />
        );
      case 'mandatory_source':
        return (
          <GPCMandatorySource
            flowId={flowId}
            decision={
              gpcDecision ?? {
                sourcing_path: 'mandatory_source',
                category: '',
                recommended_source: '',
                source_hierarchy: [],
                order_of_precedence: [],
                threshold_band: gpcThreshold?.band ?? null,
                obligations: [],
                requires_dd2579: false,
                requires_889_verification: false,
                requires_ecars: false,
                verify_flags: [],
                user_confirmed: false,
                user_override_source: null,
                selected_sources: [],
              }
            }
            threshold={gpcThreshold ?? undefined}
            buyParams={gpcBuyParams}
            unitPrice={gpcUnitPrice ?? undefined}
            requirement={gpcRequirementText}
            naics={gpcNaicsPsc?.naics ?? ''}
            psc={gpcNaicsPsc?.psc ?? ''}
            onAwardVendorChange={(vendor, isSmall) => {
              setGpcVendor(vendor);
              setGpcAwardIsSmall(isSmall);
            }}
            initialDecision={gpcDecision?.user_confirmed ? gpcDecision : undefined}
            initialProducts={gpcProducts}
            initialSourceStatus={gpcSourceStatus}
            initialCarpetIiCheck={gpcCarpetIiCheck}
            initialSelectedProductIds={gpcSelectedProductIds}
            // Stale-award self-heal: only a DELIBERATE override (one that has a
            // non-empty override reason — required by the UI when the award is
            // not the lowest-priced) survives reload. With no reason, leave the
            // award id undefined so GPCMandatorySource recomputes the cheapest
            // default and re-persists it via onAwardChange, replacing any stale
            // pre-cheapest-logic award_product_id.
            initialAwardProductId={gpcAwardOverrideReason && gpcAwardOverrideReason.trim() ? gpcAwardProductId : undefined}
            initialAwardOverrideReason={gpcAwardOverrideReason}
            onStateChange={(decision) => setGpcDecision(decision)}
            onProductsChange={(products) => setGpcProducts(products)}
            onSourceStatusChange={(status) => setGpcSourceStatus(status)}
            onCarpetIiCheckChange={setGpcCarpetIiCheck}
            onSelectionChange={(ids) => setGpcSelectedProductIds(ids)}
            onAwardChange={(id, reason) => {
              setGpcAwardProductId(id);
              setGpcAwardOverrideReason(reason);
            }}
            onComplete={({ decision, products }) => {
              setGpcDecision(decision);
              setGpcProducts(products);
              advance(true);
            }}
          />
        );
      case 'quotes':
        return (
          <GPCQuoteCapture
            // Remount ONLY when the Mandatory Source selection changes, so the
            // GPCQuoteCapture useState-initializer can't keep stale rows. The key
            // excludes quote contents so typed prices are preserved during
            // in-stage editing (those are lifted via onQuotesChange).
            key={`gpc-quotes-${[...gpcSelectedProductIds].sort().join('|')}`}
            flowId={flowId}
            initialQuotes={gpcQuotesInitial}
            onStateChange={(qs) => setGpcCompetitionQuotes(qs)}
            onQuotesChange={(qs) => setGpcCompetitionQuotes(qs)}
            onComplete={({ quotes }) => {
              setGpcCompetitionQuotes(quotes);
              advance();
            }}
          />
        );
      case '889':
        return (
          <GPC889Representation
            flowId={flowId}
            vendorName={gpcAwardVendorName || gpcDecision?.recommended_source}
            requirementTitle={gpcBuyParams.requirement_title}
            sourcingPath={gpcThreshold?.sourcingPath ?? null}
            requiredFieldsValidation={requiredFieldsValidationFor('889')}
            initial889={gpc889}
            onComplete={(rep) => {
              setGpc889(rep);
              advance();
            }}
          />
        );
      case 'abstract':
        return (
          <GPCAbstract
            flowId={flowId}
            requirementTitle={gpcBuyParams.requirement_title}
            mfr={gpcMfr}
            priceQuotes={gpcPriceQuotes}
            unitPrice={gpcUnitPrice}
            vendorName={gpcAwardVendorName || gpcDecision?.recommended_source}
            vendorIsSmall={gpcAwardProduct?.is_small_business ?? null}
            decision={gpcDecision}
            threshold={gpcThreshold}
            numItemsDetected={numItemsDetected}
            initialAbstract={gpcAbstract}
            requiredFieldsValidation={requiredFieldsValidationFor('abstract')}
            onStateChange={(abstract) => setGpcAbstract(abstract)}
            onComplete={(abstract) => {
              setGpcAbstract(abstract);
              advance();
            }}
          />
        );
      case 'dd2579':
        return (
          <GPCDd2579
            flowId={flowId}
            decision={gpcDecision}
            buyParams={gpcBuyParams}
            vendorIsSmall={gpcVendorIsSmall}
            notRequiredReason={gpcDd2579Requirement.required ? null : gpcDd2579Requirement.reason}
            initialDd2579={gpcDd2579}
            requiredFieldsValidation={requiredFieldsValidationFor('dd2579')}
            onComplete={(dd2579) => {
              setGpcDd2579(dd2579);
              advance();
            }}
          />
        );
      case 'af_form9':
        return (
          <GPCAfForm9
            flowId={flowId}
            decision={gpcDecision}
            requirementTitle={gpcBuyParams.requirement_title}
            sourceCount={gpcFormStageArgs.sourceCount}
            notRequiredReason={gpcAf9Requirement.required ? null : gpcAf9Requirement.reason}
            initialAfForm9={gpcAfForm9}
            requiredFieldsValidation={requiredFieldsValidationFor('af_form9')}
            onComplete={(af9) => {
              setGpcAfForm9(af9);
              advance();
            }}
          />
        );
      case 'ecars':
        return (
          <GPCApprovalRouting
            flowId={flowId}
            decision={gpcDecision}
            priceQuotes={gpcPriceQuotes}
            initialApproval={gpcApproval}
            onComplete={(approval) => {
              setGpcApproval(approval);
              advance();
            }}
          />
        );
      case 'tax_exempt':
        return (
          <GPCTaxExempt
            flowId={flowId}
            vendorName={gpcAwardVendorName || gpcDecision?.recommended_source}
            initialTaxExempt={gpcTaxExempt}
            requiredFieldsValidation={requiredFieldsValidationFor('tax_exempt')}
            onComplete={(tax) => {
              setGpcTaxExempt(tax);
              advance();
            }}
          />
        );
      case 'receipt':
        return (
          <GPCReceipt
            flowId={flowId}
            requirementTitle={gpcBuyParams.requirement_title}
            vendorName={gpcAwardVendorName || gpcDecision?.recommended_source}
            cardholderName={gpcApproval?.approving_official ?? ''}
            initialReceipt={gpcReceipt}
            requiredFieldsValidation={requiredFieldsValidationFor('receipt')}
            onComplete={(receipt) => {
              setGpcReceipt(receipt);
              advance();
            }}
          />
        );
      case 'iuid':
        return (
          <GPCIuid
            flowId={flowId}
            requirementTitle={gpcBuyParams.requirement_title}
            unitPrice={gpcUnitPrice}
            pilferable={gpcIuid?.pilferable ?? false}
            initialIuid={gpcIuid}
            requiredFieldsValidation={requiredFieldsValidationFor('iuid')}
            onComplete={(iuid) => {
              setGpcIuid(iuid);
              advance();
            }}
          />
        );
      default:
        return null;
    }
  };

  // Shared rating props spread into every <StageWrapper>. The guard handles
  // workflowType values absent from WORKFLOW_STAGE_BASE (e.g. 'largest'): for
  // those, ratingWorkflowType is undefined and the rating footer is omitted.
  const ratingWorkflowType =
    workflowType && (workflowType as string) in WORKFLOW_STAGE_BASE
      ? (workflowType as WorkflowType)
      : undefined;
  const onSampleContinue = () => {
    if (!isViewOnly) return;
    if (workflowType === 'gpc') {
      const next = nextEnabledGpcStage(currentStage + 1, gpcAvailableSet);
      if (next !== null) setCurrentStage(next);
      else setAppPage('flow_selector');
      return;
    }
    if (workflowType === 'complex_services') {
      if (servicesPwsOrigin === 'requirements_generation' && currentStage < 2) {
        setCurrentStage(currentStage + 1);
        return;
      }
      const next = nextEnabledComplexStage(
        servicesPwsOrigin === 'requirements_generation' && currentStage === 2
          ? 4
          : currentStage + 1,
        activeComplexApplicableSet,
      );
      if (next !== null) setCurrentStage(next);
      else setAppPage('flow_selector');
      return;
    }
    if (workflowType === 'market_research') {
      const next = marketResearchStages.findIndex((_, index) => index > currentStage
        && !marketResearchNavigation.disabledStageIndices.includes(index));
      if (next >= 0) setCurrentStage(next);
      return;
    }
    setCurrentStage(currentStage + 1);
  };
  // Only expose a generic continuation when a next stage actually exists.
  const sampleLinearStageCount: Record<string, number> = {
    one_time_construction: (standaloneMode ? standaloneOneTimeConstructionPriceEstimatorStages : activeOneTimeConstructionStages).length,
    construction_sow_generation: generationConstructionStages.length,
    sow_verification: sowStages.length,
    clause_logic: clsStages.length,
    fopr: foprStages.length,
    services_combo: servicesComboStages.length,
    services: (servicesPwsOrigin === 'requirements_generation' ? generatedServicesStages : servicesStages).length,
    services_pws_generation: servicesPwsGenerationStages.length,
  };
  const activeFlowRefreshKey = `${rolePreview}\u0000${flowId ?? ''}\u0000${permissionsRefreshVersion}`;
  const flowNeedsRevalidation = appPage === 'workflow' && !!flowId && !!workflowType
    && (!currentPermissionsLoaded || !canUseWorkflowType(workflowType)
      || (!workflowChatNavigationPending && checkedFlowRefreshKey !== activeFlowRefreshKey
        && backgroundAccessRefreshVersionRef.current !== permissionsRefreshVersion));
  const flowRevalidationOpen = appPage === 'workflow' && !!flowId && !!workflowType
    && (flowNeedsRevalidation || flowRevalidation !== null);
  const sourceWorkflowReadOnly = effectiveIsViewOnly || flowRevalidationOpen
    || (appPage === 'workflow' && !!flowId && !!workflowType && checkedFlowRefreshKey !== activeFlowRefreshKey);
  workflowWriteContextRef.current = {
    flowId,
    allowed: appPage === 'workflow' && currentPermissionsLoaded && !sourceWorkflowReadOnly,
  };
  React.useLayoutEffect(() => {
    documentAssistant.setScope({ user: userEmail ?? '', flowId,
      stage: `${appPage}:${workflowType ?? ''}:${currentStage}`,
      eligible: appPage === 'workflow' && currentPermissionsLoaded && !sourceWorkflowReadOnly,
      documentWorkflow: ['sow_verification', 'construction_sow_generation', 'one_time_construction', 'services', 'services_pws_generation', 'complex_services'].includes(workflowType ?? '')
        && !(workflowType === 'one_time_construction' && standaloneMode),
    });
  }, [documentAssistant.setScope, userEmail, flowId, appPage, workflowType, currentStage, currentPermissionsLoaded, sourceWorkflowReadOnly, standaloneMode]);
  const stageRatingProps = {
    workflowType: ratingWorkflowType,
    // View-only flag for curated training flows — spread into every
    // <StageWrapper> below so all 8 workflow types pass it through to
    // FlowContext (consumed by stages via useFlow().isViewOnly).
    isViewOnly: sourceWorkflowReadOnly,
    readOnlyReason: readOnlyReason ?? (presentationCapabilities.readOnly ? 'role_preview' as const : null),
    isSampleFlow: isTrainingExample,
    onSampleContinue: isTrainingExample && (workflowType === 'gpc' || workflowType === 'complex_services'
      || (workflowType === 'market_research' && marketResearchStages.some((_, index) => index > currentStage
        && !marketResearchNavigation.disabledStageIndices.includes(index)))
      || (workflowType && currentStage + 1 < (sampleLinearStageCount[workflowType] ?? 0)))
      ? onSampleContinue
      : undefined,
    stageRatings,
    onStageRatingChange: (stageId: string, value: RatingValue | null) =>
      setStageRatings((prev) => {
        const next = { ...prev };
        if (value) next[stageId] = value;
        else delete next[stageId];
        return next;
      }),
    onStageThumbsDown: openFeedbackDialog,
  };

  const trainingAccessRevoked = isTrainingPage && currentPermissionsLoaded
    && !permissionsLoadFailed && !canAccessActiveTraining;
  const leaveBlockedWorkflow = () => {
    if (!confirmLeaveWorkflow(
      'savedWorkflows',
      'Workflow access or stage design changed. Leave for Manage Workflows? Any unsaved edits will be lost; saved checkpoints remain available.',
    )) return;
    workflowOperationGenerationRef.current += 1;
    activeFlowRouteRef.current = null;
    setCheckedFlowRefreshKey(null);
    setFlowRevalidation(null);
    if (rolePreviewSyncError) setAuthChecking(true);
    failFlowLoad(flowRevalidation?.message ?? USER_FACING_ERROR_MESSAGES.workflow_unavailable);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastProvider>
      {/* Global Floating Navbar - visible across Home and inside workflows (hidden only during EmailGate) */}
      {!showEmailGate && !authChecking && (
        <>
          <GlobalNavbar
            userEmail={userEmail}
            accountBadgeCount={accountBadgeCount}
            unreadWorkflowNotificationCount={unreadWorkflowNotificationCount}
            accountAnchorEl={accountAnchorEl}
            accountButtonRef={accountButtonRef}
            onAccountOpen={openAccountMenu}
            onAccountClose={closeAccountMenu}
            primaryActions={<>
              {canAccessRaTraining && <Tooltip title="Warfighter Training" arrow>
                <Button aria-label="Warfighter Training" onClick={openRaTraining} startIcon={<SchoolOutlinedIcon />} size="small"
                  sx={{ whiteSpace: 'nowrap', minWidth: { xs: 40, sm: 'auto' }, '& .MuiButton-startIcon': { ml: 0, mr: { xs: 0, sm: 1 } } }}>
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Warfighter Training</Box>
                </Button>
              </Tooltip>}
              {canAccessTraining && (
              <Tooltip title="Training" arrow>
                <IconButton
                  aria-label="Training"
                  color="primary"
                  size="medium"
                  onClick={() => openTraining('navbar')}
                >
                  <SchoolOutlinedIcon />
                </IconButton>
              </Tooltip>
              )}
            </>}
            accountMenuItems={
              <>
                <MenuItem
                  onClick={() => {
                    closeAccountMenu();
                    setWorkflowNotificationFlow(null);
                    setWorkflowNotificationsOpen(true);
                  }}
                >
                  Notifications
                  <Badge
                    color="error"
                    max={99}
                    badgeContent={unreadWorkflowNotificationCount}
                    invisible={unreadWorkflowNotificationCount === 0}
                    sx={{ ml: 2 }}
                  />
                </MenuItem>
                <MenuItem onClick={() => openPeopleChats()}>
                  People chats
                  <Badge
                    color="error"
                    max={99}
                    badgeContent={contactChatInbox.unreadCount}
                    invisible={contactChatInbox.unreadCount === 0}
                    sx={{ ml: 2 }}
                  />
                </MenuItem>
                <MenuItem
                  onClick={() => openSupportIssues('customer')}
                >
                  My feedback
                  <Badge
                    color="error"
                    max={99}
                    badgeContent={unreadSupportIssueCount}
                    invisible={unreadSupportIssueCount === 0}
                    sx={{ ml: 2 }}
                  />
                </MenuItem>
                {presentationCapabilities.showSuperAdminControls && (
                  <MenuItem
                    onClick={() => openSupportIssues('admin')}
                  >
                    User Feedback
                    <Badge
                      color="error"
                      max={99}
                      badgeContent={unreadSupportIssueCount + (presentationCapabilities.showSuperAdminControls ? unreadFeedbackCount : 0)}
                      invisible={unreadSupportIssueCount + (presentationCapabilities.showSuperAdminControls ? unreadFeedbackCount : 0) === 0}
                      sx={{ ml: 2 }}
                    />
                  </MenuItem>
                )}
                {presentationCapabilities.showUsageDashboard && (
                  <MenuItem onClick={() => { closeAccountMenu(); setSupportIssueView(null); setShowManageUsers(false); setShowDashboard(true); }}>
                    Usage Dashboard
                  </MenuItem>
                )}
                {presentationCapabilities.showAdminNavigation && (
                  <MenuItem onClick={() => { closeAccountMenu(); setSupportIssueView(null); setShowDashboard(false); setShowManageUsers(true); }}>
                    Manage Users
                    <Badge
                      color="error"
                      max={99}
                      badgeContent={pendingCount}
                      invisible={!presentationCapabilities.showAdminNavigation || pendingCount === 0}
                      sx={{ ml: 2 }}
                    />
                  </MenuItem>
                )}
                {canShowRolePreviewControl({ isSuperAdmin: realIsSuperAdmin }) && (
                  <MenuItem onClick={openRolePreviewDialog}>
                    View app as
                  </MenuItem>
                )}
                <AccountChangelogMenuItem
                  allowChangelog={presentationCapabilities.allowChangelog}
                  onOpen={() => { closeAccountMenu(); setShowChangelog(true); }}
                />
                <MenuItem onClick={() => { closeAccountMenu(); handleChangeEmail(); }}>
                  Logout
                </MenuItem>
              </>
            }
            onHome={handleLogoHome}
            onManageWorkflows={() => {
              if (appPage === 'workflow') {
                if (!confirmLeaveWorkflow('savedWorkflows')) return;
                workflowOperationGenerationRef.current += 1;
                setFlowSelectorScope(workflowType);
              } else {
                setFlowSelectorScope(null);
              }
              setShowDashboard(false);
              setShowManageUsers(false);
              setSupportIssueView(null);
              setAppPage('flow_selector');
            }}
            onOpenFeedback={openFeedbackDialog}
          />
              <Dialog
                open={rolePreviewDialogOpen}
                onClose={closeRolePreviewDialog}
                aria-labelledby="role-preview-dialog-title"
                aria-describedby="role-preview-dialog-description"
                fullWidth
                maxWidth="sm"
              >
                <DialogTitle id="role-preview-dialog-title">View app as</DialogTitle>
                <DialogContent>
                  <DialogContentText id="role-preview-dialog-description">
                    Choose a role to use with your own workflows and account data. Available actions follow that role's permissions.
                  </DialogContentText>
                  <RadioGroup
                    aria-label="Role to preview"
                    name="role-preview"
                    value={pendingRolePreview}
                    onChange={(event) => setPendingRolePreview(event.target.value as RolePreview)}
                    sx={{ mt: 2 }}
                  >
                    {ROLE_PREVIEW_OPTIONS.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio />}
                        label={option.label}
                      />
                    ))}
                  </RadioGroup>
                </DialogContent>
                <DialogActions>
                  <Button onClick={closeRolePreviewDialog} disabled={rolePreviewBusy}>Cancel</Button>
                  <Button variant="contained" onClick={() => { void applyRolePreview(); }} disabled={rolePreviewBusy}>View as</Button>
                </DialogActions>
              </Dialog>
              <WorkflowNotificationsPanel
                open={workflowNotificationsOpen}
                readOnly={presentationCapabilities.readOnly}
                canAccessMarketResearch={canAccessMarketResearchNotifications(
                  workflowPermissionsLoaded,
                  presentationAllowedWorkflowTypes,
                )}
                anchorEl={accountButtonRef.current}
                flowId={workflowNotificationFlow?.id}
                workflowName={workflowNotificationFlow?.name}
                onClose={() => {
                  setWorkflowNotificationsOpen(false);
                  window.setTimeout(() => accountButtonRef.current?.focus(), 0);
                }}
                onOpenStage={handleOpenWorkflowNotificationStage}
                onOpenContinuedChat={(chat) => {
                  setAppPage('type_selector');
                  window.setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('home-chat:open-workflow-only', { detail: chat }));
                  }, 0);
                }}
                contactChatNotifications={contactChatInbox.conversations.filter(item => item.unread_count > 0)}
                onOpenContactChat={id => openPeopleChats(id)}
                onChanged={refreshWorkflowNotificationUnreadCount}
              />
              <MarketResearchNotifyShareDialog
                readOnly={!canWriteCurrentWorkflow()}
                flowId={marketResearchShareDialogContext ? flowId : null}
                context={marketResearchShareDialogContext}
                open={marketResearchShareDialogContext !== null}
                onClose={() => setMarketResearchShareDialogContext(null)}
              />
              <ChangelogDialog open={showChangelog} onClose={() => setShowChangelog(false)} />
          {isRolePreview && (
            <Alert
              severity="info"
              role="status"
              sx={{ borderRadius: 0, justifyContent: 'center' }}
              action={<Button color="inherit" size="small" onClick={() => { void changeRolePreview('super_admin'); }} disabled={rolePreviewBusy}>Return to Super Admin</Button>}
            >
              Viewing as: {ROLE_PREVIEW_OPTIONS.find((option) => option.value === rolePreview)?.label}. Actions use this role's permissions with your account data.
            </Alert>
          )}
        </>
      )}

      {/* Feedback dialog. The textarea has a fixed comfortable default size; the
          expand toggle in the title widens the dialog (sm→md) and grows the box
          (4→16 rows) without a jittery auto-resize. Text is preserved across
          toggles. */}
      {(() => {
        const fbCtx = feedbackContext ?? getFeedbackContext();
        const FB_ROWS = feedbackExpanded ? 16 : 4;
        return (
          <Dialog
            open={feedbackOpen}
            onClose={() => !feedbackSending && clearFeedbackDialog()}
            maxWidth={feedbackExpanded ? 'md' : 'sm'}
            fullWidth
            // Anchor to the top-right corner, just below the two-row navbar feedback button,
            // instead of the default centered modal position. zIndex 2301 sits just
            // ABOVE the homepage tour overlay (2300) so the form's Cancel button stays
            // clickable, while TYING with the tour tooltip (overlay+1 = 2301). The
            // tooltip renders later in the DOM, so at an equal zIndex it still paints
            // ON TOP of this dialog — keeping the tour bubble bright and its Next
            // button clickable. Same approach as the What's-new step (whatsNewStep /
            // dialogZIndex={2301}). A higher value (e.g. 2401) would let this dialog's
            // backdrop cover the bubble, dimming it and swallowing its clicks.
            sx={{ zIndex: 2301, '& .MuiDialog-container': { alignItems: 'flex-start', justifyContent: 'flex-end' } }}
            PaperProps={{
              'data-tour-id': 'feedback-dialog',
              sx: { m: 0, mt: { xs: '86px', sm: '72px' }, mr: 2 },
            } as PaperProps & { 'data-tour-id': string }}
          >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              Send Feedback
              <Tooltip title={feedbackExpanded ? 'Shrink' : 'Expand'}>
                <IconButton
                  size="small"
                  onClick={() => setFeedbackExpanded((v) => !v)}
                  aria-label={feedbackExpanded ? 'Shrink feedback box' : 'Expand feedback box'}
                >
                  {feedbackExpanded ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </DialogTitle>
            <DialogContent>
              <Stack>
                <Typography sx={{ fontSize: 14 }} color="text.secondary">
                  You’re sending feedback about {fbCtx.workflow}
                  {fbCtx.stage ? `, ${fbCtx.stage}` : ''}.
                </Typography>
              </Stack>
              <TextField
                autoFocus
                multiline
                minRows={FB_ROWS}
                maxRows={FB_ROWS}
                fullWidth
                sx={{ mt: 1 }}
                placeholder="Tell us what you think…"
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                inputProps={{ maxLength: 5000 }}
                disabled={feedbackSending}
              />
              <FeedbackAttachmentPicker
                key={feedbackAttachmentPickerKey}
                files={feedbackAttachments}
                onChange={setFeedbackAttachments}
                disabled={feedbackSending}
              />
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between' }}>
              <Button
                size="small"
                startIcon={<AssignmentOutlinedIcon />}
                onClick={() => {
                  clearFeedbackDialog();
                  openSupportIssues('customer');
                }}
                sx={{ textTransform: 'none' }}
              >
                View previous feedback
              </Button>
              <Stack direction="row" spacing={1}>
                <Button onClick={clearFeedbackDialog} disabled={feedbackSending}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  disabled={!feedbackText.trim() || feedbackSending}
                  onClick={handleSubmitFeedback}
                >
                  {feedbackSending ? 'Sending…' : 'Send'}
                </Button>
              </Stack>
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* Keep the pre-auth placeholder out of AnimatePresence. An exit that
          stalls would otherwise leave this empty page covering every route. */}
      {authChecking ? (
        rolePreviewSyncError ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ mb: 2 }}>Unable to verify the active role view.</Typography>
            <Button variant="contained" onClick={() => setRolePreviewRetry((value) => value + 1)}>
              Retry
            </Button>
          </Box>
        ) : <Box />
      ) : <Transition
        transitionKey={
          supportIssueView
            ? `supportIssues-${supportIssueView}`
            : showDashboard && !showManageUsers
            ? 'dashboard'
            : showManageUsers
            ? 'manage_users'
            : showEmailGate && !userEmail
            ? 'email_gate'
            : appPage
        }
      >
      {showEmailGate && !userEmail ? (
        // Pre-auth login gate (full-screen MUI Dialog modal). Intentionally
        // rendered OUTSIDE the sticky-footer shell — site chrome (footer) does
        // not belong on the login overlay and would clutter the centered modal.
        <EmailGate
          open={true}
          onAuthSuccess={handleAuthSuccess}
        />
      ) : (
        // App-shell sticky-footer: a viewport-filling flex column shared by
        // EVERY post-auth page (Usage Dashboard, homepage type_selector,
        // flow_selector, and the active workflow). The content area grows
        // (`flex: 1`) so the single <Footer /> (natural height, last child) is
        // pinned to the bottom when content is short and pushed below the fold
        // when content is tall (no overlap). The navbar supplies an in-flow
        // spacer, so subtract its height to avoid scrolling a short page.
        <Box sx={{ minHeight: { xs: `calc(100dvh - ${GLOBAL_NAVBAR_HEIGHT.xs}px)`, sm: `calc(100vh - ${GLOBAL_NAVBAR_HEIGHT.sm}px)` }, display: 'flex', flexDirection: 'column' }}>
        {/* Allow responsive descendants such as the Contract Tracker table to
            shrink within the column shell instead of widening this shared
            content area (and consequently the footer). */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
        <ErrorBoundary key={`${appPage}:${supportIssueView ?? ''}:${showDashboard}:${showManageUsers}`}>
        <Suspense fallback={<Box role="status" sx={{ p: 4 }}>Loading workspace…</Box>}>
      {supportIssueView === 'customer' ? (
        <SupportIssuesPage
          onBack={() => {
            setSupportIssueView(null);
            refreshSupportIssueUnreadCount();
          }}
          onUnreadCountChange={setUnreadSupportIssueCount}
        />
      ) : supportIssueView === 'admin' && presentationCapabilities.showSuperAdminControls ? (
        <SupportIssuesPage
          adminView
          developerView={presentationCapabilities.showUserFeedback}
          superAdminView={presentationCapabilities.showSuperAdminControls}
          onChatUnreadCountChange={setUnreadFeedbackCount}
          onFeedbackSourceInteraction={recordUserFeedbackSource}
          onBack={() => setSupportIssueView(null)}
        />
      ) : showDashboard && presentationCapabilities.showUsageDashboard && !showManageUsers ? (
        <UsageDashboard
          onBack={() => { setShowDashboard(false); }}
          currentUserEmail={userEmail ?? undefined}
          isSuperAdmin={presentationCapabilities.showSuperAdminControls}
          readOnly={presentationCapabilities.readOnly}
        />
      ) : showManageUsers && presentationCapabilities.showAdminNavigation ? (
        <ManageUsers
          onBack={() => { setShowManageUsers(false); if (presentationCapabilities.showAdminNavigation) refreshPendingCount(); }}
          isSuperAdmin={presentationCapabilities.showSuperAdminControls}
          onRequestsChanged={refreshPendingCount}
        />
      ) : appPage === 'type_selector' ? (
        <WorkflowTypeSelectorPage
          canAccessRaTraining={showRaTrainingTile}
          onOpenRaTraining={openRaTraining}
          canAccessTraining={canAccessTraining}
          onOpenTrainingTile={() => openTraining('recent_tile')}
          onOpenTrainingStarter={() => openTraining('chat_starter')}
          onOpenTrainingSuggestion={() => openTraining('chat_suggestion')}
          ref={typeSelectorRef}
          onSelect={(type) => {
            if (!canUseWorkflowType(type)) return;
            setFlowSelectorScope(type);
            setAppPage('flow_selector');
          }}
          allowedWorkflowTypes={presentationAllowedWorkflowTypes}
          examplesAccessScope={currentPermissionsLoaded ? currentPermissionsScope : 'loading'}
          canUseChatbot={currentPermissionsLoaded && !isDcmaOnly && presentationCapabilities.allowChatbot}
          homeChatMode={currentPermissionsLoaded ? homeChatMode : 'none'}
          workflowOnlyAudience={currentPermissionsLoaded ? workflowOnlyAudience : null}
          readOnly={presentationCapabilities.readOnly}
          allowDcmaPbp={canUseDcmaPbp}
          allowDcmaCrr={canUseDcmaCrr}
          marketResearchCustomerView={presentationCapabilities.marketResearchView === 'customer'}
          onOpenWorkflow={handleOpenGpcWorkflowFromChat}
          onOpenMrWorkflow={handleOpenMrWorkflowFromChat}
          onOpenConstructionSowWorkflow={handleOpenGeneratedConstructionSowFromChat}
          onOpenGeneratedServicesPwsWorkflow={handleOpenGeneratedServicesPwsFromChat}
          onOpenNavigationHandle={handleOpenSimilarRequirementsNavigation}
          onOpenCreatedWorkflow={handleOpenCreatedWorkflowFromChat}
          onStartWorkflowType={handleStartWorkflowTypeFromHomeChat}
          onOpenRecentWorkflow={(
            selectedFlowId,
            selectedFlowName,
            knownWorkflowType,
          ) => {
            void handleSelectFlow(
              selectedFlowId,
              selectedFlowName,
              knownWorkflowType ? { knownWorkflowType } : undefined,
            );
          }}
          onViewAllWorkflows={() => {
            setFlowSelectorScope(null);
            setAppPage('flow_selector');
          }}
          onOpenWorkflowNotifications={(selectedFlowId, selectedFlowName) => {
            setWorkflowNotificationFlow({ id: selectedFlowId, name: selectedFlowName });
            setWorkflowNotificationsOpen(true);
          }}
          userEmail={userEmail}
          onFeedbackSourceInteraction={recordHomepageFeedbackSource}
        />
      ) : appPage === 'flow_selector' ? (
        <FlowSelector
          onSelectFlow={(selectedFlowId, selectedFlowName, knownWorkflowType) =>
            handleSelectFlow(
              selectedFlowId,
              selectedFlowName,
              knownWorkflowType ? { knownWorkflowType } : undefined,
            )
          }
          onOpenWorkflowNotificationTarget={handleOpenWorkflowNotificationStage}
          onOpenContinuedChat={(chat) => {
            setAppPage('type_selector');
            window.setTimeout(() => {
              window.dispatchEvent(new CustomEvent('home-chat:open-workflow-only', { detail: chat }));
            }, 0);
          }}
          onCreateNew={handleCreateNewFlow}
          initialWorkflowTypeFilter={flowSelectorScope ?? 'all'}
          allowedWorkflowTypes={presentationAllowedWorkflowTypes}
          workflowPermissionsLoaded={currentPermissionsLoaded}
          allowDcmaPbp={canUseDcmaPbp}
          allowDcmaCrr={canUseDcmaCrr}
          canUseStandalonePriceEstimator={presentationCapabilities.canUseStandalonePriceEstimator}
          canUseProjectTracker={currentPermissionsLoaded && projectTrackerCapability.allowed && !!projectTrackerCapability.unit && !!projectTrackerCapability.revision}
          projectTrackerScopeKey={`${currentPermissionsScope}:${projectTrackerCapability.revision}:${projectTrackerCapability.unit}`}
          projectTrackerPreferenceKey={JSON.stringify([currentPermissionsScope, projectTrackerCapability.unit?.trim().toLowerCase()])}
          projectTrackerUnit={projectTrackerCapability.unit}
          onProjectTrackerScopeChange={refreshProjectTrackerScope}
          marketResearchCustomerView={presentationCapabilities.marketResearchView === 'customer'}
          openCreateRequest={openCreateRequest}
          onOpenCreateForWorkflowTypeConsumed={() => setOpenCreateRequest(null)}
          onCreateDialogClosed={() => {
            setPendingWorkflowChatSessionId(null);
            setPendingWorkflowHandoffReceipt(null);
          }}
          homeChatSessionId={pendingWorkflowChatSessionId}
          workflowHandoffReceipt={pendingWorkflowHandoffReceipt}
          authGeneration={authGenerationRef.current}
          currentUserEmail={userEmail}
          isSuperAdmin={presentationCapabilities.showAllWorkflows}
          isAdmin={presentationCapabilities.showAdminNavigation}
          rolePreview={rolePreview}
          readOnly={presentationCapabilities.readOnly}
          onHome={() => { setAppPage('type_selector'); setFlowSelectorScope(null); }}
        />
      ) : appPage === 'people_chats' ? (
        <PeopleChats key={contactChatScope} scopeKey={contactChatScope} selectedConversationId={peopleConversationId} onSelect={setPeopleConversationId} onBack={() => setAppPage('type_selector')} readOnly={isRolePreview} />
      ) : appPage === 'ra_training' ? (
        !currentPermissionsLoaded || permissionsLoadFailed || rolePreviewSyncError ? (
          <Box role="status" sx={{ p: 4 }}>Confirming Warfighter Training access…</Box>
        ) : !canAccessRaTraining || flowRevalidation ? (
          <Container sx={{ py: 4 }}>
            <Alert severity="warning">Warfighter Training access is unavailable for your account.</Alert>
          </Container>
        ) : <ResourceAdvisorTraining key={`${userEmail}:${rolePreview}`} openingId={raTrainingOpeningId} scopeKey={currentPermissionsScope}
          canStartWorkflows={currentPermissionsLoaded && !presentationCapabilities.readOnly && !isRolePreview}
          allowedWorkflowTypes={missionPartnerResearchUnavailable
            ? (presentationAllowedWorkflowTypes || ['gpc', 'one_time_construction']).filter(type => type !== 'market_research')
            : presentationAllowedWorkflowTypes}
          onStartWorkflow={action => {
            if (!currentPermissionsLoaded || presentationCapabilities.readOnly || isRolePreview || !canUseWorkflowType(action.workflow_type)) return;
            if (action.workflow_type === 'market_research' && missionPartnerResearchUnavailable) return;
            handleStartWorkflowTypeFromHomeChat(action.workflow_type, action.entry_mode ? { workflowEntryMode: action.entry_mode } : undefined);
          }} />
      ) : appPage === 'training' ? (
        <TrainingApp key={trainingOpeningId ?? 'training'} userEmail={userEmail ?? ''} onHome={leaveTraining} onFeedbackContextChange={setTrainingFeedbackContext} onOverviewReady={() => {
          if (trainingOpeningId) trainingEntryTrackerRef.current.overviewReady(trainingOpeningId);
        }} />
      ) : (
        <WorkflowChatCompanion
          key={flowId ?? 'workflow-chat-companion'}
          conversationRevision={workflowChatMountRevision}
          enabled={workflowChatLayoutRef.current?.flowId === flowId
            && workflowChatLayoutRef.current.enabled && !effectiveIsViewOnly}
          authorized={currentPermissionsLoaded && !isDcmaOnly
            && presentationCapabilities.allowChatbot && !flowRevalidationOpen && !effectiveIsViewOnly}
          sessionId={workflowChatSessionId}
          visible={currentPermissionsLoaded && !isDcmaOnly
            && presentationCapabilities.allowChatbot
            && workflowChatVisible && !workflowChatNavigationPending && !effectiveIsViewOnly}
          workflowName={flowName}
          pageContext={workflowChatPageContext}
          onClose={handleWorkflowChatClose}
          onOpen={handleWorkflowChatOpen}
          userKey={userEmail ?? undefined}
          flowId={flowId}
          onSessionIdChange={handleWorkflowChatSessionChange}
          onTurnStart={handleWorkflowChatTurnStart}
          onOpenWorkflow={handleOpenGpcWorkflowFromChat}
          onOpenMrWorkflow={handleOpenMrWorkflowFromChat}
          onOpenGeneratedServicesPwsWorkflow={handleOpenGeneratedServicesPwsFromChat}
          onOpenNavigationHandle={handleOpenSimilarRequirementsNavigation}
          onOpenCreatedWorkflow={handleOpenCreatedWorkflowFromChat}
          onStartWorkflowType={handleStartWorkflowTypeFromHomeChat}
          onBeforeWorkflowNavigation={confirmWorkflowNavigation}
        >
      {workflowType === 'dcma_crr' ? (
        canUseDcmaCrr && flowId ? <DcmaCrrWorkflow key={`${userEmail ?? ''}:${flowId}`} flowId={flowId} workflowName={flowName} readOnly={sourceWorkflowReadOnly || flowRevalidationOpen} /> : null
      ) : workflowType === 'dcma_pbp' ? (
        <DcmaPbpWorkflowGate
          key={`${userEmail ?? ''}:${flowId ?? ''}`}
          allow={canUseDcmaPbp || (flowRevalidationOpen
            && verifiedDcmaFlowRef.current === flowId)}
          flowId={flowId}
          workflowName={flowName}
          currentUserEmail={userEmail ?? undefined}
          readOnly={sourceWorkflowReadOnly} readOnlyReason={readOnlyReason}
        />
      ) : workflowType === 'clause_logic' && isComplete ? (
        // CLS workflow completion screen
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Clause Logic Complete!
            </Typography>
            <Box display="flex" gap={2} justifyContent="center">
              <Button
                variant="outlined"
                startIcon={<RestartAltIcon />}
                onClick={() => {
                  setIsComplete(false);
                  setClsCorrectionResult(null);
                  setCurrentStage(0);
                }}
                size="large"
              >
                Start New Correction
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setIsComplete(false);
                  setAppPage('type_selector');
                  setFlowSelectorScope(null);
                  setWorkflowType(null);
                  resetPipelineState();
                }}
                size="large"
              >
                Return to Homepage
              </Button>
            </Box>
          </Paper>
        </Container>
      ) : workflowType === 'other_transactions' ? (
        flowId && <OtherTransactionsWorkflow
          key={flowId}
          flowId={flowId}
          workflowName={flowName}
          currentStage={currentStage}
          onStageChange={index => { setCurrentStage(index); setMaxStageReached(previous => Math.max(previous, index)); }}
          onBack={() => currentStage > 0 ? setCurrentStage(currentStage - 1) : setAppPage('flow_selector')}
          readOnly={sourceWorkflowReadOnly}
          stageWrapperProps={stageRatingProps}
          onStagesChange={handleOtherTransactionsStagesChange}
        />
      ) : workflowType === 'post_award_actions' ? (
        <StageWrapper
          config={{
            id: postAwardStages[currentStage]?.id || 'post-award-intake',
            name: postAwardStages[currentStage]?.name || 'Contract & Package Intake',
            description: postAwardStages[currentStage]?.description || '',
          }}
          currentStage={currentStage}
          totalStages={postAwardStages.length}
          stages={postAwardStages.map((stage) => stage.name)}
          workflowName={flowName}
          workflowId={flowId}
          numItemsDetected={numItemsDetected}
          {...stageRatingProps}
          fullWidth
          onBack={() => {
            if (currentStage > 0) {
              const previous = currentStage - 1;
              localStorage.setItem(`post_award_stage_${flowId}`, String(previous));
              setCurrentStage(previous);
            } else {
              setAppPage('flow_selector');
            }
          }}
          onStageSelect={(index) => {
            // The three post-award pages are a compact working set. Keep each
            // top-level page directly reachable; incomplete pages explain the
            // next required input instead of hiding stepper navigation.
            localStorage.setItem(`post_award_stage_${flowId}`, String(index));
            setCurrentStage(index);
          }}
        >
          <PostAwardActionWorkspace
            key={flowId ?? 'new-post-award-flow'}
            flowId={flowId ?? undefined}
            resumeFlowId={postAwardResumeFlowId === flowId ? flowId ?? undefined : undefined}
            stageIndex={currentStage}
            onStageChange={(index) => {
              localStorage.setItem(`post_award_stage_${flowId}`, String(index));
              setMaxStageReached(previous => Math.max(previous, index));
              setCurrentStage(index);
            }}
            onOpenLinkedRecompete={(successorFlowId) => {
              void handleSelectFlow(successorFlowId, undefined, { knownWorkflowType: 'complex_services' });
            }}
          />
        </StageWrapper>
      ) : workflowType === 'clause_logic' ? (
        // CLS workflow rendering - 3 stages
        <StageWrapper
          config={{
            id: clsStages[currentStage]?.id || 'cls-upload',
            name: clsStages[currentStage]?.name || 'Document Upload',
            description: clsStages[currentStage]?.description || '',
          }}
          currentStage={currentStage}
          totalStages={clsStages.length}
          stages={clsStages.map(s => s.name)}
          workflowName={flowName}
          workflowId={flowId}
          numItemsDetected={numItemsDetected}
          {...stageRatingProps}
          onBack={() => {
            if (currentStage > 0) {
              setCurrentStage(currentStage - 1);
            }
          }}
          onStageSelect={(index) => {
            // View-only / training flows are fully read-only with all stage
            // data present in storage, so the stepper must always navigate to
            // any stage regardless of cache/completion gating.
            if (effectiveIsViewOnly) {
              setCurrentStage(index);
              return;
            }
            if (index <= maxStageReached ||
                (index === 1 && clsCorrectionResult) ||
                (index === 2 && clsCorrectionResult && (clsUserOverrides.size > 0 || clsHiddenClauses.size > 0 || clsManuallyAddedClauses.length > 0 || maxStageReached >= 2))) {
              setCurrentStage(index);
            } else {
              showNotificationMessage('Please complete the previous stages first', 'info');
            }
          }}
        >
          {renderCLSStage()}
        </StageWrapper>
      ) : workflowType === 'sow_verification' ? (
        // SOW Verification workflow rendering - 3 stages
        <StageWrapper
          config={{
            id: sowStages[currentStage]?.id || 'sow-upload',
            name: sowStages[currentStage]?.name || 'Document Upload',
            description: sowStages[currentStage]?.description || '',
          }}
          currentStage={currentStage}
          totalStages={sowStages.length}
          stages={sowStages.map(s => s.name)}
          workflowName={flowName}
          workflowId={flowId}
          numItemsDetected={numItemsDetected}
          {...stageRatingProps}
          onBack={() => {
            if (currentStage === 2 && !effectiveIsViewOnly) {
              setSowBackTarget(1);
              // Stage 2 is Edit mode - show warning dialog before going back
              setShowSowBackDialog(true);
            } else if (currentStage > 0) {
              localStorage.setItem(`sow_stage_${flowId}`, String(currentStage - 1));
              setCurrentStage(currentStage - 1);
            }
          }}
          onStageSelect={(index) => {
            // View-only / training flows are fully read-only with all stage
            // data present in storage, so the stepper must always navigate to
            // any stage regardless of cache/completion gating (and without the
            // edit-mode back-confirmation, which has nothing to lose here).
            if (effectiveIsViewOnly) {
              localStorage.setItem(`sow_stage_${flowId}`, String(index));
              setCurrentStage(index);
              return;
            }
            if (shouldConfirmOneTimeConstructionStepperBack(
              constructionSowOrigin,
              currentStage,
              index,
            )) {
              setSowBackTarget(index === 0 ? 0 : 1);
              setShowSowBackDialog(true);
            } else if (index <= maxStageReached) {
              localStorage.setItem(`sow_stage_${flowId}`, String(index));
              setCurrentStage(index);
            } else {
              showNotificationMessage('Please complete the previous stages first', 'info');
            }
          }}
        >
          {renderSOWStage()}
        </StageWrapper>
      ) : workflowType === 'wage_determination' ? (
        // Wage Determination workflow rendering - single page
        <StageWrapper
          config={{
            id: wdStages[currentStage]?.id || 'wd',
            name: wdStages[currentStage]?.name || 'Wage Determination',
            description: wdStages[currentStage]?.description || '',
          }}
          currentStage={currentStage}
          totalStages={wdStages.length}
          stages={wdStages.map(s => s.name)}
          workflowName={flowName}
          workflowId={flowId}
          numItemsDetected={numItemsDetected}
          {...stageRatingProps}
          onBack={() => {
            if (currentStage > 0) {
              setCurrentStage(currentStage - 1);
            }
          }}
          onStageSelect={(index) => {
            // View-only / training flows are fully read-only with all stage
            // data present in storage, so the stepper must always navigate to
            // any stage regardless of cache/completion gating.
            if (effectiveIsViewOnly) {
              setCurrentStage(index);
              return;
            }
            if (index <= maxStageReached) {
              setCurrentStage(index);
            } else {
              showNotificationMessage('Please complete the previous stages first', 'info');
            }
          }}
        >
          {renderWageDetStage()}
        </StageWrapper>
      ) : workflowType === 'pnm' ? (
        // PNM workflow rendering - 2 stages
        <StageWrapper
          config={{
            id: pnmStages[currentStage]?.id || 'pnm-upload',
            name: pnmStages[currentStage]?.name || 'Document Upload',
            description: pnmStages[currentStage]?.description || '',
          }}
          currentStage={currentStage}
          totalStages={pnmStages.length}
          stages={pnmStages.map(s => s.name)}
          workflowName={flowName}
          workflowId={flowId}
          numItemsDetected={numItemsDetected}
          {...stageRatingProps}
          onBack={() => {
            if (currentStage > 0) {
              setCurrentStage(currentStage - 1);
            }
          }}
          onStageSelect={(index) => {
            // View-only / training flows are fully read-only with all stage
            // data present in storage, so the stepper must always navigate to
            // any stage regardless of cache/completion gating.
            if (effectiveIsViewOnly) {
              setCurrentStage(index);
              return;
            }
            if (index <= maxStageReached) {
              setCurrentStage(index);
            } else {
              showNotificationMessage('Please complete the previous stages first', 'info');
            }
          }}
        >
          {renderPNMStage()}
        </StageWrapper>
      ) : workflowType === 'fopr' ? (
        // FOPR workflow rendering - 2 stages
        <StageWrapper
          config={{
            id: foprStages[currentStage]?.id || 'fopr-upload',
            name: foprStages[currentStage]?.name || 'Document Upload',
            description: foprStages[currentStage]?.description || '',
          }}
          currentStage={currentStage}
          totalStages={foprStages.length}
          stages={foprStages.map(s => s.name)}
          workflowName={flowName}
          workflowId={flowId}
          numItemsDetected={numItemsDetected}
          {...stageRatingProps}
          onBack={() => {
            if (currentStage > 0) {
              setCurrentStage(currentStage - 1);
            }
          }}
          onStageSelect={(index) => {
            // View-only / training flows are fully read-only with all stage
            // data present in storage, so the stepper must always navigate to
            // any stage regardless of cache/completion/standalone gating.
            if (effectiveIsViewOnly) {
              setCurrentStage(index);
              return;
            }
            if (standaloneMode && index < 2) {
              showNotificationMessage('Previous stages are not available in standalone mode.', 'info');
              return;
            }
            if (index <= maxStageReached) {
              setCurrentStage(index);
            } else {
              showNotificationMessage('Please complete the previous stages first', 'info');
            }
          }}
          standaloneMode={standaloneMode}
          standaloneStartIndex={2}
        >
          {renderFOPRStage()}
        </StageWrapper>
      ) : workflowType === 'services_combo' ? (
        // Services COMBO (Combined Synopsis/Solicitation) workflow rendering - 2 stages
        <StageWrapper
          config={{
            id: servicesComboStages[currentStage]?.id || 'services-combo-upload',
            name: servicesComboStages[currentStage]?.name || 'Document Upload',
            description: servicesComboStages[currentStage]?.description || '',
          }}
          currentStage={currentStage}
          totalStages={servicesComboStages.length}
          stages={servicesComboStages.map(s => s.name)}
          workflowName={flowName}
          workflowId={flowId}
          numItemsDetected={numItemsDetected}
          {...stageRatingProps}
          onBack={() => {
            if (currentStage > 0) {
              setCurrentStage(currentStage - 1);
            }
          }}
          onStageSelect={(index) => {
            // View-only / training flows are fully read-only with all stage
            // data present in storage, so the stepper must always navigate to
            // any stage regardless of cache/completion gating.
            if (effectiveIsViewOnly) {
              setCurrentStage(index);
              return;
            }
            if (index <= maxStageReached) {
              setCurrentStage(index);
            } else {
              showNotificationMessage('Please complete the previous stages first', 'info');
            }
          }}
        >
          {renderServicesComboStage()}
        </StageWrapper>
      ) : workflowType === 'services' ? (
        // Services has immutable uploaded-PWS and requirements-generation
        // entry paths. The generated path replaces the review prefix while
        // retaining the existing downstream authoring stages.
        <StageWrapper
          config={{
            id: (servicesPwsOrigin === 'requirements_generation' ? generatedServicesStages : servicesStages)[currentStage]?.id || 'services-sow-upload',
            name: (servicesPwsOrigin === 'requirements_generation' ? generatedServicesStages : servicesStages)[currentStage]?.name || 'PWS Review',
            description: (servicesPwsOrigin === 'requirements_generation' ? generatedServicesStages : servicesStages)[currentStage]?.description || '',
          }}
          currentStage={currentStage}
          totalStages={(servicesPwsOrigin === 'requirements_generation' ? generatedServicesStages : servicesStages).length}
          stages={(servicesPwsOrigin === 'requirements_generation' ? generatedServicesStages : servicesStages).map(s => s.name)}
          workflowName={flowName}
          workflowId={flowId}
          numItemsDetected={numItemsDetected}
          {...stageRatingProps}
          onBack={() => {
            if (!effectiveIsViewOnly && servicesPwsOrigin === 'existing_pws' && currentStage === 3) {
              setShowServicesBackDialog(true);
            } else if (currentStage > 0) {
              localStorage.setItem(`services_stage_${flowId}`, String(currentStage - 1));
              setCurrentStage(currentStage - 1);
            }
          }}
          onStageSelect={(index) => {
            // View-only / training flows are fully read-only with all stage
            // data present in storage, so the stepper must always navigate to
            // any stage regardless of cache/completion gating (and without the
            // edit-mode back-confirmation, which has nothing to lose here).
            if (effectiveIsViewOnly) {
              localStorage.setItem(`services_stage_${flowId}`, String(index));
              setCurrentStage(index);
              return;
            }
            if (servicesPwsOrigin === 'existing_pws' && index < currentStage && currentStage === 3) {
              setShowServicesBackDialog(true);
            } else if (index <= maxStageReached) {
              localStorage.setItem(`services_stage_${flowId}`, String(index));
              setCurrentStage(index);
            } else {
              showNotificationMessage('Please complete the previous stages first', 'info');
            }
          }}
        >
          {servicesPwsOrigin === 'requirements_generation' ? renderGeneratedServicesStage() : renderServicesStage()}
        </StageWrapper>
      ) : workflowType === 'one_time_construction' ? (
        <StageWrapper
          config={{
            id: (standaloneMode ? standaloneOneTimeConstructionPriceEstimatorStages : activeOneTimeConstructionStages)[currentStage]?.id || 'construction-sow-upload',
            name: (standaloneMode ? standaloneOneTimeConstructionPriceEstimatorStages : activeOneTimeConstructionStages)[currentStage]?.name || 'Document Upload',
            description: (standaloneMode ? standaloneOneTimeConstructionPriceEstimatorStages : activeOneTimeConstructionStages)[currentStage]?.description || '',
          }}
          currentStage={currentStage}
          totalStages={(standaloneMode ? standaloneOneTimeConstructionPriceEstimatorStages : activeOneTimeConstructionStages).length}
          stages={(standaloneMode ? standaloneOneTimeConstructionPriceEstimatorStages : activeOneTimeConstructionStages).map((stage) => stage.name)}
          workflowName={flowName}
          workflowId={flowId}
          numItemsDetected={numItemsDetected}
          {...stageRatingProps}
          onBack={() => {
            if (standaloneMode) return;
            if (currentStage === 2 && !effectiveIsViewOnly) {
              setSowBackTarget(1);
              setShowSowBackDialog(true);
            } else if (currentStage > 0) {
              const previous = currentStage - 1;
              localStorage.setItem(`one_time_construction_stage_${flowId}`, String(previous));
              setCurrentStage(previous);
            }
          }}
          onStageSelect={(index) => {
            if (standaloneMode) {
              if (index === 0) setCurrentStage(0);
              return;
            }
            if (effectiveIsViewOnly) {
              localStorage.setItem(`one_time_construction_stage_${flowId}`, String(index));
              setCurrentStage(index);
              return;
            }
            if (index < currentStage && currentStage === 2) {
              setSowBackTarget(resolveOneTimeConstructionEditReturnStage(index));
              setShowSowBackDialog(true);
            } else if (index <= maxStageReached) {
              localStorage.setItem(`one_time_construction_stage_${flowId}`, String(index));
              setCurrentStage(index);
            } else {
              showNotificationMessage('Please complete the previous stages first', 'info');
            }
          }}
        >
          {renderOneTimeConstructionStage()}
        </StageWrapper>
      ) : workflowType === 'construction_sow_generation' ? (
        <StageWrapper
          config={{
            id: generationConstructionStages[currentStage]?.id || 'construction-requirement-intake',
            name: generationConstructionStages[currentStage]?.name || 'Define Requirement',
            description: generationConstructionStages[currentStage]?.description || '',
          }}
          currentStage={currentStage}
          totalStages={generationConstructionStages.length}
          stages={generationConstructionStages.map((stage) => stage.name)}
          workflowName={flowName}
          workflowId={flowId}
          numItemsDetected={numItemsDetected}
          {...stageRatingProps}
          onBack={() => { if (currentStage > 0) { localStorage.setItem(`construction_sow_generation_stage_${flowId}`, String(currentStage - 1)); setCurrentStage(currentStage - 1); } }}
          onStageSelect={(index) => {
            if (effectiveIsViewOnly || index <= maxStageReached) { localStorage.setItem(`construction_sow_generation_stage_${flowId}`, String(index)); setCurrentStage(index); }
            else showNotificationMessage('Please complete the previous stages first', 'info');
          }}
        >
          {renderConstructionSowGenerationStage()}
        </StageWrapper>
      ) : workflowType === 'services_pws_generation' ? (
        <StageWrapper
          config={{
            id: servicesPwsGenerationStages[currentStage]?.id || 'services-pws-requirement-intake',
            name: servicesPwsGenerationStages[currentStage]?.name || 'Define Service Requirement',
            description: servicesPwsGenerationStages[currentStage]?.description || '',
          }}
          currentStage={currentStage}
          totalStages={servicesPwsGenerationStages.length}
          stages={servicesPwsGenerationStages.map((stage) => stage.name)}
          workflowName={flowName}
          workflowId={flowId}
          numItemsDetected={numItemsDetected}
          {...stageRatingProps}
          onBack={() => {
            if (currentStage > 0) {
              localStorage.setItem(`services_pws_generation_stage_${flowId}`, String(currentStage - 1));
              setCurrentStage(currentStage - 1);
            }
          }}
          onStageSelect={(index) => {
            if (effectiveIsViewOnly || index <= maxStageReached) {
              localStorage.setItem(`services_pws_generation_stage_${flowId}`, String(index));
              setCurrentStage(index);
            } else showNotificationMessage('Please complete the previous stages first', 'info');
          }}
        >
          {renderServicesPwsGenerationStage()}
        </StageWrapper>
      ) : workflowType === 'complex_services' && servicesPwsOrigin === 'requirements_generation' && currentStage < 3 ? (
        <StageWrapper
          config={{
            id: servicesPwsGenerationStages[currentStage]?.id || 'services-pws-requirement-intake',
            name: servicesPwsGenerationStages[currentStage]?.name || 'Define Service Requirement',
            description: servicesPwsGenerationStages[currentStage]?.description || '',
          }}
          currentStage={currentStage}
          totalStages={servicesPwsGenerationStages.length}
          stages={servicesPwsGenerationStages.map((stage) => stage.name)}
          workflowName={flowName}
          workflowId={flowId}
          numItemsDetected={numItemsDetected}
          {...stageRatingProps}
          onBack={() => {
            if (currentStage > 0) {
              const previous = currentStage - 1;
              localStorage.setItem(`complex_services_stage_${flowId}`, String(previous));
              setCurrentStage(previous);
            }
          }}
          onStageSelect={(index) => {
            if (effectiveIsViewOnly || index <= maxStageReached) {
              localStorage.setItem(`complex_services_stage_${flowId}`, String(index));
              setCurrentStage(index);
            } else showNotificationMessage('Please complete the previous stages first', 'info');
          }}
        >
          {renderGeneratedComplexServicesPrefixStage()}
        </StageWrapper>
      ) : workflowType === 'complex_services' ? (
        // Complex Services — DYNAMIC stepper (full 17-stage master order, greyed
        // in place). Saving the terminal `record` stage (609) leaves this
        // stepper open so completed work remains reviewable.
        <StageWrapper
          config={{
            id: complexServicesStages[currentStage]?.id || 'complex-services-sow-upload',
            name: complexServicesStages[currentStage]?.name || 'PWS Upload',
            description: complexServicesStages[currentStage]?.description || '',
          }}
          currentStage={currentStage}
          totalStages={complexServicesStages.length}
          stages={complexServicesStages.map((s) => s.name)}
          disabledStageIndices={complexDisabledIndices}
          disabledStageReasons={complexDisabledReasons}
          workflowName={flowName}
          workflowId={flowId}
          numItemsDetected={numItemsDetected}
          {...stageRatingProps}
          onBack={() => {
            const prev = prevEnabledComplexStage(currentStage - 1, activeComplexApplicableSet);
            if (prev !== null) {
              localStorage.setItem(`complex_services_stage_${flowId}`, String(prev));
              setCurrentStage(prev);
            } else if (servicesPwsOrigin === 'requirements_generation') {
              localStorage.setItem(`complex_services_stage_${flowId}`, '2');
              setCurrentStage(2);
            }
          }}
          onStageSelect={(index) => {
            if (!isComplexStageEnabled(index, activeComplexApplicableSet)) return;
            if (isViewOnly || index <= maxStageReached) {
              localStorage.setItem(`complex_services_stage_${flowId}`, String(index));
              setCurrentStage(index);
            } else {
              showNotificationMessage('Please complete the previous stages first', 'info');
            }
          }}
        >
          {renderComplexServicesStage()}
        </StageWrapper>
      ) : workflowType === 'gpc' ? (
        // Fixed base + form indices; availability controls navigation, while
        // only the server completion verdict can finish the workflow.
        gpcNavigationLoading ? (
          <Container maxWidth="xl" sx={{ py: 8, display: 'flex', justifyContent: 'center' }} data-testid="gpc-workflow-navigation-loading">
            <CircularProgress aria-label="Loading GPC workflow" />
          </Container>
        ) : isComplete ? (
          <Container maxWidth="xl" sx={{ py: 4 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              {gpcThreshold?.sourcingPath === 'mandatory_source'
                ? 'GPC workflow complete. The mandatory-source package, required forms, and eCARS sign-off are saved.'
                : 'GPC workflow complete. The threshold determination, documented open-market prices, and required forms are saved.'}
            </Alert>
          </Container>
        ) : (
          <StageWrapper
            config={{
              id: gpcStages[currentStage]?.id || 'gpc-questions',
              name: gpcStages[currentStage]?.name || 'Clarify Requirements',
              description: gpcStages[currentStage]?.description || '',
            }}
            currentStage={currentStage}
            totalStages={gpcStages.length}
            stages={gpcStages.map(s => s.name)}
            disabledStageIndices={gpcDisabledFormStageIndices}
            disabledStageReasons={gpcDisabledFormStageReasons}
            workflowName={flowName}
            workflowId={flowId}
            numItemsDetected={numItemsDetected}
            {...stageRatingProps}
            onBack={() => {
              // Back skips BACKWARD over any greyed/inapplicable stages so the
              // user always lands on an enabled stage.
              const prev = prevEnabledGpcStage(currentStage - 1, gpcAvailableSet);
              if (prev !== null) {
                localStorage.setItem(`gpc_stage_${flowId}`, String(prev));
                setCurrentStage(prev);
              }
            }}
            onStageSelect={(index) => {
              // Disabled stages are non-clickable in the stepper, but guard here
              // too: ignore selection of any greyed/inapplicable stage.
              if (!isGpcStageEnabled(index, gpcAvailableSet)) {
                return;
              }
              if (isViewOnly || index <= maxStageReached) {
                localStorage.setItem(`gpc_stage_${flowId}`, String(index));
                setCurrentStage(index);
              } else {
                showNotificationMessage('Please complete the previous stages first', 'info');
              }
            }}
          >
            {renderGPCStage()}
          </StageWrapper>
        )
      ) : workflowType === 'market_research' && marketResearchClientUpgradeRequired ? (
        <Container
          maxWidth="md"
          sx={{ py: 8 }}
          data-testid="mr-brand-name-ja-client-unavailable"
        >
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Workflow update required
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              This workflow requires Brand-name J&amp;A support that is not enabled in this client.
              Reload the application or return to Manage Workflows without advancing this workflow.
            </Typography>
            <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
              <Button variant="contained" onClick={() => window.location.reload()}>
                Reload application
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  resetPipelineState();
                  setFlowId(null);
                  setFlowName('');
                  setWorkflowType(null);
                  setFlowSelectorScope('market_research');
                  setAppPage('flow_selector');
                }}
              >
                Back to workflows
              </Button>
            </Box>
          </Paper>
        </Container>
      ) : isCustomerMarketResearch
        && marketResearchCustomerTerminal
        && !marketResearchCustomerTerminalStageView
        && flowId ? (
        <StageWrapper
          // This final row is a Customer presentation destination, never a
          // durable Market Research stage. It is appended only to this local
          // stepper view and deliberately has neither a backend stage code nor
          // a numeric StepIcon.
          config={{
            id: 'market-research-post-handoff-progress',
            name: 'Handoff & contracting progress',
            description: 'Review the downstream completion summary after handoff.',
          }}
          currentStage={marketResearchStages.length}
          totalStages={marketResearchStepperStages.length}
          stages={marketResearchStepperStages}
          compactNavigation={compactMarketResearchNavigation}
          unnumberedStageIndices={[marketResearchStages.length]}
          updatedStageIndices={marketResearchUpdatedStageIndices}
          workflowName={flowName}
          workflowId={flowId}
          numItemsDetected={numItemsDetected}
          onBack={() => {
            // This is an unnumbered presentation route. Returning from it
            // restores only the Customer's own Section C terminal route and never
            // writes a synthetic current/completed stage to the backend.
            setMarketResearchCustomerTerminal(true);
            setMarketResearchCustomerTerminalStageView(true);
            setCurrentStage(marketResearchStageIndexForBackend(8));
          }}
          onStageSelect={(stageIndex) => {
            // Earlier Customer stages remain reviewable. Selecting the
            // terminal row is intentionally a no-op; it must never become a
            // currentStage/completedStages value or generic backend stage.
            if (stageIndex >= marketResearchStages.length) return;
            setMarketResearchCustomerTerminal(true);
            setMarketResearchCustomerTerminalStageView(true);
            setCurrentStage(stageIndex);
          }}
          transitionKey="market-research-post-handoff-progress"
        >
          <MarketResearchPostHandoffProgress
            flowId={flowId}
            viewerEmail={userEmail}
            isSampleFlow={isTrainingExample}
            onBack={() => {
              setMarketResearchCustomerTerminal(true);
              setMarketResearchCustomerTerminalStageView(true);
              setCurrentStage(marketResearchStageIndexForBackend(8));
            }}
            onStageSelect={(backendCode) => {
              const stageIndex = marketResearchStages.findIndex((stage) => stage.backendCode === backendCode);
              if (stageIndex < 0 || marketResearchNavigation.disabledStageIndices.includes(stageIndex)) return false;
              setMarketResearchCustomerTerminal(true);
              setMarketResearchCustomerTerminalStageView(true);
              setCurrentStage(stageIndex);
              return true;
            }}
          />
        </StageWrapper>
      ) : hasConfirmedContractingMarketResearchFlow
        && marketResearchRaCollaborationOpen
        && flowId ? (
        <StageWrapper
          config={{
            id: 'market-research-ra-collaboration',
            name: 'Chat with Mission Partner',
            description: 'Chat with the mission partner after handoff.',
          }}
          currentStage={marketResearchStages.length}
          totalStages={marketResearchStepperStages.length}
          stages={marketResearchStepperStages}
          compactNavigation={compactMarketResearchNavigation}
          unnumberedStageIndices={[marketResearchStages.length]}
          disabledStageIndices={marketResearchStepperDisabledStageIndices}
          disabledStageReasons={marketResearchStepperDisabledStageReasons}
          updatedStageIndices={marketResearchUpdatedStageIndices}
          workflowName={flowName}
          workflowId={flowId}
          numItemsDetected={numItemsDetected}
          onBack={() => setMarketResearchRaCollaborationOpen(false)}
          onStageSelect={(stageIndex) => {
            if (stageIndex === marketResearchRaCollaborationIndex) return;
            setMarketResearchRaCollaborationOpen(false);
            void handleStageSelect(stageIndex);
          }}
          transitionKey="market-research-ra-collaboration"
        >
          <Paper component="main" sx={{ mx: 'auto', my: 4, p: { xs: 2.5, sm: 4 }, maxWidth: 860 }}>
            <Stack spacing={3}>
              <Box>
                <Typography component="h1" variant="h4" gutterBottom>Chat with Mission Partner</Typography>
                <Typography color="text.secondary">
                  Send messages to the mission partner for this workflow after handoff.
                </Typography>
              </Box>
              {marketResearchContractingHandoffVerifiedFlowId === flowId && <MarketResearchEditActivity
                flowId={flowId}
                handoffState={marketResearchContractingHandoffState}
                onNavigate={(backendCode) => {
                  const stageIndex = marketResearchStages.findIndex((stage) => stage.backendCode === backendCode);
                  if (stageIndex < 0 || marketResearchNavigation.disabledStageIndices.includes(stageIndex)) return false;
                  setMarketResearchRaCollaborationOpen(false);
                  void handleStageSelect(stageIndex);
                  return true;
                }}
              />}
              <MarketResearchHandoffConversation
                flowId={flowId}
                handoffState={marketResearchContractingHandoffState}
                counterpartLabel="Mission Partner"
                viewerEmail={userEmail}
                onHandoffState={(state) => {
                  setMarketResearchContractingHandoffState(state);
                  setMarketResearchContractingHandoffVerifiedFlowId(flowId);
                }}
              />
              <Box sx={{ display: 'flex' }}>
                <Button variant="outlined" onClick={() => setMarketResearchRaCollaborationOpen(false)}>Back</Button>
              </Box>
            </Stack>
          </Paper>
        </StageWrapper>
      ) : shouldRenderMarketResearchStage(
        isComplete,
        marketResearchStages[currentStage]?.backendCode,
        marketResearchDd2579Enabled,
      ) ? (
        // Market Research workflow rendering
        <>
          {marketResearchDd2579CoordinationStatus === 'loading' && marketResearchStages[currentStage]?.backendCode === 12 && (
            <Container maxWidth="xl" sx={{ pt: 2 }}>
              <Alert severity="info" icon={<CircularProgress size={20} />}>
                Checking whether DD/DAF 2579 is required…
              </Alert>
            </Container>
          )}
          {marketResearchDd2579CoordinationStatus === 'error' && marketResearchStages[currentStage]?.backendCode === 12 && (
            <Container maxWidth="xl" sx={{ pt: 2 }}>
              <Alert
                severity="warning"
                action={flowId ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      void loadMarketResearchDd2579Coordination(flowId).catch((error) => {
                        console.error('Failed to retry Market Research DD 2579 coordination:', error);
                      });
                    }}
                  >
                    Retry DD/DAF 2579 check
                  </Button>
                ) : undefined}
              >
                DD/DAF 2579 applicability could not be verified. Progress remains locked until the check succeeds.
              </Alert>
            </Container>
          )}
          {!(marketResearchStages[currentStage]?.backendCode === 13 && !marketResearchDd2579Enabled) && (
            <StageWrapper
              config={marketResearchStages[currentStage]}
              currentStage={currentStage}
              totalStages={marketResearchStepperStages.length}
              stages={marketResearchStepperStages}
              compactNavigation={compactMarketResearchNavigation}
              workflowName={flowName}
              workflowId={flowId}
              numItemsDetected={numItemsDetected}
              {...stageRatingProps}
              onBack={handleBack}
              onStageSelect={(stageIndex) => {
                if (stageIndex === marketResearchPostHandoffProgressIndex) {
                  if (!marketResearchCustomerTerminal) {
                    showNotificationMessage(
                      'Complete Section C before viewing contracting progress.',
                      'info',
                    );
                    return;
                  }
                  setMarketResearchCustomerTerminalStageView(false);
                  return;
                }
                if (stageIndex === marketResearchRaCollaborationIndex) {
                  if (!isMarketResearchHandoffAvailable(marketResearchContractingHandoffState)) {
                    showNotificationMessage('Chat with Mission Partner opens after handoff.', 'info');
                    return;
                  }
                  setMarketResearchRaCollaborationOpen(true);
                  return;
                }
                void handleStageSelect(stageIndex);
              }}
              standaloneMode={standaloneMode}
              standaloneStartIndex={marketResearchStageIndexForBackend(12)}
              disabledStageIndices={marketResearchStepperDisabledStageIndices}
              disabledStageReasons={marketResearchStepperDisabledStageReasons}
              updatedStageIndices={marketResearchUpdatedStageIndices}
              marketResearchBackendStageCode={marketResearchStages[currentStage]?.backendCode}
              marketResearchInlineEdits={marketResearchCurrentStageInlineEdits}
              unnumberedStageIndices={marketResearchPostHandoffProgressIndex !== null
                ? [marketResearchPostHandoffProgressIndex]
                : marketResearchRaCollaborationIndex !== null
                  ? [marketResearchRaCollaborationIndex]
                  : undefined}
              transitionKey={`market-research-${marketResearchStages[currentStage]?.backendCode ?? 'unknown'}`}
            >
              {renderStage()}
            </StageWrapper>
          )}
        </>
      ) : (
        <Container maxWidth="xl" sx={{ py: 4 }}>
          {renderStage()}
        </Container>
      )}
      
      {/* SOW Verification Edit Mode Back Confirmation Dialog */}
      <Dialog open={showSowBackDialog} onClose={() => setShowSowBackDialog(false)}>
        <DialogTitle>
          {sowBackTarget === 0 ? 'Return to Document Upload?' : 'Return to Suggestions?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            You will lose your manual edits. Export the PDF to save your changes externally.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSowBackDialog(false)}>Cancel</Button>
          <Button
            onClick={() => {
              setShowSowBackDialog(false);
              const sowStorageKey = workflowType === 'one_time_construction'
                ? `one_time_construction_stage_${flowId}`
                : `sow_stage_${flowId}`;
              localStorage.setItem(sowStorageKey, String(sowBackTarget));
              setCurrentStage(sowBackTarget);
            }}
            variant="contained"
            color="warning"
          >
            Go Back
          </Button>
        </DialogActions>
      </Dialog>

      {/* Services Manual Edit Back Confirmation Dialog */}
      <Dialog open={showServicesBackDialog} onClose={() => setShowServicesBackDialog(false)}>
        <DialogTitle>Return to Suggestions?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Going back to Suggestions will discard any manual edits made here.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowServicesBackDialog(false)}>Cancel</Button>
          <Button
            onClick={() => {
              setShowServicesBackDialog(false);
              localStorage.setItem(`services_stage_${flowId}`, String(currentStage - 1));
              setCurrentStage(currentStage - 1);
            }}
            variant="contained"
            color="warning"
          >
            Discard Edits & Go Back
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backward Navigation Warning Dialog */}
      <Dialog open={backNavDialog.open} onClose={handleBackNavCancel}>
        <DialogTitle>Navigate to earlier stage?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Modifying previous stages may invalidate results in later stages.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBackNavCancel}>Cancel</Button>
          <Button onClick={handleBackNavConfirm} variant="contained">Continue</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={marketResearchBrandNameJaFeatureEnabled && marketResearchRefreshRequirement !== null}
        onClose={() => setMarketResearchRefreshRequirement(null)}
        aria-labelledby="market-research-refresh-required-title"
      >
        <DialogTitle id="market-research-refresh-required-title">Refresh required</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This workflow changed in another session. Refresh before continuing to the required Brand-name J&amp;A.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarketResearchRefreshRequirement(null)}>Stay here</Button>
          <Button onClick={() => window.location.reload()} variant="contained">
            Refresh workflow
          </Button>
        </DialogActions>
      </Dialog>
      </WorkflowChatCompanion>
      )}
        </Suspense>
        </ErrorBoundary>
      </Box>

      <Footer />
      </Box>
      )}
      </Transition>}

      <WorkflowAccessDialog
        open={flowRevalidationOpen || (isTrainingPage
          && (appPage !== 'ra_training' || permissionsLoadFailed || rolePreviewSyncError)
          && (!currentPermissionsLoaded || flowRevalidation !== null
            || trainingAccessRevoked))}
        status={trainingAccessRevoked && flowRevalidation?.kind !== 'changed'
          ? { kind: 'changed', message: 'Training access changed. Leave and reopen Training before continuing.' }
          : flowRevalidation}
        onLeave={isTrainingPage ? leaveTraining : leaveBlockedWorkflow}
        onRetry={() => {
          if (isTrainingPage
            && (flowRevalidation?.kind === 'changed' || trainingAccessRevoked)) {
            leaveTraining();
            return;
          }
          setFlowRevalidation({ kind: 'checking' });
          setFlowRevalidationRetryVersion((version) => version + 1);
          if (rolePreviewSyncError
            || (activeFlowRoleRef.current && activeFlowRoleRef.current !== rolePreview)) {
            setRolePreviewRetry((version) => version + 1);
          } else {
            if (isTrainingPage) {
              permissionsRequestGenerationRef.current += 1;
              setPermissionsScope(null);
              setWorkflowPermissionsLoaded(false);
            }
            setPermissionsRefreshVersion((version) => version + 1);
          }
        }}
      />

      {/* Global notification snackbar — mounted at the top level (outside the
          workflow-only branch) so feedback/status messages appear on every page,
          including the homepage and Saved Workflows. */}
      <Snackbar
        open={showNotification}
        autoHideDuration={4000}
        onClose={() => setShowNotification(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowNotification(false)}
          severity={notificationType}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notificationType === 'error' ? toUserFacingError(notificationMessage) : notificationMessage}
          {notificationAction && (
            <>
              {' '}
              <Link
                href="#my-feedback"
                color="inherit"
                onClick={(event) => {
                  event.preventDefault();
                  notificationAction.onClick();
                }}
                data-testid="feedback-submission-my-feedback-link"
                sx={{ fontWeight: 'inherit', textDecoration: 'underline' }}
              >
                {notificationAction.label}
              </Link>
            </>
          )}
        </Alert>
      </Snackbar>

      {!showEmailGate && !authChecking && userEmail && (
        <MandatoryChatFeedbackGate key={userEmail} />
      )}
      </ToastProvider>
    </ThemeProvider>
  );
}

export default function AppRoot() {
  return (
    // App-wide motion shell (spec §5.1). `strict` forbids accidental full-`motion`
    // imports so the bundle stays minimal — motion primitives MUST use the
    // lightweight `m`/`m.create` from `motion/react-m` (verified: `m.create(MUI)`
    // works under strict; full `motion.create` throws). `reducedMotion` is driven
    // by the single `RESPECT_REDUCED_MOTION` switch: 'never' (default) ignores the
    // OS setting and always animates; flip the flag to 'user' to respect it (§9).
    <LazyMotion features={loadMotionFeatures} strict>
      <MotionConfig reducedMotion={RESPECT_REDUCED_MOTION ? 'user' : 'never'}>
        <ColorModeProvider>
          <DocumentAssistantProvider><App /></DocumentAssistantProvider>
        </ColorModeProvider>
      </MotionConfig>
    </LazyMotion>
  );
}
